// src/AppPrincipale.jsx — SOLSTICE v68 (migré Supabase)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAuth } from './contexts/AuthContext'
import { useSupabaseData } from './hooks/useSupabaseData'
import { supabase } from './supabase'
import ImportMigration from './pages/ImportMigration'
import GestionUtilisateurs from './pages/GestionUtilisateurs'

// ============================================
// HOOKS PERSONNALISÉS — OPTIMISATIONS CATALOGUE
// ============================================

// Hook debounce pour la recherche instantanée
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debouncedValue;
};

// Hook virtualisation de liste (rendu uniquement des items visibles)
const useVirtualList = (items, itemHeight, overscan = 5) => {
    const containerRef = useRef(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [containerHeight, setContainerHeight] = useState(400);
    const visibleRange = useMemo(() => {
        const start = Math.floor(scrollTop / itemHeight);
        const visibleCount = Math.ceil(containerHeight / itemHeight);
        return {
            start: Math.max(0, start - overscan),
            end: Math.min(items.length, start + visibleCount + overscan)
        };
    }, [scrollTop, containerHeight, itemHeight, items.length, overscan]);
    const visibleItems = useMemo(() =>
        items.slice(visibleRange.start, visibleRange.end).map((item, idx) => ({
            ...item, index: visibleRange.start + idx
        })),
        [items, visibleRange]
    );
    const totalHeight = items.length * itemHeight;
    const onScroll = useCallback((e) => { setScrollTop(e.target.scrollTop); }, []);
    useEffect(() => {
        if (containerRef.current) setContainerHeight(containerRef.current.clientHeight);
    }, []);
    return { containerRef, visibleItems, totalHeight, onScroll, startIndex: visibleRange.start };
};

// ===== COMPOSANT PAGINATION RÉUTILISABLE (charte SOLSTICE) =====
function PaginationBar({ currentPage, totalPages, perPage, totalItems, onPageChange, onPerPageChange, itemLabel = 'élément', itemLabelPlural = null }) {
  const start = totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const end   = Math.min(currentPage * perPage, totalItems);

  // Génère les numéros de pages à afficher (max 5 boutons)
  const pages = [];
  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push('…');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('…');
    pages.push(totalPages);
  }

  const [inputVal, setInputVal] = React.useState(String(currentPage));
  React.useEffect(() => { setInputVal(String(currentPage)); }, [currentPage]);

  const goTo = (p) => {
    const n = Math.max(1, Math.min(totalPages, +p));
    if (!isNaN(n)) onPageChange(n);
  };

  if (totalItems === 0) return null;

  return (
    <div className="sol-pagination-bar">
      {/* Sélecteur nombre par page */}
      <span className="sol-pg-lbl">Afficher</span>
      <select className="sol-pg-select" value={perPage} onChange={e => { onPerPageChange(+e.target.value); onPageChange(1); }}>
        {[5, 10, 20, 30, 50, 100].map(v => (
          <option key={v} value={v}>{v} / page</option>
        ))}
      </select>

      <div className="sol-pg-sep" />

      {/* Navigation */}
      <button className="sol-pg-btn" onClick={() => goTo(1)} disabled={currentPage === 1} title="Première page">«</button>
      <button className="sol-pg-btn" onClick={() => goTo(currentPage - 1)} disabled={currentPage === 1} title="Page précédente">‹</button>

      {/* Numéros de pages */}
      {pages.map((p, i) =>
        p === '…'
          ? <span key={`ell-${i}`} className="sol-pg-lbl" style={{ padding: '0 4px' }}>…</span>
          : <button key={p} className={`sol-pg-btn${p === currentPage ? ' active' : ''}`} onClick={() => goTo(p)}>{p}</button>
      )}

      <button className="sol-pg-btn" onClick={() => goTo(currentPage + 1)} disabled={currentPage === totalPages} title="Page suivante">›</button>
      <button className="sol-pg-btn" onClick={() => goTo(totalPages)} disabled={currentPage === totalPages} title="Dernière page">»</button>

      <div className="sol-pg-sep" />

      {/* Aller à la page */}
      <span className="sol-pg-lbl">Aller à</span>
      <input
        className="sol-pg-input"
        type="number"
        min="1"
        max={totalPages}
        value={inputVal}
        onChange={e => setInputVal(e.target.value)}
        onBlur={e => goTo(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') goTo(e.target.value); }}
      />
      <span className="sol-pg-lbl">/ {totalPages}</span>

      <div className="sol-pg-spacer" />

      {/* Badge info */}
      <span className="sol-pg-badge">
        <strong>{start}–{end}</strong> sur <strong>{totalItems}</strong>
      </span>
      <span className="sol-pg-total">{totalPages} page{totalPages > 1 ? 's' : ''} · {totalItems} {totalItems > 1 ? (itemLabelPlural || itemLabel + 's') : itemLabel}</span>
    </div>
  );
}

function AppPrincipale() {

// ══ SUPABASE DATA HOOK ════════════════════════════════════════════════════════
const {
  documents, clients, fournisseurs, bonsCommande,
  bordereauxLivraison, setBordereauxLivraison,
  catalogue, catalogueLoaded, settings,
  saveDocument,    deleteDocument:   _deleteDocument,
  saveClient,      deleteClient:     _deleteClient,
  saveFournisseur, deleteFournisseur:_deleteFournisseur,
  getBonCommande, saveBonCommande, deleteBonCommande:_deleteBonCommande,
  saveCatalogueArticle, deleteCatalogueArticle, toggleActifArticle: _toggleActif,
  saveSettings,    reloadCatalogue,
  lockDocument, unlockDocument, checkDocumentLock,
} = useSupabaseData()
const { profil, can, logout } = useAuth()

const mdpFormatD = (pwd) => {
  if (pwd.length < 12) return 'Minimum 12 caractères'
  if ((pwd.match(/[A-Z]/g)||[]).length < 2) return 'Au moins 2 majuscules'
  if ((pwd.match(/[0-9]/g)||[]).length < 2) return 'Au moins 2 chiffres'
  if (!/[@#$!%*?&]/.test(pwd)) return 'Au moins 1 caractère spécial (@#$!%*?&)'
  if (/(.)\1\1/.test(pwd)) return '3 caractères identiques consécutifs interdits'
  return null
}
const mdpForce = (pwd) => {
  let score = 0
  if (pwd.length >= 12) score++
  if (pwd.length >= 16) score++
  if ((pwd.match(/[A-Z]/g)||[]).length >= 2) score++
  if ((pwd.match(/[0-9]/g)||[]).length >= 2) score++
  if (/[@#$!%*?&]/.test(pwd)) score++
  if (score <= 2) return { label: 'Faible', color: '#EF4444', pct: 33 }
  if (score <= 3) return { label: 'Moyen', color: '#F59E0B', pct: 60 }
  return { label: 'Fort', color: '#10B981', pct: 100 }
}
const genererMotDePasse = () => {
  const U = 'ABCDEFGHJKLMNPQRSTUVWXYZ', L = 'abcdefghjkmnpqrstuvwxyz'
  const D = '23456789', S = '@#$!%*?&'
  const pick = s => s[Math.floor(Math.random()*s.length)]
  const all = U+L+D+S
  let tries = 0
  while (tries++ < 100) {
    let p = pick(U)+pick(U)+pick(D)+pick(D)+pick(S)
    while (p.length < 14) p += pick(all)
    p = p.split('').sort(()=>Math.random()-0.5).join('')
    if (!mdpFormatD(p)) return p
  }
  return 'SOLstice@2026!'
}
const resetMdpModal = () => {
  setShowChangerMdp(false); setMdpStep(1)
  setMdpForm({ actuel:'', nouveau:'', confirm:'' }); setMdpError('')
  setShowMdpNouv(false); setShowMdpConf(false); setMdpSucces(false)
}
const verifierMdpActuel = async () => {
  if (!mdpForm.actuel) { setMdpError('Veuillez saisir votre mot de passe actuel.'); return }
  setMdpLoading(true); setMdpError('')
  try {
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
    const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY
    const resp = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': ANON_KEY },
      body: JSON.stringify({ email: profil?.email, password: mdpForm.actuel }),
    })
    if (!resp.ok) { setMdpError('Mot de passe actuel incorrect.'); return }
    setMdpStep(2)
  } catch { setMdpError('Erreur de vérification.') }
  finally { setMdpLoading(false) }
}
const changerMotDePasse = async () => {
  const err = mdpFormatD(mdpForm.nouveau)
  if (err) { setMdpError(err); return }
  if (mdpForm.nouveau !== mdpForm.confirm) { setMdpError('Les mots de passe ne correspondent pas.'); return }
  setMdpLoading(true); setMdpError('')
  try {
    const raw = localStorage.getItem('solstice-auth')
    const s = raw ? JSON.parse(raw) : null
    const token = s?.access_token || s?.session?.access_token || ''
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
    const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY
    const resp = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'apikey': ANON_KEY },
      body: JSON.stringify({ password: mdpForm.nouveau }),
    })
    if (!resp.ok) { const b = await resp.json(); throw new Error(b.msg || b.message || `Erreur ${resp.status}`) }
    setMdpSucces(true)
    setTimeout(() => { resetMdpModal(); sessionStorage.removeItem("2fa_done"); sessionStorage.removeItem("2fa_awaiting"); localStorage.removeItem("solstice-auth"); window.location.reload() }, 4000)
  } catch (err) {
    setMdpError('Erreur : ' + err.message)
    setMdpLoading(false)
  }
}

// ── Agent automatique ──────────────────────────────────────────────────────
// Le nom de l'utilisateur connecté remplace le champ "Agent" des paramètres.
// Cela permet que les numéros de documents reflètent l'auteur de la connexion.
const settingsEffectifs = useMemo(() => {
  try {
    return {
      ...settings,
      agent: profil?.nom || settings?.agent || '',
    }
  } catch(e) {
    return { ...settings }
  }
}, [settings, profil])
// ──────────────────────────────────────────────────────────────────────────
const [showGestionUsers,    setShowGestionUsers]    = useState(false)
const [showImportMigration, setShowImportMigration] = useState(false)
// ── Locking optimiste ──────────────────────────────────────────────────────
const [lockWarning, setLockWarning] = useState(null) // { nom: string } | null
const lockedDocIdRef = useRef(null)  // UUID du document qu'on a verrouillé
const [isSaving, setIsSaving] = useState(false)
const [insertMenu, setInsertMenu] = useState(null)       // { index, top, left } | null
const [hoveredHandleIndex, setHoveredHandleIndex] = useState(null)
// ══════════════════════════════════════════════════════════════════════════════

// ===== ÉTATS FOURNISSEURS =====
const [showFournisseurs, setShowFournisseurs] = useState(false);
const [showFournisseurForm, setShowFournisseurForm] = useState(false);
const [currentFournisseur, setCurrentFournisseur] = useState(null);
const [searchFournisseur, setSearchFournisseur] = useState('');
// ===== ÉTATS BONS DE COMMANDE =====
const [showBCModal, setShowBCModal] = useState(false);
const [showBCPreview, setShowBCPreview] = useState(false);
const [currentBC, setCurrentBC] = useState(null);
const [showFournisseurSelectInBC, setShowFournisseurSelectInBC] = useState(false);
const [searchFournisseurBC, setSearchFournisseurBC] = useState('');
const [filterStatusBC, setFilterStatusBC] = useState('all');
const [searchBC, setSearchBC] = useState('');
const [bcPleinEcran, setBcPleinEcran] = useState(false);
const [isBCSaving, setIsBCSaving] = useState(false);
const [showChangerMdp, setShowChangerMdp] = useState(false);
const [mdpStep, setMdpStep] = useState(1);
const [mdpForm, setMdpForm] = useState({ actuel: '', nouveau: '', confirm: '' });
const [mdpError, setMdpError] = useState('');
const [mdpLoading, setMdpLoading] = useState(false);
const [mdpSucces, setMdpSucces] = useState(false);
const [showMdpNouv, setShowMdpNouv] = useState(false);
const [showMdpConf, setShowMdpConf] = useState(false);
// ===== FIN ÉTATS NOUVEAUX =====
const [showModal, setShowModal] = useState(false);
const [showSettings, setShowSettings] = useState(false);
const [showPreview, setShowPreview] = useState(false);
const [showClients, setShowClients] = useState(false);
const [showClientForm, setShowClientForm] = useState(false);
const [showBackupMenu, setShowBackupMenu] = useState(false);
const [showStatistiques, setShowStatistiques] = useState(false);
const [showCatalogue, setShowCatalogue] = useState(false);
const [cataloguePleinEcran, setCataloguePleinEcran] = useState(false);
// ── Enrichissement IA Catalogue ────────────────────────────────────────────
const [showEnrichissementIA, setShowEnrichissementIA] = useState(false);
const [enrichUrl, setEnrichUrl] = useState('');
const [enrichMarque, setEnrichMarque] = useState('');
const [enrichDomaine, setEnrichDomaine] = useState('');
const [enrichCodeDomaine, setEnrichCodeDomaine] = useState('');
const [enrichCatMere, setEnrichCatMere] = useState('');
const [enrichCodeCatMere, setEnrichCodeCatMere] = useState('');
const [enrichCatFille, setEnrichCatFille] = useState('');
const [enrichCodeCatFille, setEnrichCodeCatFille] = useState('');
const [enrichResultats, setEnrichResultats] = useState([]);
const [enrichSelection, setEnrichSelection] = useState([]);
const [enrichLoading, setEnrichLoading] = useState(false);
const [enrichErreur, setEnrichErreur] = useState(null);
const [enrichEtape, setEnrichEtape] = useState('form'); // 'form' | 'loading' | 'resultats'
const [docsPleinEcran, setDocsPleinEcran] = useState(false);
const [showConfigActions, setShowConfigActions] = useState(false);
const [actionsVisibles, setActionsVisibles] = useState(() => {
try {
const saved = localStorage.getItem('solstice_actions_visibles');
if (saved) return JSON.parse(saved);
} catch(e) {}
return {
visualiser: true,
editer: true,
avoir: true,
dupliquer: true,
imprimer: true,
supprimer: true
};
});
const [showConfigActionsBC, setShowConfigActionsBC] = useState(false);
const [actionsVisiblesBC, setActionsVisiblesBC] = useState(() => {
try {
const saved = localStorage.getItem('solstice_actions_visibles_bc');
if (saved) return JSON.parse(saved);
} catch(e) {}
return {
apercu: true,
editer: true,
statut: true,
dupliquer: true,
imprimer: true,
supprimer: true
};
});
const [catalogueSearchInput, setCatalogueSearchInput] = useState(''); // Input immédiat (affichage)
const catalogueSearch = useDebounce(catalogueSearchInput, 300); // Debounced (filtre)
const [filtresDomaine, setFiltresDomaine] = useState('tous');
const [filtresCatMere, setFiltresCatMere] = useState('toutes');
const [filtresCatFille, setFiltresCatFille] = useState('toutes');
const [cataloguePage, setCataloguePage] = useState(1); // Pagination catalogue
const [showCatalogueArticleModal, setShowCatalogueArticleModal] = useState(false);
const [editingCatalogueArticle, setEditingCatalogueArticle] = useState(null);
const [catalogueArticleForm, setCatalogueArticleForm] = useState({
ref: '', codeArticle: '', designation: '', description: '', categoreMere: '', codeCategorieMere: '', categorieFille: '', codeCategorieFille: '', marque: '', refFournisseur: '', unite: 'pièce', prixUnitaire: 0, tva: 'TTC', actif: true
});
// Autocomplete state for article lines
const [autocompleteIndex, setAutocompleteIndex] = useState(null);
const [autocompleteSearch, setAutocompleteSearch] = useState('');
const [showAutocomplete, setShowAutocomplete] = useState(false);
// Modal parcourir catalogue
const [showBrowseCatalogue, setShowBrowseCatalogue] = useState(false);
const [browseTargetIndex, setBrowseTargetIndex] = useState(null);
const [browseDomaine, setBrowseDomaine] = useState('tous');
const [browseCatMere, setBrowseCatMere] = useState('toutes');
const [browseCatFille, setBrowseCatFille] = useState('toutes');
const [browseSearch, setBrowseSearch] = useState('');

// Catalogue initial (nomenclature 104 - Chaudronnerie Métallique)
const CATALOGUE_INITIAL = window.__CATALOGUE__ || [];
const [currentDoc, setCurrentDoc] = useState(null);
const [selectedDocs, setSelectedDocs] = useState([]);
const [showBatchPrint, setShowBatchPrint] = useState(false);
const [showBLModal, setShowBLModal] = useState(false);
const [currentBL, setCurrentBL] = useState(null);

// Liste des unités disponibles
const UNITES = [
{ code: 'PCS', libelle: 'Pièce' },
{ code: 'PAIRE', libelle: 'Paire' },
{ code: 'JEU', libelle: 'Jeu' },
{ code: 'KG', libelle: 'Kilogramme' },
{ code: 'G', libelle: 'Gramme' },
{ code: 'L', libelle: 'Litre' },
{ code: 'M3', libelle: 'Mètre cube' },
{ code: 'ML', libelle: 'Mètre linéaire' },
{ code: 'M2', libelle: 'Mètre carré' },
{ code: 'SAC', libelle: 'Sac' },
{ code: 'BOITE', libelle: 'Boîte' },
{ code: 'CART', libelle: 'Cartouche' },
{ code: 'BTL', libelle: 'Bouteille' },
{ code: 'ROUL', libelle: 'Rouleau' },
{ code: 'AERO', libelle: 'Aérosol' },
{ code: 'RAM', libelle: 'Ramette' },
{ code: 'LIC', libelle: 'Licence' },
{ code: 'JOUR', libelle: 'Jour(s)' },
{ code: 'TACHE', libelle: 'Tâche(s)' }
];

// Liste des méthodes de paiement disponibles
const METHODES_PAIEMENT = [
{ code: 'toutes', libelle: 'Toutes' },
{ code: 'virement', libelle: 'Virement Bancaire' },
{ code: 'cheque', libelle: 'Chèque' },
{ code: 'especes', libelle: 'Espèces' },
{ code: 'mobile', libelle: 'Mobile Money' }
];
const [currentClient, setCurrentClient] = useState(null);
const [searchTerm, setSearchTerm] = useState('');
const [searchClient, setSearchClient] = useState('');
const [filterType, setFilterType] = useState('all');
const [filterStatus, setFilterStatus] = useState('all');
const [dateDebut, setDateDebut] = useState('');
const [dateFin, setDateFin] = useState('');
const [filterClient, setFilterClient] = useState('');
const [filterMontantMin, setFilterMontantMin] = useState('');
const [filterMontantMax, setFilterMontantMax] = useState('');
const [activeFilterTab, setActiveFilterTab] = useState('basic');
// ===== PAGINATION DOCUMENTS =====
const [docCurrentPage, setDocCurrentPage] = useState(1);
const [docPerPage, setDocPerPage] = useState(10);
// ===== PAGINATION BONS DE COMMANDE =====
const [bcCurrentPage, setBcCurrentPage] = useState(1);
const [bcPerPage, setBcPerPage] = useState(10);
const [showAnnulationModal, setShowAnnulationModal] = useState(false);
const [raisonAnnulation, setRaisonAnnulation] = useState('');
// ===== ÉTATS AVOIRS =====
const [showAvoirModal, setShowAvoirModal] = useState(false);
const [avoirFactureSource, setAvoirFactureSource] = useState(null); // facture d'origine
const [avoirData, setAvoirData] = useState({
motif: '',
type: 'total', // 'total' | 'partiel'
lignes: [],    // pour partiel : { idx, designation, qteOriginale, qteAvoir, puOriginal, montantAvoir }
montantLibre: 0,
noteInterne: ''
});
const [showAvoirPreview, setShowAvoirPreview] = useState(false);
const [currentAvoir, setCurrentAvoir] = useState(null);
const [showRecuModal, setShowRecuModal] = useState(false);
const [recuPaiement, setRecuPaiement] = useState(null);
const [showExportComptable, setShowExportComptable] = useState(false);
// settings from Supabase hook
const [tempSettings, setTempSettings] = useState({...settings});

// ============================================
// LANGUAGETOOL - Correction orthographique et grammaticale
// ============================================

// Fonction pour vérifier le texte avec LanguageTool
const checkTextWithLanguageTool = async (text, language = 'fr') => {
if (!text || text.trim().length < 3) return [];

try {
const formData = new URLSearchParams();
formData.append('text', text);
formData.append('language', language);
formData.append('enabledOnly', 'false');

const response = await fetch('https://api.languagetool.org/v2/check', {
method: 'POST',
headers: {
'Content-Type': 'application/x-www-form-urlencoded',
'Accept': 'application/json'
},
body: formData.toString()
});

if (!response.ok) return [];

const data = await response.json();
return data.matches || [];
} catch (error) {
console.error('Erreur LanguageTool:', error);
return [];
}
};

// Activer LanguageTool sur un champ de texte
const enableLanguageToolOnField = (fieldId) => {
const field = document.getElementById(fieldId);
if (!field) return;
// Guard : évite d'attacher plusieurs fois les listeners sur le même champ
if (field.getAttribute('data-lt-enabled') === 'true') return;
field.setAttribute('data-lt-enabled', 'true');

let timeoutId = null;
let currentPopup = null;

// Vérifier le texte après la saisie
const handleInput = () => {
clearTimeout(timeoutId);
timeoutId = setTimeout(async () => {
const text = field.value;
const matches = await checkTextWithLanguageTool(text);

if (matches.length > 0) {
// Marquer visuellement qu'il y a des erreurs
field.style.borderColor = '#F59E0B';
field.style.borderWidth = '2px';

// Stocker les erreurs dans l'attribut data
field.setAttribute('data-lt-errors', JSON.stringify(matches));
} else {
field.style.borderColor = '';
field.style.borderWidth = '';
field.removeAttribute('data-lt-errors');
}
}, 1000); // Attendre 1 seconde après la dernière frappe
};

// Afficher les suggestions au clic
const handleClick = (e) => {
const errors = field.getAttribute('data-lt-errors');
if (!errors) return;

const matches = JSON.parse(errors);
if (matches.length === 0) return;

// Fermer le popup précédent
if (currentPopup) {
currentPopup.remove();
currentPopup = null;
}

// Créer un popup avec les suggestions
const popup = document.createElement('div');
popup.className = 'lt-popup';

// position: fixed → coordonnées relatives au viewport (pas au document)
const rect = field.getBoundingClientRect();
const popupMaxW = 420;
const spaceBelow = window.innerHeight - rect.bottom - 10;
const spaceAbove = rect.top - 10;
// Afficher en bas si assez de place, sinon en haut
const topPos = spaceBelow >= 80
  ? rect.bottom + 5
  : Math.max(5, rect.top - Math.min(300, spaceAbove));
const leftPos = Math.max(5, Math.min(rect.left, window.innerWidth - popupMaxW - 5));

popup.style.top = `${topPos}px`;
popup.style.left = `${leftPos}px`;
popup.style.maxHeight = `${Math.max(80, spaceBelow >= 80 ? spaceBelow : spaceAbove)}px`;
popup.style.overflowY = 'auto';

const firstMatch = matches[0];

let popupHTML = `
<div class="lt-popup-header">
<span style="font-size: 1.2rem;">${firstMatch.rule.issueType === 'misspelling' ? '🔤' : firstMatch.rule.issueType === 'grammar' ? '📚' : '✍️'}</span>
<span class="lt-popup-title">${firstMatch.rule.category.name || 'Suggestion'}</span>
</div>
<div class="lt-popup-message">${firstMatch.message}</div>
`;

if (firstMatch.replacements && firstMatch.replacements.length > 0) {
popupHTML += '<div class="lt-suggestions">';
firstMatch.replacements.slice(0, 3).forEach((replacement, idx) => {
popupHTML += `
<div class="lt-suggestion" data-replacement="${replacement.value}" data-offset="${firstMatch.offset}" data-length="${firstMatch.length}">
${replacement.value}
</div>
`;
});
popupHTML += '</div>';
}

popupHTML += `
<div class="lt-popup-actions">
<button class="lt-btn lt-btn-ignore">Ignorer</button>
</div>
`;

popup.innerHTML = popupHTML;
document.body.appendChild(popup);
currentPopup = popup;

// Gérer les clics sur les suggestions
popup.querySelectorAll('.lt-suggestion').forEach(suggestionEl => {
suggestionEl.addEventListener('click', () => {
const replacement = suggestionEl.getAttribute('data-replacement');
const offset = parseInt(suggestionEl.getAttribute('data-offset'));
const length = parseInt(suggestionEl.getAttribute('data-length'));

const currentText = field.value;
const newText = currentText.substring(0, offset) + replacement + currentText.substring(offset + length);
field.value = newText;

// Déclencher l'événement change pour React
const event = new Event('input', { bubbles: true });
field.dispatchEvent(event);

popup.remove();
currentPopup = null;
field.style.borderColor = '';
field.style.borderWidth = '';
field.removeAttribute('data-lt-errors');
});
});

// Bouton ignorer
popup.querySelector('.lt-btn-ignore').addEventListener('click', () => {
popup.remove();
currentPopup = null;
});
};

// Fermer le popup si on clique ailleurs
const handleClickOutside = (e) => {
if (currentPopup && !currentPopup.contains(e.target) && e.target !== field) {
currentPopup.remove();
currentPopup = null;
}
};

field.addEventListener('input', handleInput);
field.addEventListener('click', handleClick);
document.addEventListener('click', handleClickOutside);

// Nettoyage
return () => {
field.removeAttribute('data-lt-enabled');
field.removeEventListener('input', handleInput);
field.removeEventListener('click', handleClick);
document.removeEventListener('click', handleClickOutside);
if (currentPopup) { currentPopup.remove(); currentPopup = null; }
};
};

// Activer LanguageTool sur tous les champs pertinents
useEffect(() => {
// Attendre que le DOM soit prêt (le guard data-lt-enabled évite les doublons)
const ltTimer = setTimeout(() => {
const fieldsToCheck = [
'raison-annulation',
'objet-document',
];
fieldsToCheck.forEach(fieldId => {
enableLanguageToolOnField(fieldId);
});
document.querySelectorAll('textarea.form-textarea, textarea.form-input').forEach((field, index) => {
if (!field.id) field.id = `lt-textarea-${index}`;
enableLanguageToolOnField(field.id);
});
document.querySelectorAll('input[placeholder*="Description"]').forEach((field, index) => {
if (!field.id) field.id = `lt-description-${index}`;
enableLanguageToolOnField(field.id);
});
}, 500);
return () => clearTimeout(ltTimer);
// Réexécuter seulement quand les modals ouvrent/ferment ou que le nombre de lignes change
// (pas sur chaque frappe — le guard data-lt-enabled gère les doublons)
}, [showModal, showClientForm, showAnnulationModal, currentDoc?.items?.length]); // eslint-disable-line

// ← chargement Supabase géré par useSupabaseData

// ← sauvegarde Supabase auto

// ← sauvegarde Supabase auto

// ← sauvegarde Supabase auto

// ← sauvegarde Supabase auto

// Sauvegarder les préférences boutons actions
useEffect(() => {
localStorage.setItem('solstice_actions_visibles', JSON.stringify(actionsVisibles));
}, [actionsVisibles]);

// Sauvegarder les préférences boutons actions BC
useEffect(() => {
localStorage.setItem('solstice_actions_visibles_bc', JSON.stringify(actionsVisiblesBC));
}, [actionsVisiblesBC]);

// ← catalogue Supabase auto

// ← BL chargés par Supabase

// ← BL sauvegardés Supabase

// Recalculer les totaux quand le mode de taxation change
useEffect(() => {
if (currentDoc && currentDoc.items) {
calculerTotaux(currentDoc.items);
}
}, [currentDoc?.taxMode]);

// Fermer le menu dropdown quand on clique ailleurs
useEffect(() => {
const handleClickOutside = (event) => {
if (showBackupMenu && !event.target.closest('.dropdown')) {
setShowBackupMenu(false);
}
};

document.addEventListener('mousedown', handleClickOutside);
return () => {
document.removeEventListener('mousedown', handleClickOutside);
};
}, [showBackupMenu]);

// Quitter le plein écran avec la touche Échap
useEffect(() => {
const handleKeyDown = (e) => {
if (e.key === 'Escape') {
if (docsPleinEcran) setDocsPleinEcran(false);
if (cataloguePleinEcran) setCataloguePleinEcran(false);
}
};
document.addEventListener('keydown', handleKeyDown);
return () => document.removeEventListener('keydown', handleKeyDown);
}, [docsPleinEcran, cataloguePleinEcran]);

// Reset page 1 quand les filtres Documents changent
useEffect(() => { setDocCurrentPage(1); }, [searchTerm, filterType, filterStatus, filterClient, dateDebut, dateFin, filterMontantMin, filterMontantMax]);
// Reset page 1 quand les filtres BC changent
useEffect(() => { setBcCurrentPage(1); }, [searchBC, filterStatusBC]);

// Statistiques
const stats = {
totalDevis: documents.filter(d => d.type === 'devis').length,
totalFactures: documents.filter(d => d.type === 'facture').length,
montantTotal: documents.reduce((sum, d) => sum + (d.total || 0), 0),
enAttente: documents.filter(d => d.statut === 'en-attente').length,
devisAcceptes: documents.filter(d => d.type === 'devis' && d.statut === 'accepte').length,
devisConverts: documents.filter(d => d.type === 'facture' && d.devisOrigine).length
};

// Fonction pour calculer les statistiques détaillées par période
const calculerStatistiquesDetailees = () => {
// Filtrer les documents par période
let documentsFiltres = documents;

if (dateDebut && dateFin) {
documentsFiltres = documents.filter(doc => {
const dateDoc = new Date(doc.date);
const debut = new Date(dateDebut);
const fin = new Date(dateFin);
fin.setHours(23, 59, 59, 999); // Inclure toute la journée de fin
return dateDoc >= debut && dateDoc <= fin;
});
}

// Statistiques des devis
const devis = documentsFiltres.filter(d => d.type === 'devis');
const devisEnAttente = devis.filter(d => d.statut === 'en-attente');
const devisAcceptes = devis.filter(d => d.statut === 'accepte');
const devisRefuses = devis.filter(d => d.statut === 'refuse');

// Statistiques des factures
const factures = documentsFiltres.filter(d => d.type === 'facture');
const facturesPayees = factures.filter(d => d.statut === 'paye');
const facturesImpayees = factures.filter(d => d.statut === 'impaye');
const facturesPartielles = factures.filter(d => d.statut === 'partiel');
const facturesAnnulees = factures.filter(d => d.statut === 'annule');
// Avoirs émis
const avoirsEmis = documentsFiltres.filter(d => d.type === 'avoir');

// Montants financiers
const montantDevisTotal = devis.reduce((sum, d) => sum + (d.total || 0), 0);
const montantDevisAcceptes = devisAcceptes.reduce((sum, d) => sum + (d.total || 0), 0);
const montantFacturesTotal = factures.reduce((sum, d) => sum + (d.total || 0), 0);
const montantFacturesPayees = facturesPayees.reduce((sum, d) => sum + (d.total || 0), 0);
const montantFacturesImpayees = facturesImpayees.reduce((sum, d) => sum + (d.total || 0), 0);

// Calcul des montants avancés et restants pour les factures partielles
const montantAvanceTotal = facturesPartielles.reduce((sum, d) => sum + (d.montantPaye || d.montantAvance || 0), 0);
const montantRestantPartielles = facturesPartielles.reduce((sum, d) => {
return sum + Math.max(0, (d.total || 0) - (d.montantPaye || d.montantAvance || 0));
}, 0);

// Montant total encaissé = factures payées + avances des partielles
const montantEncaisse = montantFacturesPayees + montantAvanceTotal;

// Montant total en attente = factures impayées + reste à payer des partielles
const montantEnAttente = montantFacturesImpayees + montantRestantPartielles;

// ===== NOUVELLES STATISTIQUES AVANCÉES =====

// 1. Statistiques par UNITÉ
const statsParUnite = {};
documentsFiltres.forEach(doc => {
(doc.items || []).forEach(item => {
const unite = item.unite || 'PCS';
if (!statsParUnite[unite]) {
statsParUnite[unite] = {
unite: unite,
quantiteTotale: 0,
nombreArticles: 0,
montantTotal: 0,
articles: []
};
}
statsParUnite[unite].quantiteTotale += item.quantite || 0;
statsParUnite[unite].nombreArticles++;
statsParUnite[unite].montantTotal += (item.quantite * item.prixUnitaire) || 0;
statsParUnite[unite].articles.push(item.description);
});
});
const statsUniteArray = Object.values(statsParUnite).sort((a, b) => b.montantTotal - a.montantTotal);

// 2. Statistiques par MÉTHODE DE PAIEMENT
const statsParMethodePaiement = {};
documentsFiltres.forEach(doc => {
const methode = doc.methodePaiement || 'toutes';
if (!statsParMethodePaiement[methode]) {
statsParMethodePaiement[methode] = {
methode: methode,
nombreDocuments: 0,
montantTotal: 0,
montantPaye: 0,
montantImpaye: 0
};
}
statsParMethodePaiement[methode].nombreDocuments++;
statsParMethodePaiement[methode].montantTotal += doc.total || 0;

if (doc.type === 'facture') {
if (doc.statut === 'paye') {
statsParMethodePaiement[methode].montantPaye += doc.total || 0;
} else if (doc.statut === 'partiel') {
statsParMethodePaiement[methode].montantPaye += doc.montantPaye || doc.montantAvance || 0;
statsParMethodePaiement[methode].montantImpaye += (doc.total - (doc.montantAvance || 0));
} else if (doc.statut === 'impaye') {
statsParMethodePaiement[methode].montantImpaye += doc.total || 0;
}
}
});
const statsMethodePaiementArray = Object.values(statsParMethodePaiement).sort((a, b) => b.montantTotal - a.montantTotal);

// 3. Statistiques par VALIDITÉ (devis uniquement)
const statsParValidite = {};
devis.forEach(d => {
const validite = d.validiteDevis || 30;
if (!statsParValidite[validite]) {
statsParValidite[validite] = {
validite: validite,
nombre: 0,
acceptes: 0,
refuses: 0,
enAttente: 0,
tauxAcceptation: 0
};
}
statsParValidite[validite].nombre++;
if (d.statut === 'accepte') statsParValidite[validite].acceptes++;
if (d.statut === 'refuse') statsParValidite[validite].refuses++;
if (d.statut === 'en-attente') statsParValidite[validite].enAttente++;
});
Object.values(statsParValidite).forEach(stat => {
stat.tauxAcceptation = stat.nombre > 0 ? ((stat.acceptes / stat.nombre) * 100).toFixed(1) : 0;
});
const statsValiditeArray = Object.values(statsParValidite).sort((a, b) => a.validite - b.validite);

// 4. TOP 10 Articles les plus vendus
const statsArticles = {};
documentsFiltres.forEach(doc => {
(doc.items || []).forEach(item => {
const desc = item.description || 'Article sans nom';
if (!statsArticles[desc]) {
statsArticles[desc] = {
description: desc,
quantiteTotale: 0,
nombreVentes: 0,
montantTotal: 0,
unite: item.unite || 'PCS',
prixMoyen: 0
};
}
statsArticles[desc].quantiteTotale += item.quantite || 0;
statsArticles[desc].nombreVentes++;
statsArticles[desc].montantTotal += (item.quantite * item.prixUnitaire) || 0;
});
});
// Calculer le prix moyen
Object.values(statsArticles).forEach(stat => {
stat.prixMoyen = stat.quantiteTotale > 0 ? stat.montantTotal / stat.quantiteTotale : 0;
});
const top10Articles = Object.values(statsArticles)
.sort((a, b) => b.montantTotal - a.montantTotal)
.slice(0, 10);

// 5. Statistiques TEMPORELLES (par mois)
const statsParMois = {};
documentsFiltres.forEach(doc => {
const date = new Date(doc.date);
const moisAnnee = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
if (!statsParMois[moisAnnee]) {
statsParMois[moisAnnee] = {
mois: moisAnnee,
nombreDevis: 0,
nombreFactures: 0,
montantDevis: 0,
montantFactures: 0,
montantEncaisse: 0
};
}
if (doc.type === 'devis') {
statsParMois[moisAnnee].nombreDevis++;
statsParMois[moisAnnee].montantDevis += doc.total || 0;
} else {
statsParMois[moisAnnee].nombreFactures++;
statsParMois[moisAnnee].montantFactures += doc.total || 0;
if (doc.statut === 'paye') {
statsParMois[moisAnnee].montantEncaisse += doc.total || 0;
} else if (doc.statut === 'partiel') {
statsParMois[moisAnnee].montantEncaisse += doc.montantAvance || 0;
}
}
});
const statsParMoisArray = Object.values(statsParMois).sort((a, b) => a.mois.localeCompare(b.mois));

// Statistiques par client (existantes + améliorations)
const statsParClient = {};
documentsFiltres.forEach(doc => {
const clientNom = (doc.client?.nom || 'Client inconnu');
if (!statsParClient[clientNom]) {
statsParClient[clientNom] = {
nom: clientNom,
nombreDevis: 0,
nombreFactures: 0,
montantDevis: 0,
montantFactures: 0,
montantPaye: 0,
montantAvance: 0,
montantImpaye: 0,
devisAcceptes: 0,
devisRefuses: 0
};
}

if (doc.type === 'devis') {
statsParClient[clientNom].nombreDevis++;
statsParClient[clientNom].montantDevis += doc.total || 0;
if (doc.statut === 'accepte') statsParClient[clientNom].devisAcceptes++;
if (doc.statut === 'refuse') statsParClient[clientNom].devisRefuses++;
} else {
statsParClient[clientNom].nombreFactures++;
statsParClient[clientNom].montantFactures += doc.total || 0;

if (doc.statut === 'paye') {
statsParClient[clientNom].montantPaye += doc.total || 0;
} else if (doc.statut === 'partiel') {
const avance = doc.montantAvance || 0;
const reste = (doc.total || 0) - avance;
statsParClient[clientNom].montantPaye += avance;
statsParClient[clientNom].montantAvance += avance;
statsParClient[clientNom].montantImpaye += reste;
} else if (doc.statut === 'impaye') {
statsParClient[clientNom].montantImpaye += doc.total || 0;
}
}
});

// Convertir en tableau et trier par montant total décroissant
const statsClientArray = Object.values(statsParClient).sort((a, b) =>
(b.montantDevis + b.montantFactures) - (a.montantDevis + a.montantFactures)
);

// Calcul des MOYENNES et KPIs
const prixMoyenDevis = devis.length > 0 ? montantDevisTotal / devis.length : 0;
const prixMoyenFacture = factures.length > 0 ? montantFacturesTotal / factures.length : 0;
const tauxRecouvrement = montantFacturesTotal > 0 ? (montantEncaisse / montantFacturesTotal * 100).toFixed(1) : 0;

return {
// Statistiques générales
totalDocuments: documentsFiltres.length,

// Devis
devis: {
total: devis.length,
enAttente: devisEnAttente.length,
acceptes: devisAcceptes.length,
refuses: devisRefuses.length,
montantTotal: montantDevisTotal,
montantAcceptes: montantDevisAcceptes,
prixMoyen: prixMoyenDevis
},

// Factures
factures: {
total: factures.length,
payees: facturesPayees.length,
impayees: facturesImpayees.length,
partielles: facturesPartielles.length,
annulees: facturesAnnulees.length,
montantTotal: montantFacturesTotal,
montantPaye: montantFacturesPayees,
montantAvance: montantAvanceTotal,
montantRestantPartielles: montantRestantPartielles,
montantImpaye: montantFacturesImpayees,
montantEncaisse: montantEncaisse,
montantEnAttente: montantEnAttente,
prixMoyen: prixMoyenFacture
},

// Par client
clients: statsClientArray,

// Taux et ratios
tauxConversion: devis.length > 0 ? ((devisAcceptes.length / devis.length) * 100).toFixed(1) : 0,
tauxPaiement: factures.length > 0 ? ((facturesPayees.length / factures.length) * 100).toFixed(1) : 0,
tauxRecouvrement: tauxRecouvrement,

// NOUVELLES STATISTIQUES
unites: statsUniteArray,
methodesPaiement: statsMethodePaiementArray,
validites: statsValiditeArray,
topArticles: top10Articles,
parMois: statsParMoisArray
};
};

// Nouveau document
const nouveauDocument = (type) => {
const now = new Date();
const annee = now.getFullYear();
const mois = String(now.getMonth() + 1).padStart(2, '0');
const jour = String(now.getDate()).padStart(2, '0');

// Récupérer les 2 premières lettres du nom de l'agent (ou "N0" si vide)
let codeAgent = 'N0';
if (settingsEffectifs.agent && settingsEffectifs.agent.trim().length >= 2) {
codeAgent = settingsEffectifs.agent.trim().substring(0, 2).toUpperCase();
} else if (settingsEffectifs.agent && settingsEffectifs.agent.trim().length === 1) {
codeAgent = settingsEffectifs.agent.trim().toUpperCase() + '0';
}

// Compter les documents du même type pour le numéro séquentiel
const numeroSequentiel = String(documents.filter(d => d.type === type).length + 1).padStart(4, '0');

// Format: D/F + AAAA + MM + XX + JJ + - + NNNN
// Exemple: D202601TO25-0001
const numero = `${type === 'devis' ? 'D' : 'F'}${annee}${mois}${codeAgent}${jour}-${numeroSequentiel}`;

setCurrentDoc({
id: null,
type,
numero,
date: now.toISOString().split('T')[0],
dateMode: 'auto', // 'auto' ou 'manual'
dateTime: now.toISOString(),
objet: '', // Objet du devis ou de la facture
client: {
nom: '',
adresse: '',
telephone: '',
email: '',
nui: '',
rccm: '',
numeroBonCommande: ''
},
items: [{
lineType: 'article',
description: '',
quantite: 1,
prixUnitaire: 0,
unite: 'PCS',
remiseType: 'aucune',
remiseValeur: 0,
montantRemise: 0,
montantApresRemise: 0,
marque: '',
showMarque: false,
modeleRef: '',
showModeleRef: false
}],
sousTotal: 0,
totalRemises: 0,
remiseGlobale: {
type: 'aucune',
valeur: 0,
montant: 0
},
fraisSupplementaires: [],
totalAvantTaxe: 0,
tva: 0,
total: 0,
statut: type === 'devis' ? 'en-attente' : 'impaye',
notes: '',
validiteDevis: type === 'devis' ? 30 : null,
taxMode: 'TTC', // Mode de taxation par défaut : TTC (avec TVA) ou HT (sans TVA)
afficherModeTaxation: false, // Afficher HT/TTC sur le document imprimé
dateEcheance: type === 'facture' ? (() => {
const d = new Date(now);
d.setDate(d.getDate() + 30);
return d.toISOString().split('T')[0];
})() : null,
delaiPaiement: 30,
paiements: [],
montantAvance: 0, // Gardé pour compatibilité
montantPaye: 0,
montantRestant: 0,
penalitesRetard: {
actives: false,
taux: 0.03,
montant: 0,
joursRetard: 0
},
methodePaiement: 'toutes', // Méthode de paiement par défaut : Toutes
cgvIncluses: true
});

setShowModal(true);
};

// Éditer document
const editerDocument = async (doc) => {
const isUUID = (id) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)

// Normaliser le document pour compatibilité avec nouveaux champs
const docNormalise = {
...doc,
dateMode: doc.dateMode || 'manual',
dateTime: doc.dateTime || new Date(doc.date + 'T12:00:00').toISOString(),
taxMode: doc.taxMode || 'TTC',
afficherModeTaxation: doc.afficherModeTaxation || false,
methodePaiement: doc.methodePaiement || 'toutes',
objet: doc.objet || '', // Objet du devis ou de la facture

// Remises
remiseGlobale: doc.remiseGlobale || { type: 'aucune', valeur: 0, montant: 0 },
fraisSupplementaires: doc.fraisSupplementaires || [],
totalRemises: doc.totalRemises || 0,
totalAvantTaxe: doc.totalAvantTaxe || doc.sousTotal || 0,

// Items avec remise
items: (doc.items || []).map(item => ({
...item,
remiseType: item.remiseType || 'aucune',
remiseValeur: item.remiseValeur || 0,
montantRemise: item.montantRemise || 0,
montantApresRemise: item.montantApresRemise || ((item.quantite || 0) * (item.prixUnitaire || 0)),
unite: item.unite || 'PCS'
})),

// Paiements
dateEcheance: doc.dateEcheance || (doc.type === 'facture' ? calculerDateEcheance(doc.date, doc.delaiPaiement || 30) : null),
delaiPaiement: doc.delaiPaiement || 30,
paiements: doc.paiements || [],
montantPaye: (() => {
  const fromPaiements = (doc.paiements || []).reduce((s, p) => s + (p.montant || 0), 0);
  if (fromPaiements > 0) return fromPaiements;
  if (doc.montantPaye > 0) return doc.montantPaye;
  return doc.montantAvance || 0; // fallback ancien champ
})(),
montantRestant: (() => {
  const fromPaiements = (doc.paiements || []).reduce((s, p) => s + (p.montant || 0), 0);
  if (fromPaiements > 0) return Math.max(0, (doc.total || 0) - fromPaiements);
  if (doc.montantRestant > 0) return doc.montantRestant;
  if (doc.montantAvance > 0) return Math.max(0, (doc.total || 0) - doc.montantAvance);
  return doc.total || 0;
})(),
penalitesRetard: doc.penalitesRetard || {
actives: false,
taux: 0.03,
montant: 0,
joursRetard: 0
},
cgvIncluses: doc.cgvIncluses !== undefined ? doc.cgvIncluses : true
};

// Recalculer les totaux pour s'assurer que remiseGlobale.montant est à jour
const totauxInit = calculerTotauxDocument(docNormalise);

// Ouverture immédiate du modal (optimistic) — le verrou est vérifié en arrière-plan
setCurrentDoc({ ...docNormalise, ...totauxInit });
setShowModal(true);

// Vérification/acquisition du verrou en arrière-plan (sans bloquer l'ouverture)
if (doc.id && isUUID(doc.id)) {
  (async () => {
    try {
      const lock = await checkDocumentLock(doc.id)
      if (lock) {
        // Verrouillé par quelqu'un d'autre → fermer et alerter
        setShowModal(false)
        setCurrentDoc(null)
        setLockWarning({ nom: lock.nom })
        return
      }
      await lockDocument(doc.id)
      lockedDocIdRef.current = doc.id
    } catch (e) {
      console.warn('Vérification verrou échouée, édition autorisée :', e.message)
    }
  })()
}
};

// Convertir un devis accepté en facture
const convertirDevisEnFacture = (devis) => {
if (devis.type !== 'devis') {
alert('Seuls les devis peuvent être convertis en facture');
return;
}

if (devis.statut !== 'accepte') {
alert('Seuls les devis acceptés peuvent être convertis en facture');
return;
}

// Vérifier si une facture existe déjà pour ce devis
const factureExistante = documents.find(doc =>
doc.type === 'facture' && doc.devisOrigine === devis.numero
);

if (factureExistante) {
// Une facture existe déjà pour ce devis
const confirmation = confirm(
`⚠️ ATTENTION : Une facture a déjà été générée depuis ce devis !\n\n` +
`📋 Devis : ${devis.numero}\n` +
`🧾 Facture existante : ${factureExistante.numero}\n` +
`💰 Montant : ${formatMontant(factureExistante.total)}\n` +
`📅 Date : ${new Date(factureExistante.date).toLocaleDateString('fr-FR')}\n\n` +
`Voulez-vous ouvrir cette facture existante ?\n\n` +
`• Cliquez sur OK pour ouvrir la facture existante\n` +
`• Cliquez sur Annuler pour fermer ce message`
);

if (confirmation) {
// Ouvrir la facture existante en mode édition
editerDocument(factureExistante);
}
return;
}

// Aucune facture existante, on peut créer une nouvelle facture
const now = new Date();
const annee = now.getFullYear();
const mois = String(now.getMonth() + 1).padStart(2, '0');
const jour = String(now.getDate()).padStart(2, '0');

// Récupérer les 2 premières lettres du nom de l'agent (ou "N0" si vide)
let codeAgent = 'N0';
if (settingsEffectifs.agent && settingsEffectifs.agent.trim().length >= 2) {
codeAgent = settingsEffectifs.agent.trim().substring(0, 2).toUpperCase();
} else if (settingsEffectifs.agent && settingsEffectifs.agent.trim().length === 1) {
codeAgent = settingsEffectifs.agent.trim().toUpperCase() + '0';
}

// Compter les factures existantes pour le numéro séquentiel
const numeroSequentiel = String(documents.filter(d => d.type === 'facture').length + 1).padStart(4, '0');

// Format: F + AAAA + MM + XX + JJ + - + NNNN
const numero = `F${annee}${mois}${codeAgent}${jour}-${numeroSequentiel}`;

// Créer la nouvelle facture basée sur le devis
const nouvelleFacture = {
id: Date.now(),
type: 'facture',
numero,
date: now.toISOString().split('T')[0],
dateMode: 'auto',
dateTime: now.toISOString(),
objet: devis.objet || '', // Reprendre l'objet du devis
client: {...devis.client}, // Copier les infos client
items: devis.items.map(item => ({...item})), // Copier les articles
sousTotal: devis.sousTotal,
tva: devis.tva,
total: devis.total,
statut: 'impaye', // Statut initial de la facture
notes: devis.notes || '',
validiteDevis: null, // Les factures n'ont pas de validité
taxMode: devis.taxMode || 'TTC', // Garder le même mode de taxation
devisOrigine: devis.numero, // Référence au devis d'origine
montantAvance: 0 // Initialiser à 0 pour les nouvelles factures
};

setCurrentDoc(nouvelleFacture);
setShowModal(true);
};

// Supprimer document
const supprimerDocument = (id) => {
if (confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
_deleteDocument(id).catch(err => alert('Erreur: ' + err.message));
}
};

// Dupliquer un document
const dupliquerDocument = (doc) => {
if (!confirm('Voulez-vous créer une copie de ce document ?')) {
return;
}

const now = new Date();
const annee = now.getFullYear();
const mois = String(now.getMonth() + 1).padStart(2, '0');
const jour = String(now.getDate()).padStart(2, '0');

let codeAgent = 'N0';
if (settingsEffectifs.agent && settingsEffectifs.agent.trim().length >= 2) {
codeAgent = settingsEffectifs.agent.trim().substring(0, 2).toUpperCase();
} else if (settingsEffectifs.agent && settingsEffectifs.agent.trim().length === 1) {
codeAgent = settingsEffectifs.agent.trim().toUpperCase() + '0';
}

const numeroSequentiel = String(documents.filter(d => d.type === doc.type).length + 1).padStart(4, '0');
const nouveauNumero = `${doc.type === 'devis' ? 'D' : 'F'}${annee}${mois}${codeAgent}${jour}-${numeroSequentiel}`;

const documentDuplique = {
...doc,
id: Date.now(),
numero: nouveauNumero,
date: now.toISOString().split('T')[0],
dateMode: 'auto',
dateTime: now.toISOString(),
statut: doc.type === 'devis' ? 'en-attente' : 'impaye',
montantAvance: 0,
paiements: [],
montantPaye: 0,
montantRestant: doc.total,
devisOrigine: null,
dateEcheance: doc.type === 'facture' ? calculerDateEcheance(now.toISOString().split('T')[0], doc.delaiPaiement || 30) : null,
penalitesRetard: {
actives: doc.penalitesRetard?.actives || false,
taux: doc.penalitesRetard?.taux || 0.03,
montant: 0,
joursRetard: 0
}
};

setCurrentDoc(documentDuplique);
setShowModal(true);
};

// Exporter la liste des documents en Excel (CSV)
const exporterExcel = (docsAExporter) => {
console.log('=== EXPORT EXCEL DÉMARRÉ ===');
console.log('Documents à exporter:', docsAExporter);
console.log('Nombre de documents:', docsAExporter ? docsAExporter.length : 0);

if (!docsAExporter || docsAExporter.length === 0) {
alert('Aucun document à exporter');
return;
}

try {
// Formater méthode de paiement pour export
const formatMethodePaiementExport = (code) => {
if (code === 'toutes') return 'Virement Bancaire, Chèque, Espèces, Mobile Money';
const methodes = {
'virement': 'Virement Bancaire',
'cheque': 'Chèque',
'especes': 'Espèces',
'mobile': 'Mobile Money'
};
return methodes[code] || 'Virement Bancaire';
};

const headers = [
'Type', 'Numéro', 'Date', 'Échéance', 'Client', 'NUI',
'Sous-total', 'Remises', 'Frais', 'HT', 'TVA', 'TTC',
'Payé', 'Reste', 'Statut', 'Méthode Paiement'
];

console.log('Headers créés:', headers);

const rows = docsAExporter.map(doc => [
doc.type === 'devis' ? 'Devis' : 'Facture',
doc.numero,
doc.date,
doc.dateEcheance || '',
(doc.client?.nom || ''),
doc.client?.nui || '',
doc.sousTotal || 0,
doc.totalRemises || 0,
(doc.fraisSupplementaires || []).reduce((sum, f) => sum + (f.montant || 0), 0),
doc.totalAvantTaxe || doc.sousTotal || 0,
doc.tva || 0,
doc.total || 0,
doc.montantPaye || 0,
doc.montantRestant || doc.total || 0,
doc.statut,
formatMethodePaiementExport(doc.methodePaiement || 'toutes')
]);

console.log('Rows créées:', rows.length);

const csv = [headers, ...rows]
.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
.join('\n');

console.log('CSV généré, taille:', csv.length, 'caractères');

const BOM = '\ufeff';
const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
console.log('Blob créé, taille:', blob.size, 'bytes');

const link = document.createElement('a');
link.href = URL.createObjectURL(blob);
link.download = `SOLSTICE_Export_${new Date().toISOString().split('T')[0]}.csv`;
console.log('Nom du fichier:', link.download);

document.body.appendChild(link);
link.click();
document.body.removeChild(link);

console.log('=== EXPORT TERMINÉ ===');
alert(`${docsAExporter.length} document(s) exporté(s) avec succès !`);
} catch (error) {
console.error('ERREUR LORS DE L\'EXPORT:', error);
alert('Erreur lors de l\'export: ' + error.message);
}
};

// ============================================================
// ===== EXPORT COMPTABLE FEC / OHADA =====
// ============================================================
// Génère un fichier CSV compatible logiciels comptables (Sage, SAARI)
// Format : Journal Ventes (JV) + Journal Achats (JA) selon plan OHADA
// Colonnes : JournalCode | JournalLib | EcritureNum | EcritureDate |
//            CompteNum | CompteLib | CompteAux | CompteAuxLib |
//            PieceRef | PieceDate | EcritureLib | Debit | Credit |
//            DateLet | ValidDate | MontantDevise | Idevise
// ============================================================
const exporterComptable = (docsAExporter, journal = 'VE') => {
  if (!docsAExporter || docsAExporter.length === 0) {
    alert('Aucun document à exporter pour la comptabilité.');
    return;
  }

  // Comptes OHADA standards
  const COMPTE_CLIENT_GENERAL = '411000'; // Clients
  const COMPTE_VENTES_HT      = '701000'; // Ventes de marchandises HT
  const COMPTE_TVA_COLLECTEE  = '443100'; // TVA collectée
  const COMPTE_AVOIRS         = '701900'; // Avoirs sur ventes
  const COMPTE_AVOIR_CLIENT   = '411000'; // Avoirs clients (contrepartie)

  const JOURNAL_VENTES  = { code: 'VE', lib: 'Journal des Ventes' };
  const JOURNAL_AVOIRS  = { code: 'AV', lib: 'Journal des Avoirs' };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.replace(/-/g, '');
  };

  const formatMontant = (val) => {
    const n = parseFloat(val) || 0;
    return n.toFixed(2).replace('.', ',');
  };

  const zero = () => '0,00';

  const headers = [
    'JournalCode', 'JournalLib', 'EcritureNum', 'EcritureDate',
    'CompteNum', 'CompteLib', 'CompteAux', 'CompteAuxLib',
    'PieceRef', 'PieceDate', 'EcritureLib',
    'Debit', 'Credit',
    'DateLet', 'ValidDate', 'MontantDevise', 'Idevise'
  ];

  const lignes = [];

  // Filtrer selon le journal demandé
  const docs = docsAExporter.filter(d => {
    if (journal === 'VE') return d.type === 'facture' && !d.estAvoir;
    if (journal === 'AV') return d.estAvoir === true || (d.type === 'avoir');
    return d.type === 'facture' || d.estAvoir === true || d.type === 'avoir'; // 'ALL'
  });

  if (docs.length === 0) {
    alert('Aucun document correspondant au journal sélectionné.');
    return;
  }

  docs.forEach((doc) => {
    const isAvoir = doc.estAvoir || doc.type === 'avoir';
    const jCode = isAvoir ? JOURNAL_AVOIRS.code : JOURNAL_VENTES.code;
    const jLib  = isAvoir ? JOURNAL_AVOIRS.lib  : JOURNAL_VENTES.lib;

    const dateEcriture = formatDate(doc.date);
    const pieceRef = doc.numero || '';
    const clientNom = (doc.client?.nom || 'Client inconnu');
    const clientCode = (doc.client && doc.client?.nui) ? doc.client?.nui : pieceRef;

    // Montants
    const montantHT  = parseFloat(doc.totalAvantTaxe || doc.sousTotal || 0);
    const montantTVA = parseFloat(doc.tva || 0);
    const montantTTC = parseFloat(doc.total || 0);

    // Pour un avoir, sens inversé (débit/crédit inversés)
    const sign = isAvoir ? -1 : 1;

    // Libellé de l'écriture
    const libEcriture = `${isAvoir ? 'Avoir' : 'Facture'} ${pieceRef} - ${clientNom}`;

    // Ligne 1 : Compte client (411) — Débit pour vente, Crédit pour avoir
    lignes.push([
      jCode, jLib,
      pieceRef, dateEcriture,
      COMPTE_CLIENT_GENERAL, 'Clients',
      clientCode, clientNom,
      pieceRef, dateEcriture,
      libEcriture,
      isAvoir ? zero() : formatMontant(montantTTC),  // Débit
      isAvoir ? formatMontant(montantTTC) : zero(),  // Crédit
      '', dateEcriture,
      formatMontant(montantTTC), 'XAF'
    ]);

    // Ligne 2 : Compte ventes HT (701) — Crédit pour vente, Débit pour avoir
    if (montantHT !== 0) {
      lignes.push([
        jCode, jLib,
        pieceRef, dateEcriture,
        isAvoir ? COMPTE_AVOIRS : COMPTE_VENTES_HT,
        isAvoir ? 'Avoirs sur ventes' : 'Ventes de marchandises',
        '', '',
        pieceRef, dateEcriture,
        libEcriture,
        isAvoir ? formatMontant(montantHT) : zero(),  // Débit
        isAvoir ? zero() : formatMontant(montantHT),  // Crédit
        '', dateEcriture,
        formatMontant(montantHT), 'XAF'
      ]);
    }

    // Ligne 3 : TVA collectée (443100) — Crédit pour vente, Débit pour avoir
    if (montantTVA > 0) {
      lignes.push([
        jCode, jLib,
        pieceRef, dateEcriture,
        COMPTE_TVA_COLLECTEE, 'TVA collectée',
        '', '',
        pieceRef, dateEcriture,
        libEcriture,
        isAvoir ? formatMontant(montantTVA) : zero(),  // Débit
        isAvoir ? zero() : formatMontant(montantTVA),  // Crédit
        '', dateEcriture,
        formatMontant(montantTVA), 'XAF'
      ]);
    }
  });

  if (lignes.length === 0) {
    alert('Aucune écriture comptable générée.');
    return;
  }

  // Construire le CSV (séparateur "|" compatible FEC français et OHADA)
  const sep = '|';
  const csvLines = [
    headers.join(sep),
    ...lignes.map(row => row.map(c => String(c).replace(/\|/g, '_')).join(sep))
  ];
  const csvContent = '\uFEFF' + csvLines.join('\r\n');

  const journalLabel = journal === 'VE' ? 'Ventes' : journal === 'AV' ? 'Avoirs' : 'Complet';
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `SOLSTICE_Journal_${journalLabel}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  alert(`✅ Export comptable réussi !\n${lignes.length} ligne(s) comptable(s) générée(s) pour ${docs.length} document(s).\n\nFichier compatible Sage / SAARI (format FEC/OHADA, séparateur "|").`);
};

// Gérer le changement de statut avec vérification pour l'annulation
const handleStatutChange = (nouveauStatut) => {
// Si le statut passe à "annulé", ouvrir le modal pour la raison
if (nouveauStatut === 'annule') {
setShowAnnulationModal(true);
} else {
// Pour les autres statuts, mettre à jour directement
setCurrentDoc({
...currentDoc,
statut: nouveauStatut,
raisonAnnulation: '' // Réinitialiser la raison si on quitte le statut annulé
});
}
};

// Confirmer l'annulation avec la raison
const confirmerAnnulation = () => {
if (!raisonAnnulation.trim()) {
alert('Veuillez indiquer une raison pour l\'annulation');
return;
}

setCurrentDoc({
...currentDoc,
statut: 'annule',
raisonAnnulation: raisonAnnulation,
dateAnnulation: new Date().toISOString()
});

setShowAnnulationModal(false);
setRaisonAnnulation('');
};

// Annuler le modal d'annulation
const annulerModalAnnulation = () => {
setShowAnnulationModal(false);
setRaisonAnnulation('');
};

// ========================================================
// ============= FONCTIONS AVOIRS (NOTES DE CRÉDIT) ========
// ========================================================

/** Génère un numéro d'avoir séquentiel : AV{AAAA}{MM}{Agent}{JJ}-{NNNN} */
const genererNumeroAvoir = () => {
const now = new Date();
const annee = now.getFullYear();
const mois = String(now.getMonth() + 1).padStart(2, '0');
const jour = String(now.getDate()).padStart(2, '0');
let codeAgent = 'N0';
if (settingsEffectifs.agent && settingsEffectifs.agent.trim().length >= 2) {
codeAgent = settingsEffectifs.agent.trim().substring(0, 2).toUpperCase();
} else if (settingsEffectifs.agent && settingsEffectifs.agent.trim().length === 1) {
codeAgent = settingsEffectifs.agent.trim().toUpperCase() + '0';
}
const avoirs = documents.filter(d => d.type === 'avoir');
const seq = String(avoirs.length + 1).padStart(4, '0');
return `AV${annee}${mois}${codeAgent}${jour}-${seq}`;
};

/** Ouvre le modal de création d'avoir pour une facture */
const ouvrirModalAvoir = (facture) => {
setAvoirFactureSource(facture);
const lignesInit = (facture.items || []).map((item, idx) => ({
idx,
designation: item.description || '',
qteOriginale: item.quantite || 1,
qteAvoir: item.quantite || 1,
puOriginal: item.prixUnitaire || 0,
montantOriginal: (item.quantite || 1) * (item.prixUnitaire || 0),
montantAvoir: (item.quantite || 1) * (item.prixUnitaire || 0),
inclure: true
}));
setAvoirData({
motif: '',
type: 'total',
lignes: lignesInit,
montantLibre: facture.total || 0,
noteInterne: ''
});
setShowAvoirModal(true);
};

/** Calcule le montant total de l'avoir selon le mode */
const calculerMontantAvoir = () => {
if (!avoirFactureSource) return 0;
if (avoirData.type === 'total') {
// Déduire les avoirs déjà émis sur cette facture
const dejaEmis = documents
.filter(d => d.type === 'avoir' && d.factureOrigineNumero === avoirFactureSource.numero)
.reduce((sum, a) => sum + (a.total || 0), 0);
return Math.max(0, (avoirFactureSource.total || 0) - dejaEmis);
}
if (avoirData.type === 'partiel') {
return avoirData.lignes
.filter(l => l.inclure)
.reduce((sum, l) => sum + (l.montantAvoir || 0), 0);
}
if (avoirData.type === 'libre') {
return parseFloat(avoirData.montantLibre) || 0;
}
return 0;
};

/** Crée et enregistre l'avoir */
const confirmerCreationAvoir = () => {
if (!avoirData.motif.trim()) {
alert('Veuillez indiquer le motif de l\'avoir.');
return;
}
const montant = calculerMontantAvoir();
if (montant <= 0) {
alert('Le montant de l\'avoir doit être supérieur à 0.');
return;
}

const now = new Date();
const numero = genererNumeroAvoir();

// Construire les lignes de l'avoir
let lignesAvoir = [];
if (avoirData.type === 'total') {
lignesAvoir = (avoirFactureSource.items || []).map(item => ({
...item,
montantApresRemise: (item.quantite || 1) * (item.prixUnitaire || 0)
}));
} else if (avoirData.type === 'partiel') {
lignesAvoir = avoirData.lignes
.filter(l => l.inclure && l.montantAvoir > 0)
.map(l => ({
description: l.designation,
quantite: l.qteAvoir,
prixUnitaire: l.puOriginal,
unite: (avoirFactureSource.items[l.idx] || {}).unite || 'PCS',
remiseType: 'aucune', remiseValeur: 0,
montantRemise: 0,
montantApresRemise: l.montantAvoir
}));
} else {
lignesAvoir = [{
description: avoirData.motif,
quantite: 1,
prixUnitaire: montant,
unite: 'PCS',
remiseType: 'aucune', remiseValeur: 0,
montantRemise: 0,
montantApresRemise: montant
}];
}

const nouvelAvoir = {
id: Date.now(),
type: 'avoir',
numero,
date: now.toISOString().split('T')[0],
dateTime: now.toISOString(),
factureOrigineNumero: avoirFactureSource.numero,
factureOrigineId: avoirFactureSource.id,
client: { ...avoirFactureSource.client },
motif: avoirData.motif,
typeAvoir: avoirData.type,
noteInterne: avoirData.noteInterne,
items: lignesAvoir,
total: montant,
sousTotal: montant,
totalRemises: 0,
totalAvantTaxe: montant,
tva: 0,
statut: 'avoir',
taxMode: avoirFactureSource.taxMode || 'TTC',
remiseGlobale: { type: 'aucune', valeur: 0, montant: 0 },
fraisSupplementaires: [],
paiements: [],
montantPaye: 0,
montantRestant: 0
};

// Imputer l'avoir sur la facture d'origine : réduire le solde restant
const facturesMaj = documents.map(d => {
if (d.id !== avoirFactureSource.id) return d;
const totalAvoirs = documents
.filter(av => av.type === 'avoir' && av.factureOrigineId === d.id)
.reduce((sum, av) => sum + (av.total || 0), 0) + montant;
const montantPayeNet = (d.montantPaye || 0) + montant;
const montantRestantNet = Math.max(0, (d.total || 0) - montantPayeNet);
let nouveauStatut = d.statut;
if (montantRestantNet <= 0) nouveauStatut = 'paye';
else if (montantPayeNet > 0) nouveauStatut = 'partiel';
return {
...d,
montantPaye: montantPayeNet,
montantRestant: montantRestantNet,
statut: nouveauStatut,
avoirs: [...(d.avoirs || []), numero]
};
});

setDocuments([...facturesMaj, nouvelAvoir]);
setShowAvoirModal(false);
setAvoirFactureSource(null);

// Proposer la prévisualisation
setTimeout(() => {
setCurrentAvoir(nouvelAvoir);
setShowAvoirPreview(true);
}, 200);
};

// Fermer l'éditeur et libérer le verrou
const fermerEditeur = useCallback(async () => {
document.querySelectorAll('.lt-popup').forEach(p => p.remove())
if (lockedDocIdRef.current) {
  await unlockDocument(lockedDocIdRef.current)
  lockedDocIdRef.current = null
}
setShowModal(false)
setCurrentDoc(null)
}, [unlockDocument])

// Libérer le verrou au démontage du composant
useEffect(() => {
return () => {
  if (lockedDocIdRef.current) {
    unlockDocument(lockedDocIdRef.current)
    lockedDocIdRef.current = null
  }
}
}, [unlockDocument])

// Fermer le menu d'insertion au clic en dehors
useEffect(() => {
if (!insertMenu) return
const close = () => setInsertMenu(null)
window.addEventListener('click', close)
return () => window.removeEventListener('click', close)
}, [insertMenu])

// Heartbeat pendant l'édition — garde la connexion TCP Supabase chaude
// Heartbeat pendant l'édition — ping différé 3s au retour + toutes les 2 min
// Cause du bug : quand la fenêtre est minimisée, Chrome déconnecte le WebSocket Realtime.
// Au retour, Realtime tente de se reconnecter. Si on sauvegarde en même temps → contention.
// Le délai de 3s laisse Realtime terminer sa reconnexion avant le ping de préchauffage.
useEffect(() => {
if (!showModal) return
const doPing = () => supabase.from('documents').select('id').limit(1).then(() => {})
let pingTimer = null
const onVisible = () => {
  if (document.visibilityState === 'visible') {
    // 3s : laisse Realtime se reconnecter d'abord
    pingTimer = setTimeout(doPing, 3000)
  } else {
    if (pingTimer) { clearTimeout(pingTimer); pingTimer = null }
  }
}
document.addEventListener('visibilitychange', onVisible)
const interval = setInterval(doPing, 2 * 60 * 1000)
return () => {
  document.removeEventListener('visibilitychange', onVisible)
  clearInterval(interval)
  if (pingTimer) clearTimeout(pingTimer)
}
}, [showModal])

useEffect(() => {
if (!showBCModal) return
const doPing = () => supabase.from('bons_commande').select('id').limit(1).then(() => {})
let pingTimer = null
const onVisible = () => {
  if (document.visibilityState === 'visible') {
    pingTimer = setTimeout(doPing, 3000)
  } else {
    if (pingTimer) { clearTimeout(pingTimer); pingTimer = null }
  }
}
document.addEventListener('visibilitychange', onVisible)
const interval = setInterval(doPing, 2 * 60 * 1000)
return () => {
  document.removeEventListener('visibilitychange', onVisible)
  clearInterval(interval)
  if (pingTimer) clearTimeout(pingTimer)
}
}, [showBCModal])

// Fermer les popups LanguageTool quand la fenêtre perd le focus (changement d'onglet ou d'appli)
// — évite qu'un popup invisible (z-index 999999) reste ouvert et intercepte les clics au retour
useEffect(() => {
const closeLtPopups = () => document.querySelectorAll('.lt-popup').forEach(p => p.remove())
window.addEventListener('blur', closeLtPopups)
document.addEventListener('visibilitychange', closeLtPopups)
return () => {
  window.removeEventListener('blur', closeLtPopups)
  document.removeEventListener('visibilitychange', closeLtPopups)
}
}, [])

// Sauvegarder document
const sauvegarderDocument = async () => {
// Fermer tout popup LanguageTool qui pourrait intercepter les clics (z-index 999999)
document.querySelectorAll('.lt-popup').forEach(p => p.remove())
if (isSaving) return;
if (!(currentDoc.client?.nom||'')) {
alert('Veuillez renseigner le nom du client');
return;
}

// Vérifier qu'il y a au moins un article réel (ni section, ni note)
const articlesReels = currentDoc.items.filter(item =>
item.lineType !== 'section' && item.lineType !== 'note'
);

if (articlesReels.length === 0 || !articlesReels.some(item => item.description && item.description.trim())) {
alert('Veuillez ajouter au moins un article');
return;
}

// Calculer date d'échéance si pas définie
if (!currentDoc.dateEcheance && currentDoc.type === 'facture') {
currentDoc.dateEcheance = calculerDateEcheance(currentDoc.date, currentDoc.delaiPaiement || 30);
}

// Recalculer tous les totaux avec remises et frais
const totauxCalcules = calculerTotauxDocument(currentDoc);
const documentFinal = {
...currentDoc,
tauxTVA: settings.tauxTVA || 19.25,
...totauxCalcules
};

// Calculer pénalités si facture
if (documentFinal.type === 'facture') {
const penalites = calculerPenalites(documentFinal);
documentFinal.penalitesRetard = {
...documentFinal.penalitesRetard,
...penalites
};
}

setIsSaving(true);
const saveWithTimeout = () => Promise.race([
  saveDocument(documentFinal),
  new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 30000))
]);
try {
  try {
    await saveWithTimeout();
  } catch(firstErr) {
    if (firstErr.message === 'timeout') {
      // Connexion instable — pause 3s puis réessai (laisse le temps au réseau de se stabiliser)
      await new Promise(r => setTimeout(r, 3000));
      await saveWithTimeout();
    } else {
      throw firstErr;
    }
  }
} catch(err) {
  // Déverrouiller sans attendre (la connexion est peut-être cassée, on ne bloque pas l'UI)
  if (lockedDocIdRef.current) {
    unlockDocument(lockedDocIdRef.current).catch(e => console.warn('Erreur déverrouillage:', e.message))
    lockedDocIdRef.current = null
  }
  setIsSaving(false);
  const msg = err.message === 'timeout'
    ? 'Délai dépassé après 2 tentatives — vérifiez votre connexion'
    : err.message;
  alert('Erreur sauvegarde: ' + msg);
  return;
} finally {
  setIsSaving(false);
}

if (lockedDocIdRef.current) {
  unlockDocument(lockedDocIdRef.current).catch(() => {})
  lockedDocIdRef.current = null
}
setShowModal(false);
setCurrentDoc(null);
};

// Gérer les changements de client
const handleClientChange = (field, value) => {
setCurrentDoc({
...currentDoc,
client: {
...currentDoc.client,
[field]: value
}
});
};

// === FONCTIONS CATALOGUE (mémorisées pour perf) ===
// Listes déroulantes en cascade — évite re-itération 5000 articles à chaque keystroke
const domaines = useMemo(() => [...new Set(catalogue.map(a => a.domaine).filter(Boolean))], [catalogue]);

const catsM = useMemo(() => {
const base = filtresDomaine !== 'tous' ? catalogue.filter(a => a.domaine === filtresDomaine) : catalogue;
return [...new Set(base.map(a => a.categoreMere).filter(Boolean))];
}, [catalogue, filtresDomaine]);

const catsF = useMemo(() => {
let base = catalogue;
if (filtresDomaine !== 'tous') base = base.filter(a => a.domaine === filtresDomaine);
if (filtresCatMere !== 'toutes') base = base.filter(a => a.categoreMere === filtresCatMere);
return [...new Set(base.map(a => a.categorieFille).filter(Boolean))];
}, [catalogue, filtresDomaine, filtresCatMere]);

const catalogueFiltré = useMemo(() => {
const q = catalogueSearch.toLowerCase();
return catalogue.filter(a => {
if (filtresDomaine !== 'tous' && a.domaine !== filtresDomaine) return false;
if (filtresCatMere !== 'toutes' && a.categoreMere !== filtresCatMere) return false;
if (filtresCatFille !== 'toutes' && a.categorieFille !== filtresCatFille) return false;
if (q) {
return a.ref.toLowerCase().includes(q) ||
a.designation.toLowerCase().includes(q) ||
(a.codeArticle || '').toLowerCase().includes(q) ||
(a.description || '').toLowerCase().includes(q);
}
return true;
});
}, [catalogue, catalogueSearch, filtresDomaine, filtresCatMere, filtresCatFille]);

// Pagination du catalogue (100 articles par page)
const ITEMS_PAR_PAGE_CATALOGUE = 100;
const cataloguePageCount = Math.ceil(catalogueFiltré.length / ITEMS_PAR_PAGE_CATALOGUE);
const cataloguePaginé = useMemo(() => {
  const start = (cataloguePage - 1) * ITEMS_PAR_PAGE_CATALOGUE;
  const end = start + ITEMS_PAR_PAGE_CATALOGUE;
  return catalogueFiltré.slice(start, end);
}, [catalogueFiltré, cataloguePage]);

// Reset cascaded filters on parent change
const changerFiltresDomaine = (val) => {
setFiltresDomaine(val);
setFiltresCatMere('toutes');
setFiltresCatFille('toutes');
setCataloguePage(1);
};
const changerFiltresCatMere = (val) => {
setFiltresCatMere(val);
setFiltresCatFille('toutes');
setCataloguePage(1);
};

const categoriesCatalogue = () => catsM;

const sauvegarderArticleCatalogue = () => {
const form = catalogueArticleForm;
if (!form.ref || !form.designation) {
alert('La référence et la désignation sont obligatoires.');
return;
}
const editingRef = editingCatalogueArticle !== null ? catalogue[editingCatalogueArticle]?.ref : null;
if (!editingRef && catalogue.find(a => a.ref === form.ref)) {
alert('Cette référence existe déjà dans le catalogue.');
return;
}
saveCatalogueArticle(form, editingRef).catch(err => alert('Erreur catalogue: ' + err.message));
setShowCatalogueArticleModal(false);
setEditingCatalogueArticle(null);
};

const supprimerArticleCatalogue = (index) => {
if (confirm('Supprimer cet article du catalogue ?')) {
const ref = catalogue[index]?.ref;
if (ref) deleteCatalogueArticle(ref).catch(err => alert('Erreur: ' + err.message));
}
};

const toggleActifArticle = (index) => {
const ref = catalogue[index]?.ref;
if (ref) _toggleActif(ref).catch(err => console.warn(err));
};

const ouvrirNouvelArticleCatalogue = () => {
setEditingCatalogueArticle(null);
setCatalogueArticleForm({ ref:'', codeArticle:'', designation:'', description:'', categoreMere:'', codeCategorieMere:'', categorieFille:'', codeCategorieFille:'', marque:'', refFournisseur:'', unite:'pièce', prixUnitaire:0, tva:'TTC', actif:true });
setShowCatalogueArticleModal(true);
};

const ouvrirEditionArticleCatalogue = (index) => {
setEditingCatalogueArticle(index);
setCatalogueArticleForm({ ...catalogue[index] });
setShowCatalogueArticleModal(true);
};

const importerCatalogueCSV = (e) => {
const file = e.target.files[0];
if (!file) return;
const reader = new FileReader();
reader.onload = (evt) => {
try {
const lines = evt.target.result.split('\n').filter(l => l.trim());
const headers = lines[0].split(';').map(h => h.trim().toLowerCase().replace(/[^a-z]/g, ''));
const articles = [];
for (let i = 1; i < lines.length; i++) {
const cols = lines[i].split(';');
const get = (key) => {
const idx = headers.indexOf(key);
return idx >= 0 ? (cols[idx] || '').trim().replace(/^"|"$/g, '') : '';
};
const codeArticle = get('codearticle') || get('code') || get('ref') || get('rfrence') || get('reference');
const art = {
codeArticle: codeArticle,
ref: codeArticle,
designation: get('designation') || get('dsignation') || get('nomdlarticle') || get('nom'),
description: get('description') || get('descriptionarticle') || '',
categoreMere: get('categoriemere') || get('catgoriemre') || get('catgorie') || '',
codeCategorieMere: get('codecategoriemere') || get('codecatmre') || '',
categorieFille: get('categoriefille') || get('catgoriefille') || '',
codeCategorieFille: get('codecategoriefille') || get('codecatfille') || '',
marque: get('marque') || '',
refFournisseur: get('reffournisseur') || get('rfrence fournisseur') || '',
unite: get('unite') || get('unit') || 'pièce',
uniteLibelle: get('unite') || 'pièce',
prixUnitaire: parseFloat(get('prixunitaire') || get('prix') || '0') || 0,
tva: get('tva') || 'TTC',
actif: true
};
if (art.ref && art.designation) articles.push(art);
}
if (articles.length === 0) {
alert('Aucun article trouvé. Format attendu (séparateur ;) : codeArticle;ref;designation;description;categoreMere;codeCategorieMere;categorieFille;codeCategorieFille;marque;unite;prixUnitaire;tva');
return;
}
const merged = [...catalogue];
let added = 0, updated = 0;
articles.forEach(art => {
const idx = merged.findIndex(a => a.ref === art.ref);
if (idx >= 0) { merged[idx] = art; updated++; }
else { merged.push(art); added++; }
});
setCatalogue(merged);
alert('Import réussi : ' + added + ' article(s) ajouté(s), ' + updated + ' mis à jour.');
} catch (err) {
alert('Erreur lors de l\'import : ' + err.message);
}
};
reader.readAsText(file, 'UTF-8');
e.target.value = '';
};

const exporterCatalogueCSV = () => {
const headers = 'codeArticle;ref;designation;description;categoreMere;codeCategorieMere;categorieFille;codeCategorieFille;marque;unite;prixUnitaire;tva';
const rows = catalogue.map(a =>
[a.codeArticle||a.ref, a.ref, a.designation, (a.description||'').replace(/;/g,','), a.categoreMere, a.codeCategorieMere||'', a.categorieFille||'', a.codeCategorieFille||'', a.marque||'', a.unite, a.prixUnitaire, a.tva].join(';')
);
const csv = [headers, ...rows].join('\n');
const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
const url = URL.createObjectURL(blob);
const a = document.createElement('a');
a.href = url; a.download = 'catalogue-videosurveillance-112.csv'; a.click();
URL.revokeObjectURL(url);
};

const reinitialiserCatalogue = () => {
if (confirm('Réinitialiser le catalogue avec la base complète (domaines 101–112, 7 157 articles) ? Les ajouts et modifications seront perdus.')) {
setCatalogue(CATALOGUE_INITIAL);
reloadCatalogue();
}
};

// ── ENRICHISSEMENT IA CATALOGUE ────────────────────────────────────────────

// Génère la prochaine référence disponible pour une catégorie fille donnée
const genererProchaineRef = async (codeCategorieFille) => {
  const { data } = await supabase
    .from('catalogue')
    .select('ref')
    .ilike('ref', `${codeCategorieFille}%`)
    .order('ref', { ascending: false });
  if (!data || data.length === 0) return codeCategorieFille + '001';
  const sequences = data
    .map(a => parseInt(a.ref.slice(-3), 10))
    .filter(n => !isNaN(n));
  const max = Math.max(0, ...sequences);
  return codeCategorieFille + String(max + 1).padStart(3, '0');
};

// Lance l'analyse — mode local (serveur Node) ou production (Edge Function Supabase)
const lancerEnrichissementIA = async () => {
  setEnrichErreur(null);
  setEnrichEtape('loading');
  try {
    const payload = {
      url:                enrichUrl,
      domaine:            enrichDomaine,
      codeDomaine:        enrichCodeDomaine,
      categorieMere:      enrichCatMere,
      codeCategorieMere:  enrichCodeCatMere,
      categorieFille:     enrichCatFille,
      codeCategorieFille: enrichCodeCatFille,
      marque:             enrichMarque,
    };

    // Appel direct navigateur : Jina AI (lecture URL) + Gemini (extraction)
    // Timeout global de 90 secondes
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Délai dépassé (90 s). Réessayez ou utilisez une URL plus courte.')), 90_000)
    );

    const appel = (async () => {
      // ── Étape 1 : Jina AI Reader ──────────────────────────────────────────
      const jinaRes = await fetch(`https://r.jina.ai/${encodeURIComponent(payload.url)}`, {
        headers: { 'Accept': 'text/plain', 'X-Return-Format': 'markdown' },
      });
      if (!jinaRes.ok) throw new Error(`Impossible de lire l'URL (${jinaRes.status}). Vérifiez qu'elle est publique.`);
      const contenu = (await jinaRes.text()).slice(0, 60_000);

      // ── Étape 2 : OpenRouter API (modèle gratuit) ─────────────────────────
      const OR_KEY = import.meta.env.VITE_OPENROUTER_API_KEY;
      if (!OR_KEY) throw new Error('VITE_OPENROUTER_API_KEY manquante dans .env.local');

      const promptTexte = `Tu es un assistant spécialisé en extraction de données produits pour SOLSTICE.

CATÉGORIE CIBLE :
- Domaine : ${payload.domaine} (code ${payload.codeDomaine})
- Catégorie mère : ${payload.categorieMere} (code ${payload.codeCategorieMere})
- Catégorie fille : ${payload.categorieFille} (code ${payload.codeCategorieFille})
${payload.marque ? `- Marque : ${payload.marque}` : ''}

CATALOGUE FOURNISSEUR :
--- DEBUT ---
${contenu}
--- FIN ---

Extrais UNIQUEMENT les produits de la catégorie "${payload.categorieFille}".
Retourne un tableau JSON valide, sans texte avant ou après.
Format de chaque objet :
{"designation":"...","description":"...","marque":"...","refFournisseur":"...","unite":"pièce","prixUnitaire":0,"tva":"TTC"}
Si aucun produit : retourne [].`;

      const orRes = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${OR_KEY}`,
        },
        body: JSON.stringify({
          model: 'nvidia/nemotron-3-super-120b-a12b:free',
          messages: [{ role: 'user', content: promptTexte }],
          temperature: 0.1,
          max_tokens: 8192,
        }),
      });
      if (!orRes.ok) {
        if (orRes.status === 429) throw new Error('Quota OpenRouter atteint. Réessayez dans quelques minutes.');
        const det = await orRes.text();
        throw new Error(`Erreur OpenRouter (${orRes.status}) : ${det.slice(0, 200)}`);
      }
      const orData = await orRes.json();
      const jsonBrut = orData?.choices?.[0]?.message?.content ?? '[]';

      let articles = [];
      try { articles = JSON.parse(jsonBrut); } catch {
        const m = jsonBrut.match(/\[[\s\S]*\]/);
        if (m) try { articles = JSON.parse(m[0]); } catch { articles = []; }
      }
      if (!Array.isArray(articles)) articles = [];

      const unitesValides = ['pièce','mètre','litre','kg','boîte','rouleau','lot','m²','m³'];
      articles = articles
        .filter(a => a && typeof a.designation === 'string' && a.designation.trim())
        .map(a => ({
          designation:    String(a.designation || '').trim().slice(0, 255),
          description:    String(a.description || '').trim().slice(0, 500),
          marque:         String(a.marque || payload.marque || '').trim().slice(0, 100),
          refFournisseur: String(a.refFournisseur || '').trim().slice(0, 100),
          unite:          unitesValides.includes(String(a.unite||'').toLowerCase()) ? String(a.unite).toLowerCase() : 'pièce',
          prixUnitaire:   typeof a.prixUnitaire === 'number' ? Math.max(0, a.prixUnitaire) : 0,
          tva:            a.tva === 'HT' ? 'HT' : 'TTC',
        }));

      return { articles, sourceUrl: payload.url, articlesExtraits: articles.length, erreur: articles.length === 0 ? 'Aucun article extrait pour cette catégorie.' : null };
    })();

    const data = await Promise.race([appel, timeout]);
    if (data?.erreur && (!data?.articles || data.articles.length === 0)) {
      setEnrichErreur(data.erreur);
      setEnrichResultats([]);
      setEnrichEtape('resultats');
      return;
    }

    // Vérifier les doublons : ref exacte OU désignation identique dans même catFille
    const articlesAvecDoublons = (data?.articles || []).map(a => {
      const designationNorm = a.designation.trim().toLowerCase();
      const doublon = catalogue.some(c =>
        c.codeCategorieFille === enrichCodeCatFille &&
        c.designation.trim().toLowerCase() === designationNorm
      );
      return { ...a, _doublon: doublon };
    });

    setEnrichResultats(articlesAvecDoublons);
    // Pré-sélectionner tous les articles non-doublons
    setEnrichSelection(
      articlesAvecDoublons
        .map((a, i) => (!a._doublon ? i : null))
        .filter(i => i !== null)
    );
    if (data?.erreur) setEnrichErreur(data.erreur);
    setEnrichEtape('resultats');
  } catch (err) {
    setEnrichErreur(err.message || 'Erreur inattendue. Vérifiez la connexion et réessayez.');
    setEnrichResultats([]);
    setEnrichEtape('resultats');
  }
};

// Enregistre les articles validés dans Supabase
const validerEnrichissementIA = async () => {
  if (enrichSelection.length === 0) return;

  const articlesAInserer = enrichSelection.map(idx => enrichResultats[idx]);

  try {
    // Récupérer le max actuel UNE SEULE FOIS puis incrémenter localement
    const { data: existants } = await supabase
      .from('catalogue')
      .select('ref')
      .ilike('ref', `${enrichCodeCatFille}%`)
      .order('ref', { ascending: false });

    const sequences = (existants || [])
      .map(a => parseInt(a.ref.slice(-3), 10))
      .filter(n => !isNaN(n));
    let nextSeq = Math.max(0, ...sequences) + 1;

    // Utiliser saveCatalogueArticle du hook (gère les colonnes et RLS correctement)
    for (const article of articlesAInserer) {
      const ref = enrichCodeCatFille + String(nextSeq).padStart(3, '0');
      nextSeq++;
      await saveCatalogueArticle({
        ref,
        codeArticle:        ref,
        designation:        article.designation,
        description:        article.description || '',
        domaine:            enrichDomaine,
        categoreMere:       enrichCatMere,
        codeCategorieMere:  enrichCodeCatMere,
        categorieFille:     enrichCatFille,
        codeCategorieFille: enrichCodeCatFille,
        marque:             article.marque || enrichMarque || '',
        refFournisseur:     article.refFournisseur || '',
        unite:              article.unite || 'pièce',
        prixUnitaire:       article.prixUnitaire ?? 0,
        tva:                article.tva || 'TTC',
        actif:              true,
      }, null);
    }

    setShowEnrichissementIA(false);
    setEnrichResultats([]);
    setEnrichSelection([]);
    setEnrichEtape('form');
  } catch (err) {
    setEnrichErreur(`Erreur enregistrement : ${err.message}`);
  }
};

const getSuggestionsAutocomplete = (search) => {
if (!search || search.length < 2) return [];
const q = search.toLowerCase();
return catalogue.filter(a => a.actif && (
(a.ref || '').toLowerCase().includes(q) ||
(a.codeArticle || '').toLowerCase().includes(q) ||
a.designation.toLowerCase().includes(q) ||
(a.categorieFille || '').toLowerCase().includes(q)
)).slice(0, 8);
};

const insererArticleDepuisCatalogue = (lineIndex, art) => {
// art.unite is already the SOLSTICE code (M2, ML, KG, etc.) from Python import
const uniteCode = art.unite || 'PCS';
const newItems = [...currentDoc.items];
newItems[lineIndex] = {
...newItems[lineIndex],
description: '[' + (art.codeArticle || art.ref) + '] ' + art.designation,
unite: uniteCode,
prixUnitaire: art.prixUnitaire || 0,
marque: newItems[lineIndex].marque || '',
showMarque: newItems[lineIndex].showMarque || false,
modeleRef: newItems[lineIndex].modeleRef || '',
showModeleRef: newItems[lineIndex].showModeleRef || false,
};
setCurrentDoc({ ...currentDoc, items: newItems });
calculerTotaux(newItems);
setShowAutocomplete(false);
setAutocompleteIndex(null);
setAutocompleteSearch('');
};

// Fonctions modal parcourir catalogue — v62 optimisé avec useMemo + useDebounce
const ouvrirBrowseCatalogue = useCallback((lineIndex) => {
setBrowseTargetIndex(lineIndex);
setBrowseDomaine('tous');
setBrowseCatMere('toutes');
setBrowseCatFille('toutes');
setBrowseSearch('');
setShowBrowseCatalogue(true);
}, []);

// Index mémoïsé du catalogue actif — calculé une seule fois
const catalogueIndex = useMemo(() => {
const actifs = catalogue.filter(a => a.actif);
const byDomaine = {};
const byCatMere = {};
const byCatFille = {};
actifs.forEach((item, idx) => {
const d = item.domaine || 'Non classé';
if (!byDomaine[d]) byDomaine[d] = [];
byDomaine[d].push(idx);
const cm = item.categoreMere || 'Non classé';
if (!byCatMere[cm]) byCatMere[cm] = [];
byCatMere[cm].push(idx);
const cf = item.categorieFille || 'Non classé';
if (!byCatFille[cf]) byCatFille[cf] = [];
byCatFille[cf].push(idx);
});
return { actifs, byDomaine, byCatMere, byCatFille };
}, [catalogue]);

const debouncedBrowseSearch = useDebounce(browseSearch, 150);

// Listes de filtres mémoïsées
const getBrowseDomainesList = useCallback(() =>
Object.keys(catalogueIndex.byDomaine).sort(),
[catalogueIndex]);

const getBrowseCatsMeresList = useCallback(() => {
if (browseDomaine === 'tous') return Object.keys(catalogueIndex.byCatMere).sort();
const indices = new Set(catalogueIndex.byDomaine[browseDomaine] || []);
return [...new Set(catalogueIndex.actifs.filter((_, i) => indices.has(i)).map(a => a.categoreMere).filter(Boolean))].sort();
}, [catalogueIndex, browseDomaine]);

const getBrowseCatsFillesList = useCallback(() => {
if (browseCatMere === 'toutes') {
if (browseDomaine === 'tous') return Object.keys(catalogueIndex.byCatFille).sort();
const indices = new Set(catalogueIndex.byDomaine[browseDomaine] || []);
return [...new Set(catalogueIndex.actifs.filter((_, i) => indices.has(i)).map(a => a.categorieFille).filter(Boolean))].sort();
}
const indices = new Set(catalogueIndex.byCatMere[browseCatMere] || []);
return [...new Set(catalogueIndex.actifs.filter((_, i) => indices.has(i)).map(a => a.categorieFille).filter(Boolean))].sort();
}, [catalogueIndex, browseDomaine, browseCatMere]);

// Liste d'articles filtrée — mémoïsée avec pagination (50 par page)
const [browsePage, setBrowsePage] = useState(1);
const BROWSE_PAGE_SIZE = 50;

const browseArticlesFiltres = useMemo(() => {
let base = catalogueIndex.actifs;
if (browseDomaine !== 'tous') base = base.filter(a => a.domaine === browseDomaine);
if (browseCatMere !== 'toutes') base = base.filter(a => a.categoreMere === browseCatMere);
if (browseCatFille !== 'toutes') base = base.filter(a => a.categorieFille === browseCatFille);
if (debouncedBrowseSearch) {
const q = debouncedBrowseSearch.toLowerCase();
const terms = q.split(/\s+/).filter(t => t.length > 0);
base = base.filter(a => {
const txt = `${a.designation} ${a.ref||''} ${a.codeArticle||''} ${a.categorieFille||''}`.toLowerCase();
return terms.every(t => txt.includes(t));
});
}
return base;
}, [catalogueIndex, browseDomaine, browseCatMere, browseCatFille, debouncedBrowseSearch]);

const browseTotalPages = Math.ceil(browseArticlesFiltres.length / BROWSE_PAGE_SIZE);
const browseArticlesPaginés = useMemo(() => {
const start = (browsePage - 1) * BROWSE_PAGE_SIZE;
return browseArticlesFiltres.slice(start, start + BROWSE_PAGE_SIZE);
}, [browseArticlesFiltres, browsePage]);

// Reset page when filters change
useEffect(() => { setBrowsePage(1); }, [browseDomaine, browseCatMere, browseCatFille, debouncedBrowseSearch]);

// Compatibilité : getBrowseArticlesList() utilisée dans le JSX existant
const getBrowseArticlesList = useCallback(() => browseArticlesFiltres, [browseArticlesFiltres]);

const selectionnerArticleBrowse = useCallback((art) => {
insererArticleDepuisCatalogue(browseTargetIndex, art);
setShowBrowseCatalogue(false);
}, [browseTargetIndex]);

const changerBrowseDomaine = useCallback((val) => {
setBrowseDomaine(val);
setBrowseCatMere('toutes');
setBrowseCatFille('toutes');
}, []);
const changerBrowseCatMere = (val) => {
setBrowseCatMere(val);
setBrowseCatFille('toutes');
};

// Helper: description complète avec Marque / Modèle concaténés
const getItemSubtitle = (item) => {
const parts = [];
if (item.showMarque && item.marque) parts.push('Marque : ' + item.marque);
if (item.showModeleRef && item.modeleRef) parts.push('Modèle / Réf. : ' + item.modeleRef);
return parts.join('  —  ');
};
const getItemDescriptionComplete = (item) => {
const sub = getItemSubtitle(item);
return sub ? (item.description || '') + '  |  ' + sub : (item.description || '');
};

// Rendu des lignes de tableau d'impression (gère Article / Section / Note + sous-totaux)
const renderPrintItems = (items, config = {}) => {
const taxMode = config.taxMode !== undefined ? config.taxMode : currentDoc.taxMode;
const tauxTVA = config.tauxTVA !== undefined ? config.tauxTVA : (currentDoc.tauxTVA || settings.tauxTVA || 19.25);
const afficherModeTaxation = config.afficherModeTaxation !== undefined ? config.afficherModeTaxation : (currentDoc.afficherModeTaxation || false);
const stMap = calculerSousTotaux(items, taxMode, tauxTVA);
const rows = [];
const fs = config.tableBodySize || '8.5pt';
const cp = config.tableCellPadding || '4px 3px';
const compact = config.compact || false;

items.forEach((item, index) => {
const lt = item.lineType || 'article';
if (lt === 'section') {
rows.push(
<tr key={`ps-${index}`} style={{ background: '#F0F0F0', borderTop: '1px solid #888', borderBottom: '1px solid #888', pageBreakInside: 'avoid' }}>
<td colSpan={compact ? 5 : 6} style={{ padding: compact ? '3px 5px' : '4px 6px', fontWeight: 'bold', fontSize: fs, letterSpacing: '0.03em' }}>
▸ {item.sectionLabel || 'Section'}
</td>
</tr>
);
} else if (lt === 'note') {
rows.push(
<tr key={`pn-${index}`}>
<td colSpan={compact ? 5 : 6} style={{ padding: compact ? '2px 5px 2px 10px' : '3px 6px 3px 12px', fontStyle: 'italic', fontSize: '9pt', color: '#333', borderBottom: '0.5px dotted #ccc' }}>
✎ {item.noteLabel || ''}
</td>
</tr>
);
} else {
if (compact) {
rows.push(
<tr key={`pa-${index}`} style={{ borderBottom: '0.5px solid #E5E7EB', pageBreakInside: 'avoid' }}>
<td style={{ padding: cp, fontSize: fs }}>{getItemDescriptionComplete(item)}</td>
<td style={{ padding: cp, textAlign: 'center', fontSize: fs }}>{formatUnite(item.unite)}</td>
<td style={{ padding: cp, textAlign: 'center', fontSize: fs }}>{item.quantite}</td>
<td style={{ padding: cp, textAlign: 'right', fontSize: fs }}>{formatMontant(item.prixUnitaire)}</td>
<td style={{ padding: cp, textAlign: 'right', fontWeight: '600', fontSize: fs }}>{formatMontant(item.montantApresRemise || (item.quantite * item.prixUnitaire))}</td>
</tr>
);
} else {
rows.push(
<tr key={`pa-${index}`} style={{ borderBottom: '0.5px solid #ddd', pageBreakInside: 'avoid' }}>
<td style={{ padding: cp, verticalAlign: 'top' }}>{getItemDescriptionComplete(item)}</td>
<td style={{ padding: cp, textAlign: 'center', fontSize: '10pt' }}>{formatUnite(item.unite)}</td>
<td style={{ padding: cp, textAlign: 'center' }}>{item.quantite}</td>
<td style={{ padding: cp, textAlign: 'right', fontFamily: 'monospace', fontSize: '10pt' }}>{formatMontant(item.prixUnitaire)}</td>
<td style={{ padding: cp, textAlign: 'right', fontSize: '7.5pt', color: item.montantRemise > 0 ? '#000' : '#999' }}>
{item.montantRemise > 0 ? `-${formatMontant(item.montantRemise)}` : '—'}
</td>
<td style={{ padding: cp, textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>
{formatMontant(item.montantApresRemise || (item.quantite * item.prixUnitaire))}
</td>
</tr>
);
}
}
const next = items[index + 1];
const endOfGroup = !next || (next.lineType || 'article') === 'section';
if (lt !== 'section' && endOfGroup) {
let spi = null;
for (let j = index; j >= 0; j--) { if ((items[j].lineType || 'article') === 'section') { spi = j; break; } }
if (spi !== null && stMap[spi] !== undefined) {
const secLabel = items[spi].sectionLabel || 'Section';
rows.push(
<tr key={`pst-${spi}-${index}`} style={{ background: '#F7F7F7', borderTop: '0.5px solid #bbb', pageBreakInside: 'avoid' }}>
<td colSpan={compact ? 4 : 5} style={{ padding: '3px 6px', textAlign: 'right', fontSize: '9pt', color: '#444', fontStyle: 'italic' }}>
Sous-total {secLabel}{afficherModeTaxation ? ` (${taxMode === 'TTC' ? 'TTC' : 'HT'})` : ''} :
</td>
<td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace', fontSize: '9pt', fontWeight: 'bold' }}>
{formatMontant(stMap[spi])}
</td>
</tr>
);
}
}
});
return rows;
};

// Ajouter un article
const ajouterArticle = () => {
setCurrentDoc({
...currentDoc,
items: [...currentDoc.items, {
lineType: 'article',
description: '',
quantite: 1,
prixUnitaire: 0,
unite: 'PCS',
remiseType: 'aucune',
remiseValeur: 0,
montantRemise: 0,
montantApresRemise: 0,
marque: '',
showMarque: false,
modeleRef: '',
showModeleRef: false
}]
});
};

// Ajouter une section (titre de groupe)
const ajouterSection = () => {
setCurrentDoc({
...currentDoc,
items: [...currentDoc.items, {
lineType: 'section',
sectionLabel: 'Nouvelle section',
description: '',
quantite: 0,
prixUnitaire: 0,
unite: 'PCS',
remiseType: 'aucune',
remiseValeur: 0,
montantRemise: 0,
montantApresRemise: 0
}]
});
};

// Ajouter une note (commentaire libre)
const ajouterNote = () => {
setCurrentDoc({
...currentDoc,
items: [...currentDoc.items, {
lineType: 'note',
noteLabel: '',
description: '',
quantite: 0,
prixUnitaire: 0,
unite: 'PCS',
remiseType: 'aucune',
remiseValeur: 0,
montantRemise: 0,
montantApresRemise: 0
}]
});
};

// Insérer une ligne juste APRÈS l'index donné (depuis la poignée de drag)
const insererLigne = (afterIndex, type) => {
const newItem = type === 'section'
  ? { lineType: 'section', sectionLabel: 'Nouvelle section', description: '', quantite: 0, prixUnitaire: 0, unite: 'PCS', remiseType: 'aucune', remiseValeur: 0, montantRemise: 0, montantApresRemise: 0 }
  : type === 'note'
  ? { lineType: 'note', noteLabel: '', description: '', quantite: 0, prixUnitaire: 0, unite: 'PCS', remiseType: 'aucune', remiseValeur: 0, montantRemise: 0, montantApresRemise: 0 }
  : { lineType: 'article', description: '', quantite: 1, prixUnitaire: 0, unite: 'PCS', remiseType: 'aucune', remiseValeur: 0, montantRemise: 0, montantApresRemise: 0, marque: '', showMarque: false, modeleRef: '', showModeleRef: false }
const newItems = [...currentDoc.items]
newItems.splice(afterIndex + 1, 0, newItem)
setCurrentDoc({ ...currentDoc, items: newItems })
calculerTotaux(newItems)
setInsertMenu(null)
setHoveredHandleIndex(null)
};

// Calculer sous-totaux par section
// calculerSousTotaux : retourne { sectionIdx -> montant }
// Si taxMode === 'TTC', chaque article est compté TTC (montantHT * (1 + tauxTVA/100))
// Si taxMode === 'HT' ou absent, on prend juste le montant HT après remise
const calculerSousTotaux = (items, taxMode, tauxTVA) => {
const result = {};
let currentSectionIdx = null;
const tva = (taxMode === 'TTC') ? ((tauxTVA || settings.tauxTVA || 19.25) / 100) : 0;
(items || []).forEach((item, idx) => {
if ((item.lineType || 'article') === 'section') {
currentSectionIdx = idx;
result[idx] = 0;
} else if ((item.lineType || 'article') === 'article' && currentSectionIdx !== null) {
const montantHT = item.montantApresRemise !== undefined
? item.montantApresRemise
: ((item.quantite || 0) * (item.prixUnitaire || 0));
const montantLigne = montantHT * (1 + tva);
result[currentSectionIdx] = (result[currentSectionIdx] || 0) + montantLigne;
}
});
return result;
};

// Drag & Drop state refs
const dragSrcIndex = useRef(null);

// Déplacer un item (drag & drop)
const deplacerItem = (fromIndex, toIndex) => {
if (fromIndex === toIndex) return;
const newItems = [...currentDoc.items];
const [moved] = newItems.splice(fromIndex, 1);
newItems.splice(toIndex, 0, moved);
setCurrentDoc({ ...currentDoc, items: newItems });
calculerTotaux(newItems);
};

// Supprimer un article
const supprimerArticle = (index) => {
const newItems = currentDoc.items.filter((_, i) => i !== index);
setCurrentDoc({
...currentDoc,
items: newItems
});
calculerTotaux(newItems);
};

// Modifier un article
const modifierArticle = (index, field, value) => {
const newItems = [...currentDoc.items];
newItems[index] = {
...newItems[index],
[field]: field === 'quantite' || field === 'prixUnitaire' ? parseFloat(value) || 0 : value
};
setCurrentDoc({
...currentDoc,
items: newItems
});
calculerTotaux(newItems);
};

// Calculer ligne avec remise
const calculerLigneAvecRemise = (item) => {
// Les sections et notes ont toujours une valeur monétaire = 0
if (item.lineType === 'section' || item.lineType === 'note') {
return { ...item, montantAvantRemise: 0, montantRemise: 0, montantApresRemise: 0 };
}
const montantAvantRemise = (item.quantite || 0) * (item.prixUnitaire || 0);
let montantRemise = 0;

if (item.remiseType === 'pourcentage') {
montantRemise = (montantAvantRemise * (item.remiseValeur || 0)) / 100;
} else if (item.remiseType === 'montant') {
montantRemise = item.remiseValeur || 0;
}

const montantApresRemise = montantAvantRemise - montantRemise;

return {
...item,
montantAvantRemise,
montantRemise,
montantApresRemise
};
};

// Calculer tous les totaux du document
const calculerTotauxDocument = (doc) => {
// 1. Calculer chaque ligne avec remise
const itemsAvecRemises = (doc.items || []).map(calculerLigneAvecRemise);

// 2. Somme des lignes après remises individuelles
const sousTotal = itemsAvecRemises.reduce((sum, item) =>
sum + (item.montantApresRemise || 0), 0
);

// 3. Calcul remise globale
let montantRemiseGlobale = 0;
if (doc.remiseGlobale && doc.remiseGlobale.type === 'pourcentage') {
montantRemiseGlobale = (sousTotal * (doc.remiseGlobale.valeur || 0)) / 100;
} else if (doc.remiseGlobale && doc.remiseGlobale.type === 'montant') {
montantRemiseGlobale = doc.remiseGlobale.valeur || 0;
}

// 4. Somme des frais supplémentaires
const totalFrais = (doc.fraisSupplementaires || []).reduce((sum, frais) =>
sum + (frais.montant || 0), 0
);

// Frais taxables (inclus dans la base TVA) vs non-taxables (ajoutés après TVA)
// En mode HT la distinction n'a pas d'effet (tva=0 de toute façon)
const totalFraisTaxables = doc.taxMode === 'TTC'
? (doc.fraisSupplementaires || []).reduce((sum, f) => sum + (f.taxable !== false ? (f.montant || 0) : 0), 0)
: totalFrais;
const totalFraisNonTaxables = doc.taxMode === 'TTC'
? (doc.fraisSupplementaires || []).reduce((sum, f) => sum + (f.taxable === false ? (f.montant || 0) : 0), 0)
: 0;

// 5. Total avant taxe (base HT pour le calcul TVA)
const totalAvantTaxe = sousTotal - montantRemiseGlobale + totalFraisTaxables;

// 6. Calcul TVA
const tauxTVA = (doc.tauxTVA !== undefined ? doc.tauxTVA : (settings.tauxTVA || 19.25)) / 100;
const tva = doc.taxMode === 'TTC' ? (totalAvantTaxe * tauxTVA) : 0;

// 7. Total final (frais non-taxables ajoutés après TVA)
const total = totalAvantTaxe + tva + totalFraisNonTaxables;

// 8. Total des remises
const totalRemisesLignes = itemsAvecRemises.reduce((sum, item) =>
sum + (item.montantRemise || 0), 0
);
const totalRemises = totalRemisesLignes + montantRemiseGlobale;

// 9. Montant restant à payer
const montantPaye = (doc.paiements || []).reduce((sum, p) => sum + (p.montant || 0), 0);
const montantRestant = total - montantPaye;

return {
items: itemsAvecRemises,
sousTotal,
totalRemises,
remiseGlobale: {
...(doc.remiseGlobale || { type: 'aucune', valeur: 0 }),
montant: montantRemiseGlobale
},
totalAvantTaxe,
tva,
total,
montantPaye,
montantRestant
};
};

// Calculer pénalités de retard
const calculerPenalites = (facture) => {
if (!facture.penalitesRetard || !facture.penalitesRetard.actives || !facture.dateEcheance) {
return { joursRetard: 0, montant: 0 };
}

const dateEcheance = new Date(facture.dateEcheance);
const aujourdhui = new Date();

if (aujourdhui <= dateEcheance) {
return { joursRetard: 0, montant: 0 };
}

const joursRetard = Math.floor((aujourdhui - dateEcheance) / (1000 * 60 * 60 * 24));
const tauxJournalier = (facture.penalitesRetard.taux || 0.03) / 100;
const montantPenalites = (facture.montantRestant || 0) * tauxJournalier * joursRetard;

return {
joursRetard,
montant: Math.round(montantPenalites)
};
};

// Calculer date d'échéance
const calculerDateEcheance = (dateEmission, delaiJours) => {
const date = new Date(dateEmission);
date.setDate(date.getDate() + (delaiJours || 30));
return date.toISOString().split('T')[0];
};

// Calculer les totaux (version simplifiée pour compatibilité)
const calculerTotaux = (items) => {
const totaux = calculerTotauxDocument({ ...currentDoc, items });
setCurrentDoc(prev => ({
...prev,
...totaux
}));
};

// Télécharger PDF
const telechargerPDF = (doc) => {
setCurrentDoc(doc);
setShowPreview(true);

// Changer le titre de la page pour l'impression
const originalTitle = document.title;
document.title = `${doc.type === 'devis' ? 'Devis' : 'Facture'} ${doc.numero}`;

setTimeout(() => {
window.print();
// Restaurer le titre original après l'impression
setTimeout(() => {
document.title = originalTitle;
}, 1000);
}, 500);
};

// Fonction pour basculer la sélection d'un document
const toggleDocSelection = (docId) => {
setSelectedDocs(prev => {
if (prev.includes(docId)) {
return prev.filter(id => id !== docId);
} else {
return [...prev, docId];
}
});
};

// Fonction pour sélectionner tous les documents filtrés
const selectAllDocs = (filteredDocs) => {
const allIds = filteredDocs.map(doc => doc.id);
if (selectedDocs.length === allIds.length) {
setSelectedDocs([]);
} else {
setSelectedDocs(allIds);
}
};

// Fonction pour ouvrir le modal d'impression en lot
const ouvrirImpressionLot = () => {
if (selectedDocs.length === 0) {
alert('Veuillez sélectionner au moins un document');
return;
}
setShowBatchPrint(true);
};

// Fonction pour imprimer les documents sélectionnés en lot
const imprimerLot = () => {
if (selectedDocs.length === 0) return;

const docsAImprimer = documents.filter(doc => selectedDocs.includes(doc.id));
setShowBatchPrint(false);

// Variable pour stocker l'index du document en cours
let currentIndex = 0;

// Fonction pour imprimer le prochain document
const imprimerProchain = () => {
if (currentIndex >= docsAImprimer.length) {
// Tous les documents ont été imprimés
setShowPreview(false);
setSelectedDocs([]);
alert(`Impression terminée: ${docsAImprimer.length} document${docsAImprimer.length > 1 ? 's' : ''} envoyé${docsAImprimer.length > 1 ? 's' : ''} à l'imprimante`);
return;
}

const doc = docsAImprimer[currentIndex];
setCurrentDoc(doc);
setShowPreview(true);

// Changer le titre de la page pour l'impression
const originalTitle = document.title;
document.title = `${doc.type === 'devis' ? 'Devis' : 'Facture'} ${doc.numero} (${currentIndex + 1}/${docsAImprimer.length})`;

setTimeout(() => {
window.print();
// Restaurer le titre et passer au suivant
setTimeout(() => {
document.title = originalTitle;
currentIndex++;
imprimerProchain();
}, 1000);
}, 500);
};

// Démarrer l'impression du premier document
imprimerProchain();
};

// Gestion des clients
const ouvrirClients = () => {
setShowClients(true);
};

const nouveauClient = () => {
setCurrentClient({
id: Date.now(),
nom: '',
adresse: '',
telephone: '',
email: '',
nui: '',
rccm: ''
});
setShowClientForm(true);
};

const editerClient = (client) => {
setCurrentClient({...client});
setShowClientForm(true);
};

const supprimerClient = (id) => {
if (confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
_deleteClient(id).catch(err => alert('Erreur: ' + err.message));
}
};

const sauvegarderClient = async () => {
if (!currentClient.nom) {
alert('Veuillez renseigner le nom du client');
return;
}

try {
  await saveClient(currentClient);
} catch(err) {
  alert('Erreur client: ' + err.message);
  return;
}

setShowClientForm(false);
setCurrentClient(null);
};

const handleClientFormChange = (field, value) => {
setCurrentClient({
...currentClient,
[field]: value
});
};

const selectionnerClient = (client) => {
if (currentDoc) {
setCurrentDoc({
...currentDoc,
client: {
nom: client.nom,
adresse: client.adresse,
telephone: client.telephone,
email: client.email,
nui: client.nui || '',
rccm: client.rccm || '',
numeroBonCommande: currentDoc.client?.numeroBonCommande || '' // Garder le N° BC existant du document
}
});
}
setShowClients(false);
setSearchClient('');
};

const filteredClients = clients.filter(client => {
// Si pas de recherche, afficher tous les clients
if (!searchClient || searchClient.trim() === '') {
return true;
}

const search = searchClient.toLowerCase();
const nom = (client.nom || '').toLowerCase();
const email = (client.email || '').toLowerCase();
const telephone = (client.telephone || '');

return nom.includes(search) ||
email.includes(search) ||
telephone.includes(search);
});

// ============================================
// FONCTIONS FOURNISSEURS
// ============================================

const ouvrirFournisseurs = () => { setShowFournisseurs(true); };

const nouveauFournisseur = () => {
setCurrentFournisseur({
id: null,
nom: '',
adresse: '',
telephone: '',
email: '',
nui: '',
rccm: '',
compteBancaire: '',
delaiLivraison: ''
});
setShowFournisseurForm(true);
};

const editerFournisseur = (f) => {
setCurrentFournisseur({
  ...f,
  compteBancaire: f.compte_bancaire || '',
  delaiLivraison: f.delai_livraison || '',
});
setShowFournisseurForm(true);
};

const supprimerFournisseur = (id) => {
if (confirm('Êtes-vous sûr de vouloir supprimer ce fournisseur ?')) {
_deleteFournisseur(id).catch(err => alert('Erreur: ' + err.message));
}
};

const sauvegarderFournisseur = () => {
if (!currentFournisseur.nom) {
alert('Veuillez renseigner le nom du fournisseur');
return;
}
saveFournisseur(currentFournisseur).catch(err => alert('Erreur fournisseur: ' + err.message));
setShowFournisseurForm(false);
setCurrentFournisseur(null);
};

const handleFournisseurFormChange = (field, value) => {
setCurrentFournisseur({ ...currentFournisseur, [field]: value });
};

const filteredFournisseurs = fournisseurs.filter(f => {
if (!searchFournisseur || searchFournisseur.trim() === '') return true;
const s = searchFournisseur.toLowerCase();
return (f.nom||'').toLowerCase().includes(s) ||
       (f.email||'').toLowerCase().includes(s) ||
       (f.telephone||'').includes(s);
});

// ============================================
// FONCTIONS BONS DE COMMANDE
// ============================================

const generateBCNumber = () => {
const now = new Date();
const annee = now.getFullYear();
const mois = String(now.getMonth() + 1).padStart(2, '0');
const jour = String(now.getDate()).padStart(2, '0');

// Même logique codeAgent que les devis/factures
let codeAgent = 'N0';
if (settingsEffectifs.agent && settingsEffectifs.agent.trim().length >= 2) {
codeAgent = settingsEffectifs.agent.trim().substring(0, 2).toUpperCase();
} else if (settingsEffectifs.agent && settingsEffectifs.agent.trim().length === 1) {
codeAgent = settingsEffectifs.agent.trim().toUpperCase() + '0';
}

// Numéro séquentiel basé sur le total des BC existants
const numeroSequentiel = String(bonsCommande.length + 1).padStart(4, '0');

// Format: BC + AAAA + MM + codeAgent + JJ - NNNN
// Exemple: BC20260330CM25-0001
return `BC${annee}${mois}${codeAgent}${jour}-${numeroSequentiel}`;
};

const nouveauBC = () => {
const now = new Date();
setCurrentBC({
id: Date.now(),
numero: generateBCNumber(),
date: now.toISOString().split('T')[0],
dateMode: 'auto',
dateTime: now.toISOString(),
statut: 'en-attente',
objet: '',
fournisseur: { nom: '', adresse: '', telephone: '', email: '', nui: '', rccm: '' },
items: [],
sousTotal: 0,
tva: 0,
total: 0,
taxMode: 'TTC',
afficherModeTaxation: false,
notes: '',
delaiLivraison: '',
lieuLivraison: '',
modeleBC: 'classique'
});
setShowBCModal(true);
};

const editerBC = async (bc) => {
try {
  const bcFrais = await getBonCommande(bc.id)
  setCurrentBC({...bcFrais, items: (bcFrais.items || []).map(i => ({...i}))})
} catch {
  setCurrentBC({...bc, items: (bc.items || []).map(i => ({...i}))})
}
setShowBCModal(true);
};

const supprimerBC = (id) => {
if (confirm('Êtes-vous sûr de vouloir supprimer ce bon de commande ?')) {
_deleteBonCommande(id).catch(err => alert('Erreur: ' + err.message));
}
};

const sauvegarderBC = async () => {
document.querySelectorAll('.lt-popup').forEach(p => p.remove());
if (isBCSaving) return;
if (!currentBC?.fournisseur?.nom) {
  alert('Veuillez renseigner le nom du fournisseur');
  return;
}
setIsBCSaving(true);
// Promise.race pur — saveBonCommande utilise fetch() natif (bypass auth.getSession() Supabase)
const saveWithTimeout = (bc) => Promise.race([
  saveBonCommande(bc),
  new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 30000))
]);
let saveOk = false;
try {
  try {
    await saveWithTimeout(currentBC);
    saveOk = true;
  } catch (firstErr) {
    if (firstErr.message === 'timeout') {
      await new Promise(r => setTimeout(r, 5000));
      await saveWithTimeout(currentBC);
      saveOk = true;
    } else {
      throw firstErr;
    }
  }
} catch (err) {
  const msg = err.message === 'timeout'
    ? 'Délai dépassé après 2 tentatives — vérifiez votre connexion'
    : err.message;
  alert('Erreur BC: ' + msg);
} finally {
  setIsBCSaving(false);
  if (saveOk) {
    setShowBCModal(false);
    setCurrentBC(null);
  }
}
};

const handleBCFournisseurChange = (field, value) => {
setCurrentBC({ ...currentBC, fournisseur: { ...currentBC.fournisseur, [field]: value } });
};

const selectionnerFournisseurPourBC = (f) => {
setCurrentBC({
...currentBC,
fournisseur: { nom: f.nom, adresse: f.adresse||'', telephone: f.telephone||'', email: f.email||'', nui: f.nui||'', rccm: f.rccm||'' },
delaiLivraison: currentBC.delaiLivraison || f.delaiLivraison || ''
});
setShowFournisseurSelectInBC(false);
setSearchFournisseurBC('');
};

const ajouterArticleBC = () => {
setCurrentBC({
...currentBC,
items: [...(currentBC.items||[]), { id: Date.now(), description: '', unite: 'PCS', quantite: 1, prixUnitaire: 0, total: 0 }]
});
};

const modifierArticleBC = (index, field, value) => {
const items = (currentBC.items||[]).map((item, i) => {
if (i !== index) return item;
const updated = { ...item, [field]: value };
if (field === 'quantite' || field === 'prixUnitaire') {
updated.total = (parseFloat(updated.quantite)||0) * (parseFloat(updated.prixUnitaire)||0);
}
return updated;
});
const sousTotal = items.reduce((s, it) => s + (it.total||0), 0);
const tauxTVAVal = settings.tauxTVA || 19.25;
const tvaVal = currentBC.taxMode === 'TTC' ? sousTotal * (tauxTVAVal / 100) : 0;
setCurrentBC({ ...currentBC, items, sousTotal, tva: tvaVal, total: sousTotal + tvaVal });
};

const supprimerArticleBC = (index) => {
const items = (currentBC.items||[]).filter((_, i) => i !== index);
const sousTotal = items.reduce((s, it) => s + (it.total||0), 0);
const tauxTVAVal = settings.tauxTVA || 19.25;
const tvaVal = currentBC.taxMode === 'TTC' ? sousTotal * (tauxTVAVal / 100) : 0;
setCurrentBC({ ...currentBC, items, sousTotal, tva: tvaVal, total: sousTotal + tvaVal });
};

const recalculerTotauxBC = (items, taxMode) => {
const sousTotal = (items||[]).reduce((s, it) => s + (it.total||0), 0);
const tauxTVAVal = settings.tauxTVA || 19.25;
const tvaVal = taxMode === 'TTC' ? sousTotal * (tauxTVAVal / 100) : 0;
return { sousTotal, tva: tvaVal, total: sousTotal + tvaVal };
};

const imprimerBC = (bc) => {
const modele = bc.modeleBC || 'classique';
const tauxTVA = settings.tauxTVA || 19.25;
const articleItems = (bc.items||[]).filter(i => (i.lineType||'article') === 'article');

const renderLignesHTML = () => {
return (bc.items||[]).map((item, idx) => {
const lt = item.lineType || 'article';
if (lt === 'section') {
return `<tr style="background:#F0F0F0;border-top:1px solid #999;border-bottom:1px solid #999"><td colspan="6" style="padding:6px 8px;font-weight:bold;font-size:9pt">▸ ${item.sectionLabel||'Section'}</td></tr>`;
} else if (lt === 'note') {
return `<tr><td colspan="6" style="padding:3px 8px 3px 16px;font-style:italic;font-size:8pt;color:#666">✎ ${item.noteLabel||''}</td></tr>`;
} else {
const totalHT = (item.quantite||0) * (item.prixUnitaire||0);
const tvI = bc.taxMode === 'TTC' ? totalHT * (tauxTVA/100) : 0;
const totalLine = totalHT + tvI;
return `<tr style="border-bottom:1px solid #E2E8F0">
<td style="padding:6px 8px">${item.description||''}${item.marque ? '<br><span style="font-size:7pt;color:#888">Marque: '+item.marque+'</span>' : ''}</td>
<td style="padding:6px 8px;text-align:center">${item.unite||'PCS'}</td>
<td style="padding:6px 8px;text-align:center">${item.quantite||0}</td>
<td style="padding:6px 8px;text-align:right;font-family:monospace">${(item.prixUnitaire||0).toLocaleString('fr-FR')}</td>
${bc.taxMode==='TTC' ? `<td style="padding:6px 8px;text-align:center">${tauxTVA}%</td>` : ''}
<td style="padding:6px 8px;text-align:right;font-family:monospace;font-weight:bold">${totalLine.toLocaleString('fr-FR')}</td>
</tr>`;
}
}).join('');
};

let bodyHTML = '';

if (modele === 'classique') {
bodyHTML = `
<div style="font-family:Arial,sans-serif;padding:2rem;background:white;max-width:900px;margin:0 auto">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:1.5rem;padding-bottom:1rem;border-bottom:3px solid #0A2540">
    <div>
      ${settings.logo ? `<img src="${settings.logo}" style="max-height:60px;max-width:160px;margin-bottom:0.5rem;display:block" alt="logo"/>` : ''}
      <div style="font-weight:800;font-size:1.2rem;color:#0A2540">${settings.raisonSociale||'SOLSTICE'}</div>
      <div style="font-size:0.78rem;color:#64748B;line-height:1.6">${settings.adresse||''}</div>
      ${settings.telephone ? `<div style="font-size:0.78rem;color:#64748B">📞 ${settings.telephone}</div>` : ''}
      ${settings.email ? `<div style="font-size:0.78rem;color:#64748B">✉ ${settings.email}</div>` : ''}
      ${settings.nui ? `<div style="font-size:0.75rem;color:#64748B">NUI: ${settings.nui}</div>` : ''}
      ${settings.rccm ? `<div style="font-size:0.75rem;color:#64748B">RCCM: ${settings.rccm}</div>` : ''}
    </div>
    <div style="text-align:right">
      <div style="background:linear-gradient(135deg,#0A2540,#1E3A5F);color:white;padding:0.75rem 1.5rem;border-radius:10px;margin-bottom:0.75rem">
        <div style="font-size:0.7rem;font-weight:700;letter-spacing:0.1em;opacity:0.85">BON DE COMMANDE N°</div>
        <div style="font-size:1.3rem;font-weight:800;font-family:monospace">${bc.numero}</div>
      </div>
      <div style="font-size:0.8rem;color:#64748B">Date : <strong>${new Date(bc.date).toLocaleDateString('fr-FR')}</strong></div>
      ${bc.statut ? `<div style="margin-top:0.4rem;padding:0.25rem 0.75rem;background:${bc.statut==='recu'?'#DCFCE7':bc.statut==='annule'?'#FEE2E2':'#FEF3C7'};color:${bc.statut==='recu'?'#166534':bc.statut==='annule'?'#991B1B':'#92400E'};border-radius:6px;font-size:0.75rem;font-weight:700;display:inline-block">${bc.statut==='en-attente'?'En attente':bc.statut==='envoye'?'Envoyé':bc.statut==='recu-partiel'?'Reçu partiellement':bc.statut==='recu'?'Reçu':'Annulé'}</div>` : ''}
    </div>
  </div>
  ${bc.objet ? `<div style="margin-bottom:1.5rem;padding:0.75rem 1rem;background:#F0F4FF;border-left:4px solid #0A2540;border-radius:4px"><div style="font-size:0.7rem;color:#64748B;font-weight:700;text-transform:uppercase;margin-bottom:0.25rem">Objet</div><div style="font-size:0.95rem;font-weight:600;color:#1E293B">${bc.objet}</div></div>` : ''}
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin-bottom:1.5rem">
    <div style="padding:1rem;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:8px">
      <div style="font-size:0.7rem;font-weight:700;color:#0A2540;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:0.5rem">🏭 FOURNISSEUR</div>
      <div style="font-weight:700;color:#1E293B;font-size:1rem">${(bc.fournisseur?.nom||'')}</div>
      ${bc.fournisseur?.adresse ? `<div style="font-size:0.78rem;color:#64748B;margin-top:0.25rem">${bc.fournisseur?.adresse}</div>` : ''}
      ${bc.fournisseur?.telephone ? `<div style="font-size:0.78rem;color:#64748B">📞 ${bc.fournisseur?.telephone}</div>` : ''}
      ${bc.fournisseur?.email ? `<div style="font-size:0.78rem;color:#64748B">✉ ${bc.fournisseur?.email}</div>` : ''}
      ${bc.fournisseur?.nui ? `<div style="font-size:0.75rem;color:#64748B;margin-top:0.25rem">NUI: ${bc.fournisseur?.nui}</div>` : ''}
      ${bc.fournisseur?.rccm ? `<div style="font-size:0.75rem;color:#64748B">RCCM: ${bc.fournisseur?.rccm}</div>` : ''}
    </div>
    <div style="padding:1rem;background:#F0F4FF;border:1px solid #C7D2FE;border-radius:8px">
      <div style="font-size:0.7rem;font-weight:700;color:#0A2540;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:0.5rem">📦 LIVRAISON</div>
      ${bc.delaiLivraison ? `<div style="font-size:0.85rem;color:#1E293B"><strong>Délai :</strong> ${bc.delaiLivraison}</div>` : '<div style="font-size:0.8rem;color:#94A3B8">Délai non précisé</div>'}
      ${bc.lieuLivraison ? `<div style="font-size:0.85rem;color:#1E293B;margin-top:0.25rem"><strong>Lieu :</strong> ${bc.lieuLivraison}</div>` : ''}
    </div>
  </div>
  <table style="width:100%;border-collapse:collapse;font-size:0.85rem;margin-bottom:1.5rem">
    <thead><tr style="background:linear-gradient(135deg,#0A2540,#1E3A5F);color:white">
      <th style="padding:0.75rem;text-align:left">Désignation</th>
      <th style="padding:0.75rem;text-align:center;width:70px">Unité</th>
      <th style="padding:0.75rem;text-align:center;width:70px">Qté</th>
      <th style="padding:0.75rem;text-align:right;width:120px">Prix Unit.</th>
      ${bc.taxMode==='TTC' ? '<th style="padding:0.75rem;text-align:center;width:60px">TVA</th>' : ''}
      <th style="padding:0.75rem;text-align:right;width:130px">Total</th>
    </tr></thead>
    <tbody>${renderLignesHTML()}</tbody>
  </table>
  <div style="display:flex;justify-content:flex-end;margin-bottom:1.5rem">
    <div style="min-width:360px;background:#F8FAFC;border:2px solid #E2E8F0;border-radius:10px;padding:1.25rem">
      <div style="display:flex;justify-content:space-between;margin-bottom:0.5rem;font-size:0.875rem"><span style="color:#64748B">${bc.afficherModeTaxation ? 'Sous-total HT:' : 'Sous-total:'}</span><span style="font-family:monospace;font-weight:600">${bc.sousTotal.toLocaleString('fr-FR')} FCFA</span></div>
      ${bc.taxMode==='TTC' ? `<div style="display:flex;justify-content:space-between;margin-bottom:0.75rem;padding-bottom:0.75rem;border-bottom:1px solid #E2E8F0;font-size:0.875rem"><span style="color:#64748B">TVA (${tauxTVA}%):</span><span style="font-family:monospace;font-weight:600">${bc.tva.toLocaleString('fr-FR')} FCFA</span></div>` : '<div style="border-bottom:1px solid #E2E8F0;margin-bottom:0.75rem"></div>'}
      <div style="display:flex;justify-content:space-between;align-items:center;padding-top:0.5rem;border-top:3px solid #0A2540">
        <span style="font-size:1.1rem;font-weight:bold;color:#0A2540">${bc.afficherModeTaxation ? `TOTAL ${bc.taxMode==='TTC'?'TTC':'HT'}:` : 'TOTAL:'}</span>
        <span style="font-size:1.4rem;font-weight:bold;font-family:monospace;color:#FF6B35">${bc.total.toLocaleString('fr-FR')} FCFA</span>
      </div>
    </div>
  </div>
  ${bc.notes ? `<div style="padding:0.75rem 1rem;background:#FEF3C7;border-left:4px solid #F59E0B;border-radius:4px;margin-bottom:1.5rem"><div style="font-weight:700;font-size:0.8rem;color:#92400E;margin-bottom:0.4rem">NOTES / CONDITIONS:</div><div style="font-size:0.8rem;color:#78350F;white-space:pre-wrap">${bc.notes}</div></div>` : ''}
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:3rem;margin-top:2.5rem;padding-top:1rem;border-top:2px solid #E2E8F0">
    <div style="text-align:center"><div style="border-top:1px solid #CBD5E1;padding-top:0.5rem;margin-top:3rem;font-size:0.75rem;color:#64748B">Signature & Cachet — ${settings.raisonSociale||'SOLSTICE'}</div></div>
    <div style="text-align:center"><div style="border-top:1px solid #CBD5E1;padding-top:0.5rem;margin-top:3rem;font-size:0.75rem;color:#64748B">Signature & Cachet — Fournisseur</div></div>
  </div>
</div>`;
} else if (modele === 'compact') {
bodyHTML = `
<div style="font-family:Arial,sans-serif;padding:1.5rem;background:#F5F5F0;max-width:900px;margin:0 auto">
  <div style="background:white;border:2px solid #000;padding:1.5rem;margin-bottom:1rem">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:1rem;padding-bottom:1rem;border-bottom:2px solid #000">
      <div>${settings.logo ? `<img src="${settings.logo}" style="max-height:50px;max-width:120px;display:block;margin-bottom:0.4rem" alt="logo"/>` : ''}<div style="font-weight:bold;font-size:1.1rem;text-transform:uppercase">${settings.raisonSociale||'SOLSTICE'}</div><div style="font-size:0.75rem;color:#555">${settings.adresse||''}</div>${settings.telephone ? `<div style="font-size:0.75rem;color:#555">Tél: ${settings.telephone}</div>` : ''}${settings.nui ? `<div style="font-size:0.72rem;color:#555">NUI: ${settings.nui}</div>` : ''}</div>
      <div style="text-align:right"><div style="font-size:1.8rem;font-weight:bold;letter-spacing:0.25em;border:2px solid #000;padding:0.5rem 1rem">BON DE COMMANDE</div><div style="font-family:monospace;font-size:1rem;margin-top:0.5rem">N° ${bc.numero}</div><div style="font-size:0.8rem;margin-top:0.3rem">Date: ${new Date(bc.date).toLocaleDateString('fr-FR')}</div></div>
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin-bottom:1rem">
      <div style="border:1px solid #000;padding:0.75rem"><div style="font-size:0.7rem;font-weight:bold;text-transform:uppercase;margin-bottom:0.4rem">Fournisseur</div><div style="font-weight:bold">${(bc.fournisseur?.nom||'')}</div>${bc.fournisseur?.adresse ? `<div style="font-size:0.75rem">${bc.fournisseur?.adresse}</div>` : ''}${bc.fournisseur?.telephone ? `<div style="font-size:0.75rem">Tél: ${bc.fournisseur?.telephone}</div>` : ''}${bc.fournisseur?.nui ? `<div style="font-size:0.72rem">NUI: ${bc.fournisseur?.nui}</div>` : ''}</div>
      <div style="border:1px solid #000;padding:0.75rem"><div style="font-size:0.7rem;font-weight:bold;text-transform:uppercase;margin-bottom:0.4rem">Livraison</div>${bc.delaiLivraison ? `<div style="font-size:0.82rem"><strong>Délai:</strong> ${bc.delaiLivraison}</div>` : ''} ${bc.lieuLivraison ? `<div style="font-size:0.82rem"><strong>Lieu:</strong> ${bc.lieuLivraison}</div>` : ''}</div>
    </div>
    ${bc.objet ? `<div style="border:1px solid #ddd;padding:0.5rem 0.75rem;margin-bottom:1rem;font-size:0.85rem"><strong>Objet:</strong> ${bc.objet}</div>` : ''}
    <table style="width:100%;border-collapse:collapse;font-size:0.8rem">
      <thead><tr style="border:1px solid #000;border-bottom:2px solid #000"><th style="padding:0.5rem;text-align:left;border-right:1px solid #ccc">Désignation</th><th style="padding:0.5rem;text-align:center;border-right:1px solid #ccc;width:60px">Unité</th><th style="padding:0.5rem;text-align:center;border-right:1px solid #ccc;width:60px">Qté</th><th style="padding:0.5rem;text-align:right;border-right:1px solid #ccc;width:110px">Prix Unit.</th>${bc.taxMode==='TTC'?'<th style="padding:0.5rem;text-align:center;border-right:1px solid #ccc;width:55px">TVA</th>':''}<th style="padding:0.5rem;text-align:right;width:120px">Total</th></tr></thead>
      <tbody>${renderLignesHTML()}</tbody>
    </table>
    <div style="display:flex;justify-content:flex-end;margin-top:1rem">
      <div style="width:300px">
        <div style="display:flex;justify-content:space-between;padding:0.3rem 0;font-size:0.82rem;border-bottom:1px solid #ccc"><span>${bc.afficherModeTaxation ? 'Sous-total HT:' : 'Sous-total:'}</span><span style="font-family:monospace">${bc.sousTotal.toLocaleString('fr-FR')} FCFA</span></div>
        ${bc.taxMode==='TTC' ? `<div style="display:flex;justify-content:space-between;padding:0.3rem 0;font-size:0.82rem;border-bottom:1px solid #ccc"><span>TVA (${tauxTVA}%):</span><span style="font-family:monospace">${bc.tva.toLocaleString('fr-FR')} FCFA</span></div>` : ''}
        <div style="display:flex;justify-content:space-between;padding:0.5rem 0;font-size:1rem;font-weight:bold;border:2px solid #000;padding:0.5rem"><span>${bc.afficherModeTaxation ? `TOTAL ${bc.taxMode==='TTC'?'TTC':'HT'}:` : 'TOTAL:'}</span><span style="font-family:monospace">${bc.total.toLocaleString('fr-FR')} FCFA</span></div>
      </div>
    </div>
    ${bc.notes ? `<div style="margin-top:1rem;padding:0.6rem;border:1px solid #F59E0B;background:#FEF3C7;font-size:0.78rem"><strong>Notes:</strong> ${bc.notes}</div>` : ''}
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:3rem;margin-top:2rem;padding-top:1rem;border-top:1px solid #000">
      <div style="text-align:center"><div style="border-top:1px solid #000;padding-top:0.4rem;margin-top:2.5rem;font-size:0.72rem">Signature — ${settings.raisonSociale||'SOLSTICE'}</div></div>
      <div style="text-align:center"><div style="border-top:1px solid #000;padding-top:0.4rem;margin-top:2.5rem;font-size:0.72rem">Signature — Fournisseur</div></div>
    </div>
  </div>
</div>`;
} else if (modele === 'moderne') {
// MODÈLE MODERNE - Adapté du modèle devis
bodyHTML = `
<div style="font-family:Arial,sans-serif;background:#F5F5F0;padding:0.625rem">
  <div style="background:white;max-width:900px;margin:0 auto">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;padding:1.5rem;border-bottom:3px solid #1E3A5F">
      <div>
        ${settings.logo ? `<img src="${settings.logo}" style="max-height:55px;max-width:150px;margin-bottom:0.5rem;display:block" alt="logo"/>` : ''}
        <div style="font-weight:800;font-size:1.2rem;color:#1E293B">${settings.raisonSociale||'SOLSTICE'}</div>
        <div style="font-size:0.78rem;color:#64748B;line-height:1.5">${settings.adresse||''}</div>
        ${settings.telephone ? `<div style="font-size:0.78rem;color:#64748B">📞 ${settings.telephone}</div>` : ''}
        ${settings.email ? `<div style="font-size:0.78rem;color:#64748B">✉ ${settings.email}</div>` : ''}
      </div>
      <div style="text-align:right">
        <div style="background:#1E3A5F;color:white;padding:0.75rem 1.25rem;border-radius:8px;margin-bottom:0.5rem">
          <div style="font-size:0.65rem;font-weight:700;letter-spacing:0.1em;opacity:0.8">BON DE COMMANDE</div>
          <div style="font-size:1.2rem;font-weight:800;font-family:monospace">${bc.numero}</div>
        </div>
        <div style="font-size:0.75rem;color:#64748B">📅 ${new Date(bc.date).toLocaleDateString('fr-FR')}</div>
      </div>
    </div>
    ${bc.objet ? `<div style="margin:1rem 1.5rem;padding:0.75rem;background:#F0F4FF;border-left:3px solid #1E3A5F"><div style="font-size:0.7rem;font-weight:700;color:#64748B;margin-bottom:0.25rem">OBJET</div><div style="font-weight:600;color:#1E293B">${bc.objet}</div></div>` : ''}
    <div style="padding:0 1.5rem">
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin-bottom:1.5rem">
        <div style="padding:1rem;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:6px">
          <div style="font-size:0.7rem;font-weight:700;color:#1E3A5F;margin-bottom:0.5rem">🏭 FOURNISSEUR</div>
          <div style="font-weight:700;color:#1E293B">${(bc.fournisseur?.nom||'')}</div>
          ${bc.fournisseur?.adresse ? `<div style="font-size:0.75rem;color:#64748B;margin-top:0.2rem">${bc.fournisseur?.adresse}</div>` : ''}
          ${bc.fournisseur?.telephone ? `<div style="font-size:0.75rem;color:#64748B">📞 ${bc.fournisseur?.telephone}</div>` : ''}
        </div>
        <div style="padding:1rem;background:#F0F4FF;border:1px solid #C7D2FE;border-radius:6px">
          <div style="font-size:0.7rem;font-weight:700;color:#1E3A5F;margin-bottom:0.5rem">📦 LIVRAISON</div>
          ${bc.delaiLivraison ? `<div style="font-size:0.8rem;color:#1E293B"><strong>Délai:</strong> ${bc.delaiLivraison}</div>` : '<div style="font-size:0.75rem;color:#94A3B8">Non précisé</div>'}
          ${bc.lieuLivraison ? `<div style="font-size:0.8rem;color:#1E293B;margin-top:0.2rem"><strong>Lieu:</strong> ${bc.lieuLivraison}</div>` : ''}
        </div>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:10pt;margin-bottom:1.5rem">
        <thead><tr style="background:#1E3A5F;color:white">
          <th style="padding:0.75rem;text-align:left">DÉSIGNATION</th>
          <th style="padding:0.75rem;text-align:center;width:70px">UNITÉ</th>
          <th style="padding:0.75rem;text-align:center;width:70px">QTÉ</th>
          <th style="padding:0.75rem;text-align:right;width:110px">PRIX UNIT.</th>
          ${bc.taxMode==='TTC' ? '<th style="padding:0.75rem;text-align:center;width:60px">TVA</th>' : ''}
          <th style="padding:0.75rem;text-align:right;width:120px">TOTAL</th>
        </tr></thead>
        <tbody>${renderLignesHTML()}</tbody>
      </table>
      <div style="display:flex;justify-content:flex-end;margin-bottom:1.5rem">
        <div style="min-width:340px;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:8px;padding:1rem">
          <div style="display:flex;justify-content:space-between;margin-bottom:0.4rem;font-size:0.85rem"><span style="color:#64748B">${bc.afficherModeTaxation ? 'Sous-total HT:' : 'Sous-total:'}</span><span style="font-family:monospace;font-weight:600">${bc.sousTotal.toLocaleString('fr-FR')} FCFA</span></div>
          ${bc.taxMode==='TTC' ? `<div style="display:flex;justify-content:space-between;padding-bottom:0.75rem;border-bottom:1px solid #E2E8F0;font-size:0.85rem"><span style="color:#64748B">TVA (${tauxTVA}%):</span><span style="font-family:monospace;font-weight:600">${bc.tva.toLocaleString('fr-FR')} FCFA</span></div>` : ''}
          <div style="display:flex;justify-content:space-between;align-items:center;padding-top:0.75rem;border-top:2px solid #1E3A5F">
            <span style="font-size:1rem;font-weight:bold;color:#1E293B">${bc.afficherModeTaxation ? `TOTAL ${bc.taxMode==='TTC'?'TTC':'HT'}:` : 'TOTAL:'}</span>
            <span style="font-size:1.3rem;font-weight:bold;font-family:monospace;color:#1E3A5F">${bc.total.toLocaleString('fr-FR')} FCFA</span>
          </div>
        </div>
      </div>
      ${bc.notes ? `<div style="padding:0.75rem;background:#FEF3C7;border-radius:6px;border-left:3px solid #F59E0B;margin-bottom:1.5rem"><div style="font-weight:700;font-size:0.75rem;color:#92400E;margin-bottom:0.3rem">📝 NOTES</div><div style="font-size:0.8rem;color:#78350F;white-space:pre-wrap">${bc.notes}</div></div>` : ''}
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:2rem;padding:1.5rem 0 1rem">
        <div style="text-align:center"><div style="border-top:1px solid #000;padding-top:0.4rem;margin-top:2.5rem;font-size:0.7rem;color:#64748B">Signature — ${settings.raisonSociale||'SOLSTICE'}</div></div>
        <div style="text-align:center"><div style="border-top:1px solid #000;padding-top:0.4rem;margin-top:2.5rem;font-size:0.7rem;color:#64748B">Signature — Fournisseur</div></div>
      </div>
    </div>
  </div>
</div>`;
} else if (modele === 'compact') {
// MODÈLE COMPACT
bodyHTML = `
<div style="font-family:Arial,sans-serif;background:#F5F5F0;padding:0.625rem">
  <div style="background:white;max-width:900px;margin:0 auto;padding:1.5rem">
    <div style="display:flex;justify-content:space-between;margin-bottom:1rem;padding-bottom:0.75rem;border-bottom:2px solid #000">
      <div>
        ${settings.logo ? `<img src="${settings.logo}" style="max-height:50px;max-width:140px;margin-bottom:0.4rem;display:block" alt="logo"/>` : ''}
        <div style="font-weight:700;font-size:1.1rem;color:#000">${settings.raisonSociale||'SOLSTICE'}</div>
        <div style="font-size:0.7rem;color:#333">${settings.adresse||''}</div>
        ${settings.telephone ? `<div style="font-size:0.7rem;color:#333">${settings.telephone}</div>` : ''}
      </div>
      <div style="text-align:right">
        <div style="font-size:0.6rem;font-weight:700;color:#666;letter-spacing:0.1em">BON DE COMMANDE</div>
        <div style="font-size:1.2rem;font-weight:800;font-family:monospace;color:#000">${bc.numero}</div>
        <div style="font-size:0.7rem;color:#666">${new Date(bc.date).toLocaleDateString('fr-FR')}</div>
      </div>
    </div>
    ${bc.objet ? `<div style="margin-bottom:1rem;padding:0.6rem 0.8rem;background:#F5F5F5;border-left:2px solid #000"><div style="font-size:0.65rem;font-weight:700;color:#666;margin-bottom:0.2rem">OBJET</div><div style="font-size:0.85rem;font-weight:600;color:#000">${bc.objet}</div></div>` : ''}
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.75rem;margin-bottom:1rem">
      <div style="padding:0.75rem;border:1px solid #DDD">
        <div style="font-size:0.65rem;font-weight:700;color:#666;margin-bottom:0.4rem">FOURNISSEUR</div>
        <div style="font-weight:700;font-size:0.9rem;color:#000">${(bc.fournisseur?.nom||'')}</div>
        ${bc.fournisseur?.adresse ? `<div style="font-size:0.7rem;color:#333;margin-top:0.2rem">${bc.fournisseur?.adresse}</div>` : ''}
        ${bc.fournisseur?.telephone ? `<div style="font-size:0.7rem;color:#333">${bc.fournisseur?.telephone}</div>` : ''}
      </div>
      <div style="padding:0.75rem;border:1px solid #DDD">
        <div style="font-size:0.65rem;font-weight:700;color:#666;margin-bottom:0.4rem">LIVRAISON</div>
        ${bc.delaiLivraison ? `<div style="font-size:0.75rem;color:#000">Délai: ${bc.delaiLivraison}</div>` : '<div style="font-size:0.7rem;color:#999">Non précisé</div>'}
        ${bc.lieuLivraison ? `<div style="font-size:0.75rem;color:#000;margin-top:0.2rem">Lieu: ${bc.lieuLivraison}</div>` : ''}
      </div>
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:10pt;margin-bottom:1rem;border:1px solid #000">
      <thead><tr style="background:#000;color:white">
        <th style="padding:0.5rem;text-align:left;font-size:0.65rem;font-weight:700;letter-spacing:0.05em">DÉSIGNATION</th>
        <th style="padding:0.5rem;text-align:center;width:60px;font-size:0.65rem;font-weight:700">UNITÉ</th>
        <th style="padding:0.5rem;text-align:center;width:50px;font-size:0.65rem;font-weight:700">QTÉ</th>
        <th style="padding:0.5rem;text-align:right;width:100px;font-size:0.65rem;font-weight:700">PRIX U.</th>
        ${bc.taxMode==='TTC' ? '<th style="padding:0.5rem;text-align:center;width:50px;font-size:0.65rem;font-weight:700">TVA</th>' : ''}
        <th style="padding:0.5rem;text-align:right;width:110px;font-size:0.65rem;font-weight:700">TOTAL</th>
      </tr></thead>
      <tbody>${renderLignesHTML()}</tbody>
    </table>
    <div style="display:flex;justify-content:flex-end;margin-bottom:1rem">
      <div style="min-width:320px;border:1px solid #000;padding:0.75rem">
        <div style="display:flex;justify-content:space-between;margin-bottom:0.3rem;font-size:0.8rem"><span style="color:#333">${bc.afficherModeTaxation ? 'Sous-total HT:' : 'Sous-total:'}</span><span style="font-family:monospace;font-weight:600">${bc.sousTotal.toLocaleString('fr-FR')} FCFA</span></div>
        ${bc.taxMode==='TTC' ? `<div style="display:flex;justify-content:space-between;padding-bottom:0.5rem;border-bottom:1px solid #DDD;font-size:0.8rem"><span style="color:#333">TVA (${tauxTVA}%):</span><span style="font-family:monospace;font-weight:600">${bc.tva.toLocaleString('fr-FR')} FCFA</span></div>` : ''}
        <div style="display:flex;justify-content:space-between;align-items:center;padding-top:0.5rem;border-top:2px solid #000">
          <span style="font-size:0.95rem;font-weight:bold;color:#000">${bc.afficherModeTaxation ? `TOTAL ${bc.taxMode==='TTC'?'TTC':'HT'}:` : 'TOTAL:'}</span>
          <span style="font-size:1.2rem;font-weight:bold;font-family:monospace;color:#000">${bc.total.toLocaleString('fr-FR')} FCFA</span>
        </div>
      </div>
    </div>
    ${bc.notes ? `<div style="padding:0.6rem;background:#F5F5F5;border:1px solid #DDD;margin-bottom:1rem"><div style="font-weight:700;font-size:0.7rem;color:#666;margin-bottom:0.2rem">NOTES</div><div style="font-size:0.75rem;color:#333;white-space:pre-wrap">${bc.notes}</div></div>` : ''}
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:1.5rem;padding-top:1.5rem;border-top:1px solid #DDD">
      <div style="text-align:center"><div style="border-top:1px solid #000;padding-top:0.3rem;margin-top:2rem;font-size:0.65rem;color:#666">Signature ${settings.raisonSociale||'SOLSTICE'}</div></div>
      <div style="text-align:center"><div style="border-top:1px solid #000;padding-top:0.3rem;margin-top:2rem;font-size:0.65rem;color:#666">Signature Fournisseur</div></div>
    </div>
  </div>
</div>`;
} else if (modele === 'noir-blanc') {
// MODÈLE NOIR ET BLANC
bodyHTML = `
<div style="font-family:Arial,sans-serif;background:white;padding:1.5rem;max-width:900px;margin:0 auto">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:1.25rem;padding-bottom:1rem;border-bottom:2px solid #000">
    <div>
      ${settings.logo ? `<img src="${settings.logo}" style="max-height:55px;max-width:150px;margin-bottom:0.5rem;display:block;filter:grayscale(100%)" alt="logo"/>` : ''}
      <div style="font-weight:800;font-size:1.2rem;color:#000">${settings.raisonSociale||'SOLSTICE'}</div>
      <div style="font-size:0.75rem;color:#000;line-height:1.5">${settings.adresse||''}</div>
      ${settings.telephone ? `<div style="font-size:0.75rem;color:#000">Tél: ${settings.telephone}</div>` : ''}
      ${settings.email ? `<div style="font-size:0.75rem;color:#000">Email: ${settings.email}</div>` : ''}
    </div>
    <div style="text-align:right;border:2px solid #000;padding:0.75rem 1rem">
      <div style="font-size:0.65rem;font-weight:700;letter-spacing:0.1em;margin-bottom:0.25rem">BON DE COMMANDE</div>
      <div style="font-size:1.3rem;font-weight:800;font-family:monospace;color:#000">${bc.numero}</div>
      <div style="font-size:0.75rem;color:#000;margin-top:0.25rem">Date: ${new Date(bc.date).toLocaleDateString('fr-FR')}</div>
    </div>
  </div>
  ${bc.objet ? `<div style="margin-bottom:1.25rem;padding:0.75rem;border:1px solid #000"><div style="font-size:0.7rem;font-weight:700;margin-bottom:0.25rem">OBJET</div><div style="font-size:0.9rem;font-weight:600;color:#000">${bc.objet}</div></div>` : ''}
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin-bottom:1.25rem">
    <div style="padding:0.75rem;border:1px solid #000">
      <div style="font-size:0.7rem;font-weight:700;margin-bottom:0.4rem">FOURNISSEUR</div>
      <div style="font-weight:700;font-size:0.95rem;color:#000">${(bc.fournisseur?.nom||'')}</div>
      ${bc.fournisseur?.adresse ? `<div style="font-size:0.75rem;color:#000;margin-top:0.2rem">${bc.fournisseur?.adresse}</div>` : ''}
      ${bc.fournisseur?.telephone ? `<div style="font-size:0.75rem;color:#000">Tél: ${bc.fournisseur?.telephone}</div>` : ''}
    </div>
    <div style="padding:0.75rem;border:1px solid #000">
      <div style="font-size:0.7rem;font-weight:700;margin-bottom:0.4rem">LIVRAISON</div>
      ${bc.delaiLivraison ? `<div style="font-size:0.8rem;color:#000">Délai: ${bc.delaiLivraison}</div>` : '<div style="font-size:0.75rem;color:#666">Non précisé</div>'}
      ${bc.lieuLivraison ? `<div style="font-size:0.8rem;color:#000;margin-top:0.2rem">Lieu: ${bc.lieuLivraison}</div>` : ''}
    </div>
  </div>
  <table style="width:100%;border-collapse:collapse;font-size:10pt;margin-bottom:1.25rem;border:2px solid #000">
    <thead><tr style="background:#000;color:white">
      <th style="padding:0.6rem;text-align:left;font-size:0.7rem;font-weight:700;letter-spacing:0.05em">DÉSIGNATION</th>
      <th style="padding:0.6rem;text-align:center;width:70px;font-size:0.7rem;font-weight:700">UNITÉ</th>
      <th style="padding:0.6rem;text-align:center;width:60px;font-size:0.7rem;font-weight:700">QTÉ</th>
      <th style="padding:0.6rem;text-align:right;width:110px;font-size:0.7rem;font-weight:700">PRIX UNIT.</th>
      ${bc.taxMode==='TTC' ? '<th style="padding:0.6rem;text-align:center;width:55px;font-size:0.7rem;font-weight:700">TVA</th>' : ''}
      <th style="padding:0.6rem;text-align:right;width:120px;font-size:0.7rem;font-weight:700">TOTAL</th>
    </tr></thead>
    <tbody>${renderLignesHTML()}</tbody>
  </table>
  <div style="display:flex;justify-content:flex-end;margin-bottom:1.25rem">
    <div style="min-width:340px;border:2px solid #000;padding:1rem">
      <div style="display:flex;justify-content:space-between;margin-bottom:0.4rem;font-size:0.85rem"><span style="color:#000">${bc.afficherModeTaxation ? 'Sous-total HT:' : 'Sous-total:'}</span><span style="font-family:monospace;font-weight:700">${bc.sousTotal.toLocaleString('fr-FR')} FCFA</span></div>
      ${bc.taxMode==='TTC' ? `<div style="display:flex;justify-content:space-between;padding-bottom:0.6rem;border-bottom:1px solid #000;font-size:0.85rem"><span style="color:#000">TVA (${tauxTVA}%):</span><span style="font-family:monospace;font-weight:700">${bc.tva.toLocaleString('fr-FR')} FCFA</span></div>` : ''}
      <div style="display:flex;justify-content:space-between;align-items:center;padding-top:0.6rem;border-top:2px solid #000">
        <span style="font-size:1rem;font-weight:bold;color:#000">${bc.afficherModeTaxation ? `TOTAL ${bc.taxMode==='TTC'?'TTC':'HT'}:` : 'TOTAL:'}</span>
        <span style="font-size:1.3rem;font-weight:bold;font-family:monospace;color:#000">${bc.total.toLocaleString('fr-FR')} FCFA</span>
      </div>
    </div>
  </div>
  ${bc.notes ? `<div style="padding:0.75rem;border:1px solid #000;margin-bottom:1.25rem"><div style="font-weight:700;font-size:0.75rem;margin-bottom:0.3rem">NOTES</div><div style="font-size:0.8rem;color:#000;white-space:pre-wrap">${bc.notes}</div></div>` : ''}
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:2rem;padding-top:1.5rem;border-top:2px solid #000">
    <div style="text-align:center"><div style="border-top:2px solid #000;padding-top:0.4rem;margin-top:2.5rem;font-size:0.7rem;color:#000;font-weight:600">Signature & Cachet<br>${settings.raisonSociale||'SOLSTICE'}</div></div>
    <div style="text-align:center"><div style="border-top:2px solid #000;padding-top:0.4rem;margin-top:2.5rem;font-size:0.7rem;color:#000;font-weight:600">Signature & Cachet<br>Fournisseur</div></div>
  </div>
</div>`;
} else {
// Par défaut, utiliser le modèle moderne si non reconnu
bodyHTML = `</div>`;
}

const printWin = window.open('', '_blank', 'width=1000,height=800');
printWin.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>BC ${bc.numero}</title>
<style>
* { margin:0; padding:0; box-sizing:border-box; }
body { background:white; font-family:Arial,sans-serif; }
.no-print { display:flex; }
@media print {
  @page { size:A4 portrait; margin:8mm; }
  body { -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; }
  .no-print { display:none !important; visibility:hidden !important; height:0 !important; overflow:hidden !important; }
}
</style></head><body>
<div class="no-print" style="padding:0.75rem 1rem;background:#F1F5F9;border-bottom:2px solid #E2E8F0;gap:0.75rem;justify-content:flex-end;align-items:center;position:sticky;top:0;z-index:9999">
  <span style="font-size:0.8rem;color:#64748B;margin-right:auto">BC ${bc.numero} — ${bc.fournisseur?.nom||''}</span>
  <button onclick="window.print()" style="padding:0.5rem 1.25rem;background:#0A2540;color:white;border:none;border-radius:8px;cursor:pointer;font-weight:600;font-size:0.875rem">🖨️ Imprimer</button>
  <button onclick="window.close()" style="padding:0.5rem 1.25rem;background:#EF4444;color:white;border:none;border-radius:8px;cursor:pointer;font-weight:600;font-size:0.875rem">✕ Fermer</button>
</div>
${bodyHTML}
</body></html>`);
printWin.document.close();
};

const filteredBC = bonsCommande.filter(bc => {
const s = searchBC.toLowerCase();
const matchSearch = !s || (bc.numero||'').toLowerCase().includes(s) || (bc.fournisseur?.nom||'').toLowerCase().includes(s) || (bc.objet||'').toLowerCase().includes(s);
const matchStatus = filterStatusBC === 'all' || bc.statut === filterStatusBC;
return matchSearch && matchStatus;
});

// Fonctions de sauvegarde et restauration
const exportToJSON = () => {
// Créer un objet contenant TOUTES les données de l'application
// Le delta catalogue (personnalisations utilisateur) est inclus pour portabilité
let catalogueDelta = [];
// catalogue delta ignoré — catalogue dans Supabase
const exportData = {
version: "1.0",
exportDate: new Date().toISOString(),
documents: documents,
clients: clients,
fournisseurs: fournisseurs,
bonsCommande: bonsCommande,
settings: settings,
catalogueDelta: catalogueDelta
};

// Créer le nom du fichier avec la date
const now = new Date();
const dateStr = now.toISOString().split('T')[0];
const fileName = `SOLSTICE_backup_${dateStr}.json`;

// Créer le lien de téléchargement
const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportData, null, 2));
const downloadAnchorNode = document.createElement('a');
downloadAnchorNode.setAttribute("href", dataStr);
downloadAnchorNode.setAttribute("download", fileName);
document.body.appendChild(downloadAnchorNode);
downloadAnchorNode.click();
downloadAnchorNode.remove();

alert(`✅ Sauvegarde réussie !\n\n📊 ${documents.length} document(s)\n👥 ${clients.length} client(s)\n🏭 ${fournisseurs.length} fournisseur(s)\n📦 ${bonsCommande.length} bon(s) de commande\n⚙️ Paramètres inclus\n\nFichier: ${fileName}`);
};

const importFromJSON = (event) => {
const file = event.target.files[0];
if (!file) return;

const reader = new FileReader();
reader.onload = (e) => {
try {
const importedData = JSON.parse(e.target.result);

if (importedData.version) {
// Nouveau format complet
// 1. Restaurer les documents
if (importedData.documents && Array.isArray(importedData.documents)) {
setDocuments(prev => {
const allDocs = [...prev, ...importedData.documents];
const uniqueDocs = allDocs.filter((doc, index, self) =>
index === self.findIndex((t) => t.id === doc.id)
);
// importé Supabase
return uniqueDocs;
});
}

// 2. Restaurer les clients
if (importedData.clients && Array.isArray(importedData.clients)) {
setClients(prev => {
const allClients = [...prev, ...importedData.clients];
const uniqueClients = allClients.filter((client, index, self) =>
index === self.findIndex((t) => t.id === client.id)
);
// importé Supabase
return uniqueClients;
});
}

// 2b. Restaurer les fournisseurs
if (importedData.fournisseurs && Array.isArray(importedData.fournisseurs)) {
setFournisseurs(prev => {
const all = [...prev, ...importedData.fournisseurs];
const unique = all.filter((f, index, self) => index === self.findIndex(t => t.id === f.id));
// fournisseurs importés Supabase
return unique;
});
}

// 2c. Restaurer les bons de commande
if (importedData.bonsCommande && Array.isArray(importedData.bonsCommande)) {
setBonsCommande(prev => {
const all = [...prev, ...importedData.bonsCommande];
const unique = all.filter((bc, index, self) => index === self.findIndex(t => t.id === bc.id));
// BC importés Supabase
return unique;
});
}

// 3. Restaurer les paramètres (demander confirmation)
if (importedData.settings) {
const restoreSettings = confirm(
"⚙️ Voulez-vous aussi restaurer les paramètres de l'entreprise ?\n\n" +
"Cela remplacera vos paramètres actuels (raison sociale, NUI, RCCM, etc.)\n\n" +
"Cliquez sur OK pour restaurer les paramètres, ou Annuler pour les garder tels quels."
);

if (restoreSettings) {
setSettings(importedData.settings);
setTempSettings(importedData.settings);
if (importedData.settings) saveSettings(importedData.settings).catch(console.error);
}
}

alert(
`✅ Restauration réussie !\n\n` +
`📊 ${importedData.documents?.length || 0} document(s) restauré(s)\n` +
`👥 ${importedData.clients?.length || 0} client(s) restauré(s)\n` +
`🏭 ${importedData.fournisseurs?.length || 0} fournisseur(s) restauré(s)\n` +
`📦 ${importedData.bonsCommande?.length || 0} bon(s) de commande restauré(s)\n` +
`⚙️ Paramètres: ${importedData.settings ? 'restaurés' : 'non inclus'}\n\n` +
`Date de sauvegarde: ${importedData.exportDate ? new Date(importedData.exportDate).toLocaleDateString('fr-FR') : 'inconnue'}`
);

} else if (Array.isArray(importedData)) {
// Ancien format (compatibilité)
setDocuments(prev => {
const allDocs = [...prev, ...importedData];
const uniqueDocs = allDocs.filter((doc, index, self) =>
index === self.findIndex((t) => t.id === doc.id)
);
// importé Supabase
return uniqueDocs;
});
alert(`✅ Importation réussie !\n\n📊 ${importedData.length} document(s) importé(s)`);
} else {
alert("❌ Format de fichier non reconnu.\n\nAssurez-vous d'utiliser un fichier de sauvegarde SOLSTICE valide.");
}

} catch (error) {
console.error("Erreur d'importation:", error);
alert("❌ Erreur lors de la lecture du fichier.\n\nLe fichier est peut-être corrompu ou dans un format incompatible.");
}
};
reader.readAsText(file);
event.target.value = "";
};

const resetAllData = () => {
const confirmation = confirm(
"⚠️ ATTENTION : SUPPRESSION TOTALE ⚠️\n\n" +
"Cette action va supprimer DÉFINITIVEMENT :\n" +
`📊 ${documents.length} document(s)\n` +
`👥 ${clients.length} client(s)\n` +
`🏭 ${fournisseurs.length} fournisseur(s)\n` +
`📦 ${bonsCommande.length} bon(s) de commande\n` +
"⚙️ Tous les paramètres\n\n" +
"Cette action est IRRÉVERSIBLE !\n\n" +
"Voulez-vous vraiment continuer ?"
);

if (!confirmation) return;

const doubleConfirmation = confirm(
"🚨 DERNIÈRE CONFIRMATION 🚨\n\n" +
"Êtes-vous ABSOLUMENT SÛR de vouloir tout supprimer ?\n\n" +
"💡 Conseil : Exportez vos données avant de supprimer !"
);

if (doubleConfirmation) {
alert('Réinitialisation: contactez votre administrateur Supabase.');

setClients([]);
// reset clients Supabase

setFournisseurs([]);
// reset fournisseurs Supabase

setBonsCommande([]);
// reset BC Supabase

const defaultSettings = {
raisonSociale: 'SOLSTICE',
adresse: '',
telephone: '',
email: '',
siteWeb: '',
nui: '',
rccm: '',
logo: '',
agent: '',
tauxTVA: 19.25
};
setSettings(defaultSettings);
setTempSettings(defaultSettings);
saveSettings(defaultSettings).catch(console.error);

alert("✅ Toutes les données ont été supprimées.\n\nL'application a été réinitialisée aux valeurs par défaut.");
}
};

// Paramètres
const ouvrirParametres = () => {
setTempSettings({...settings});
setShowSettings(true);
};

const handleSettingsChange = (e) => {
const { name, value } = e.target;
setTempSettings({
...tempSettings,
[name]: value
});
};

const handleLogoUpload = (e) => {
const file = e.target.files[0];
if (file) {
if (file.size > 2 * 1024 * 1024) {
alert('Le fichier est trop volumineux (max 2MB)');
return;
}

const reader = new FileReader();
reader.onload = (event) => {
setTempSettings({
...tempSettings,
logo: event.target.result
});
};
reader.readAsDataURL(file);
}
};

const supprimerLogo = () => {
setTempSettings({
...tempSettings,
logo: ''
});
};

const sauvegarderParametres = () => {
saveSettings(tempSettings).then(() => setShowSettings(false)).catch(err => alert('Erreur paramètres: ' + err.message));
};

const annulerParametres = () => {
setTempSettings({...settings});
setShowSettings(false);
};

// Filtrer les documents
const filteredDocuments = documents.filter(doc => {
// Filtre par recherche textuelle (numéro ou client)
const matchSearch = doc.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
((doc.client||{}).nom||'').toLowerCase().includes(searchTerm.toLowerCase());

// Filtre par type (devis/facture/avoir)
const matchType = filterType === 'all' || doc.type === filterType;

// Filtre par statut
const matchStatus = filterStatus === 'all' || doc.statut === filterStatus;

// Filtre par nom de client
const matchClient = !filterClient ||
((doc.client||{}).nom||'').toLowerCase().includes(filterClient.toLowerCase());

// Filtre par date de début
const matchDateDebut = !dateDebut ||
new Date(doc.date) >= new Date(dateDebut);

// Filtre par date de fin
const matchDateFin = !dateFin ||
new Date(doc.date) <= new Date(dateFin + 'T23:59:59');

// Filtre par montant minimum (les avoirs ont un montant négatif sur la facture)
const montantDoc = doc.type === 'avoir' ? doc.total : doc.total;
const matchMontantMin = !filterMontantMin ||
montantDoc >= parseFloat(filterMontantMin);

// Filtre par montant maximum
const matchMontantMax = !filterMontantMax ||
montantDoc <= parseFloat(filterMontantMax);

return matchSearch && matchType && matchStatus && matchClient &&
matchDateDebut && matchDateFin && matchMontantMin && matchMontantMax;
});

// Réinitialiser tous les filtres
const resetFilters = () => {
setSearchTerm('');
setFilterType('all');
setFilterStatus('all');
setFilterClient('');
setDateDebut('');
setDateFin('');
setFilterMontantMin('');
setFilterMontantMax('');
};

// Générer un bordereau de transmission
const genererBordereau = (documents) => {
if (!documents || documents.length === 0) {
alert('Aucun document sélectionné pour le bordereau');
return;
}

// Déterminer le type de document (FACTURES ou DEVIS)
const typeDoc = documents[0].type === 'facture' ? 'FACTURES' : 'DEVIS';
const typeDocPluriel = typeDoc;

// Déterminer le client (depuis les filtres ou du premier document)
const nomClient = filterClient || (documents[0]?.client?.nom||'CLIENT NON SPÉCIFIÉ') || 'CLIENT NON SPÉCIFIÉ';

// Déterminer la période
let periodeDebut = dateDebut;
let periodeFin = dateFin;

if (!periodeDebut || !periodeFin) {
const dates = documents.map(f => new Date(f.date)).sort((a, b) => a - b);
periodeDebut = dates[0].toISOString().split('T')[0];
periodeFin = dates[dates.length - 1].toISOString().split('T')[0];
}

// Déterminer le statut
let statutLabel = '';
if (filterStatus && filterStatus !== 'all') {
switch(filterStatus) {
case 'impaye': statutLabel = 'IMPAYÉE'; break;
case 'paye': statutLabel = 'PAYÉE'; break;
case 'partiel': statutLabel = 'PARTIELLEMENT PAYÉE'; break;
case 'en-attente': statutLabel = 'EN ATTENTE'; break;
case 'accepte': statutLabel = 'ACCEPTÉE'; break;
case 'refuse': statutLabel = 'REFUSÉE'; break;
case 'annule': statutLabel = 'ANNULÉE'; break;
default: statutLabel = filterStatus.toUpperCase();
}
} else if (documents.length > 0) {
const statut = documents[0].statut;
switch(statut) {
case 'impaye': statutLabel = 'IMPAYÉE'; break;
case 'paye': statutLabel = 'PAYÉE'; break;
case 'partiel': statutLabel = 'PARTIELLEMENT PAYÉE'; break;
case 'en-attente': statutLabel = 'EN ATTENTE'; break;
case 'accepte': statutLabel = 'ACCEPTÉE'; break;
case 'refuse': statutLabel = 'REFUSÉE'; break;
case 'annule': statutLabel = 'ANNULÉE'; break;
default: statutLabel = statut.toUpperCase();
}
}

// Vérifier s'il y a au moins une facture partiellement payée
// Une facture est partielle si statut === 'partiel' OU si elle a un montantAvance > 0
const hasPartialPayment = documents.some(d => d.statut === 'partiel' || (d.montantAvance && d.montantAvance > 0));

// Calculer les totaux
const montantTotal = documents.reduce((sum, f) => sum + (f.total || 0), 0);
// Pour le montant perçu, on utilise montantPaye (journal + avance) pour les factures partielles
const montantPercuTotal = documents.reduce((sum, f) => {
if (f.statut === 'paye') {
return sum + (f.total || 0);
} else if (f.statut === 'partiel') {
return sum + (f.montantPaye || 0);
} else {
return sum + (f.montantPaye || 0);
}
}, 0);
const montantRestantTotal = montantTotal - montantPercuTotal;
const nombreDocuments = documents.length;

// Formater les montants en FCFA
const formatMontantBordereau = (montant) => {
return Math.round(montant).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Date et heure de génération
const now = new Date();
const dateStr = now.toLocaleDateString('fr-FR');
const heureStr = now.toLocaleTimeString('fr-FR');

// Récupérer les infos entreprise
const raisonSociale = settings.raisonSociale || 'SOLSTICE';
const logoEntreprise = settings.logo || '';

// Générer le HTML du tableau en fonction de la présence de paiements partiels
const generateTableRows = () => {
if (hasPartialPayment) {
return documents.map((d, index) => {
const montantAttendu = d.total || 0;
let montantPercu = 0;
if (d.statut === 'paye') {
montantPercu = d.total || 0;
} else if (d.statut === 'partiel') {
montantPercu = d.montantPaye || 0;
} else {
montantPercu = d.montantPaye || 0;
}
const resteAPercevoir = montantAttendu - montantPercu;
return `
<tr>
<td class="col-num">${index + 1}</td>
<td>${d.numero}</td>
<td>${new Date(d.date).toLocaleDateString('fr-FR')}</td>
<td class="col-montant">${formatMontantBordereau(montantAttendu)}</td>
<td class="col-montant-percu">${formatMontantBordereau(montantPercu)}</td>
<td class="col-reste">${formatMontantBordereau(resteAPercevoir)}</td>
</tr>
`;
}).join('');
} else {
return documents.map((d, index) => `
<tr>
<td class="col-num">${index + 1}</td>
<td>${d.numero}</td>
<td>${new Date(d.date).toLocaleDateString('fr-FR')}</td>
<td class="col-montant">${formatMontantBordereau(d.total)}</td>
</tr>
`).join('');
}
};

const generateEmptyRows = () => '';

// Générer l'en-tête du tableau
const generateTableHeader = () => {
if (hasPartialPayment) {
return `
<thead>
<tr>
<th class="col-num">N°</th>
<th class="col-numero">Numéro de ${typeDoc === 'FACTURES' ? 'Facture' : 'Devis'}</th>
<th class="col-date">Date d'émission</th>
<th class="col-montant">Paiement attendu (FCFA)</th>
<th class="col-montant-percu">Montant perçu (FCFA)</th>
<th class="col-reste">Reste à percevoir (FCFA)</th>
</tr>
</thead>
`;
} else {
return `
<thead>
<tr>
<th class="col-num">N°</th>
<th class="col-numero">Numéro de ${typeDoc === 'FACTURES' ? 'Facture' : 'Devis'}</th>
<th class="col-date">Date d'émission</th>
<th class="col-montant">Montant (FCFA)</th>
</tr>
</thead>
`;
}
};

// Générer la section totaux
const generateTotalsSection = () => {
if (hasPartialPayment) {
return `
<div class="totals-section">
<div class="total-row">
<div class="total-label">Nombre de ${typeDocPluriel} déposé${typeDocPluriel.endsWith('S') ? 's' : ''} :</div>
<div class="total-value">${nombreDocuments}</div>
</div>
<div class="total-row">
<div class="total-label">Total paiement attendu :</div>
<div class="total-value">${formatMontantBordereau(montantTotal)} FCFA</div>
</div>
<div class="total-row">
<div class="total-label">Total montant perçu :</div>
<div class="total-value" style="color: #059669;">${formatMontantBordereau(montantPercuTotal)} FCFA</div>
</div>
<div class="total-row main-total">
<div class="total-label">Total reste à percevoir :</div>
<div class="total-value" style="color: #DC2626;">${formatMontantBordereau(montantRestantTotal)} FCFA</div>
</div>
</div>
`;
} else {
return `
<div class="totals-section">
<div class="total-row">
<div class="total-label">Nombre de ${typeDocPluriel} déposé${typeDocPluriel.endsWith('S') ? 's' : ''} :</div>
<div class="total-value">${nombreDocuments}</div>
</div>
<div class="total-row main-total">
<div class="total-label">
Total ${typeDocPluriel} ${statutLabel}${statutLabel.endsWith('E') || statutLabel.endsWith('ÉE') ? 'S' : ''} :
</div>
<div class="total-value">
${formatMontantBordereau(montantTotal)} FCFA
</div>
</div>
</div>
`;
}
};

const bordereauHTML = `
<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Bordereau de Transmission des ${typeDocPluriel}</title>
<style>
@page {
size: A4;
margin: 1.5cm 2cm;
}

* {
margin: 0;
padding: 0;
box-sizing: border-box;
}

body {
font-family: 'Arial', sans-serif;
line-height: 1.4;
color: #000;
background: white;
}

.container {
max-width: 21cm;
margin: 0 auto;
padding: 20px;
}

/* En-tête avec logo */
.header-logo {
margin-bottom: 20px;
padding-bottom: 15px;
border-bottom: 3px solid #000;
}

.header-logo img {
max-height: 80px;
max-width: 250px;
margin-bottom: 10px;
object-fit: contain;
}

.header-logo h1 {
font-size: 28pt;
font-weight: bold;
color: #000;
margin-bottom: 5px;
letter-spacing: 2px;
}

.header-logo .subtitle {
font-size: 11pt;
color: #333;
font-weight: normal;
}

/* Titre principal */
.main-title {
text-align: center;
margin: 30px 0 25px 0;
padding: 15px;
background: #f8f8f8;
border: 2px solid #000;
}

.main-title h2 {
font-size: 16pt;
font-weight: bold;
text-transform: uppercase;
margin-bottom: 0;
letter-spacing: 1px;
}

/* Informations du bordereau */
.info-section {
margin: 25px 0;
padding: 15px;
background: #f5f5f5;
border-left: 4px solid #000;
}

.info-row {
display: flex;
margin: 8px 0;
font-size: 11pt;
}

.info-label {
font-weight: bold;
min-width: 180px;
text-transform: uppercase;
}

.info-value {
flex: 1;
font-weight: 600;
}

/* Tableau des documents */
.documents-table {
width: 100%;
border-collapse: collapse;
margin: 25px 0;
border: 2px solid #000;
}

.documents-table thead {
background: #e0e0e0;
}

.documents-table th {
border: 1px solid #000;
padding: 12px 8px;
text-align: left;
font-weight: bold;
font-size: 10pt;
text-transform: uppercase;
}

.documents-table th.col-num {
width: 5%;
text-align: center;
}

.documents-table th.col-numero {
width: 20%;
}

.documents-table th.col-date {
width: 18%;
}

.documents-table th.col-montant,
.documents-table th.col-montant-percu,
.documents-table th.col-reste {
width: 19%;
text-align: right;
}

.documents-table td {
border: 1px solid #000;
padding: 10px 8px;
font-size: 10pt;
}

.documents-table td.col-num {
text-align: center;
font-weight: bold;
background: #f8f8f8;
}

.documents-table td.col-montant,
.documents-table td.col-montant-percu,
.documents-table td.col-reste {
text-align: right;
font-weight: 500;
}

.documents-table td.col-montant-percu {
color: #059669;
}

.documents-table td.col-reste {
color: #DC2626;
font-weight: bold;
}

.documents-table tbody tr:nth-child(even) {
background: #fafafa;
}

.documents-table tbody tr.empty-row {
background: white;
height: 35px;
}

/* Section totaux */
.totals-section {
margin: 25px 0;
padding: 20px;
background: #f0f0f0;
border: 2px solid #000;
}

.total-row {
display: flex;
justify-content: space-between;
align-items: center;
padding: 10px 0;
font-size: 12pt;
}

.total-row.main-total {
border-top: 2px solid #000;
padding-top: 15px;
margin-top: 10px;
}

.total-label {
font-weight: bold;
text-transform: uppercase;
}

.total-value {
font-weight: bold;
font-size: 14pt;
}

/* Pied de page */
.footer {
margin-top: 40px;
padding-top: 20px;
border-top: 2px solid #000;
}

.footer-row {
display: flex;
justify-content: space-between;
margin: 10px 0;
font-size: 9pt;
}

.footer-section {
flex: 1;
padding: 0 10px;
}

.footer-section h4 {
font-size: 10pt;
margin-bottom: 8px;
text-transform: uppercase;
border-bottom: 1px solid #ccc;
padding-bottom: 5px;
}

.signature-box {
margin-top: 30px;
text-align: center;
}

.signature-line {
margin-top: 60px;
border-top: 1px solid #000;
width: 200px;
display: inline-block;
}

.signature-label {
margin-top: 5px;
font-size: 9pt;
font-weight: bold;
}

/* Mentions légales */
.legal-notice {
margin-top: 30px;
padding: 15px;
background: #f9f9f9;
border: 1px solid #ddd;
font-size: 8pt;
color: #666;
text-align: center;
}

@media print {
.no-print {
display: none !important;
}

body {
print-color-adjust: exact;
-webkit-print-color-adjust: exact;
}

.container {
padding: 0;
}
}

.no-print {
position: fixed;
bottom: 20px;
right: 20px;
z-index: 1000;
}

.no-print button {
padding: 12px 24px;
margin: 0 5px;
border: none;
border-radius: 8px;
cursor: pointer;
font-size: 14px;
font-weight: 600;
transition: all 0.3s ease;
box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

.btn-print {
background: #10B981;
color: white;
}

.btn-print:hover {
background: #059669;
transform: translateY(-2px);
box-shadow: 0 6px 16px rgba(16,185,129,0.4);
}

.btn-close {
background: #64748B;
color: white;
}

.btn-close:hover {
background: #475569;
transform: translateY(-2px);
}
</style>
</head>
<body>
<div class="container">
<!-- En-tête avec logo -->
<div class="header-logo" style="display: flex; align-items: center; justify-content: center; gap: 15px; text-align: left;">
${logoEntreprise ? `<img src="${logoEntreprise}" alt="Logo ${raisonSociale}" style="max-height: 60px; max-width: 120px; margin: 0;">` : ''}
<div>
<h1 style="margin: 0; font-size: 24pt;">${raisonSociale}</h1>
<div class="subtitle" style="margin: 0;">Système de Gestion de Devis et Factures</div>
</div>
</div>

<!-- Titre principal -->
<div class="main-title">
<h2>BORDEREAU DE TRANSMISSION DES ${typeDocPluriel}</h2>
</div>

<!-- Informations du bordereau -->
<div class="info-section">
<div class="info-row">
<div class="info-label">Entreprise :</div>
<div class="info-value">${nomClient.toUpperCase()}</div>
</div>
<div class="info-row">
<div class="info-label">Période :</div>
<div class="info-value">
Du ${new Date(periodeDebut).toLocaleDateString('fr-FR')}
au ${new Date(periodeFin).toLocaleDateString('fr-FR')}
</div>
</div>
<div class="info-row">
<div class="info-label">Statut des ${typeDocPluriel} :</div>
<div class="info-value">${statutLabel}${hasPartialPayment ? ' (avec paiements partiels)' : ''}</div>
</div>
<div class="info-row">
<div class="info-label">Date de génération :</div>
<div class="info-value">${dateStr} à ${heureStr}</div>
</div>
</div>

<!-- Tableau des documents -->
<table class="documents-table">
${generateTableHeader()}
<tbody>
${generateTableRows()}
${generateEmptyRows()}
</tbody>
</table>

${generateTotalsSection()}

<!-- Pied de page -->
<div class="footer">
<div class="footer-row">
<div class="footer-section">
<h4>Émetteur</h4>
<div class="signature-box">
<div>Nom et Prénom :</div>
<div class="signature-line"></div>
<div class="signature-label">Signature et Cachet</div>
</div>
</div>
<div class="footer-section">
<h4>Récepteur</h4>
<div class="signature-box">
<div>Nom et Prénom :</div>
<div class="signature-line"></div>
<div class="signature-label">Signature et Cachet</div>
</div>
</div>
</div>
</div>

<!-- Mentions légales -->
<div class="legal-notice">
Document généré automatiquement par SOLSTICE v3.0 - ${dateStr} à ${heureStr}<br>
Ce bordereau fait foi de la réception des documents mentionnés ci-dessus.
</div>
</div>

<!-- Boutons d'action -->
<div class="no-print">
<button class="btn-print" onclick="window.print()">
🖨️ Imprimer
</button>
<button class="btn-close" onclick="window.close()">
✕ Fermer
</button>
</div>
</body>
</html>
`;

// Ouvrir dans une nouvelle fenêtre
const printWindow = window.open('', '_blank', 'width=900,height=700');
printWindow.document.write(bordereauHTML);
printWindow.document.close();
};

// Générer un état récapitulatif des factures (A4 paysage, avec colonne Objet et Statut)
const genererRecapitulatif = (documents) => {
if (!documents || documents.length === 0) {
alert('Aucun document sélectionné pour le récapitulatif');
return;
}

const typeDoc = documents[0].type === 'facture' ? 'FACTURES' : 'DEVIS';
const nomClient = filterClient || (documents[0]?.client?.nom || 'CLIENT NON SPÉCIFIÉ');

let periodeDebut = dateDebut;
let periodeFin = dateFin;
if (!periodeDebut || !periodeFin) {
const dates = documents.map(f => new Date(f.date)).sort((a, b) => a - b);
periodeDebut = dates[0].toISOString().split('T')[0];
periodeFin = dates[dates.length - 1].toISOString().split('T')[0];
}

let statutLabel = '';
if (filterStatus && filterStatus !== 'all') {
const map = { impaye:'IMPAYÉE', paye:'PAYÉE', partiel:'PARTIELLEMENT PAYÉE', 'en-attente':'EN ATTENTE', accepte:'ACCEPTÉE', refuse:'REFUSÉE', annule:'ANNULÉE' };
statutLabel = map[filterStatus] || filterStatus.toUpperCase();
} else if (documents.length > 0) {
const map = { impaye:'IMPAYÉE', paye:'PAYÉE', partiel:'PARTIELLEMENT PAYÉE', 'en-attente':'EN ATTENTE', accepte:'ACCEPTÉE', refuse:'REFUSÉE', annule:'ANNULÉE' };
statutLabel = map[documents[0].statut] || documents[0].statut.toUpperCase();
}

const formatM = (n) => Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const montantTotal = documents.reduce((s, f) => s + (f.total || 0), 0);
const montantPercuTotal = documents.reduce((s, f) => {
if (f.statut === 'paye') return s + (f.total || 0);
return s + (f.montantPaye || 0);
}, 0);
const montantRestantTotal = montantTotal - montantPercuTotal;
const now = new Date();
const dateStr = now.toLocaleDateString('fr-FR');
const heureStr = now.toLocaleTimeString('fr-FR');
const raisonSociale = settings.raisonSociale || 'SOLSTICE';
const logoEntreprise = settings.logo || '';

const statutBadge = (statut) => {
const badges = {
paye:        { label: 'Payée',      bg: '#DCFCE7', color: '#16A34A' },
partiel:     { label: 'Partiel',    bg: '#FEF3C7', color: '#D97706' },
impaye:      { label: 'Impayée',    bg: '#FEE2E2', color: '#DC2626' },
'en-attente':{ label: 'En attente', bg: '#E0F2FE', color: '#0369A1' },
annule:      { label: 'Annulée',    bg: '#F1F5F9', color: '#64748B' },
accepte:     { label: 'Acceptée',   bg: '#DCFCE7', color: '#16A34A' },
refuse:      { label: 'Refusée',    bg: '#FEE2E2', color: '#DC2626' },
};
const b = badges[statut] || { label: statut, bg: '#F1F5F9', color: '#64748B' };
return `<span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:7.5pt;font-weight:bold;background:${b.bg};color:${b.color}">${b.label}</span>`;
};

const tableRows = documents.map((d, i) => {
const attendu = d.total || 0;
const percu = d.statut === 'paye' ? attendu : (d.montantPaye || 0);
const reste = attendu - percu;
return `
<tr style="${i % 2 === 1 ? 'background:#FAFAFA' : ''}">
<td style="text-align:center;color:#888;font-size:8pt;border:1px solid #ccc;padding:7px 6px">${i + 1}</td>
<td style="font-weight:bold;color:#0A2540;white-space:nowrap;border:1px solid #ccc;padding:7px 6px">${d.numero}</td>
<td style="font-style:italic;color:#334155;border:1px solid #ccc;padding:7px 6px">${d.objet ? d.objet : '—'}</td>
<td style="white-space:nowrap;color:#475569;border:1px solid #ccc;padding:7px 6px">${new Date(d.date).toLocaleDateString('fr-FR')}</td>
<td style="border:1px solid #ccc;padding:7px 6px">${statutBadge(d.statut)}</td>
<td style="text-align:right;font-family:'Courier New',monospace;border:1px solid #ccc;padding:7px 6px">${formatM(attendu)}</td>
<td style="text-align:right;font-family:'Courier New',monospace;font-weight:bold;color:#16A34A;border:1px solid #ccc;padding:7px 6px">${percu > 0 ? formatM(percu) : '—'}</td>
<td style="text-align:right;font-family:'Courier New',monospace;font-weight:bold;color:${reste > 0 ? '#DC2626' : '#16A34A'};border:1px solid #ccc;padding:7px 6px">${formatM(reste)}</td>
</tr>`;
}).join('');

const recapHTML = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>État Récapitulatif des ${typeDoc}</title>
<style>
@page { size: A4 landscape; margin: 1.2cm 1.8cm; }
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family: Arial, sans-serif; font-size: 10pt; color: #000; background: white; }
.container { max-width: 27cm; margin: 0 auto; padding: 16px; }
@media print { .no-print { display:none !important; } body { print-color-adjust:exact; -webkit-print-color-adjust:exact; } .container { padding:0; } }
.no-print { position:fixed; bottom:20px; right:20px; z-index:1000; }
.no-print button { padding:12px 24px; margin:0 5px; border:none; border-radius:8px; cursor:pointer; font-size:14px; font-weight:600; box-shadow:0 4px 12px rgba(0,0,0,0.2); }
</style>
</head>
<body>
<div class="container">

<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;padding-bottom:12px;border-bottom:3px solid #000">
<div>
${logoEntreprise ? `<img src="${logoEntreprise}" alt="${raisonSociale}" style="max-height:55px;max-width:160px;margin-bottom:6px;display:block">` : ''}
<div style="font-size:22pt;font-weight:bold;letter-spacing:2px">${raisonSociale}</div>
<div style="font-size:9pt;color:#444;margin-top:2px">Système de Gestion de Devis et Factures</div>
</div>
<div style="text-align:right;font-size:8.5pt;color:#555;line-height:1.7">
Généré le ${dateStr} à ${heureStr}
</div>
</div>

<div style="text-align:center;margin:14px 0 12px 0">
<div style="font-size:16pt;font-weight:bold;letter-spacing:1px;text-transform:uppercase;border-bottom:2px solid #000;display:inline-block;padding-bottom:4px">État Récapitulatif des ${typeDoc}</div>
${statutLabel ? `<div style="margin-top:6px"><span style="display:inline-block;background:#0A2540;color:white;font-size:8pt;font-weight:bold;padding:3px 12px;border-radius:12px;letter-spacing:1px">${statutLabel}</span></div>` : ''}
</div>

<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:14px">
<div style="border:1px solid #ccc;border-radius:4px;padding:7px 10px;background:#FAFAFA">
<div style="font-size:7pt;font-weight:bold;text-transform:uppercase;color:#666;margin-bottom:3px">Client</div>
<div style="font-size:10pt;font-weight:bold;color:#0A2540">${nomClient.toUpperCase()}</div>
</div>
<div style="border:1px solid #ccc;border-radius:4px;padding:7px 10px;background:#FAFAFA">
<div style="font-size:7pt;font-weight:bold;text-transform:uppercase;color:#666;margin-bottom:3px">Période</div>
<div style="font-size:10pt;font-weight:bold;color:#0A2540">Du ${new Date(periodeDebut).toLocaleDateString('fr-FR')} au ${new Date(periodeFin).toLocaleDateString('fr-FR')}</div>
</div>
<div style="border:1px solid #ccc;border-radius:4px;padding:7px 10px;background:#FAFAFA">
<div style="font-size:7pt;font-weight:bold;text-transform:uppercase;color:#666;margin-bottom:3px">Nombre de ${typeDoc.toLowerCase()}</div>
<div style="font-size:10pt;font-weight:bold;color:#0A2540">${documents.length}</div>
</div>
</div>

<table style="width:100%;border-collapse:collapse;margin-bottom:14px;font-size:9pt">
<thead>
<tr style="background:#0A2540;color:white">
<th style="padding:7px 6px;text-align:center;font-size:7.5pt;text-transform:uppercase;width:3%;border:1px solid #0A2540">N°</th>
<th style="padding:7px 6px;text-align:left;font-size:7.5pt;text-transform:uppercase;width:11%;border:1px solid #0A2540">Numéro</th>
<th style="padding:7px 6px;text-align:left;font-size:7.5pt;text-transform:uppercase;width:28%;border:1px solid #0A2540">Objet de la facturation</th>
<th style="padding:7px 6px;text-align:left;font-size:7.5pt;text-transform:uppercase;width:9%;border:1px solid #0A2540">Date</th>
<th style="padding:7px 6px;text-align:left;font-size:7.5pt;text-transform:uppercase;width:10%;border:1px solid #0A2540">Statut</th>
<th style="padding:7px 6px;text-align:right;font-size:7.5pt;text-transform:uppercase;width:13%;border:1px solid #0A2540">Paiement attendu (FCFA)</th>
<th style="padding:7px 6px;text-align:right;font-size:7.5pt;text-transform:uppercase;width:13%;border:1px solid #0A2540">Montant perçu (FCFA)</th>
<th style="padding:7px 6px;text-align:right;font-size:7.5pt;text-transform:uppercase;width:13%;border:1px solid #0A2540">Reste à percevoir (FCFA)</th>
</tr>
</thead>
<tbody>${tableRows}</tbody>
</table>

<div style="display:flex;justify-content:flex-end;margin-bottom:18px">
<div style="border:2px solid #0A2540;border-radius:6px;overflow:hidden;min-width:320px">
<div style="display:flex;justify-content:space-between;padding:6px 14px;font-size:9pt;border-bottom:1px solid #E2E8F0">
<span style="font-weight:600">Nombre de ${typeDoc.toLowerCase()} :</span>
<span style="font-weight:bold;font-family:'Courier New',monospace">${documents.length}</span>
</div>
<div style="display:flex;justify-content:space-between;padding:9px 14px;font-size:10pt;font-weight:bold;background:#0A2540;color:white">
<span>Total paiement attendu :</span>
<span style="font-family:'Courier New',monospace">${formatM(montantTotal)} FCFA</span>
</div>
<div style="display:flex;justify-content:space-between;padding:6px 14px;font-size:9pt;font-weight:bold;background:#F0FDF4;color:#16A34A;border-bottom:1px solid #E2E8F0">
<span>Total montant perçu :</span>
<span style="font-family:'Courier New',monospace">${formatM(montantPercuTotal)} FCFA</span>
</div>
<div style="display:flex;justify-content:space-between;padding:6px 14px;font-size:9pt;font-weight:bold;background:#FEF2F2;color:#DC2626">
<span>Total reste à percevoir :</span>
<span style="font-family:'Courier New',monospace">${formatM(montantRestantTotal)} FCFA</span>
</div>
</div>
</div>

<div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:16px;border-top:1px solid #ccc;padding-top:14px">
<div style="border:1px solid #ccc;border-radius:4px;padding:10px 14px;min-height:80px">
<div style="font-size:7.5pt;font-weight:bold;text-transform:uppercase;color:#666;margin-bottom:8px;border-bottom:1px solid #eee;padding-bottom:4px">Émis par (${raisonSociale})</div>
<div style="font-size:9pt;color:#333;line-height:1.9">
Nom : ________________________________<br>
Fonction : ________________________________<br>
Signature &amp; cachet :
</div>
</div>
<div style="border:1px solid #ccc;border-radius:4px;padding:10px 14px;min-height:80px">
<div style="font-size:7.5pt;font-weight:bold;text-transform:uppercase;color:#666;margin-bottom:8px;border-bottom:1px solid #eee;padding-bottom:4px">Reçu par (Client)</div>
<div style="font-size:9pt;color:#333;line-height:1.9">
Nom : ________________________________<br>
Fonction : ________________________________<br>
Signature &amp; cachet :
</div>
</div>
</div>

<div style="text-align:center;font-size:7.5pt;color:#888;margin-top:16px;padding-top:10px;border-top:1px solid #ddd">
Document généré automatiquement par SOLSTICE — ${dateStr} à ${heureStr}<br>
Cet état récapitulatif fait foi des ${typeDoc.toLowerCase()} émises à la date de génération.
</div>

</div>
<div class="no-print">
<button onclick="window.print()" style="background:#10B981;color:white">🖨️ Imprimer</button>
<button onclick="window.close()" style="background:#64748B;color:white">✕ Fermer</button>
</div>
</body>
</html>`;

const w = window.open('', '_blank', 'width=1100,height=750');
w.document.write(recapHTML);
w.document.close();
};

// Générer un bordereau de livraison pour les factures sélectionnées
const genererBordereauxLivraison = () => {
const facturesSelectionnees = filteredDocuments.filter(d =>
selectedDocs.includes(d.id) && d.type === 'facture'
);

if (facturesSelectionnees.length === 0) {
alert('Veuillez sélectionner au moins une facture pour générer un bordereau de livraison');
return;
}

const now = new Date();
const dateStr = now.toISOString().split('T')[0];
const heureStr = now.toLocaleTimeString('fr-FR');

// Récupérer les infos entreprise
const raisonSociale = settings.raisonSociale || 'SOLSTICE';
const logoEntreprise = settings.logo || '';

// Générer un BL pour chaque facture sélectionnée
facturesSelectionnees.forEach((facture, index) => {
// Générer le numéro de BL complémentaire à la facture
// Format: BL-[NUMERO_FACTURE]-[TIMESTAMP]
const numeroBL = `BL-${facture.numero}-${Date.now().toString().slice(-4)}`;

const nouveauBL = {
id: Date.now() + index,
numeroBL: numeroBL,
factureId: facture.id,
factureNumero: facture.numero,
dateGeneration: now.toISOString(),
client: facture.client,
items: facture.items,
totalQuantite: facture.items.reduce((sum, item) => sum + (item.quantite || 0), 0)
};

// Sauvegarder le BL dans l'état
setBordereauxLivraison(prev => [...prev, nouveauBL]);

// Ouvrir le BL dans une nouvelle fenêtre
const bordereauHTML = `
<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Bordereau de Livraison ${numeroBL}</title>
<style>
@page {
size: A4;
margin: 1cm;
}

* {
margin: 0;
padding: 0;
box-sizing: border-box;
}

body {
font-family: 'Arial', sans-serif;
line-height: 1.4;
color: #000;
background: white;
padding: 20px;
}

.container {
max-width: 21cm;
margin: 0 auto;
border: 2px solid #000;
padding: 20px;
position: relative;
}

/* En-tête */
.header {
display: grid;
grid-template-columns: 100px 1fr 200px;
gap: 15px;
align-items: start;
margin-bottom: 20px;
padding-bottom: 15px;
border-bottom: 2px solid #000;
}

.logo-box {
width: 90px;
height: 90px;
border: 2px solid #000;
display: flex;
align-items: center;
justify-content: center;
padding: 5px;
}

.logo-box img {
max-width: 100%;
max-height: 100%;
object-fit: contain;
}

.company-info {
font-size: 9pt;
line-height: 1.4;
}

.company-name {
font-size: 12pt;
font-weight: bold;
text-transform: uppercase;
margin-bottom: 5px;
}

.bl-number-box {
border: 2px solid #000;
padding: 10px;
text-align: center;
background: #f5f5f5;
}

.bl-label {
font-size: 8pt;
font-weight: bold;
margin-bottom: 3px;
}

.bl-number {
font-size: 10pt;
font-weight: bold;
font-family: monospace;
color: #000;
}

.bl-date {
font-size: 8pt;
margin-top: 5px;
}

/* Titre */
.title {
text-align: center;
font-size: 18pt;
font-weight: bold;
letter-spacing: 3px;
margin: 20px 0;
padding: 10px;
border-top: 2px solid #000;
border-bottom: 2px solid #000;
text-transform: uppercase;
}

/* Référence facture */
.facture-ref {
background: #e8e8e8;
padding: 8px;
margin-bottom: 15px;
border-left: 4px solid #000;
font-size: 9pt;
}

.facture-ref strong {
font-size: 10pt;
}

/* Informations client */
.client-section {
margin-bottom: 20px;
padding: 10px;
border: 1px solid #000;
}

.client-label {
font-size: 8pt;
font-weight: bold;
text-transform: uppercase;
margin-bottom: 5px;
color: #333;
}

.client-name {
font-size: 11pt;
font-weight: bold;
margin-bottom: 5px;
}

.client-details {
font-size: 9pt;
line-height: 1.4;
}

/* Tableau des articles */
.items-table {
width: 100%;
border-collapse: collapse;
margin-bottom: 20px;
font-size: 9pt;
}

.items-table thead {
background: #e0e0e0;
}

.items-table th {
border: 1px solid #000;
padding: 8px;
text-align: left;
font-weight: bold;
font-size: 8pt;
text-transform: uppercase;
}

.items-table td {
border: 1px solid #000;
padding: 6px 8px;
vertical-align: top;
}

.items-table tbody tr:nth-child(even) {
background: #f9f9f9;
}

.col-num { width: 5%; text-align: center; }
.col-desc { width: 50%; }
.col-unit { width: 15%; text-align: center; }
.col-qty { width: 15%; text-align: center; }
.col-obs { width: 15%; text-align: center; }

/* Totaux */
.totals-section {
display: flex;
justify-content: space-between;
margin: 20px 0;
padding: 15px;
background: #f5f5f5;
border: 2px solid #000;
}

.total-box {
text-align: center;
}

.total-label {
font-size: 8pt;
font-weight: bold;
text-transform: uppercase;
margin-bottom: 5px;
}

.total-value {
font-size: 14pt;
font-weight: bold;
}

/* Signatures */
.signatures {
display: grid;
grid-template-columns: 1fr 1fr 1fr;
gap: 20px;
margin-top: 30px;
padding-top: 20px;
border-top: 2px solid #000;
}

.signature-box {
text-align: center;
border: 1px solid #ccc;
padding: 10px;
min-height: 100px;
display: flex;
flex-direction: column;
justify-content: space-between;
}

.signature-label {
font-size: 8pt;
font-weight: bold;
text-transform: uppercase;
border-bottom: 1px solid #ccc;
padding-bottom: 5px;
margin-bottom: 30px;
}

.signature-line {
border-top: 1px solid #000;
margin-top: auto;
padding-top: 5px;
font-size: 7pt;
font-style: italic;
}

/* Footer */
.footer {
margin-top: 20px;
padding-top: 10px;
border-top: 1px solid #000;
font-size: 8pt;
text-align: center;
color: #666;
}

/* Cachet */
.stamp-area {
position: absolute;
bottom: 100px;
right: 50px;
width: 120px;
height: 120px;
border: 3px solid #ff0000;
border-radius: 50%;
display: flex;
align-items: center;
justify-content: center;
transform: rotate(-15deg);
opacity: 0.3;
pointer-events: none;
}

.stamp-text {
color: #ff0000;
font-weight: bold;
font-size: 10pt;
text-align: center;
}

@media print {
body { margin: 0; padding: 0; }
.no-print { display: none !important; }
.container { border: 2px solid #000; }
}

.no-print {
position: fixed;
bottom: 20px;
right: 20px;
z-index: 1000;
display: flex;
gap: 10px;
}

.btn {
padding: 10px 20px;
border: none;
border-radius: 5px;
cursor: pointer;
font-weight: bold;
font-size: 14px;
}

.btn-print {
background: #10B981;
color: white;
}

.btn-close {
background: #EF4444;
color: white;
}
</style>
</head>
<body>
<div class="container">
<!-- En-tête -->
<div class="header">
<div class="logo-box">
${logoEntreprise ? `<img src="${logoEntreprise}" alt="Logo">` : '<div style="font-size: 8pt; font-weight: bold;">LOGO</div>'}
</div>

<div class="company-info">
<div class="company-name">${raisonSociale}</div>
<div>${settings.adresse || ''}</div>
<div>Tél: ${settings.telephone || ''}</div>
<div>${settings.email || ''}</div>
<div style="margin-top: 5px; font-size: 8pt;">
${settings.nui ? `NUI: ${settings.nui}` : ''}
${settings.rccm ? ` | RCCM: ${settings.rccm}` : ''}
</div>
</div>

<div class="bl-number-box">
<div class="bl-label">BORDEREAU DE LIVRAISON N°</div>
<div class="bl-number">${numeroBL}</div>
<div class="bl-date">Date: ${new Date(nouveauBL.dateGeneration).toLocaleDateString('fr-FR')}</div>
</div>
</div>

<!-- Titre -->
<div class="title">Bordereau de Livraison</div>

<!-- Référence Facture -->
<div class="facture-ref">
<strong>Référence Facture :</strong> ${facture.numero} |
<strong>Date Facture :</strong> ${new Date(facture.date).toLocaleDateString('fr-FR')} |
<strong>Montant :</strong> ${formatMontant(facture.total)}
</div>

<!-- Informations Client -->
<div class="client-section">
<div class="client-label">Destinataire / Client :</div>
<div class="client-name">${(facture.client?.nom||'')}</div>
<div class="client-details">
${facture.client?.adresse ? `<div>${facture.client?.adresse}</div>` : ''}
${facture.client?.telephone ? `<div>Tél: ${facture.client?.telephone}</div>` : ''}
${facture.client?.email ? `<div>Email: ${facture.client?.email}</div>` : ''}
${facture.client?.nui ? `<div>NUI: ${facture.client?.nui}</div>` : ''}
</div>
</div>

<!-- Tableau des articles -->
<table class="items-table">
<thead>
<tr>
<th class="col-num">N°</th>
<th class="col-desc">Désignation</th>
<th class="col-unit">Unité</th>
<th class="col-qty">Qté Livrée</th>
<th class="col-obs">Observations</th>
</tr>
</thead>
<tbody>
${(() => {
const articleItems = facture.items.filter(i => (i.lineType || 'article') === 'article');
let artNum = 0;
return facture.items.map((item, idx) => {
const lt = item.lineType || 'article';
if (lt === 'section') {
return `<tr style="background:#F0F0F0;border-top:1px solid #999;border-bottom:1px solid #999"><td colspan="5" style="padding:4px 6px;font-weight:bold;font-size:8pt">▸ ${item.sectionLabel || 'Section'}</td></tr>`;
} else if (lt === 'note') {
return `<tr><td colspan="5" style="padding:2px 6px 2px 12px;font-style:italic;font-size:7pt;color:#666">${item.noteLabel || ''}</td></tr>`;
} else {
artNum++;
const sub = [(item.showMarque && item.marque ? "Marque : " + item.marque : ""), (item.showModeleRef && item.modeleRef ? "Modèle / Réf. : " + item.modeleRef : "")].filter(Boolean).join(" — ");
const desc = sub ? (item.description || "") + "  |  " + sub : (item.description || "");
return `<tr><td class="col-num">${artNum}</td><td class="col-desc">${desc}</td><td class="col-unit">${formatUnite(item.unite)}</td><td class="col-qty">${item.quantite}</td><td class="col-obs"></td></tr>`;
}
}).join('');
})()}
${(() => {
const articleCount = facture.items.filter(i => (i.lineType || 'article') === 'article').length;
const emptyCount = Math.max(0, 5 - articleCount);
return Array(emptyCount).fill('').map((_, idx) => `<tr><td class="col-num">${articleCount + idx + 1}</td><td class="col-desc" style="height:30px"></td><td class="col-unit"></td><td class="col-qty"></td><td class="col-obs"></td></tr>`).join('');
})()}
</tbody>
</table>

<!-- Totaux -->
<div class="totals-section">
<div class="total-box">
<div class="total-label">Total Articles</div>
<div class="total-value">${facture.items.filter(i => (i.lineType || 'article') === 'article').length}</div>
</div>
<div class="total-box">
<div class="total-label">Quantité Totale</div>
<div class="total-value">${facture.items.filter(i => (i.lineType || 'article') === 'article').reduce((sum, item) => sum + (item.quantite || 0), 0)}</div>
</div>
<div class="total-box">
<div class="total-label">Colis</div>
<div class="total-value">...........</div>
</div>
</div>

<!-- Signatures -->
<div class="signatures">
<div class="signature-box">
<div class="signature-label">Préparé par</div>
<div class="signature-line">Nom & Signature</div>
</div>
<div class="signature-box">
<div class="signature-label">Chauffeur / Livreur</div>
<div class="signature-line">Nom & Signature</div>
</div>
<div class="signature-box">
<div class="signature-label">Client (Réception)</div>
<div class="signature-line">Nom, Date & Signature<br>Cachet entreprise</div>
</div>
</div>

<!-- Footer -->
<div class="footer">
<p><strong>Important :</strong> Ce bordereau doit accompagner la marchandise. Le client doit vérifier l'état et la quantité des articles à la réception.</p>
<p style="margin-top: 5px; font-size: 7pt;">Document généré le ${dateStr} à ${heureStr} | ${raisonSociale}</p>
</div>

<!-- Zone de cachet (filigrane) -->
<div class="stamp-area">
<div class="stamp-text">LIVRÉ</div>
</div>
</div>

<div class="no-print">
<button class="btn btn-print" onclick="window.print()">
🖨️ Imprimer BL
</button>
<button class="btn btn-close" onclick="window.close()">
✕ Fermer
</button>
</div>
</body>
</html>
`;

const printWindow = window.open('', '_blank', 'width=900,height=700');
printWindow.document.write(bordereauHTML);
printWindow.document.close();
});

// Message de confirmation
alert(`${facturesSelectionnees.length} Bordereau(x) de Livraison généré(s) avec succès !\n\nChaque BL est associé à sa facture respective.`);

// Réinitialiser la sélection après génération
setSelectedDocs([]);
};

// Formater le montant
const formatMontant = (montant) => {
return new Intl.NumberFormat('fr-FR', {
minimumFractionDigits: 0,
maximumFractionDigits: 0
}).format(montant) + ' FCFA';
};

// Formater l'affichage de la méthode de paiement
const formatMethodePaiement = (code) => {
if (code === 'toutes') {
return 'Virement Bancaire, Chèque, Espèces, Mobile Money';
}
const methode = METHODES_PAIEMENT.find(m => m.code === code);
return methode ? methode.libelle : 'Virement Bancaire';
};

// Formater l'affichage des unités
const formatUnite = (code) => {
const unite = UNITES.find(u => u.code === code);
return unite ? unite.libelle : code || 'PCS';
};

// ── Facture Express ──────────────────────────────────────────────────────────
const genererFactureExpress = () => {
const factures = filteredDocuments.filter(d => selectedDocs.includes(d.id) && d.type === 'facture');
if (factures.length === 0) {
  alert('Veuillez sélectionner au moins une facture pour générer une Facture Express');
  return;
}

const fmt = (n) => {
  const v = Math.round(n || 0);
  return v.toLocaleString('fr-FR').replace(/\u202f/g, '\u00a0') + '\u00a0FCFA';
};

// nombreEnLettres embarqué (copie autonome pour le HTML)
const NEL = (nombre) => {
  if (!nombre || nombre === 0) return 'zéro';
  const u = ['','un','deux','trois','quatre','cinq','six','sept','huit','neuf'];
  const d = ['','dix','vingt','trente','quarante','cinquante','soixante','soixante-dix','quatre-vingt','quatre-vingt-dix'];
  const ex = ['dix','onze','douze','treize','quatorze','quinze','seize'];
  const cg = (n) => {
    if (n===0) return ''; if (n<10) return u[n];
    if (n>=10&&n<17) return ex[n-10]; if (n===17) return 'dix-sept'; if (n===18) return 'dix-huit'; if (n===19) return 'dix-neuf';
    if (n>=20&&n<70){const dz=Math.floor(n/10),un=n%10;if(un===0)return d[dz];if(un===1&&dz===2)return 'vingt et un';if(un===1&&dz===3)return 'trente et un';if(un===1&&dz===4)return 'quarante et un';if(un===1&&dz===5)return 'cinquante et un';if(un===1&&dz===6)return 'soixante et un';return d[dz]+'-'+u[un];}
    if(n>=70&&n<80){const un=n-60;if(un===11)return 'soixante et onze';if(un>=10&&un<17)return 'soixante-'+ex[un-10];return 'soixante-'+cg(un);}
    if(n>=80&&n<90){const un=n-80;if(un===0)return 'quatre-vingts';return 'quatre-vingt-'+u[un];}
    if(n>=90){const un=n-80;if(un>=10&&un<17)return 'quatre-vingt-'+ex[un-10];return 'quatre-vingt-'+cg(un);}
    return '';
  };
  const cn = (n) => {
    if(n<100)return cg(n);
    if(n<1000){const c=Math.floor(n/100),r=n%100;let res=c===1?'cent':u[c]+' cent';if(r>0)res+=' '+cg(r);else if(c>1)res+='s';return res;}
    if(n<1000000){const m=Math.floor(n/1000),r=n%1000;let res=m===1?'mille':cn(m)+' mille';if(r>0)res+=' '+cn(r);return res;}
    if(n<1000000000){const m=Math.floor(n/1000000),r=n%1000000;let res=m===1?'un million':cn(m)+' millions';if(r>0)res+=' '+cn(r);return res;}
    return 'nombre trop grand';
  };
  return cn(Math.floor(nombre));
};

const resolveMethodePaiement = (code) => {
  if (!code || code === 'toutes') return 'Virement Bancaire, Chèque, Espèces, Mobile Money';
  const map = { virement: 'Virement Bancaire', cheque: 'Chèque', especes: 'Espèces', mobile: 'Mobile Money' };
  return map[code] || code;
};

const pagesHTML = factures.map((doc, idx) => {
  const client = doc.client || {};
  const fromPaiements = (doc.paiements || []).reduce((s, p) => s + (p.montant || 0), 0);
  const montantPaye = fromPaiements > 0 ? fromPaiements : ((doc.montantPaye || 0) > 0 ? doc.montantPaye : (doc.montantAvance || 0));
  const restant = Math.max(0, (doc.total || 0) - montantPaye);
  const montantAffiche = restant > 0 ? restant : (doc.total || 0);
  const frais = doc.fraisSupplementaires || [];
  const remise = doc.remiseGlobale?.montant || 0;
  const showTVA = doc.afficherModeTaxation && doc.taxMode === 'TTC' && (doc.tva || 0) > 0;
  const penalites = doc.penalitesRetard;
  const montantPenalites = (penalites?.actif && penalites?.montant > 0) ? penalites.montant : 0;
  const logoHTML = settings.logo ? `<img src="${settings.logo}" alt="Logo" style="max-height:80px;max-width:200px;object-fit:contain;display:block;margin-bottom:8px;">` : '';
  const isLast = idx === factures.length - 1;
  const dateDoc = new Date(doc.date);
  const dateFormatLong = dateDoc.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const dateFormatCourt = dateDoc.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();

  return `<div class="page"${!isLast ? ' style="page-break-after:always;"' : ''}>
  <div class="header">
    <div class="header-left">
      ${logoHTML}
      <div class="company-name">${settings.raisonSociale || 'SOLSTICE'}</div>
      ${settings.adresse ? `<div>${settings.adresse.replace(/\n/g,'<br>')}</div>` : ''}
      ${settings.telephone ? `<div>Tél : ${settings.telephone}</div>` : ''}
      ${settings.email ? `<div>${settings.email}</div>` : ''}
      ${settings.siteWeb ? `<div>${settings.siteWeb}</div>` : ''}
      ${settings.nui ? `<div style="margin-top:6px;"><strong>NUI : ${settings.nui}</strong></div>` : ''}
      ${settings.rccm ? `<div><strong>RCCM : ${settings.rccm}</strong></div>` : ''}
    </div>
    <div class="header-right">
      <div class="invoice-box">
        <div class="invoice-label">FACTURE N°</div>
        <div class="invoice-number">${doc.numero}</div>
      </div>
      <div style="margin-top:12px;line-height:1.5;">
        <div><strong>DATE : ${dateFormatLong}</strong></div>
        <div>${dateFormatCourt}</div>
        ${doc.dateEcheance ? `<div style="margin-top:5px;"><strong>ÉCHÉANCE :</strong> ${new Date(doc.dateEcheance).toLocaleDateString('fr-FR')}</div>` : ''}
      </div>
    </div>
  </div>
  <div class="double-line"></div>
  <div class="main-title">F A C T U R E</div>
  <div class="double-line"></div>
  ${doc.objet ? `<div class="objet-section"><div class="objet-label">OBJET :</div><div class="objet-text">${doc.objet}</div></div>` : ''}
  <div class="body-grid">
    <div class="body-left">
      <div class="section-label">FACTURÉ À :</div>
      <div class="client-box">
        <div class="client-name">${client.nom || '—'}</div>
        ${client.adresse ? `<div>${client.adresse}</div>` : ''}
        ${client.ville ? `<div>${client.ville}${client.pays && client.pays !== 'Cameroun' ? ', '+client.pays : ''}</div>` : ''}
        ${client.telephone ? `<div>Tél : ${client.telephone}</div>` : ''}
        ${client.email ? `<div>${client.email}</div>` : ''}
        ${client.nui ? `<div style="margin-top:4px;"><strong>NUI : ${client.nui}</strong></div>` : ''}
        ${client.rccm ? `<div><strong>RCCM : ${client.rccm}</strong></div>` : ''}
      </div>
      ${doc.methodePaiement ? `<div class="section-label" style="margin-top:18px;">MÉTHODE DE PAIEMENT :</div><div class="paiement-box">${resolveMethodePaiement(doc.methodePaiement)}</div>` : ''}
    </div>
    <div class="body-right">
      <table class="totaux-table">
        <tr><td>SOUS-TOTAL</td><td class="amount">${fmt(doc.sousTotal)}</td></tr>
        ${remise > 0 ? `<tr><td>REMISE</td><td class="amount">- ${fmt(remise)}</td></tr>` : ''}
        ${frais.map(f => `<tr><td>${f.description || 'Frais supplémentaires'}</td><td class="amount">${fmt(f.montant)}</td></tr>`).join('')}
        ${showTVA ? `<tr><td>TVA (${settings.tauxTVA || 19.25}%)</td><td class="amount">${fmt(doc.tva)}</td></tr>` : ''}
        ${montantPenalites > 0 ? `<tr><td>PÉNALITÉS DE RETARD</td><td class="amount">${fmt(montantPenalites)}</td></tr>` : ''}
        <tr class="total-row"><td>TOTAL</td><td class="amount">${fmt(doc.total)}</td></tr>
        ${montantPaye > 0 ? `<tr class="paye-row"><td>MONTANT PAYÉ</td><td class="amount">- ${fmt(montantPaye)}</td></tr><tr class="restant-row"><td><strong>RESTE À PAYER</strong></td><td class="amount"><strong>${fmt(restant)}</strong></td></tr>` : ''}
      </table>
      <div class="big-total-box">
        <div class="big-total-label">TOTAL</div>
        <div class="big-total-amount">${fmt(montantAffiche)}</div>
      </div>
      <div class="arrete">
        <em><strong>ARRÊTÉ LA PRÉSENTE FACTURE À :</strong></em><br>
        <em>${NEL(Math.floor(montantAffiche))} francs CFA</em>
      </div>
    </div>
  </div>
  <div class="footer">
    <div class="footer-left">
      <strong>MERCI POUR VOTRE CONFIANCE!</strong>
      ${settings.siteWeb ? `<br>${settings.siteWeb}` : ''}
    </div>
    <div class="footer-right">
      <div id="qr-${doc.id.replace(/-/g,'')}" style="width:80px;height:80px;"></div>
      <div style="font-size:7pt;text-align:center;margin-top:3px;font-weight:bold;">SCANNEZ MOI</div>
    </div>
  </div>
</div>`;
}).join('');

const qrInits = factures.map(doc => {
  const fromP = (doc.paiements || []).reduce((s, p) => s + (p.montant || 0), 0);
  const mp = fromP > 0 ? fromP : ((doc.montantPaye||0)>0 ? doc.montantPaye : (doc.montantAvance||0));
  const r = Math.max(0, (doc.total||0) - mp);
  const ma = r > 0 ? r : (doc.total||0);
  const safeId = doc.id.replace(/-/g,'');
  return `if(typeof QRCode!=='undefined'&&document.getElementById('qr-${safeId}')){new QRCode(document.getElementById('qr-${safeId}'),{text:'${doc.numero} | Total: ${Math.round(ma)} FCFA',width:80,height:80,correctLevel:QRCode.CorrectLevel.M});}`;
}).join('\n');

const html = `<!DOCTYPE html>
<html lang="fr"><head><meta charset="UTF-8"><title>Facture Express</title>
<script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"><\/script>
<style>
@page{size:A4;margin:1.5cm 2cm;}
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:Arial,sans-serif;font-size:10pt;color:#000;background:white;line-height:1.5;}
.page{max-width:21cm;margin:0 auto;padding:10px 0;}
.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:18px;padding-bottom:15px;border-bottom:3px solid #000;}
.header-left{flex:1;font-size:10pt;line-height:1.6;}
.company-name{font-size:20pt;font-weight:bold;letter-spacing:2px;margin-bottom:4px;}
.header-right{min-width:210px;margin-left:20px;}
.invoice-box{border:2px solid #000;padding:10px 14px;}
.invoice-label{font-size:8pt;font-weight:bold;text-transform:uppercase;letter-spacing:1px;}
.invoice-number{font-size:13pt;font-weight:bold;margin-top:4px;letter-spacing:1px;}
.double-line{border-top:3px solid #000;margin-top:3px;padding-top:3px;border-bottom:1.5px solid #000;margin-bottom:8px;}
.main-title{text-align:center;font-size:22pt;font-weight:bold;letter-spacing:14px;padding:12px 0;}
.objet-section{border:1.5px solid #000;padding:12px 15px;margin:16px 0;}
.objet-label{font-size:8pt;font-weight:bold;text-transform:uppercase;margin-bottom:5px;}
.objet-text{font-weight:bold;font-size:11pt;line-height:1.5;}
.body-grid{display:grid;grid-template-columns:1fr 1.1fr;gap:20px;margin:18px 0;}
.section-label{font-size:8pt;font-weight:bold;text-transform:uppercase;margin-bottom:7px;}
.client-box{border:1.5px solid #000;padding:12px 14px;line-height:1.6;}
.client-name{font-weight:bold;font-size:11pt;margin-bottom:3px;}
.paiement-box{border:1.5px solid #000;padding:10px 14px;line-height:1.6;}
.totaux-table{width:100%;border-collapse:collapse;}
.totaux-table tr{border-bottom:1px solid #ddd;}
.totaux-table td{padding:7px 4px;}
.totaux-table td.amount{text-align:right;font-weight:600;white-space:nowrap;padding-left:15px;}
.total-row td{border-top:2px solid #000;font-weight:bold;font-size:11pt;padding-top:9px;}
.paye-row td{color:#444;}
.restant-row td{font-size:11pt;}
.big-total-box{border:2px solid #000;text-align:center;padding:14px 10px;margin-top:14px;}
.big-total-label{font-size:9pt;font-weight:bold;text-transform:uppercase;letter-spacing:1px;margin-bottom:5px;}
.big-total-amount{font-size:17pt;font-weight:bold;letter-spacing:2px;}
.arrete{margin-top:11px;font-size:9pt;text-align:center;line-height:1.6;border-top:1px solid #ccc;padding-top:9px;}
.footer{display:flex;justify-content:space-between;align-items:flex-end;margin-top:28px;padding-top:14px;border-top:2px solid #000;font-size:10pt;}
</style></head><body>
${pagesHTML}
<script>
window.onload=function(){
${qrInits}
setTimeout(function(){window.print();},900);
};
<\/script>
</body></html>`;

const w = window.open('', '_blank');
w.document.write(html);
w.document.close();
};

// Convertir un nombre en lettres (français)
const nombreEnLettres = (nombre) => {
if (nombre === 0) return 'zéro';

const unites = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];
const dizaines = ['', 'dix', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante-dix', 'quatre-vingt', 'quatre-vingt-dix'];
const exceptions = ['dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize'];

const convertirGroupe = (n) => {
if (n === 0) return '';
if (n < 10) return unites[n];
if (n >= 10 && n < 17) return exceptions[n - 10];
if (n === 17) return 'dix-sept';
if (n === 18) return 'dix-huit';
if (n === 19) return 'dix-neuf';
if (n >= 20 && n < 70) {
const d = Math.floor(n / 10);
const u = n % 10;
if (u === 0) return dizaines[d];
if (u === 1 && d === 2) return 'vingt et un';
if (u === 1 && d === 3) return 'trente et un';
if (u === 1 && d === 4) return 'quarante et un';
if (u === 1 && d === 5) return 'cinquante et un';
if (u === 1 && d === 6) return 'soixante et un';
return dizaines[d] + '-' + unites[u];
}
if (n >= 70 && n < 80) {
const u = n - 60;
if (u === 11) return 'soixante et onze';
if (u >= 10 && u < 17) return 'soixante-' + exceptions[u - 10];
return 'soixante-' + convertirGroupe(u);
}
if (n >= 80 && n < 90) {
const u = n - 80;
if (u === 0) return 'quatre-vingts';
return 'quatre-vingt-' + unites[u];
}
if (n >= 90) {
const u = n - 80;
if (u >= 10 && u < 17) return 'quatre-vingt-' + exceptions[u - 10];
return 'quatre-vingt-' + convertirGroupe(u);
}
return '';
};

const convertirNombre = (n) => {
if (n < 100) return convertirGroupe(n);
if (n < 1000) {
const c = Math.floor(n / 100);
const r = n % 100;
let result = c === 1 ? 'cent' : unites[c] + ' cent';
if (r > 0) result += ' ' + convertirGroupe(r);
else if (c > 1) result += 's';
return result;
}
if (n < 1000000) {
const m = Math.floor(n / 1000);
const r = n % 1000;
let result = m === 1 ? 'mille' : convertirNombre(m) + ' mille';
if (r > 0) result += ' ' + convertirNombre(r);
return result;
}
if (n < 1000000000) {
const m = Math.floor(n / 1000000);
const r = n % 1000000;
let result = m === 1 ? 'un million' : convertirNombre(m) + ' millions';
if (r > 0) result += ' ' + convertirNombre(r);
return result;
}
return 'nombre trop grand';
};

return convertirNombre(Math.floor(nombre));
};

// ── Imprimer en gros — documents complets dans un seul fichier ───────────────
const imprimerEnGros = () => {
const docs = filteredDocuments.filter(d => selectedDocs.includes(d.id));
if (docs.length === 0) { alert('Aucun document sélectionné'); return; }

const types = [...new Set(docs.map(d => d.type))];
if (types.length > 1) {
alert('Veuillez sélectionner des documents du même type :\nuniquement des factures OU uniquement des devis.');
return;
}

const isFacture = docs[0].type === 'facture';
const fmt = (n) => Math.round(n || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const methodeLabel = (code) => {
const map = { virement:'Virement Bancaire', cheque:'Chèque', especes:'Espèces', mobile:'Mobile Money', toutes:'Virement Bancaire, Chèque, Espèces, Mobile Money' };
return map[code] || code || '';
};

const logoHTML = settings.logo ? `<img src="${settings.logo}" alt="Logo" style="max-height:65px;max-width:160px;object-fit:contain;display:block;margin-bottom:8px;">` : '';

const buildItems = (items) => {
let rows = [];
let artNum = 0;
let sectionLabel = null;
let sectionTotal = 0;
let sectionHasArticles = false;

const flushSection = () => {
if (sectionHasArticles) {
rows.push(`<tr style="background:#F0F4FF">
<td colspan="4" style="padding:5px 8px;font-size:8pt;font-weight:bold;color:#0A2540;text-align:right;border-bottom:1px solid #CBD5E1;border-top:1px solid #CBD5E1">
Sous-total${sectionLabel ? ' — ' + sectionLabel : ''} :
</td>
<td style="padding:5px 8px;text-align:right;font-family:'Courier New',monospace;font-weight:bold;color:#0A2540;font-size:8.5pt;border-bottom:1px solid #CBD5E1;border-top:1px solid #CBD5E1">
${fmt(sectionTotal)}
</td>
</tr>`);
}
sectionLabel = null;
sectionTotal = 0;
sectionHasArticles = false;
};

items.forEach(item => {
const lt = item.lineType || 'article';
if (lt === 'section') {
flushSection();
sectionLabel = item.sectionLabel || 'Section';
rows.push(`<tr style="background:#E2E8F0;border-top:1.5px solid #94A3B8;border-bottom:1.5px solid #94A3B8">
<td colspan="5" style="padding:5px 8px;font-weight:bold;font-size:8pt;color:#334155">▸ ${item.sectionLabel || 'Section'}</td>
</tr>`);
} else if (lt === 'note') {
rows.push(`<tr><td colspan="5" style="padding:2px 8px 2px 18px;font-style:italic;font-size:7.5pt;color:#64748B;border-bottom:1px solid #F1F5F9">✎ ${item.noteLabel || ''}</td></tr>`);
} else {
artNum++;
const pu = item.prixUnitaire || 0;
const total = item.montantApresRemise || ((item.quantite || 0) * pu);
sectionTotal += total;
sectionHasArticles = true;
const sub = [
item.showMarque && item.marque ? 'Marque : ' + item.marque : '',
item.showModeleRef && item.modeleRef ? 'Réf. : ' + item.modeleRef : '',
item.description || ''
].filter(Boolean).join(' — ');
rows.push(`<tr style="${artNum % 2 === 0 ? 'background:#FAFAFA' : ''}">
<td style="text-align:center;color:#94A3B8;font-size:7.5pt;padding:6px 4px;border-bottom:1px solid #E2E8F0">${artNum}</td>
<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0">
<div style="font-weight:600;color:#1E293B;font-size:8.5pt">${item.designation || ''}</div>
${sub ? `<div style="font-size:7.5pt;color:#64748B;margin-top:2px">${sub}</div>` : ''}
</td>
<td style="text-align:center;padding:6px 4px;color:#475569;font-size:8.5pt;border-bottom:1px solid #E2E8F0;white-space:nowrap">${formatUnite(item.unite)}</td>
<td style="text-align:right;padding:6px 8px;font-family:'Courier New',monospace;font-size:8.5pt;border-bottom:1px solid #E2E8F0">${item.quantite}</td>
<td style="text-align:right;padding:6px 8px;font-family:'Courier New',monospace;font-size:8.5pt;border-bottom:1px solid #E2E8F0">${fmt(total)}</td>
</tr>`);
}
});
flushSection();
return rows.join('');
};

const pagesHTML = docs.map((doc, idx) => {
const client = doc.client || {};
const remise = doc.remiseGlobale?.montant || 0;
const frais = doc.fraisSupplementaires || [];
const showTVA = doc.afficherModeTaxation && doc.taxMode === 'TTC' && (doc.tva || 0) > 0;
const penalites = doc.penalitesRetard;
const montantPenalites = (penalites?.actif && penalites?.montant > 0) ? penalites.montant : 0;
const restant = doc.montantRestant || 0;
const montantAffiche = restant > 0 ? restant : (doc.total || 0);
const isLast = idx === docs.length - 1;
const dateDoc = new Date(doc.date);
const dateStr = dateDoc.toLocaleDateString('fr-FR');
const echeanceStr = doc.dateEcheance ? new Date(doc.dateEcheance).toLocaleDateString('fr-FR') : null;
const qrData = encodeURIComponent(`${doc.numero} | ${(doc.client?.nom||'')} | ${Math.round(montantAffiche)} FCFA`);
const qrImgSrc = `https://api.qrserver.com/v1/create-qr-code/?size=75x75&data=${qrData}`;

return `<div class="page"${!isLast ? ' style="page-break-after:always"' : ''}>

<div class="header">
<div class="header-left">
${logoHTML}
<div class="company-name">${settings.raisonSociale || 'SOLSTICE'}</div>
<div class="company-info">
${settings.adresse ? settings.adresse.replace(/\n/g, '<br>') + '<br>' : ''}
${settings.telephone ? 'Tél : ' + settings.telephone + (settings.email ? ' &nbsp;|&nbsp; ' + settings.email : '') + '<br>' : (settings.email ? settings.email + '<br>' : '')}
${settings.siteWeb ? settings.siteWeb + '<br>' : ''}
${settings.nui ? '<strong>NUI : ' + settings.nui + '</strong>' + (settings.rccm ? ' &nbsp;|&nbsp; <strong>RCCM : ' + settings.rccm + '</strong>' : '') : ''}
</div>
</div>
<div class="header-right">
<div class="doc-badge">
<div class="badge-label">${isFacture ? 'Facture' : 'Devis'} N°</div>
<div class="badge-num">${doc.numero}</div>
</div>
<div class="doc-meta">
<strong>Date :</strong> ${dateStr}<br>
${isFacture && echeanceStr ? `<strong>Échéance :</strong> ${echeanceStr}<br>` : ''}
${!isFacture && echeanceStr ? `<strong>Validité :</strong> ${echeanceStr}<br>` : ''}
</div>
</div>
</div>

<div class="doc-title">${isFacture ? 'F A C T U R E' : 'D E V I S'}</div>

${doc.objet ? `<div class="objet-box"><div class="objet-label">Objet</div><div class="objet-text">${doc.objet}</div></div>` : ''}

<div class="body-grid">
<div>
<div class="section-label">${isFacture ? 'Facturé à' : 'Établi pour'}</div>
<div class="client-box">
<div class="client-name">${client.nom || '—'}</div>
${client.adresse ? `<div class="client-detail">${client.adresse}</div>` : ''}
${client.ville ? `<div class="client-detail">${client.ville}${client.pays && client.pays !== 'Cameroun' ? ', ' + client.pays : ''}</div>` : ''}
${client.telephone ? `<div class="client-detail">Tél : ${client.telephone}</div>` : ''}
${client.email ? `<div class="client-detail">${client.email}</div>` : ''}
${client.nui || client.rccm ? `<div class="client-ids">${client.nui ? '<strong>NUI :</strong> ' + client.nui : ''}${client.nui && client.rccm ? ' &nbsp; ' : ''}${client.rccm ? '<strong>RCCM :</strong> ' + client.rccm : ''}</div>` : ''}
</div>
${!isFacture && echeanceStr ? `<div class="validite-box">⚠ Ce devis est valable jusqu'au ${echeanceStr}.</div>` : ''}
${isFacture && doc.methodePaiement ? `<div class="section-label" style="margin-top:10px">Méthode de paiement</div><div class="paiement-box">${methodeLabel(doc.methodePaiement)}</div>` : ''}
</div>
<div>
<div class="section-label">Récapitulatif financier</div>
<table class="totaux-table">
<tr><td>Sous-total HT</td><td class="amount">${fmt(doc.sousTotal)} FCFA</td></tr>
${remise > 0 ? `<tr><td>Remise</td><td class="amount">- ${fmt(remise)} FCFA</td></tr>` : ''}
${frais.map(f => `<tr><td>${f.description || 'Frais supplémentaires'}</td><td class="amount">${fmt(f.montant)} FCFA</td></tr>`).join('')}
${showTVA ? `<tr><td>TVA (${settings.tauxTVA || 19.25}%)</td><td class="amount">${fmt(doc.tva)} FCFA</td></tr>` : ''}
${montantPenalites > 0 ? `<tr><td>Pénalités de retard</td><td class="amount">${fmt(montantPenalites)} FCFA</td></tr>` : ''}
<tr class="total-row"><td>TOTAL</td><td class="amount">${fmt(doc.total)} FCFA</td></tr>
${isFacture && doc.montantPaye > 0 ? `<tr class="paye-row"><td>Montant payé</td><td class="amount">- ${fmt(doc.montantPaye)} FCFA</td></tr><tr class="restant-row"><td><strong>Reste à payer</strong></td><td class="amount"><strong>${fmt(restant)} FCFA</strong></td></tr>` : ''}
</table>
<div class="big-total-box">
<div class="big-total-label">${isFacture && restant > 0 ? 'Net à payer' : 'Total'}</div>
<div class="big-total-amount">${fmt(montantAffiche)} FCFA</div>
</div>
<div class="arrete"><em><strong>Arrêté à :</strong> ${nombreEnLettres(Math.floor(montantAffiche))} francs CFA</em></div>
</div>
</div>

<div class="section-label" style="margin-bottom:6px">Détail des ${isFacture ? 'prestations' : 'fournitures'}</div>
<table class="items-table">
<thead>
<tr>
<th style="width:24px;text-align:center">N°</th>
<th>Désignation / Description</th>
<th style="width:50px;text-align:center">Unité</th>
<th style="width:38px;text-align:right">Qté</th>
<th style="width:110px;text-align:right">Total HT (FCFA)</th>
</tr>
</thead>
<tbody>${buildItems(doc.items || [])}</tbody>
</table>

${doc.conditions ? `<div class="conditions-box"><div class="conditions-label">Conditions de règlement</div>${doc.conditions}</div>` : ''}

<div class="doc-footer">
<div class="footer-left">
<strong>Merci pour votre confiance !</strong>
${settings.siteWeb ? '<br>' + settings.siteWeb : ''}
<br><span style="font-size:7.5pt;color:#94A3B8">Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</span>
</div>
<div style="text-align:center">
<img src="${qrImgSrc}" width="75" height="75" alt="QR Code" onerror="this.style.display='none'">
<div style="font-size:6.5pt;font-weight:bold;color:#94A3B8;margin-top:3px">SCANNEZ-MOI</div>
</div>
</div>

</div>`;
}).join('');

const html = `<!DOCTYPE html>
<html lang="fr"><head><meta charset="UTF-8">
<title>${isFacture ? 'Factures' : 'Devis'} — Impression en gros (${docs.length} document${docs.length > 1 ? 's' : ''})</title>
<style>
@page{size:A4 portrait;margin:1.4cm 1.8cm;}
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:Arial,sans-serif;font-size:10pt;color:#1a1a1a;background:white;}
.page{max-width:21cm;margin:0 auto;padding:0;}
.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:12px;border-bottom:3px solid #0A2540;margin-bottom:14px;}
.header-left{flex:1;}
.company-name{font-size:17pt;font-weight:bold;letter-spacing:2px;color:#0A2540;}
.company-info{font-size:8.5pt;color:#475569;line-height:1.65;margin-top:4px;}
.header-right{min-width:190px;margin-left:20px;text-align:right;}
.doc-badge{display:inline-block;border:2.5px solid #0A2540;padding:7px 14px;text-align:center;margin-bottom:8px;}
.badge-label{font-size:7.5pt;font-weight:bold;text-transform:uppercase;letter-spacing:1px;color:#475569;}
.badge-num{font-size:13pt;font-weight:bold;letter-spacing:1px;color:#0A2540;margin-top:2px;}
.doc-meta{font-size:9pt;line-height:1.8;color:#334155;}
.doc-title{text-align:center;font-size:19pt;font-weight:bold;letter-spacing:12px;padding:9px 0;border-top:2px solid #0A2540;border-bottom:2px solid #0A2540;margin-bottom:12px;color:#0A2540;}
.objet-box{border:1.5px solid #CBD5E1;border-left:4px solid #0A2540;padding:7px 12px;margin-bottom:12px;background:#F8FAFC;}
.objet-label{font-size:7pt;font-weight:bold;text-transform:uppercase;color:#64748B;margin-bottom:2px;letter-spacing:.5px;}
.objet-text{font-size:10.5pt;font-weight:bold;color:#0F172A;}
.body-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:14px;}
.section-label{font-size:7.5pt;font-weight:bold;text-transform:uppercase;color:#64748B;margin-bottom:5px;letter-spacing:.5px;}
.client-box{border:1.5px solid #CBD5E1;padding:9px 12px;line-height:1.7;font-size:9.5pt;}
.client-name{font-weight:bold;font-size:11pt;color:#0A2540;margin-bottom:2px;}
.client-detail{color:#475569;font-size:9pt;}
.client-ids{margin-top:5px;padding-top:5px;border-top:1px solid #E2E8F0;font-size:8.5pt;}
.validite-box{margin-top:8px;border:1px solid #FDE68A;background:#FFFBEB;padding:6px 10px;font-size:8.5pt;color:#92400E;}
.paiement-box{border:1.5px solid #CBD5E1;padding:7px 12px;font-size:9pt;color:#334155;}
.totaux-table{width:100%;border-collapse:collapse;}
.totaux-table td{padding:5px 4px;font-size:9.5pt;}
.totaux-table td.amount{text-align:right;font-weight:600;white-space:nowrap;padding-left:10px;font-family:'Courier New',monospace;}
.totaux-table tr{border-bottom:1px solid #F1F5F9;}
.totaux-table tr.total-row td{border-top:2px solid #0A2540;font-weight:bold;font-size:11pt;padding-top:7px;}
.totaux-table tr.paye-row td{color:#64748B;font-size:9pt;}
.totaux-table tr.restant-row td{color:#DC2626;}
.big-total-box{border:2px solid #0A2540;text-align:center;padding:10px;margin-top:10px;background:#F8FAFC;}
.big-total-label{font-size:8pt;font-weight:bold;text-transform:uppercase;letter-spacing:1px;color:#64748B;margin-bottom:3px;}
.big-total-amount{font-size:15pt;font-weight:bold;color:#0A2540;letter-spacing:1px;}
.arrete{margin-top:8px;font-size:8.5pt;text-align:center;color:#475569;line-height:1.5;border-top:1px solid #E2E8F0;padding-top:7px;}
.items-table{width:100%;border-collapse:collapse;font-size:8.5pt;margin-bottom:12px;}
.items-table thead tr{background:#0A2540;color:white;}
.items-table th{padding:7px 6px;font-size:7.5pt;font-weight:bold;text-transform:uppercase;letter-spacing:.3px;}
.items-table td{vertical-align:top;}
.conditions-box{border:1px solid #CBD5E1;border-left:3px solid #64748B;padding:7px 12px;margin-bottom:12px;background:#FAFAFA;font-size:8.5pt;color:#475569;line-height:1.6;}
.conditions-label{font-size:7pt;font-weight:bold;text-transform:uppercase;color:#94A3B8;margin-bottom:3px;}
.doc-footer{display:flex;justify-content:space-between;align-items:flex-end;padding-top:12px;border-top:2px solid #0A2540;margin-top:12px;}
.footer-left{font-size:9pt;color:#475569;line-height:1.7;}
@media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact;}.no-print{display:none!important;}}
.no-print{position:fixed;bottom:20px;right:20px;z-index:1000;display:flex;gap:8px;}
.no-print button{padding:11px 22px;border:none;border-radius:8px;cursor:pointer;font-size:13px;font-weight:600;box-shadow:0 4px 12px rgba(0,0,0,.2);}
</style></head><body>
${pagesHTML}
<div class="no-print">
<button onclick="window.print()" style="background:#10B981;color:white">🖨️ Imprimer</button>
<button onclick="window.close()" style="background:#64748B;color:white">✕ Fermer</button>
</div>
</body></html>`;

const w = window.open('', '_blank', 'width=900,height=750');
w.document.write(html);
w.document.close();
};

return (
<div className="app-container">

{/* ══ BANDEAU UTILISATEUR ══════════════════════════════════════════════════ */}
{profil && (
  <div style={{
    position:'fixed',top:12,right:16,zIndex:9999,
    display:'flex',alignItems:'center',gap:'0.6rem',
    background:'rgba(15,23,42,0.96)',backdropFilter:'blur(12px)',
    border:'1px solid rgba(255,255,255,0.1)',borderRadius:14,
    padding:'0.45rem 0.9rem',boxShadow:'0 4px 20px rgba(0,0,0,0.45)',
    fontFamily:"'Bricolage Grotesque',sans-serif",
  }}>
    <div style={{width:30,height:30,borderRadius:'50%',
      background:'linear-gradient(135deg,#FF6B35,#00D4AA)',
      display:'flex',alignItems:'center',justifyContent:'center',
      fontSize:'0.82rem',fontWeight:800,color:'white',flexShrink:0}}>
      {(profil?.nom||'?')[0].toUpperCase()}
    </div>
    <div style={{lineHeight:1.2}}>
      <div style={{color:'white',fontSize:'0.78rem',fontWeight:700}}>
        {profil?.nom} {profil?.prenom||''}
      </div>
      <div style={{color:'#64748B',fontSize:'0.65rem',textTransform:'uppercase',letterSpacing:'0.05em'}}>
        {(profil?.role||'').replace('_',' ')}
      </div>
    </div>
    <div style={{width:1,height:24,background:'rgba(255,255,255,0.08)',margin:'0 0.1rem'}}/>
    {can('import','write') && (
      <button onClick={()=>setShowImportMigration(true)}
        title="Importer des données"
        style={{background:'rgba(255,107,53,0.15)',border:'1px solid rgba(255,107,53,0.3)',
          borderRadius:8,color:'#FF6B35',padding:'0.3rem 0.55rem',
          cursor:'pointer',fontSize:'0.85rem',fontWeight:700,lineHeight:1}}>
        📥
      </button>
    )}
    {can('users','read') && (
      <button onClick={()=>setShowGestionUsers(true)}
        title="Gérer les utilisateurs"
        style={{background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.1)',
          borderRadius:8,color:'#94A3B8',padding:'0.3rem 0.55rem',
          cursor:'pointer',fontSize:'0.85rem',lineHeight:1}}>
        👥
      </button>
    )}
    <button onClick={()=>setShowChangerMdp(true)}
      title="Changer mon mot de passe"
      style={{background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.1)',
        borderRadius:8,color:'#94A3B8',padding:'0.3rem 0.55rem',
        cursor:'pointer',fontSize:'0.85rem',lineHeight:1}}>
      🔑
    </button>
    <button onClick={()=>{if(window.confirm('Se déconnecter ?')){
      sessionStorage.removeItem('2fa_done')
      sessionStorage.removeItem('2fa_awaiting')
      localStorage.removeItem('solstice-auth')
      window.location.reload()
    }}}
      title="Se déconnecter"
      style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.25)',
        borderRadius:8,color:'#FCA5A5',padding:'0.3rem 0.55rem',
        cursor:'pointer',fontSize:'0.8rem',fontWeight:600,lineHeight:1}}>
      🚪
    </button>
  </div>
)}

{/* ══ MODAL CHANGER MOT DE PASSE ═══════════════════════════════════════════ */}
{showChangerMdp && (
  <div onClick={e=>{if(e.target===e.currentTarget)resetMdpModal()}}
    style={{position:'fixed',inset:0,zIndex:10000,background:'rgba(0,0,0,0.75)',
      backdropFilter:'blur(8px)',display:'flex',alignItems:'center',justifyContent:'center'}}>
    <div style={{background:'#0F172A',border:'1px solid rgba(255,255,255,0.1)',
      borderRadius:20,width:'100%',maxWidth:460,padding:'2rem',
      boxShadow:'0 25px 80px rgba(0,0,0,0.6)',fontFamily:"'Bricolage Grotesque',sans-serif"}}>

      {/* Indicateur d'étape */}
      <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'1.5rem'}}>
        <h3 style={{margin:0,fontSize:'1.1rem',fontWeight:800,color:'white'}}>🔑 Changer mon mot de passe</h3>
        {!mdpSucces && <span style={{marginLeft:'auto',fontSize:'0.75rem',color:'#64748B',fontWeight:600}}>Étape {mdpStep}/2</span>}
      </div>

      {/* SUCCÈS — déconnexion imminente */}
      {mdpSucces && (
        <div style={{textAlign:'center',padding:'1.5rem 0'}}>
          <div style={{fontSize:'3rem',marginBottom:'1rem'}}>✅</div>
          <p style={{margin:'0 0 0.75rem',color:'white',fontWeight:700,fontSize:'1rem'}}>
            Votre nouveau mot de passe a été enregistré.
          </p>
          <p style={{margin:'0 0 1.5rem',color:'#94A3B8',fontSize:'0.9rem',lineHeight:1.6}}>
            Vous allez être déconnecté afin de vous reconnecter avec vos nouveaux identifiants.
          </p>
          <div style={{height:4,borderRadius:4,background:'rgba(255,255,255,0.08)',overflow:'hidden'}}>
            <div style={{height:'100%',width:'100%',background:'linear-gradient(90deg,#00D4AA,#10B981)',
              animation:'mdpCountdown 4s linear forwards'}} />
          </div>
          <style>{`@keyframes mdpCountdown { from { width:100% } to { width:0% } }`}</style>
          <p style={{margin:'0.5rem 0 0',color:'#64748B',fontSize:'0.78rem'}}>Déconnexion dans 4 secondes…</p>
        </div>
      )}

      {/* ÉTAPE 1 — vérification mot de passe actuel */}
      {!mdpSucces && mdpStep === 1 && (
        <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
          <p style={{margin:0,color:'#94A3B8',fontSize:'0.85rem'}}>Confirmez votre identité avant de modifier le mot de passe.</p>
          <div>
            <label style={{display:'block',color:'#94A3B8',fontSize:'0.75rem',marginBottom:'0.4rem',textTransform:'uppercase',letterSpacing:'0.05em'}}>Mot de passe actuel</label>
            <input type="password" value={mdpForm.actuel} placeholder="Votre mot de passe actuel"
              autoFocus
              onKeyDown={e=>e.key==='Enter'&&verifierMdpActuel()}
              onChange={e=>{setMdpForm(p=>({...p,actuel:e.target.value}));setMdpError('')}}
              style={{width:'100%',padding:'0.8rem 1rem',borderRadius:10,
                border:`1.5px solid ${mdpError?'rgba(239,68,68,0.5)':'rgba(255,255,255,0.1)'}`,
                background:'rgba(255,255,255,0.05)',color:'white',fontSize:'0.9rem',outline:'none',boxSizing:'border-box'}} />
          </div>
          {mdpError && <p style={{margin:0,color:'#FCA5A5',fontSize:'0.82rem'}}>⚠️ {mdpError}</p>}
          <div style={{display:'flex',gap:'0.75rem',justifyContent:'flex-end',marginTop:'0.5rem'}}>
            <button onClick={resetMdpModal}
              style={{background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',
                color:'#94A3B8',borderRadius:10,padding:'0.65rem 1.25rem',cursor:'pointer',fontSize:'0.85rem',fontWeight:600}}>
              Annuler
            </button>
            <button onClick={verifierMdpActuel} disabled={mdpLoading}
              style={{background:mdpLoading?'rgba(0,212,170,0.2)':'linear-gradient(135deg,#00D4AA,#00B894)',
                border:'none',borderRadius:10,padding:'0.65rem 1.5rem',
                color:'white',cursor:mdpLoading?'not-allowed':'pointer',fontSize:'0.85rem',fontWeight:700}}>
              {mdpLoading ? '⏳ Vérification…' : 'Continuer →'}
            </button>
          </div>
        </div>
      )}

      {/* ÉTAPE 2 — nouveau mot de passe */}
      {!mdpSucces && mdpStep === 2 && (() => {
        const force = mdpForce(mdpForm.nouveau)
        const errFormat = mdpForm.nouveau ? mdpFormatD(mdpForm.nouveau) : null
        const matchOk = mdpForm.nouveau && mdpForm.confirm && mdpForm.nouveau === mdpForm.confirm
        return (
          <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
            {/* Règles Format D */}
            <div style={{background:'rgba(255,107,53,0.06)',border:'1px solid rgba(255,107,53,0.2)',
              borderRadius:10,padding:'0.75rem 1rem',fontSize:'0.78rem',color:'#94A3B8',lineHeight:1.6}}>
              <strong style={{color:'#FF6B35'}}>Format D requis :</strong> ≥12 car. · 2 majuscules · 2 chiffres · 1 spécial (@#$!%*?&) · pas de 3 identiques consécutifs
            </div>

            {/* Générateur */}
            <button onClick={()=>{const g=genererMotDePasse();setMdpForm(p=>({...p,nouveau:g,confirm:g}));setMdpError('')}}
              style={{background:'rgba(124,58,237,0.15)',border:'1px solid rgba(124,58,237,0.35)',
                color:'#C4B5FD',borderRadius:10,padding:'0.55rem 1rem',cursor:'pointer',
                fontSize:'0.82rem',fontWeight:700,textAlign:'left'}}>
              ✨ Générer un mot de passe sécurisé
            </button>

            {/* Nouveau mot de passe */}
            <div>
              <label style={{display:'block',color:'#94A3B8',fontSize:'0.75rem',marginBottom:'0.4rem',textTransform:'uppercase',letterSpacing:'0.05em'}}>Nouveau mot de passe</label>
              <div style={{position:'relative'}}>
                <input type={showMdpNouv?'text':'password'} value={mdpForm.nouveau}
                  placeholder="Nouveau mot de passe"
                  onChange={e=>{setMdpForm(p=>({...p,nouveau:e.target.value}));setMdpError('')}}
                  style={{width:'100%',padding:'0.8rem 2.5rem 0.8rem 1rem',borderRadius:10,
                    border:`1.5px solid ${errFormat?'rgba(239,68,68,0.5)':mdpForm.nouveau&&!errFormat?'rgba(16,185,129,0.5)':'rgba(255,255,255,0.1)'}`,
                    background:'rgba(255,255,255,0.05)',color:'white',fontSize:'0.9rem',outline:'none',boxSizing:'border-box'}} />
                <span onClick={()=>setShowMdpNouv(v=>!v)}
                  style={{position:'absolute',right:'0.75rem',top:'50%',transform:'translateY(-50%)',
                    cursor:'pointer',fontSize:'0.9rem',color:'#64748B'}}>
                  {showMdpNouv?'🙈':'👁️'}
                </span>
              </div>
              {/* Barre de force */}
              {mdpForm.nouveau && (
                <div style={{marginTop:'0.4rem'}}>
                  <div style={{height:4,borderRadius:4,background:'rgba(255,255,255,0.06)',overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${force.pct}%`,background:force.color,transition:'width 0.3s,background 0.3s'}} />
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',marginTop:'0.25rem'}}>
                    <span style={{fontSize:'0.72rem',color:force.color,fontWeight:700}}>{force.label}</span>
                    {errFormat && <span style={{fontSize:'0.72rem',color:'#FCA5A5'}}>⚠️ {errFormat}</span>}
                  </div>
                </div>
              )}
            </div>

            {/* Confirmer */}
            <div>
              <label style={{display:'block',color:'#94A3B8',fontSize:'0.75rem',marginBottom:'0.4rem',textTransform:'uppercase',letterSpacing:'0.05em'}}>Confirmer le mot de passe</label>
              <div style={{position:'relative'}}>
                <input type={showMdpConf?'text':'password'} value={mdpForm.confirm}
                  placeholder="Répéter le nouveau mot de passe"
                  onChange={e=>{setMdpForm(p=>({...p,confirm:e.target.value}));setMdpError('')}}
                  style={{width:'100%',padding:'0.8rem 2.5rem 0.8rem 1rem',borderRadius:10,
                    border:`1.5px solid ${mdpForm.confirm&&!matchOk?'rgba(239,68,68,0.5)':matchOk?'rgba(16,185,129,0.5)':'rgba(255,255,255,0.1)'}`,
                    background:'rgba(255,255,255,0.05)',color:'white',fontSize:'0.9rem',outline:'none',boxSizing:'border-box'}} />
                <span onClick={()=>setShowMdpConf(v=>!v)}
                  style={{position:'absolute',right:'0.75rem',top:'50%',transform:'translateY(-50%)',
                    cursor:'pointer',fontSize:'0.9rem',color:'#64748B'}}>
                  {showMdpConf?'🙈':'👁️'}
                </span>
              </div>
              {mdpForm.confirm && !matchOk && <p style={{margin:'0.25rem 0 0',color:'#FCA5A5',fontSize:'0.72rem'}}>⚠️ Les mots de passe ne correspondent pas</p>}
              {matchOk && <p style={{margin:'0.25rem 0 0',color:'#10B981',fontSize:'0.72rem'}}>✓ Les mots de passe correspondent</p>}
            </div>

            {mdpError && <p style={{margin:0,color:'#FCA5A5',fontSize:'0.82rem'}}>⚠️ {mdpError}</p>}

            <div style={{display:'flex',gap:'0.75rem',justifyContent:'flex-end',marginTop:'0.5rem'}}>
              <button onClick={()=>{setMdpStep(1);setMdpError('');setMdpForm(p=>({...p,nouveau:'',confirm:''}))}}
                style={{background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',
                  color:'#94A3B8',borderRadius:10,padding:'0.65rem 1.25rem',cursor:'pointer',fontSize:'0.85rem',fontWeight:600}}>
                ← Retour
              </button>
              <button onClick={changerMotDePasse} disabled={mdpLoading||!!errFormat||!matchOk}
                style={{background:mdpLoading||errFormat||!matchOk?'rgba(255,107,53,0.2)':'linear-gradient(135deg,#FF6B35,#E55A2B)',
                  border:'none',borderRadius:10,padding:'0.65rem 1.5rem',
                  color:mdpLoading||errFormat||!matchOk?'#64748B':'white',
                  cursor:mdpLoading||errFormat||!matchOk?'not-allowed':'pointer',fontSize:'0.85rem',fontWeight:700}}>
                {mdpLoading ? '⏳ En cours…' : '💾 Enregistrer'}
              </button>
            </div>
          </div>
        )
      })()}
    </div>
  </div>
)}

{/* ══ MODAL IMPORT MIGRATION ════════════════════════════════════════════════ */}
{showImportMigration && (
  <div onClick={e=>{if(e.target===e.currentTarget)setShowImportMigration(false)}}
    style={{position:'fixed',inset:0,zIndex:10000,background:'rgba(0,0,0,0.75)',
      backdropFilter:'blur(8px)',display:'flex',alignItems:'flex-start',
      justifyContent:'center',padding:'2rem 1rem',overflowY:'auto'}}>
    <div style={{background:'#0F172A',border:'1px solid rgba(255,255,255,0.1)',
      borderRadius:20,width:'100%',maxWidth:860,
      boxShadow:'0 25px 80px rgba(0,0,0,0.6)',
      fontFamily:"'Bricolage Grotesque',sans-serif"}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'1.25rem 1.75rem',borderBottom:'1px solid rgba(255,255,255,0.08)'}}>
        <span style={{color:'white',fontWeight:800,fontSize:'1.1rem'}}>
          📥 Import des données — migration v67 → v68
        </span>
        <button onClick={()=>setShowImportMigration(false)}
          style={{width:36,height:36,borderRadius:9,
            border:'1.5px solid rgba(255,255,255,0.12)',
            background:'rgba(255,255,255,0.05)',color:'#94A3B8',
            fontSize:'1.1rem',cursor:'pointer',display:'flex',
            alignItems:'center',justifyContent:'center'}}>✕</button>
      </div>
      <ImportMigration />
    </div>
  </div>
)}

{/* ══ MODAL GESTION UTILISATEURS ════════════════════════════════════════════ */}
{showGestionUsers && (
  <div onClick={e=>{if(e.target===e.currentTarget)setShowGestionUsers(false)}}
    style={{position:'fixed',inset:0,zIndex:10000,background:'rgba(0,0,0,0.75)',
      backdropFilter:'blur(8px)',display:'flex',alignItems:'flex-start',
      justifyContent:'center',padding:'1rem',overflowY:'auto'}}>
    <div style={{background:'#0F172A',border:'1px solid rgba(255,255,255,0.1)',
      borderRadius:20,width:'100%',maxWidth:1400,minHeight:'90vh',
      boxShadow:'0 25px 80px rgba(0,0,0,0.6)',
      fontFamily:"'Bricolage Grotesque',sans-serif"}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'1.25rem 1.75rem',borderBottom:'1px solid rgba(255,255,255,0.08)'}}>
        <span style={{color:'white',fontWeight:800,fontSize:'1.1rem'}}>
          👥 Gestion des utilisateurs
        </span>
        <button onClick={()=>setShowGestionUsers(false)}
          style={{width:36,height:36,borderRadius:9,
            border:'1.5px solid rgba(255,255,255,0.12)',
            background:'rgba(255,255,255,0.05)',color:'#94A3B8',
            fontSize:'1.1rem',cursor:'pointer',display:'flex',
            alignItems:'center',justifyContent:'center'}}>✕</button>
      </div>
      <GestionUtilisateurs />
    </div>
  </div>
)}

{/* Header */}
<div className="header">
<div className="header-content">
<div className="logo">
<div className="logo-icon">S</div>
<div className="logo-text">
<h1>SOLSTICE</h1>
<p>Gestion Devis & Factures</p>
</div>
</div>

<div className="header-actions">
{can('settings','write') && (
<button className="btn btn-secondary" onClick={ouvrirParametres}>
⚙️ Paramètres
</button>
)}
<button className="btn btn-secondary" onClick={ouvrirClients}>
👥 Clients
</button>
<button className="btn btn-secondary" onClick={ouvrirFournisseurs}
style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #0A2540 100%)', color: 'white' }}>
🏭 Fournisseurs
</button>
<button className="btn btn-secondary" onClick={() => setShowCatalogue(true)}
style={{ background: 'linear-gradient(135deg, #0A2540 0%, #00D4AA 100%)', color: 'white' }}>
📚 Catalogue {catalogueLoaded && catalogue.length > 0 ? `(${catalogue.length.toLocaleString()})` : !catalogueLoaded ? '⏳' : ''}
</button>

{/* Menu Sauvegarde avec dropdown — super_admin uniquement */}
{can('import','write') && (
<div className="dropdown">
<button
className="btn btn-secondary"
onClick={() => setShowBackupMenu(!showBackupMenu)}
title="Gérer les sauvegardes"
>
💾 Sauvegarde ▾
</button>

<div className={`dropdown-menu ${showBackupMenu ? 'show' : ''}`}>
<button
className="dropdown-item"
onClick={() => {
exportToJSON();
setShowBackupMenu(false);
}}
>
<span className="dropdown-item-icon">📤</span>
<span>Exporter JSON</span>
</button>

<label className="dropdown-item" style={{ cursor: 'pointer' }}>
<span className="dropdown-item-icon">📥</span>
<span>Importer JSON</span>
<input
type="file"
accept=".json"
onChange={(e) => {
importFromJSON(e);
setShowBackupMenu(false);
}}
style={{ display: 'none' }}
/>
</label>

<div className="dropdown-divider"></div>

<button
className="dropdown-item danger"
onClick={() => {
resetAllData();
setShowBackupMenu(false);
}}
>
<span className="dropdown-item-icon">🗑️</span>
<span>Réinitialiser</span>
</button>
</div>
</div>
)}

<button className="btn btn-primary" onClick={() => nouveauDocument('devis')}>
➕ Nouveau Devis
</button>
<button className="btn btn-primary" onClick={() => nouveauDocument('facture')}>
➕ Nouvelle Facture
</button>
<button className="btn btn-primary" onClick={nouveauBC}
style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #0A2540 100%)', border: '2px solid #00D4AA' }}>
📦 Nouveau BC
</button>
<button
className="btn btn-success"
onClick={() => {
console.log('BOUTON EXPORT CLIQUÉ');
console.log('filteredDocuments:', filteredDocuments);
exporterExcel(filteredDocuments);
}}
title="Exporter la liste en Excel (CSV)"
style={{ background: '#10B981' }}
>
📊 Export Excel
</button>
<button
className="btn btn-success"
onClick={() => setShowExportComptable(true)}
title="Exporter les journaux comptables (FEC/OHADA) compatibles Sage / SAARI"
style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' }}
>
📒 Export Comptable
</button>
<button className="btn btn-secondary" onClick={() => setShowStatistiques(true)}
style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
📊 Statistiques
</button>
</div>
</div>
</div>

{/* Statistiques */}
<div className="cards-grid">
<div className="stat-card">
<div className="stat-card-header">
<span className="stat-card-title">Devis</span>
<span className="stat-card-icon">📋</span>
</div>
<div className="stat-card-value">{stats.totalDevis}</div>
<div className="stat-card-label">Documents créés</div>
</div>

<div className="stat-card">
<div className="stat-card-header">
<span className="stat-card-title">Factures</span>
<span className="stat-card-icon">🧾</span>
</div>
<div className="stat-card-value">{stats.totalFactures}</div>
<div className="stat-card-label">Documents créés</div>
</div>

<div className="stat-card">
<div className="stat-card-header">
<span className="stat-card-title">Montant Total</span>
<span className="stat-card-icon">💰</span>
</div>
<div className="stat-card-value">{formatMontant(stats.montantTotal)}</div>
<div className="stat-card-label">Chiffre d'affaires</div>
</div>

<div className="stat-card">
<div className="stat-card-header">
<span className="stat-card-title">Devis Acceptés</span>
<span className="stat-card-icon">✅</span>
</div>
<div className="stat-card-value">{stats.devisAcceptes}</div>
<div className="stat-card-label">
{stats.devisConverts > 0 && (
<span style={{ fontSize: '0.75rem', color: '#10B981' }}>
{stats.devisConverts} convertis 🔗
</span>
)}
{stats.devisConverts === 0 && 'À convertir en facture'}
</div>
</div>
</div>

{/* Liste des documents */}
<div className={`documents-section${docsPleinEcran ? ' plein-ecran' : ''}`}>
{/* ─── EN-TÊTE (titre + bouton plein écran) — toujours visible ─── */}
<div className={docsPleinEcran ? 'docs-plein-ecran-header' : ''} style={docsPleinEcran ? {} : {}}>
<div className="documents-header">
<div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', flex: 1 }}>
<h2 className="documents-title">📄 Mes Documents</h2>
{/* Bouton plein écran */}
<button
className={`btn-plein-ecran${docsPleinEcran ? ' actif' : ''}`}
onClick={() => setDocsPleinEcran(v => !v)}
title={docsPleinEcran ? 'Quitter le plein écran' : 'Afficher en plein écran'}
>
{docsPleinEcran
? <><span style={{fontSize:'1rem'}}>⊡</span> Quitter le plein écran</>
: <><span style={{fontSize:'1rem'}}>⛶</span> Plein écran</>
}
</button>
{/* Bouton configurer les actions */}
<button
onClick={() => setShowConfigActions(true)}
className="btn-plein-ecran"
title="Personnaliser les boutons d'action visibles"
style={{ gap: '0.4rem' }}
>
<span style={{fontSize:'1rem'}}>⚙️</span> Actions
</button>
</div>

{/* Onglets de filtrage */}
<div style={{ width: '100%' }}>
{/* Tabs Navigation */}
<div style={{
display: 'flex',
gap: '0.25rem',
marginBottom: '1rem',
borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
}}>
<button
onClick={() => setActiveFilterTab('basic')}
style={{
padding: '0.75rem 1.5rem',
background: activeFilterTab === 'basic'
? 'rgba(255, 255, 255, 0.1)'
: 'transparent',
border: 'none',
borderBottom: activeFilterTab === 'basic'
? '3px solid #00D4AA'
: '3px solid transparent',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.875rem',
color: activeFilterTab === 'basic' ? 'white' : 'rgba(255, 255, 255, 0.6)',
transition: 'all 0.3s ease',
fontFamily: 'inherit',
borderRadius: '8px 8px 0 0'
}}
>
🔍 Filtres de base
</button>
<button
onClick={() => setActiveFilterTab('advanced')}
style={{
padding: '0.75rem 1.5rem',
background: activeFilterTab === 'advanced'
? 'rgba(255, 255, 255, 0.1)'
: 'transparent',
border: 'none',
borderBottom: activeFilterTab === 'advanced'
? '3px solid #00D4AA'
: '3px solid transparent',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.875rem',
color: activeFilterTab === 'advanced' ? 'white' : 'rgba(255, 255, 255, 0.6)',
transition: 'all 0.3s ease',
fontFamily: 'inherit',
borderRadius: '8px 8px 0 0'
}}
>
⚙️ Filtres avancés
</button>
<button
onClick={() => setActiveFilterTab('reporting')}
style={{
padding: '0.75rem 1.5rem',
background: activeFilterTab === 'reporting'
? 'rgba(255, 255, 255, 0.1)'
: 'transparent',
border: 'none',
borderBottom: activeFilterTab === 'reporting'
? '3px solid #00D4AA'
: '3px solid transparent',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.875rem',
color: activeFilterTab === 'reporting' ? 'white' : 'rgba(255, 255, 255, 0.6)',
transition: 'all 0.3s ease',
fontFamily: 'inherit',
borderRadius: '8px 8px 0 0'
}}
>
📊 Reporting
</button>
</div>

{/* Tab Content - Filtres de base */}
{activeFilterTab === 'basic' && (
<div style={{
display: 'flex',
gap: '0.75rem',
flexWrap: 'wrap',
padding: '1rem',
background: 'rgba(255, 255, 255, 0.05)',
backdropFilter: 'blur(20px)',
border: '1px solid rgba(255, 255, 255, 0.1)',
borderRadius: '0 12px 12px 12px'
}}>
<input
type="text"
className="search-input"
placeholder="🔍 Rechercher par N° ou client..."
value={searchTerm}
onChange={(e) => setSearchTerm(e.target.value)}
style={{ flex: '2', minWidth: '200px' }}
/>
<select
className="filter-select"
value={filterType}
onChange={(e) => setFilterType(e.target.value)}
style={{ flex: '1', minWidth: '150px' }}
>
<option value="all">📋 Tous les types</option>
<option value="devis">📋 Devis</option>
<option value="facture">🧾 Factures</option>
<option value="avoir">🔵 Avoirs</option>
</select>
<select
className="filter-select"
value={filterStatus}
onChange={(e) => setFilterStatus(e.target.value)}
style={{ flex: '1', minWidth: '150px' }}
>
<option value="all">⏱️ Tous les statuts</option>
<option value="en-attente">⏳ En attente</option>
<option value="accepte">✅ Acceptée</option>
<option value="refuse">❌ Refusée</option>
<option value="paye">💰 Payée</option>
<option value="partiel">💳 Partiellement payée</option>
<option value="impaye">⚠️ Impayée</option>
<option value="avoir">🔵 Avoir (note de crédit)</option>
</select>
<button
onClick={resetFilters}
style={{
padding: '0.75rem 1.5rem',
background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
color: 'white',
border: 'none',
borderRadius: '10px',
cursor: 'pointer',
fontWeight: '600',
fontSize: '0.875rem',
transition: 'all 0.3s ease',
whiteSpace: 'nowrap',
boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
}}
onMouseEnter={(e) => {
e.target.style.transform = 'translateY(-2px)';
e.target.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
}}
onMouseLeave={(e) => {
e.target.style.transform = 'translateY(0)';
e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
}}
>
🔄 Reset
</button>
</div>
)}

{/* Tab Content - Filtres avancés */}
{activeFilterTab === 'advanced' && (
<div style={{
display: 'flex',
gap: '0.75rem',
flexWrap: 'wrap',
padding: '1rem',
background: 'rgba(255, 255, 255, 0.05)',
backdropFilter: 'blur(20px)',
border: '1px solid rgba(255, 255, 255, 0.1)',
borderRadius: '0 12px 12px 12px'
}}>
{/* Client */}
<div style={{ flex: '1', minWidth: '150px', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
<label style={{
fontSize: '0.7rem',
fontWeight: '600',
color: 'rgba(255, 255, 255, 0.6)',
textTransform: 'uppercase',
letterSpacing: '0.5px'
}}>
👤 Client
</label>
<input
type="text"
className="search-input"
placeholder="Nom du client..."
value={filterClient}
onChange={(e) => setFilterClient(e.target.value)}
/>
</div>

{/* Date début */}
<div style={{ flex: '1', minWidth: '150px', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
<label style={{
fontSize: '0.7rem',
fontWeight: '600',
color: 'rgba(255, 255, 255, 0.6)',
textTransform: 'uppercase',
letterSpacing: '0.5px'
}}>
📅 Date début
</label>
<input
type="date"
className="search-input"
value={dateDebut}
onChange={(e) => setDateDebut(e.target.value)}
/>
</div>

{/* Date fin */}
<div style={{ flex: '1', minWidth: '150px', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
<label style={{
fontSize: '0.7rem',
fontWeight: '600',
color: 'rgba(255, 255, 255, 0.6)',
textTransform: 'uppercase',
letterSpacing: '0.5px'
}}>
📅 Date fin
</label>
<input
type="date"
className="search-input"
value={dateFin}
onChange={(e) => setDateFin(e.target.value)}
/>
</div>

{/* Montant min */}
<div style={{ flex: '1', minWidth: '150px', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
<label style={{
fontSize: '0.7rem',
fontWeight: '600',
color: 'rgba(255, 255, 255, 0.6)',
textTransform: 'uppercase',
letterSpacing: '0.5px'
}}>
💰 Montant min
</label>
<input
type="number"
className="search-input"
placeholder="0 FCFA"
value={filterMontantMin}
onChange={(e) => setFilterMontantMin(e.target.value)}
/>
</div>

{/* Montant max */}
<div style={{ flex: '1', minWidth: '150px', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
<label style={{
fontSize: '0.7rem',
fontWeight: '600',
color: 'rgba(255, 255, 255, 0.6)',
textTransform: 'uppercase',
letterSpacing: '0.5px'
}}>
💰 Montant max
</label>
<input
type="number"
className="search-input"
placeholder="∞"
value={filterMontantMax}
onChange={(e) => setFilterMontantMax(e.target.value)}
/>
</div>

{/* Bouton Reset */}
<div style={{ flex: '0', minWidth: 'auto', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
<label style={{
fontSize: '0.7rem',
fontWeight: '600',
color: 'transparent',
textTransform: 'uppercase',
letterSpacing: '0.5px'
}}>
&nbsp;
</label>
<button
onClick={resetFilters}
style={{
padding: '0.75rem 1.5rem',
background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
color: 'white',
border: 'none',
borderRadius: '10px',
cursor: 'pointer',
fontWeight: '600',
fontSize: '0.875rem',
transition: 'all 0.3s ease',
whiteSpace: 'nowrap',
boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
height: '100%'
}}
onMouseEnter={(e) => {
e.target.style.transform = 'translateY(-2px)';
e.target.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
}}
onMouseLeave={(e) => {
e.target.style.transform = 'translateY(0)';
e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
}}
>
🔄 Réinitialiser
</button>
</div>
</div>
)}

{/* Tab Content - Reporting */}
{activeFilterTab === 'reporting' && (
<div style={{
padding: '1.5rem',
background: 'rgba(255, 255, 255, 0.05)',
backdropFilter: 'blur(20px)',
border: '1px solid rgba(255, 255, 255, 0.1)',
borderRadius: '0 12px 12px 12px'
}}>
{/* En-tête Reporting */}
<div style={{ marginBottom: '1.5rem' }}>
<h3 style={{
fontSize: '1.2rem',
fontWeight: 700,
color: 'white',
marginBottom: '0.5rem',
display: 'flex',
alignItems: 'center',
gap: '0.5rem'
}}>
📊 Génération de Bordereau de Transmission
</h3>
<p style={{
fontSize: '0.875rem',
color: 'rgba(255, 255, 255, 0.7)',
lineHeight: '1.6'
}}>
Sélectionnez les documents à inclure dans le bordereau, puis générez le document d'accompagnement.
</p>
</div>

{/* Message si aucun document */}
{filteredDocuments.length === 0 && (
<div style={{
textAlign: 'center',
padding: '3rem 2rem',
background: 'rgba(239, 68, 68, 0.1)',
border: '2px dashed rgba(239, 68, 68, 0.3)',
borderRadius: '12px',
color: 'rgba(255, 255, 255, 0.8)'
}}>
<div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
<div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
Aucun document trouvé
</div>
<div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
Utilisez les filtres ci-dessus pour afficher les documents
</div>
</div>
)}

{/* Liste des documents avec checkboxes */}
{filteredDocuments.length > 0 && (
<>
{/* Barre d'actions */}
<div style={{
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
marginBottom: '1rem',
padding: '1rem',
background: 'rgba(255, 255, 255, 0.05)',
borderRadius: '8px',
flexWrap: 'wrap',
gap: '1rem'
}}>
<div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
<label style={{
display: 'flex',
alignItems: 'center',
gap: '0.5rem',
color: 'white',
fontSize: '0.9rem',
fontWeight: 600,
cursor: 'pointer'
}}>
<input
type="checkbox"
checked={selectedDocs.length === filteredDocuments.length && filteredDocuments.length > 0}
onChange={(e) => {
if (e.target.checked) {
setSelectedDocs(filteredDocuments.map(d => d.id));
} else {
setSelectedDocs([]);
}
}}
style={{ width: '18px', height: '18px', cursor: 'pointer' }}
/>
Tout sélectionner
</label>
<div style={{
color: 'rgba(255, 255, 255, 0.7)',
fontSize: '0.85rem'
}}>
{selectedDocs.length} / {filteredDocuments.length} sélectionné{selectedDocs.length > 1 ? 's' : ''}
</div>
</div>

{selectedDocs.length > 0 && (
<div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
<button
onClick={() => genererBordereau(filteredDocuments.filter(d => selectedDocs.includes(d.id)))}
style={{
padding: '0.75rem 1.5rem',
background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
color: 'white',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.9rem',
transition: 'all 0.3s ease',
boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
display: 'flex',
alignItems: 'center',
gap: '0.5rem'
}}
onMouseEnter={(e) => {
e.target.style.transform = 'translateY(-2px)';
e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
}}
onMouseLeave={(e) => {
e.target.style.transform = 'translateY(0)';
e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
}}
>
📄 Générer Bordereau Transmission ({selectedDocs.length})
</button>

<button
onClick={() => genererRecapitulatif(filteredDocuments.filter(d => selectedDocs.includes(d.id)))}
style={{
padding: '0.75rem 1.5rem',
background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
color: 'white',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.9rem',
transition: 'all 0.3s ease',
boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
display: 'flex',
alignItems: 'center',
gap: '0.5rem'
}}
onMouseEnter={(e) => {
e.target.style.transform = 'translateY(-2px)';
e.target.style.boxShadow = '0 6px 16px rgba(124, 58, 237, 0.4)';
}}
onMouseLeave={(e) => {
e.target.style.transform = 'translateY(0)';
e.target.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.3)';
}}
>
📊 État Récapitulatif ({selectedDocs.length})
</button>

<button
onClick={genererBordereauxLivraison}
style={{
padding: '0.75rem 1.5rem',
background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
color: 'white',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.9rem',
transition: 'all 0.3s ease',
boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
display: 'flex',
alignItems: 'center',
gap: '0.5rem'
}}
onMouseEnter={(e) => {
e.target.style.transform = 'translateY(-2px)';
e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
}}
onMouseLeave={(e) => {
e.target.style.transform = 'translateY(0)';
e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
}}
>
🚚 Générer Bordereau de Livraison BL ({selectedDocs.length})
</button>

{filteredDocuments.filter(d => selectedDocs.includes(d.id) && d.type === 'facture').length > 0 && (
<button
onClick={genererFactureExpress}
style={{
padding: '0.75rem 1.5rem',
background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
color: 'white',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.9rem',
transition: 'all 0.3s ease',
boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
display: 'flex',
alignItems: 'center',
gap: '0.5rem'
}}
onMouseEnter={(e) => {
e.target.style.transform = 'translateY(-2px)';
e.target.style.boxShadow = '0 6px 16px rgba(124, 58, 237, 0.4)';
}}
onMouseLeave={(e) => {
e.target.style.transform = 'translateY(0)';
e.target.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.3)';
}}
>
⚡ Facture Express ({filteredDocuments.filter(d => selectedDocs.includes(d.id) && d.type === 'facture').length})
</button>
)}
</div>
)}
</div>

{/* Résumé de la sélection */}
{selectedDocs.length > 0 && (
<div style={{
marginTop: '1.5rem',
marginBottom: '1.5rem',
padding: '1.25rem',
background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
border: '2px solid rgba(16, 185, 129, 0.3)',
borderRadius: '12px'
}}>
<div style={{
fontSize: '0.95rem',
fontWeight: 600,
color: 'white',
marginBottom: '0.75rem'
}}>
📋 Résumé de la sélection :
</div>
<div style={{
display: 'grid',
gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
gap: '0.75rem',
fontSize: '0.85rem',
color: 'rgba(255, 255, 255, 0.9)'
}}>
<div>
<strong>Documents sélectionnés :</strong> {selectedDocs.length}
</div>
<div>
<strong>Type :</strong> {
filteredDocuments.filter(d => selectedDocs.includes(d.id))[0]?.type === 'devis' ? 'DEVIS' : 'FACTURES'
}
</div>
<div>
<strong>Factures sélectionnées :</strong> {
filteredDocuments.filter(d => selectedDocs.includes(d.id) && d.type === 'facture').length
}
</div>
<div>
<strong>Montant total :</strong> {formatMontant(
filteredDocuments
.filter(d => selectedDocs.includes(d.id))
.reduce((sum, doc) => sum + (doc.total || 0), 0)
)}
</div>
</div>

{/* Avertissement si des devis sont sélectionnés pour BL */}
{filteredDocuments.filter(d => selectedDocs.includes(d.id) && d.type === 'devis').length > 0 && (
<div style={{
marginTop: '1rem',
padding: '0.75rem',
background: 'rgba(245, 158, 11, 0.2)',
border: '1px solid rgba(245, 158, 11, 0.5)',
borderRadius: '6px',
fontSize: '0.8rem',
color: '#FBBF24'
}}>
⚠️ <strong>Note :</strong> Le Bordereau de Livraison (BL) ne peut être généré que pour les factures. Les devis sélectionnés seront ignorés pour la génération des BL.
</div>
)}
</div>
)}

{/* Tableau des documents — Style Midnight Épuré */}
<div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)', background: '#0F172A' }}>
<table style={{ width: '100%', borderCollapse: 'collapse' }}>
<thead>
<tr style={{ background: 'rgba(10,37,64,0.8)' }}>
<th style={{ width: '50px', textAlign: 'center', padding: '0.85rem 0.5rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
<input type="checkbox" checked={selectedDocs.length === filteredDocuments.length && filteredDocuments.length > 0} onChange={(e) => { if (e.target.checked) { setSelectedDocs(filteredDocuments.map(d => d.id)); } else { setSelectedDocs([]); } }} style={{ width: '16px', height: '16px', accentColor: '#00D4AA' }} />
</th>
{['Type','Numéro','Client','Date','Statut','Montant'].map(h => (
<th key={h} style={{ padding: '0.85rem 0.75rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid rgba(255,255,255,0.08)', textAlign: h === 'Montant' ? 'right' : 'left' }}>{h}</th>
))}
</tr>
</thead>
<tbody>
{filteredDocuments.map((doc) => {
const isSelected = selectedDocs.includes(doc.id)
return (
<tr key={doc.id} style={{ background: isSelected ? 'rgba(0,212,170,0.08)' : 'transparent', borderLeft: isSelected ? '3px solid #00D4AA' : '3px solid transparent', borderBottom: '1px solid rgba(255,255,255,0.05)', transition: 'all 0.15s', cursor: 'pointer' }}
onClick={() => setSelectedDocs(isSelected ? selectedDocs.filter(id => id !== doc.id) : [...selectedDocs, doc.id])}>
<td style={{ textAlign: 'center', padding: '0.75rem 0.5rem' }}>
<input type="checkbox" checked={isSelected} onChange={() => {}} style={{ width: '16px', height: '16px', accentColor: '#00D4AA' }} />
</td>
<td style={{ padding: '0.75rem' }}>
<span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 600, background: doc.type === 'devis' ? 'rgba(59,130,246,0.15)' : 'rgba(16,185,129,0.15)', color: doc.type === 'devis' ? '#60A5FA' : '#34D399' }}>{doc.type === 'devis' ? '📋 Devis' : '🧾 Facture'}</span>
</td>
<td style={{ padding: '0.75rem', fontWeight: 700, color: 'white', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.85rem' }}>{doc.numero}</td>
<td style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem' }}>{doc.client?.nom||'—'}</td>
<td style={{ padding: '0.75rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>{new Date(doc.date).toLocaleDateString('fr-FR')}</td>
<td style={{ padding: '0.75rem' }}>
<span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 600, background: doc.statut === 'paye' ? 'rgba(16,185,129,0.15)' : doc.statut === 'impaye' ? 'rgba(239,68,68,0.15)' : doc.statut === 'partiel' ? 'rgba(251,191,36,0.15)' : 'rgba(148,163,184,0.1)', color: doc.statut === 'paye' ? '#34D399' : doc.statut === 'impaye' ? '#F87171' : doc.statut === 'partiel' ? '#FCD34D' : '#94A3B8' }}>
{doc.statut === 'en-attente' ? '⏳ En attente' : doc.statut === 'paye' ? '✅ Payée' : doc.statut === 'partiel' ? '⚠️ Partielle' : doc.statut === 'impaye' ? '❌ Impayée' : doc.statut === 'accepte' ? '✅ Acceptée' : doc.statut === 'refuse' ? '❌ Refusée' : '❌ Annulée'}
</span>
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', color: '#00D4AA', fontSize: '0.9rem' }}>{formatMontant(doc.total)}</td>
</tr>
)
})}
</tbody>
</table>
</div>

{/* Résumé de la sélection */}
{selectedDocs.length > 0 && (
<div style={{
marginTop: '1.5rem',
padding: '1.25rem',
background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
border: '2px solid rgba(16, 185, 129, 0.3)',
borderRadius: '12px'
}}>
<div style={{
fontSize: '0.95rem',
fontWeight: 600,
color: 'white',
marginBottom: '0.75rem'
}}>
📋 Résumé de la sélection pour le bordereau :
</div>
<div style={{
display: 'grid',
gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
gap: '0.75rem',
fontSize: '0.85rem',
color: 'rgba(255, 255, 255, 0.9)'
}}>
<div>
<strong>Documents sélectionnés :</strong> {selectedDocs.length}
</div>
<div>
<strong>Type :</strong> {
filteredDocuments.filter(d => selectedDocs.includes(d.id))[0]?.type === 'devis' ? 'DEVIS' : 'FACTURES'
}
</div>
{filterClient && (
<div>
<strong>Client :</strong> {filterClient}
</div>
)}
{!filterClient && filteredDocuments.filter(d => selectedDocs.includes(d.id)).length > 0 && (
<div>
<strong>Client :</strong> {filteredDocuments.filter(d => selectedDocs.includes(d.id))[0]?.client?.nom||''}
</div>
)}
<div>
<strong>Montant total :</strong> {formatMontant(
filteredDocuments
.filter(d => selectedDocs.includes(d.id))
.reduce((sum, doc) => sum + (doc.total || 0), 0)
)}
</div>
</div>
</div>
)}
</>
)}
</div>
)}

{/* Indicateur de filtrage actif */}
{(searchTerm || filterType !== 'all' || filterStatus !== 'all' || filterClient ||
dateDebut || dateFin || filterMontantMin || filterMontantMax) && (
<div style={{
padding: '0.75rem 1rem',
background: 'linear-gradient(135deg, rgba(0, 212, 170, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)',
border: '1px solid rgba(0, 212, 170, 0.3)',
borderRadius: '8px',
marginTop: '1rem',
display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
flexWrap: 'wrap',
gap: '0.5rem'
}}>
<div style={{ fontSize: '0.875rem', color: 'white', fontWeight: '600' }}>
🔍 Filtres actifs : {filteredDocuments.length} document{filteredDocuments.length > 1 ? 's' : ''} trouvé{filteredDocuments.length > 1 ? 's' : ''}
</div>
<div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', fontSize: '0.75rem' }}>
{searchTerm && (
<span style={{
padding: '0.25rem 0.5rem',
background: '#3B82F6',
color: 'white',
borderRadius: '4px',
fontWeight: '500'
}}>
🔍 {searchTerm}
</span>
)}
{filterType !== 'all' && (
<span style={{
padding: '0.25rem 0.5rem',
background: '#10B981',
color: 'white',
borderRadius: '4px',
fontWeight: '500'
}}>
{filterType === 'devis' ? '📋 Devis' : '🧾 Factures'}
</span>
)}
{filterStatus !== 'all' && (
<span style={{
padding: '0.25rem 0.5rem',
background: '#8B5CF6',
color: 'white',
borderRadius: '4px',
fontWeight: '500'
}}>
📊 {filterStatus}
</span>
)}
{filterClient && (
<span style={{
padding: '0.25rem 0.5rem',
background: '#F59E0B',
color: 'white',
borderRadius: '4px',
fontWeight: '500'
}}>
👤 {filterClient}
</span>
)}
{(dateDebut || dateFin) && (
<span style={{
padding: '0.25rem 0.5rem',
background: '#EC4899',
color: 'white',
borderRadius: '4px',
fontWeight: '500'
}}>
📅 {dateDebut || '...'} → {dateFin || '...'}
</span>
)}
{(filterMontantMin || filterMontantMax) && (
<span style={{
padding: '0.25rem 0.5rem',
background: '#14B8A6',
color: 'white',
borderRadius: '4px',
fontWeight: '500'
}}>
💰 {filterMontantMin || '0'} - {filterMontantMax || '∞'}
</span>
)}
</div>
</div>
)}
</div>
</div>
</div>{/* fin docs-plein-ecran-header */}

{/* ─── CORPS SCROLLABLE en plein écran ─── */}
<div className={docsPleinEcran ? 'docs-plein-ecran-body' : ''}>

{/* Bouton d'impression en lot */}
{selectedDocs.length > 0 && (
<div style={{
padding: '1rem',
background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
border: '2px solid rgba(59, 130, 246, 0.3)',
borderRadius: '12px',
marginBottom: '1rem',
display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
flexWrap: 'wrap',
gap: '1rem'
}}>
<div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
<div style={{
background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)',
color: 'white',
width: '40px',
height: '40px',
borderRadius: '10px',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
fontSize: '1.25rem',
fontWeight: 'bold',
boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
}}>
{selectedDocs.length}
</div>
<div>
<div style={{ fontWeight: 'bold', fontSize: '1rem', color: '#1E293B' }}>
{selectedDocs.length} document{selectedDocs.length > 1 ? 's' : ''} sélectionné{selectedDocs.length > 1 ? 's' : ''}
</div>
<div style={{ fontSize: '0.875rem', color: '#64748B' }}>
Prêt pour l'impression en lot
</div>
</div>
</div>
<div style={{ display: 'flex', gap: '0.75rem' }}>
<button
onClick={() => setSelectedDocs([])}
style={{
padding: '0.625rem 1.25rem',
background: 'white',
border: '2px solid #E5E7EB',
borderRadius: '8px',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.875rem',
color: '#64748B',
transition: 'all 0.2s'
}}
onMouseOver={(e) => {
e.target.style.background = '#F8FAFC';
e.target.style.borderColor = '#CBD5E1';
}}
onMouseOut={(e) => {
e.target.style.background = 'white';
e.target.style.borderColor = '#E5E7EB';
}}
>
✕ Désélectionner
</button>
<button
onClick={ouvrirImpressionLot}
style={{
padding: '0.625rem 1.5rem',
background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.875rem',
color: 'white',
boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
transition: 'all 0.2s',
display: 'flex',
alignItems: 'center',
gap: '0.5rem'
}}
onMouseOver={(e) => {
e.target.style.transform = 'translateY(-2px)';
e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
}}
onMouseOut={(e) => {
e.target.style.transform = 'translateY(0)';
e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
}}
>
🖨️ Imprimer en lot
</button>

<button
onClick={imprimerEnGros}
style={{
padding: '0.625rem 1.5rem',
background: 'linear-gradient(135deg, #0A2540 0%, #1E3A5F 100%)',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.875rem',
color: 'white',
boxShadow: '0 4px 12px rgba(10, 37, 64, 0.3)',
transition: 'all 0.2s',
display: 'flex',
alignItems: 'center',
gap: '0.5rem'
}}
onMouseOver={(e) => {
e.target.style.transform = 'translateY(-2px)';
e.target.style.boxShadow = '0 6px 16px rgba(10, 37, 64, 0.4)';
}}
onMouseOut={(e) => {
e.target.style.transform = 'translateY(0)';
e.target.style.boxShadow = '0 4px 12px rgba(10, 37, 64, 0.3)';
}}
>
🖨️ Imprimer en gros
</button>
</div>
</div>
)}

{filteredDocuments.length === 0 ? (
<div className="empty-state">
<div className="empty-state-icon">📭</div>
<h3 className="empty-state-title">Aucun document</h3>
<p className="empty-state-text">
Créez votre premier devis ou facture pour commencer
</p>
</div>
) : (
<>
<PaginationBar
  currentPage={docCurrentPage}
  totalPages={Math.ceil(filteredDocuments.length / docPerPage) || 1}
  perPage={docPerPage}
  totalItems={filteredDocuments.length}
  onPageChange={setDocCurrentPage}
  onPerPageChange={setDocPerPage}
  itemLabel="document"
  itemLabelPlural="documents"
/>
<div className="table-container">
<table className="documents-table">
<thead>
<tr>
<th style={{ width: '40px', textAlign: 'center' }}>
<input
type="checkbox"
checked={selectedDocs.length === filteredDocuments.length && filteredDocuments.length > 0}
onChange={() => selectAllDocs(filteredDocuments)}
title="Tout sélectionner"
style={{ cursor: 'pointer', width: '16px', height: '16px' }}
/>
</th>
<th>Numéro</th>
<th>Type</th>
<th>Client</th>
<th>Date</th>
<th>Montant</th>
<th>Statut</th>
<th>Actions</th>
</tr>
</thead>
<tbody>
{filteredDocuments.slice((docCurrentPage - 1) * docPerPage, docCurrentPage * docPerPage).map(doc => {
// Vérifier si une facture existe pour ce devis
const factureExiste = doc.type === 'devis' && doc.statut === 'accepte'
? documents.find(d => d.type === 'facture' && d.devisOrigine === doc.numero)
: null;

return (
<tr key={doc.id}>
<td style={{ textAlign: 'center' }}>
<input
type="checkbox"
checked={selectedDocs.includes(doc.id)}
onChange={() => toggleDocSelection(doc.id)}
style={{ cursor: 'pointer', width: '16px', height: '16px' }}
/>
</td>
<td className="doc-number">
{doc.type === 'devis' && doc.statut === 'accepte' ? (
<span
onClick={() => convertirDevisEnFacture(doc)}
className="clickable-devis-number"
title={factureExiste
? `Facture déjà créée : ${factureExiste.numero}. Cliquez pour la voir.`
: "Cliquez pour générer une facture depuis ce devis"
}
style={{
color: factureExiste ? '#3B82F6' : '#10B981',
position: 'relative'
}}
>
{factureExiste ? '🔗' : '🧾'} {doc.numero}
{factureExiste && (
<span style={{
marginLeft: '0.5rem',
fontSize: '0.7rem',
background: 'rgba(59, 130, 246, 0.2)',
padding: '0.15rem 0.4rem',
borderRadius: '4px',
fontWeight: 'normal',
color: '#1E40AF'
}}>
✓ Facturée
</span>
)}
</span>
) : (
<span>
{doc.type === 'avoir' && <span style={{ color: '#7C3AED', marginRight: '0.3rem' }}>🔵</span>}
{doc.numero}
</span>
)}
{/* Lien facture d'origine pour les avoirs */}
{doc.type === 'avoir' && doc.factureOrigineNumero && (
<div style={{ fontSize: '0.68rem', color: '#7C3AED', marginTop: '0.2rem', fontFamily: 'JetBrains Mono, monospace' }}>
← {doc.factureOrigineNumero}
</div>
)}
</td>
<td>
<span style={{
padding: '0.25rem 0.75rem',
borderRadius: '6px',
fontSize: '0.75rem',
fontWeight: 600,
background: doc.type === 'devis' ? 'rgba(59, 130, 246, 0.1)' : doc.type === 'avoir' ? 'rgba(139, 92, 246, 0.12)' : 'rgba(16, 185, 129, 0.1)',
color: doc.type === 'devis' ? '#3B82F6' : doc.type === 'avoir' ? '#7C3AED' : '#10B981'
}}>
{doc.type === 'devis' ? '📋 Devis' : doc.type === 'avoir' ? '🔵 Avoir' : '🧾 Facture'}
</span>
{doc.type === 'facture' && doc.devisOrigine && (
<div style={{
fontSize: '0.65rem',
color: '#64748B',
marginTop: '0.25rem',
fontFamily: 'JetBrains Mono, monospace'
}}>
← {doc.devisOrigine}
</div>
)}
{/* Avoirs liés à une facture */}
{doc.type === 'facture' && (doc.avoirs || []).length > 0 && (
<div style={{ marginTop: '0.25rem', display: 'flex', flexWrap: 'wrap', gap: '0.2rem' }}>
{(doc.avoirs || []).map(numAv => (
<span key={numAv} className="avoir-badge-ref"
onClick={() => {
const av = documents.find(d => d.numero === numAv);
if (av) { setCurrentAvoir(av); setShowAvoirPreview(true); }
}}
title={`Voir l'avoir ${numAv}`}>
🔵 {numAv}
</span>
))}
</div>
)}
</td>
<td className="doc-client">{(doc.client||{}).nom}</td>
<td className="doc-date">
{new Date(doc.date).toLocaleDateString('fr-FR')}
{doc.dateMode === 'auto' && doc.dateTime && (
<div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)' }}>
{new Date(doc.dateTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
</div>
)}
</td>
<td className="doc-amount">
{formatMontant(doc.total)}
{doc.type === 'facture' && doc.statut === 'partiel' && (doc.montantPaye || 0) > 0 && (
<div style={{
fontSize: '0.7rem',
color: '#F59E0B',
marginTop: '0.25rem',
fontFamily: 'JetBrains Mono, monospace'
}}>
Avancé: {formatMontant(doc.montantPaye)}<br/>
Reste: {formatMontant(Math.max(0, (doc.total || 0) - (doc.montantPaye || 0)))}
</div>
)}
</td>
<td>
<span className={`status-badge status-${doc.statut}`}>
{doc.statut === 'en-attente' && '⏳ En attente'}
{doc.statut === 'accepte' && (
<>
✅ Acceptée
{doc.type === 'devis' && (
<span style={{
marginLeft: '0.5rem',
fontSize: '0.7rem',
background: 'rgba(16, 185, 129, 0.2)',
padding: '0.15rem 0.4rem',
borderRadius: '4px',
fontWeight: 'normal'
}}>
→ Facture
</span>
)}
</>
)}
{doc.statut === 'refuse' && '❌ Refusée'}
{doc.statut === 'paye' && '✅ Payée'}
{doc.statut === 'partiel' && '⚠️ Partiellement payée'}
{doc.statut === 'impaye' && '❌ Impayée'}
{doc.statut === 'annule' && (
<span
title={doc.raisonAnnulation ? `Raison: ${doc.raisonAnnulation}` : 'Facture annulée'}
style={{ cursor: doc.raisonAnnulation ? 'help' : 'default' }}
>
❌ Annulée
{doc.raisonAnnulation && ' ℹ️'}
</span>
)}
{doc.statut === 'avoir' && '🔵 Avoir'}
</span>
</td>
<td>
<div className="action-buttons">
{actionsVisibles.visualiser && (
<button
className="action-btn btn-view"
onClick={() => {
setCurrentDoc(doc);
setShowPreview(true);
}}
title="Visualiser"
>
👁️
</button>
)}
{actionsVisibles.editer && (
<button
className="action-btn btn-edit"
onClick={() => editerDocument(doc)}
title="Éditer"
>
✏️
</button>
)}
{/* Bouton Avoir : uniquement pour les factures non-annulées */}
{actionsVisibles.avoir && doc.type === 'facture' && doc.statut !== 'annule' && (
<button
className="action-btn"
onClick={() => ouvrirModalAvoir(doc)}
title="Émettre un avoir (note de crédit)"
style={{
background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
color: 'white',
width: 'auto',
padding: '0 0.7rem',
fontSize: '0.72rem',
fontWeight: 700,
letterSpacing: '0.02em',
gap: '0.3rem',
flexDirection: 'row'
}}
>
🔵 <span>Avoir</span>
</button>
)}
{/* Bouton Aperçu avoir */}
{doc.type === 'avoir' && (
<button
className="action-btn"
onClick={() => { setCurrentAvoir(doc); setShowAvoirPreview(true); }}
title="Aperçu de l'avoir"
style={{ background: 'linear-gradient(135deg, #7C3AED, #5B21B6)', color: 'white' }}
>
👁️
</button>
)}
{actionsVisibles.dupliquer && (
<button
className="action-btn"
onClick={() => dupliquerDocument(doc)}
title="Dupliquer"
style={{ background: '#3B82F6', color: 'white' }}
>
📄
</button>
)}
{actionsVisibles.imprimer && (
<button
className="action-btn btn-download"
onClick={() => telechargerPDF(doc)}
title="Imprimer / PDF"
>
🖨️
</button>
)}
{actionsVisibles.supprimer && (
<button
className="action-btn btn-delete"
onClick={() => supprimerDocument(doc.id)}
title="Supprimer"
>
🗑️
</button>
)}
</div>
</td>
</tr>
);
})}
</tbody>
</table>
</div>
</>
)}
</div>{/* fin docs-plein-ecran-body */}
</div>{/* fin documents-section */}

{/* ============================================================ */}
{/* SECTION BONS DE COMMANDE                                      */}
{/* ============================================================ */}
<div className={`documents-section${bcPleinEcran ? ' plein-ecran' : ''}`} style={{ marginTop: '2rem' }}>
<div className={bcPleinEcran ? 'docs-plein-ecran-header' : ''}>
<div className="documents-header">
<div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', flex: 1 }}>
<h2 className="documents-title">📦 Bons de Commande</h2>
<span style={{ background: 'linear-gradient(135deg,#0A2540,#1E3A5F)', color: 'white', padding: '0.2rem 0.7rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700 }}>
{bonsCommande.length} BC
</span>
<button className={`btn-plein-ecran${bcPleinEcran ? ' actif' : ''}`} onClick={() => setBcPleinEcran(v => !v)}>
{bcPleinEcran ? <><span>⊡</span> Quitter plein écran</> : <><span>⛶</span> Plein écran</>}
</button>
<button
onClick={() => setShowConfigActionsBC(true)}
className="btn-plein-ecran"
title="Personnaliser les boutons d'action des bons de commande"
style={{ gap: '0.4rem' }}
>
<span style={{fontSize:'1rem'}}>⚙️</span> Actions
</button>
</div>
<div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
<input type="text" className="search-input" placeholder="🔍 Rechercher un BC..." value={searchBC}
onChange={e => setSearchBC(e.target.value)} style={{ minWidth: '220px' }} />
<select className="filter-select" value={filterStatusBC} onChange={e => setFilterStatusBC(e.target.value)}>
<option value="all">Tous les statuts</option>
<option value="en-attente">⏳ En attente</option>
<option value="envoye">📤 Envoyé</option>
<option value="recu-partiel">📬 Reçu partiellement</option>
<option value="recu">✅ Reçu</option>
<option value="annule">❌ Annulé</option>
</select>
<button className="btn btn-primary" onClick={nouveauBC}
style={{ background: 'linear-gradient(135deg,#1E3A5F,#0A2540)', border: '2px solid #00D4AA', whiteSpace: 'nowrap' }}>
📦 Nouveau BC
</button>
</div>
</div>
</div>

<div className={bcPleinEcran ? 'docs-plein-ecran-body' : ''}>
{filteredBC.length === 0 ? (
<div style={{ padding: '3rem', textAlign: 'center', color: '#64748B' }}>
<p style={{ fontSize: '3rem', marginBottom: '1rem' }}>📦</p>
<p style={{ fontSize: '1.1rem', fontWeight: 600 }}>
{bonsCommande.length === 0 ? 'Aucun bon de commande créé' : 'Aucun résultat pour cette recherche'}
</p>
<p style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
{bonsCommande.length === 0 && 'Cliquez sur "Nouveau BC" pour commencer.'}
</p>
</div>
) : (
<>
<PaginationBar
  currentPage={bcCurrentPage}
  totalPages={Math.ceil(filteredBC.length / bcPerPage) || 1}
  perPage={bcPerPage}
  totalItems={filteredBC.length}
  onPageChange={setBcCurrentPage}
  onPerPageChange={setBcPerPage}
  itemLabel="bon de commande"
  itemLabelPlural="bons de commande"
/>
<div style={{ overflowX: 'auto' }}>
<table className="documents-table">
<thead>
<tr>
<th>Numéro</th>
<th>Date</th>
<th>Fournisseur</th>
<th>Objet</th>
<th>Montant</th>
<th>Délai Livraison</th>
<th>Statut</th>
<th>Actions</th>
</tr>
</thead>
<tbody>
{filteredBC.slice((bcCurrentPage - 1) * bcPerPage, bcCurrentPage * bcPerPage).map(bc => {
const statutColors = {
'en-attente': { bg: '#FEF3C7', color: '#92400E', label: '⏳ En attente' },
'envoye':     { bg: '#DBEAFE', color: '#1E40AF', label: '📤 Envoyé' },
'recu-partiel':{ bg: '#FEE2E2', color: '#991B1B', label: '📬 Reçu partiel' },
'recu':        { bg: '#DCFCE7', color: '#166534', label: '✅ Reçu' },
'annule':      { bg: '#F1F5F9', color: '#64748B', label: '❌ Annulé' }
};
const s = statutColors[bc.statut] || statutColors['en-attente'];
return (
<tr key={bc.id}>
<td><span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: 'rgba(255, 255, 255, 0.8)' }}>{bc.numero}</span></td>
<td>{new Date(bc.date).toLocaleDateString('fr-FR')}</td>
<td style={{ fontWeight: 600 }}>{bc.fournisseur?.nom || '—'}</td>
<td style={{ color: '#64748B', fontSize: '0.875rem' }}>{bc.objet || '—'}</td>
<td style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{formatMontant(bc.total||0)}</td>
<td style={{ fontSize: '0.85rem', color: '#475569' }}>
  {bc.delaiLivraison || (bc.dateLivraison
    ? new Date(bc.dateLivraison).toLocaleDateString('fr-FR')
    : '—')}
</td>
<td>
<span style={{ background: s.bg, color: s.color, padding: '0.25rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
{s.label}
</span>
</td>
<td>
<div className="action-buttons">
{actionsVisiblesBC.apercu && (
<button className="action-btn" onClick={() => { setCurrentBC(bc); setShowBCPreview(true); }}
title="Aperçu / Imprimer" style={{ background: 'linear-gradient(135deg,#3B82F6,#1D4ED8)', color: 'white' }}>👁️</button>
)}
{actionsVisiblesBC.editer && (
<button className="action-btn" onClick={() => editerBC(bc)}
title="Éditer" style={{ background: 'linear-gradient(135deg,#F59E0B,#D97706)', color: 'white' }}>✏️</button>
)}
{actionsVisiblesBC.statut && (
<button className="action-btn" onClick={() => {
const newStatuts = ['en-attente','envoye','recu-partiel','recu','annule'];
const idx = newStatuts.indexOf(bc.statut||'en-attente');
const next = newStatuts[(idx+1) % newStatuts.length];
saveBonCommande({...bc, statut: next}).catch(err => alert('Erreur statut: ' + err.message));
}} title="Changer le statut" style={{ background: 'linear-gradient(135deg,#10B981,#059669)', color: 'white' }}>🔄</button>
)}
{actionsVisiblesBC.dupliquer && (
<button className="action-btn" onClick={() => {
if (!confirm('Voulez-vous créer une copie de ce bon de commande ?')) return;
const dup = { ...bc, items: bc.items ? bc.items.map(i=>({...i})) : [], id: null, numero: '', date: new Date().toISOString().split('T')[0], statut: 'en-attente' };
saveBonCommande(dup).then(() => alert('Bon de commande dupliqué !')).catch(err => alert('Erreur: ' + err.message));
}} title="Dupliquer" style={{ background: '#3B82F6', color: 'white' }}>📄</button>
)}
{actionsVisiblesBC.imprimer && (
<button className="action-btn" onClick={() => imprimerBC(bc)}
title="Imprimer" style={{ background: 'linear-gradient(135deg,#10B981,#059669)', color: 'white' }}>🖨️</button>
)}
{actionsVisiblesBC.supprimer && (
<button className="action-btn btn-delete" onClick={() => supprimerBC(bc.id)} title="Supprimer">🗑️</button>
)}
</div>
</td>
</tr>
);
})}
</tbody>
</table>
</div>
</>
)}
</div>
</div>{/* fin section BC */}

{/* Modal Nouveau/Éditer Document */}
{showModal && currentDoc && (
<div className="modal-overlay">
<div className="modal-content">
<div className="modal-header">
<h2 className="modal-title">
{currentDoc.type === 'devis' ? '📋' : '🧾'}
{documents.find(d => d.id === currentDoc.id) ? 'Éditer' : (currentDoc.type === 'devis' ? 'Nouveau' : 'Nouvelle')}
{currentDoc.type === 'devis' ? ' Devis' : ' Facture'}
{documents.find(d => d.id === currentDoc.id) && currentDoc.numero && (
<span style={{
marginLeft: '0.75rem',
padding: '0.375rem 0.875rem',
background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(16, 185, 129, 0.15) 100%)',
border: '2px solid rgba(59, 130, 246, 0.3)',
borderRadius: '8px',
fontSize: '0.9rem',
fontWeight: '600',
color: '#1E293B',
fontFamily: 'JetBrains Mono, monospace'
}}>
N° {currentDoc.numero}
</span>
)}
</h2>
<button className="close-btn" onClick={fermerEditeur}>✕</button>
</div>

<div className="modal-body">
{/* Indication si la facture provient d'un devis */}
{currentDoc.type === 'facture' && currentDoc.devisOrigine && (
<div style={{
marginBottom: '1.5rem',
padding: '1rem',
background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
borderRadius: '12px',
border: '2px solid rgba(16, 185, 129, 0.3)',
display: 'flex',
alignItems: 'center',
gap: '0.75rem'
}}>
<span style={{ fontSize: '1.5rem' }}>🔄</span>
<div>
<div style={{ fontWeight: 600, color: '#059669', marginBottom: '0.25rem' }}>
Facture générée depuis un devis
</div>
<div style={{ fontSize: '0.85rem', color: '#64748B' }}>
Devis d'origine : <strong>{currentDoc.devisOrigine}</strong>
</div>
</div>
</div>
)}

{/* Informations générales */}
<div className="form-grid">
<div className="form-group">
<label className="form-label">Numéro</label>
<input
type="text"
className="form-input"
value={currentDoc.numero}
readOnly
style={{ background: '#f5f5f5' }}
/>
</div>

{/* Mode de date avec switch */}
<div className="form-group">
<label className="form-label">Mode de date</label>
<div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
<input
type="radio"
name="dateMode"
value="auto"
checked={currentDoc.dateMode === 'auto'}
onChange={(e) => {
const now = new Date();
setCurrentDoc({
...currentDoc,
dateMode: 'auto',
date: now.toISOString().split('T')[0],
dateTime: now.toISOString()
});
}}
/>
<span style={{ fontSize: '0.875rem' }}>🕐 Automatique (date + heure actuelles)</span>
</label>
<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
<input
type="radio"
name="dateMode"
value="manual"
checked={currentDoc.dateMode === 'manual'}
onChange={(e) => setCurrentDoc({...currentDoc, dateMode: 'manual'})}
/>
<span style={{ fontSize: '0.875rem' }}>📅 Manuelle (date seulement)</span>
</label>
</div>
</div>

<div className="form-group">
<label className="form-label">
Date {currentDoc.dateMode === 'manual' && '(modifiable pour antidater)'}
</label>
<input
type="date"
className="form-input"
value={currentDoc.date}
onChange={(e) => setCurrentDoc({...currentDoc, date: e.target.value})}
readOnly={currentDoc.dateMode === 'auto'}
style={{ background: currentDoc.dateMode === 'auto' ? '#f5f5f5' : 'white' }}
/>
{currentDoc.dateMode === 'auto' && (
<small style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '0.25rem', display: 'block' }}>
Date et heure actualisées automatiquement
</small>
)}
</div>

{/* Champ Objet */}
<div className="form-group" style={{ gridColumn: '1 / -1' }}>
<label className="form-label">Objet du {currentDoc.type === 'devis' ? 'devis' : 'de la facture'}</label>
<input
id="objet-document"
type="text"
className="form-input lt-enabled"
value={currentDoc.objet || ''}
onChange={(e) => setCurrentDoc({...currentDoc, objet: e.target.value})}
placeholder={`Ex: ${currentDoc.type === 'devis' ? 'Fourniture de matériel informatique' : 'Prestation de services informatiques'}`}
style={{
fontWeight: '500',
fontSize: '0.95rem'
}}
/>
</div>
</div>

{/* Informations client */}
<h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: 'var(--text-dark)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
<span>👤 Informations Client</span>
<button
type="button"
className="btn btn-secondary"
onClick={() => setShowClients(true)}
style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
>
📋 Sélectionner un client
</button>
</h3>
<div className="form-grid">
<div className="form-group" style={{ gridColumn: '1 / -1' }}>
<label className="form-label">Nom du client *</label>
<input
type="text"
className="form-input"
value={(currentDoc.client?.nom||'')}
onChange={(e) => handleClientChange('nom', e.target.value)}
placeholder="Entreprise ou Nom Prénom"
/>
</div>
<div className="form-group">
<label className="form-label">Téléphone</label>
<input
type="tel"
className="form-input"
value={currentDoc.client?.telephone}
onChange={(e) => handleClientChange('telephone', e.target.value)}
placeholder="+237 6XX XXX XXX"
/>
</div>
<div className="form-group">
<label className="form-label">Email</label>
<input
type="email"
className="form-input"
value={currentDoc.client?.email}
onChange={(e) => handleClientChange('email', e.target.value)}
placeholder="client@exemple.com"
/>
</div>
<div className="form-group">
<label className="form-label">NUI (Numéro d'Identification Unique)</label>
<input
type="text"
className="form-input"
value={currentDoc.client?.nui || ''}
onChange={(e) => handleClientChange('nui', e.target.value)}
placeholder="M000000000000X"
/>
</div>
<div className="form-group">
<label className="form-label">RCCM (Registre du Commerce)</label>
<input
type="text"
className="form-input"
value={currentDoc.client?.rccm || ''}
onChange={(e) => handleClientChange('rccm', e.target.value)}
placeholder="RC/XXX/XXXX/X/XXXXX"
/>
</div>
<div className="form-group">
<label className="form-label">N° BC (Numéro de bon de commande)</label>
<input
type="text"
className="form-input"
value={currentDoc.client?.numeroBonCommande || ''}
onChange={(e) => handleClientChange('numeroBonCommande', e.target.value)}
placeholder="003"
/>
</div>
<div className="form-group" style={{ gridColumn: '1 / -1' }}>
<label className="form-label">Adresse</label>
<input
type="text"
className="form-input"
value={currentDoc.client?.adresse}
onChange={(e) => handleClientChange('adresse', e.target.value)}
placeholder="Adresse complète"
/>
</div>
</div>

{/* Sélection du mode de taxation HT/TTC */}
<div style={{
marginTop: '2rem',
marginBottom: '1.5rem',
padding: '1rem',
background: 'linear-gradient(135deg, rgba(0, 212, 170, 0.1) 0%, rgba(255, 107, 53, 0.1) 100%)',
borderRadius: '12px',
border: '2px solid rgba(0, 212, 170, 0.2)'
}}>
<h3 style={{
color: 'var(--text-dark)',
marginBottom: '1rem',
display: 'flex',
alignItems: 'center',
gap: '0.5rem'
}}>
💰 Mode de Taxation
</h3>
<div style={{
display: 'flex',
gap: '1rem',
flexWrap: 'wrap',
alignItems: 'center'
}}>
<label style={{
display: 'flex',
alignItems: 'center',
gap: '0.5rem',
cursor: 'pointer',
padding: '0.75rem 1.25rem',
background: currentDoc.taxMode === 'TTC' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.5)',
borderRadius: '8px',
border: currentDoc.taxMode === 'TTC' ? '2px solid var(--success)' : '2px solid transparent',
transition: 'all 0.3s ease',
fontWeight: currentDoc.taxMode === 'TTC' ? '600' : '400'
}}>
<input
type="radio"
name="taxMode"
value="TTC"
checked={currentDoc.taxMode === 'TTC'}
onChange={(e) => {
setCurrentDoc({
...currentDoc,
taxMode: 'TTC'
});
calculerTotaux(currentDoc.items);
}}
style={{ accentColor: 'var(--success)' }}
/>
<span style={{ fontSize: '0.95rem' }}>
✅ <strong>TTC</strong> (avec TVA {settings.tauxTVA || 19.25}%)
</span>
</label>
<label style={{
display: 'flex',
alignItems: 'center',
gap: '0.5rem',
cursor: 'pointer',
padding: '0.75rem 1.25rem',
background: currentDoc.taxMode === 'HT' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.5)',
borderRadius: '8px',
border: currentDoc.taxMode === 'HT' ? '2px solid #3B82F6' : '2px solid transparent',
transition: 'all 0.3s ease',
fontWeight: currentDoc.taxMode === 'HT' ? '600' : '400'
}}>
<input
type="radio"
name="taxMode"
value="HT"
checked={currentDoc.taxMode === 'HT'}
onChange={(e) => {
setCurrentDoc({
...currentDoc,
taxMode: 'HT'
});
calculerTotaux(currentDoc.items);
}}
style={{ accentColor: '#3B82F6' }}
/>
<span style={{ fontSize: '0.95rem' }}>
🚫 <strong>HT</strong> (sans TVA)
</span>
</label>
</div>
<div style={{
marginTop: '0.75rem',
fontSize: '0.8rem',
color: 'var(--text-light)',
fontStyle: 'italic'
}}>
💡 Sélectionnez <strong>TTC</strong> pour appliquer la TVA de {settings.tauxTVA || 19.25}% ou <strong>HT</strong> pour un document sans TVA
</div>

{/* Toggle afficher mode taxation */}
<div
  onClick={() => setCurrentDoc({...currentDoc, afficherModeTaxation: !currentDoc.afficherModeTaxation})}
  style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0.75rem 1rem', marginTop: '0.75rem',
    background: currentDoc.afficherModeTaxation ? 'rgba(0,168,150,0.08)' : 'rgba(0,0,0,0.03)',
    border: `1px solid ${currentDoc.afficherModeTaxation ? 'rgba(0,168,150,0.4)' : '#E2E8F0'}`,
    borderRadius: '10px', cursor: 'pointer', userSelect: 'none',
  }}>
  <div>
    <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1E293B' }}>
      🏷️ Afficher le mode de taxation sur le document imprimé
    </div>
    <div style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '0.15rem' }}>
      {currentDoc.afficherModeTaxation
        ? (currentDoc.taxMode === 'TTC'
            ? 'Activé — SOUS-TOTAL HT · TOTAL TTC · TOTAL À PAYER TTC'
            : 'Activé — SOUS-TOTAL HT · TOTAL HT · TOTAL À PAYER HT')
        : 'Désactivé — SOUS-TOTAL · TOTAL · TOTAL À PAYER'}
    </div>
  </div>
  <div style={{
    width: 44, height: 24, borderRadius: 12, flexShrink: 0,
    background: currentDoc.afficherModeTaxation ? '#00A896' : '#CBD5E1',
    position: 'relative', transition: 'background 0.2s',
  }}>
    <div style={{
      position: 'absolute', top: 3,
      left: currentDoc.afficherModeTaxation ? 23 : 3,
      width: 18, height: 18, borderRadius: '50%',
      background: 'white', transition: 'left 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
    }}/>
  </div>
</div>

</div>

{/* Lignes de facturation structurées */}
<div className="items-section">
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
<h3 style={{ color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
📦 Lignes de facturation
<span style={{ fontSize: '0.72rem', fontWeight: 600, background: 'rgba(0,212,170,0.12)', border: '1px solid rgba(0,212,170,0.25)', color: '#00D4AA', padding: '0.15rem 0.55rem', borderRadius: '10px', fontFamily: 'JetBrains Mono, monospace' }}>
{currentDoc.items.filter(i => (i.lineType||'article') === 'article').length} art. · {currentDoc.items.filter(i => i.lineType === 'section').length} sect. · {currentDoc.items.filter(i => i.lineType === 'note').length} notes
</span>
</h3>
<div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
<button className="btn btn-secondary" onClick={ajouterArticle} style={{ background: 'linear-gradient(135deg,#0A2540,#1E3A5F)', color: 'white', border: 'none', padding: '0.55rem 1rem', fontSize: '0.82rem', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
<span style={{ fontSize: '1rem', lineHeight: 1 }}>＋</span> Article
</button>
<button onClick={ajouterSection} style={{ background: 'linear-gradient(135deg,#27500A,#3B6D11)', color: 'white', border: 'none', padding: '0.55rem 1rem', fontSize: '0.82rem', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
<span style={{ fontSize: '1rem', lineHeight: 1 }}>＋</span> Section
</button>
<button onClick={ajouterNote} style={{ background: 'linear-gradient(135deg,#633806,#854F0B)', color: 'white', border: 'none', padding: '0.55rem 1rem', fontSize: '0.82rem', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
<span style={{ fontSize: '1rem', lineHeight: 1 }}>＋</span> Note
</button>
</div>
</div>

<div style={{ overflowX: 'auto', border: '1.5px solid #E2E8F0', borderRadius: '14px', overflow: 'hidden' }}>
<table className="items-table" style={{ marginBottom: 0 }}>
<thead>
<tr>
<th style={{ width: '28px', padding: '0.6rem 0.4rem' }}></th>
<th style={{ minWidth: '250px' }}>Description / Désignation</th>
<th style={{ minWidth: '120px' }}>Unité</th>
<th style={{ minWidth: '100px' }}>Quantité</th>
<th style={{ minWidth: '120px' }}>Prix Unit.</th>
<th style={{ minWidth: '180px' }}>Remise</th>
<th style={{ minWidth: '120px' }}>Total</th>
<th style={{ minWidth: '50px' }}>Actions</th>
</tr>
</thead>
<tbody>
{(() => {
const sousTotaux = calculerSousTotaux(currentDoc.items, currentDoc.taxMode, currentDoc.tauxTVA || settings.tauxTVA);
const rows = [];
currentDoc.items.forEach((item, index) => {
const lineType = item.lineType || 'article';

if (lineType === 'section') {
// ── LIGNE SECTION ──
rows.push(
<tr key={`s-${index}`}
draggable
onDragStart={(e) => { dragSrcIndex.current = index; e.dataTransfer.effectAllowed = 'move'; }}
onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.boxShadow = 'inset 0 2px 0 #00D4AA'; }}
onDragLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
onDrop={(e) => { e.currentTarget.style.boxShadow = ''; deplacerItem(dragSrcIndex.current, index); }}
style={{ background: 'linear-gradient(135deg,#EAF3DE,#F0F9E8)', borderLeft: '4px solid #3B6D11', cursor: 'grab' }}
>
<td style={{ padding: '0.4rem 0.3rem', width: '32px', cursor: 'grab', userSelect: 'none' }}>
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
    <button onMouseDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setInsertMenu(insertMenu?.index === index ? null : { index, top: r.bottom + 4, left: r.left }) }} title="Insérer une ligne après" style={{ width:'22px', height:'22px', borderRadius:'6px', border:'1.5px solid #3B6D11', background: insertMenu?.index === index ? '#3B6D11' : 'white', color: insertMenu?.index === index ? 'white' : '#3B6D11', fontSize:'1rem', fontWeight:900, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0, lineHeight:1 }}>＋</button>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', opacity: 0.45 }}>{[0,1,2,3,4,5].map(i=><div key={i} style={{ width:'3px', height:'3px', borderRadius:'50%', background:'#27500A' }}/>)}</div>
  </div>
</td>
<td colSpan={6} style={{ padding: '0.75rem 0.875rem' }}>
<div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
<div style={{ width: '22px', height: '22px', background: '#C0DD97', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
<span style={{ fontSize: '0.75rem', color: '#27500A', fontWeight: 700 }}>§</span>
</div>
<input
type="text"
value={item.sectionLabel || ''}
onChange={(e) => modifierArticle(index, 'sectionLabel', e.target.value)}
placeholder="Titre de la section…"
style={{ flex: 1, border: 'none', background: 'transparent', fontSize: '0.95rem', fontWeight: 700, color: '#27500A', outline: 'none', fontFamily: 'Bricolage Grotesque, sans-serif' }}
/>
<span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '0.15rem 0.55rem', background: '#C0DD97', color: '#27500A', borderRadius: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Section</span>
</div>
</td>
<td style={{ padding: '0.5rem' }}>
<button onClick={() => supprimerArticle(index)} style={{ width: '28px', height: '28px', border: '1.5px solid #FEE2E2', background: 'white', borderRadius: '7px', color: '#EF4444', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
</td>
</tr>
);
} else if (lineType === 'note') {
// ── LIGNE NOTE ──
rows.push(
<tr key={`n-${index}`}
draggable
onDragStart={(e) => { dragSrcIndex.current = index; e.dataTransfer.effectAllowed = 'move'; }}
onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.boxShadow = 'inset 0 2px 0 #BA7517'; }}
onDragLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
onDrop={(e) => { e.currentTarget.style.boxShadow = ''; deplacerItem(dragSrcIndex.current, index); }}
style={{ background: 'linear-gradient(135deg,#FAEEDA,#FEF3E2)', borderLeft: '4px solid #BA7517', cursor: 'grab' }}
>
<td style={{ padding: '0.4rem 0.3rem', width: '32px', cursor: 'grab', userSelect: 'none' }}>
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
    <button onMouseDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setInsertMenu(insertMenu?.index === index ? null : { index, top: r.bottom + 4, left: r.left }) }} title="Insérer une ligne après" style={{ width:'22px', height:'22px', borderRadius:'6px', border:'1.5px solid #BA7517', background: insertMenu?.index === index ? '#BA7517' : 'white', color: insertMenu?.index === index ? 'white' : '#854F0B', fontSize:'1rem', fontWeight:900, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0, lineHeight:1 }}>＋</button>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', opacity: 0.45 }}>{[0,1,2,3].map(i=><div key={i} style={{ width:'3px', height:'3px', borderRadius:'50%', background:'#854F0B' }}/>)}</div>
  </div>
</td>
<td colSpan={6} style={{ padding: '0.65rem 0.875rem' }}>
<div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
<div style={{ width: '22px', height: '22px', background: '#FAC775', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
<span style={{ fontSize: '0.75rem', color: '#633806', fontWeight: 700 }}>✎</span>
</div>
<input
type="text"
value={item.noteLabel || ''}
onChange={(e) => modifierArticle(index, 'noteLabel', e.target.value)}
placeholder="Commentaire libre, remarque, condition particulière…"
style={{ flex: 1, border: 'none', background: 'transparent', fontSize: '0.85rem', fontStyle: 'italic', color: '#633806', outline: 'none', fontFamily: 'Bricolage Grotesque, sans-serif' }}
/>
<span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '0.15rem 0.55rem', background: '#FAC775', color: '#633806', borderRadius: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Note</span>
</div>
</td>
<td style={{ padding: '0.5rem' }}>
<button onClick={() => supprimerArticle(index)} style={{ width: '28px', height: '28px', border: '1.5px solid #FEE2E2', background: 'white', borderRadius: '7px', color: '#EF4444', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
</td>
</tr>
);
} else {
// ── LIGNE ARTICLE (standard) ──
rows.push(
<tr key={`a-${index}`}
draggable
onDragStart={(e) => { dragSrcIndex.current = index; e.dataTransfer.effectAllowed = 'move'; }}
onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = '#F0FDF9'; }}
onDragLeave={(e) => { e.currentTarget.style.background = ''; }}
onDrop={(e) => { e.currentTarget.style.background = ''; deplacerItem(dragSrcIndex.current, index); }}
>
<td style={{ padding: '0.4rem 0.3rem', width: '32px', cursor: 'grab', userSelect: 'none' }}>
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
    <button onMouseDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setInsertMenu(insertMenu?.index === index ? null : { index, top: r.bottom + 4, left: r.left }) }} title="Insérer une ligne après" style={{ width:'22px', height:'22px', borderRadius:'6px', border:'1.5px solid #00D4AA', background: insertMenu?.index === index ? '#00D4AA' : 'white', color: insertMenu?.index === index ? 'white' : '#00D4AA', fontSize:'1rem', fontWeight:900, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0, lineHeight:1 }}>＋</button>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', opacity: 0.3 }}>{[0,1,2,3,4,5].map(i=><div key={i} style={{ width:'3px', height:'3px', borderRadius:'50%', background:'#64748B' }}/>)}</div>
  </div>
</td>
<td style={{ position: 'relative', overflow: 'visible' }}>
<div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
<input
type="text"
value={autocompleteIndex === index ? autocompleteSearch : item.description}
onChange={(e) => {
const val = e.target.value;
setAutocompleteIndex(index);
setAutocompleteSearch(val);
setShowAutocomplete(true);
modifierArticle(index, 'description', val);
}}
onFocus={() => {
setAutocompleteIndex(index);
setAutocompleteSearch(item.description);
}}
onBlur={() => setTimeout(() => { setShowAutocomplete(false); }, 200)}
placeholder="🔍 Référence ou désignation..."
data-autocomplete-input={index}
style={{ flex: 1 }}
/>
<button
title="Parcourir le catalogue"
onClick={() => ouvrirBrowseCatalogue(index)}
style={{
flexShrink: 0,
padding: '0.4rem 0.55rem',
background: 'linear-gradient(135deg, #0A2540, #00D4AA)',
border: 'none', borderRadius: '6px',
cursor: 'pointer', fontSize: '0.9rem',
color: 'white', fontWeight: 700,
lineHeight: 1,
boxShadow: '0 2px 6px rgba(0,212,170,0.3)'
}}
>📚</button>
</div>
{showAutocomplete && autocompleteIndex === index && getSuggestionsAutocomplete(autocompleteSearch).length > 0 && (
<div style={{
position: 'fixed',
top: (() => {
const el = document.querySelector('[data-autocomplete-input="' + index + '"]');
if (el) { const r = el.getBoundingClientRect(); return r.bottom + 4 + 'px'; }
return '50%';
})(),
left: (() => {
const el = document.querySelector('[data-autocomplete-input="' + index + '"]');
if (el) { const r = el.getBoundingClientRect(); return r.left + 'px'; }
return '50%';
})(),
width: (() => {
const el = document.querySelector('[data-autocomplete-input="' + index + '"]');
if (el) { return Math.max(el.getBoundingClientRect().width, 400) + 'px'; }
return '400px';
})(),
zIndex: 99999,
background: 'white',
border: '2px solid #00D4AA',
borderRadius: '10px',
boxShadow: '0 16px 48px rgba(0,0,0,0.25)',
maxHeight: '260px',
overflowY: 'auto'
}}>
<div style={{ padding: '0.4rem 0.75rem', background: 'linear-gradient(90deg,#0A2540,#00D4AA)', fontSize: '0.72rem', color: 'white', fontWeight: 600, borderRadius: '7px 7px 0 0' }}>
📚 {getSuggestionsAutocomplete(autocompleteSearch).length} article(s) trouvé(s) — cliquez pour insérer
</div>
{getSuggestionsAutocomplete(autocompleteSearch).map((art, si) => (
<div key={si}
onMouseDown={() => insererArticleDepuisCatalogue(index, art)}
style={{
padding: '0.65rem 0.75rem', cursor: 'pointer',
borderBottom: '1px solid #F1F5F9',
display: 'flex', justifyContent: 'space-between', alignItems: 'center',
background: 'white',
transition: 'background 0.1s'
}}
onMouseEnter={e => e.currentTarget.style.background = '#F0FDF9'}
onMouseLeave={e => e.currentTarget.style.background = 'white'}
>
<div style={{ flex: 1, minWidth: 0 }}>
<div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
<span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem', color: 'white', background: '#0A2540', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 700, whiteSpace: 'nowrap' }}>{art.codeArticle || art.ref}</span>
{art.ref && art.codeArticle && <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem', color: '#00D4AA', background: '#F0FDF9', padding: '0.1rem 0.35rem', borderRadius: '3px', whiteSpace: 'nowrap' }}>{art.ref}</span>}
<span style={{ fontSize: '0.875rem', color: '#1E293B', fontWeight: 600 }}>{art.designation}</span>
</div>
<div style={{ fontSize: '0.68rem', color: '#94A3B8', marginTop: '2px' }}>
{art.categorieFille && <span style={{ marginRight: '0.5rem' }}>📁 {art.categorieFille}</span>}
{art.categoreMere && <span>📂 {art.categoreMere}</span>}
</div>
</div>
<div style={{ textAlign: 'right', whiteSpace: 'nowrap', marginLeft: '1rem' }}>
<span style={{ fontSize: '0.72rem', color: '#64748B', display: 'block', background: '#F1F5F9', padding: '0.1rem 0.35rem', borderRadius: '3px', fontFamily: 'monospace' }}>{art.unite} · {art.uniteLibelle || ''}</span>
<span style={{ fontSize: '0.82rem', fontWeight: 700, color: art.prixUnitaire > 0 ? '#0A2540' : '#CBD5E1' }}>
{art.prixUnitaire > 0 ? art.prixUnitaire.toLocaleString('fr-FR') + ' XAF' : '— XAF'}
</span>
</div>
</div>
))}
</div>
)}
{/* Marque & Modèle/Référence */}
<div style={{ marginTop: '0.4rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
{/* Toggles */}
<div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
<label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', fontSize: '0.75rem', color: '#64748B', userSelect: 'none' }}>
<input
type="checkbox"
checked={item.showMarque || false}
onChange={e => modifierArticle(index, 'showMarque', e.target.checked)}
style={{ accentColor: '#00D4AA', width: '13px', height: '13px' }}
/>
<span>+ Marque</span>
</label>
<label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', fontSize: '0.75rem', color: '#64748B', userSelect: 'none' }}>
<input
type="checkbox"
checked={item.showModeleRef || false}
onChange={e => modifierArticle(index, 'showModeleRef', e.target.checked)}
style={{ accentColor: '#00D4AA', width: '13px', height: '13px' }}
/>
<span>+ Modèle / Référence</span>
</label>
</div>
{/* Champ Marque */}
{item.showMarque && (
<div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
<span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#0A2540', whiteSpace: 'nowrap', minWidth: '45px' }}>Marque :</span>
<input
type="text"
value={item.marque || ''}
onChange={e => modifierArticle(index, 'marque', e.target.value)}
placeholder="ex : ArcelorMittal"
style={{ flex: 1, padding: '0.3rem 0.5rem', border: '1.5px solid #00D4AA', borderRadius: '5px', fontSize: '0.8rem', background: '#F0FDF9' }}
/>
</div>
)}
{/* Champ Modèle / Référence */}
{item.showModeleRef && (
<div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
<span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#0A2540', whiteSpace: 'nowrap', minWidth: '45px' }}>Modèle :</span>
<input
type="text"
value={item.modeleRef || ''}
onChange={e => modifierArticle(index, 'modeleRef', e.target.value)}
placeholder="ex : DC01-1.0 / AM-REF-001"
style={{ flex: 1, padding: '0.3rem 0.5rem', border: '1.5px solid #00D4AA', borderRadius: '5px', fontSize: '0.8rem', background: '#F0FDF9' }}
/>
</div>
)}
</div>
</td>
<td>
<select
value={item.unite || 'PCS'}
onChange={(e) => modifierArticle(index, 'unite', e.target.value)}
style={{ width: '100%', padding: '0.5rem' }}
>
{UNITES.map(unite => (
<option key={unite.code} value={unite.code}>
{unite.code} - {unite.libelle}
</option>
))}
</select>
</td>
<td>
<input
type="number"
value={item.quantite}
onChange={(e) => modifierArticle(index, 'quantite', e.target.value)}
min="1"
/>
</td>
<td>
<input
type="number"
value={item.prixUnitaire}
onChange={(e) => modifierArticle(index, 'prixUnitaire', e.target.value)}
min="0"
placeholder="0"
/>
</td>
<td>
<div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
<select
value={item.remiseType || 'aucune'}
onChange={(e) => modifierArticle(index, 'remiseType', e.target.value)}
style={{ width: '80px', padding: '0.4rem', fontSize: '0.9rem' }}
>
<option value="aucune">-</option>
<option value="pourcentage">%</option>
<option value="montant">FCFA</option>
</select>
{item.remiseType && item.remiseType !== 'aucune' && (
<input
type="number"
value={item.remiseValeur || 0}
onChange={(e) => modifierArticle(index, 'remiseValeur', e.target.value)}
placeholder="Valeur"
min="0"
style={{ width: '90px', padding: '0.4rem' }}
/>
)}
</div>
</td>
<td style={{ fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>
{formatMontant(item.montantApresRemise || (item.quantite * item.prixUnitaire))}
</td>
<td>
<button className="btn btn-danger btn-icon" onClick={() => supprimerArticle(index)}>🗑️</button>
</td>
</tr>
);
}

// Après la dernière ligne d'un groupe (avant une section ou fin de liste),
// insérer le sous-total si une section parent existe
const prochaine = currentDoc.items[index + 1];
const estFinDeGroupe = !prochaine || (prochaine.lineType || 'article') === 'section';
if (lineType !== 'section' && estFinDeGroupe) {
let sectionParentIdx = null;
for (let j = index; j >= 0; j--) {
if ((currentDoc.items[j].lineType || 'article') === 'section') { sectionParentIdx = j; break; }
}
if (sectionParentIdx !== null && sousTotaux[sectionParentIdx] !== undefined) {
const secLabel = currentDoc.items[sectionParentIdx].sectionLabel || 'Section';
const stSuffix = currentDoc.taxMode === 'TTC' ? 'TTC' : 'HT';
rows.push(
<tr key={`st-${sectionParentIdx}-${index}`}>
<td colSpan={8} style={{ padding: 0 }}>
<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', padding: '0.5rem 0.875rem', background: '#F1F5F9', borderTop: '1.5px dashed #CBD5E1' }}>
<span style={{ fontSize: '0.78rem', color: '#64748B', fontWeight: 600 }}>
Sous-total {secLabel}
{currentDoc.afficherModeTaxation && <span style={{ marginLeft: '4px', fontSize: '0.68rem', fontWeight: 700, padding: '0.1rem 0.4rem', borderRadius: '6px', background: currentDoc.taxMode === 'TTC' ? 'rgba(16,185,129,0.12)' : 'rgba(59,130,246,0.12)', color: currentDoc.taxMode === 'TTC' ? '#059669' : '#2563EB' }}>{stSuffix}</span>}
:
</span>
<div style={{ width: '1px', height: '16px', background: '#CBD5E1' }}></div>
<span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.875rem', fontWeight: 700, color: '#1E293B' }}>
{formatMontant(sousTotaux[sectionParentIdx])}
</span>
</div>
</td>
</tr>
);
}
}
});
return rows;
})()}
</tbody>
</table>
</div>

{/* Menu d'insertion contextuel (position fixed pour passer au-dessus de overflow:hidden) */}
{insertMenu && (
<div
  onClick={e => e.stopPropagation()}
  style={{
    position: 'fixed', top: insertMenu.top, left: insertMenu.left,
    zIndex: 99999, background: 'white',
    border: '1.5px solid #E2E8F0', borderRadius: '10px',
    boxShadow: '0 8px 28px rgba(0,0,0,0.18)', minWidth: '170px', overflow: 'hidden'
  }}
>
  <div style={{ padding: '0.4rem 0.75rem', background: 'linear-gradient(90deg,#0A2540,#1E3A5F)', fontSize: '0.7rem', color: 'white', fontWeight: 600, letterSpacing: '0.04em' }}>
    Insérer après cette ligne
  </div>
  {[
    { type: 'article', label: '＋ Article',  bg: '#F0FDF9', color: '#0A2540',  border: '#00D4AA' },
    { type: 'section', label: '§ Section',   bg: '#F0F9E8', color: '#27500A',  border: '#3B6D11' },
    { type: 'note',    label: '✎ Note',      bg: '#FEF3E2', color: '#633806',  border: '#BA7517' },
  ].map(({ type, label, bg, color, border }) => (
    <button
      key={type}
      onClick={() => insererLigne(insertMenu.index, type)}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        padding: '0.6rem 1rem', border: 'none', borderBottom: `1px solid #F1F5F9`,
        background: 'white', color, fontSize: '0.85rem', fontWeight: 600,
        cursor: 'pointer', transition: 'background 0.1s'
      }}
      onMouseEnter={e => { e.currentTarget.style.background = bg; e.currentTarget.style.borderLeft = `3px solid ${border}`; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderLeft = 'none'; }}
    >{label}</button>
  ))}
</div>
)}

</div>


{/* Remise Globale */}
<div style={{ marginTop: '1.5rem', padding: '1rem', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
<h4 style={{ marginBottom: '1rem', color: 'var(--primary-color)', fontSize: '1rem' }}>💰 Remise Globale</h4>
<div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
<div>
<label style={{ fontSize: '0.8rem', color: '#64748B', display: 'block', marginBottom: '0.25rem' }}>Type</label>
<select
className="form-select"
style={{ width: '160px' }}
value={currentDoc.remiseGlobale?.type || 'aucune'}
onChange={(e) => {
const newType = e.target.value;
const updatedDoc = {
...currentDoc,
remiseGlobale: { type: newType, valeur: 0, montant: 0 }
};
const totaux = calculerTotauxDocument(updatedDoc);
setCurrentDoc({ ...updatedDoc, ...totaux });
}}
>
<option value="aucune">Aucune remise</option>
<option value="pourcentage">Pourcentage (%)</option>
<option value="montant">Montant fixe (FCFA)</option>
</select>
</div>
{currentDoc.remiseGlobale?.type && currentDoc.remiseGlobale.type !== 'aucune' && (
<>
<div>
<label style={{ fontSize: '0.8rem', color: '#64748B', display: 'block', marginBottom: '0.25rem' }}>
{currentDoc.remiseGlobale.type === 'pourcentage' ? 'Valeur (%)' : 'Montant (FCFA)'}
</label>
<input
type="number"
className="form-input"
style={{ width: '130px' }}
value={currentDoc.remiseGlobale.valeur || 0}
onChange={(e) => setCurrentDoc({
...currentDoc,
remiseGlobale: {
...currentDoc.remiseGlobale,
valeur: parseFloat(e.target.value) || 0
}
})}
min="0"
placeholder={currentDoc.remiseGlobale.type === 'pourcentage' ? 'Ex: 10' : 'Ex: 500'}
/>
</div>
<button
className="btn btn-primary btn-sm"
style={{ marginBottom: '2px' }}
onClick={() => {
const totaux = calculerTotauxDocument(currentDoc);
setCurrentDoc(prev => ({ ...prev, ...totaux }));
}}
>
✓ Appliquer
</button>
{currentDoc.remiseGlobale.montant > 0 && (
<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.75rem', background: '#DCFCE7', borderRadius: '6px', border: '1px solid #BBF7D0', fontSize: '0.85rem', marginBottom: '2px' }}>
<span style={{ color: '#166534', fontWeight: '600' }}>
✓ Remise : -{formatMontant(currentDoc.remiseGlobale.montant)}
</span>
<button
style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontWeight: 'bold', fontSize: '1rem', padding: '0 0.25rem' }}
onClick={() => {
const updatedDoc = {
...currentDoc,
remiseGlobale: { type: 'aucune', valeur: 0, montant: 0 }
};
const totaux = calculerTotauxDocument(updatedDoc);
setCurrentDoc({ ...updatedDoc, ...totaux });
}}
title="Annuler la remise"
>×</button>
</div>
)}
</>
)}
</div>
</div>

{/* Frais Supplémentaires */}
<div style={{ marginTop: '1.5rem', padding: '1rem', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
<h4 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '1rem' }}>📦 Frais Supplémentaires</h4>
<button
className="btn btn-success btn-sm"
onClick={() => {
setCurrentDoc({
...currentDoc,
fraisSupplementaires: [
...(currentDoc.fraisSupplementaires || []),
{ libelle: '', montant: 0, taxable: true }
]
});
}}
>
+ Ajouter frais
</button>
</div>
{currentDoc.fraisSupplementaires && currentDoc.fraisSupplementaires.length > 0 && (
<div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
{currentDoc.taxMode === 'TTC' && (
<div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 90px 40px', gap: '0.5rem', paddingBottom: '0.25rem', borderBottom: '1px solid #E2E8F0' }}>
<span style={{ fontSize: '0.75rem', color: '#64748B', fontWeight: '600' }}>Libellé</span>
<span style={{ fontSize: '0.75rem', color: '#64748B', fontWeight: '600' }}>Montant</span>
<span style={{ fontSize: '0.75rem', color: '#64748B', fontWeight: '600', textAlign: 'center' }}>TVA ?</span>
<span></span>
</div>
)}
{currentDoc.fraisSupplementaires.map((frais, index) => {
const isTaxable = frais.taxable !== false;
const isHT = currentDoc.taxMode !== 'TTC';
return (
<div key={index} style={{ display: 'grid', gridTemplateColumns: currentDoc.taxMode === 'TTC' ? '1fr 150px 90px 40px' : '1fr 150px 40px', gap: '0.5rem', alignItems: 'center' }}>
<input
type="text"
className="form-input"
value={frais.libelle}
onChange={(e) => {
const newFrais = [...currentDoc.fraisSupplementaires];
newFrais[index] = { ...newFrais[index], libelle: e.target.value };
const updated = { ...currentDoc, fraisSupplementaires: newFrais };
const totaux = calculerTotauxDocument(updated);
setCurrentDoc({ ...updated, ...totaux });
}}
placeholder="Libellé (ex: Frais de livraison)"
/>
<input
type="number"
className="form-input"
value={frais.montant}
onChange={(e) => {
const newFrais = [...currentDoc.fraisSupplementaires];
newFrais[index] = { ...newFrais[index], montant: parseFloat(e.target.value) || 0 };
const updated = { ...currentDoc, fraisSupplementaires: newFrais };
const totaux = calculerTotauxDocument(updated);
setCurrentDoc({ ...updated, ...totaux });
}}
min="0"
placeholder="Montant"
/>
{currentDoc.taxMode === 'TTC' && (
<button
title={isHT ? 'Non applicable en mode HT' : (isTaxable ? 'TVA incluse — cliquer pour exclure' : 'TVA exclue — cliquer pour inclure')}
disabled={isHT}
onClick={() => {
const newFrais = [...currentDoc.fraisSupplementaires];
newFrais[index] = { ...newFrais[index], taxable: !isTaxable };
const updated = { ...currentDoc, fraisSupplementaires: newFrais };
const totaux = calculerTotauxDocument(updated);
setCurrentDoc({ ...updated, ...totaux });
}}
style={{
padding: '0.35rem 0.5rem',
borderRadius: '6px',
border: `1px solid ${isHT ? '#CBD5E1' : (isTaxable ? '#10B981' : '#94A3B8')}`,
background: isHT ? '#F1F5F9' : (isTaxable ? '#ECFDF5' : '#F8FAFC'),
color: isHT ? '#94A3B8' : (isTaxable ? '#059669' : '#64748B'),
cursor: isHT ? 'not-allowed' : 'pointer',
fontSize: '0.75rem',
fontWeight: '600',
whiteSpace: 'nowrap',
textAlign: 'center',
lineHeight: '1'
}}
>
{isTaxable ? '✓ TVA' : '✗ TVA'}
</button>
)}
<button
className="btn btn-danger btn-icon"
onClick={() => {
const newFrais = currentDoc.fraisSupplementaires.filter((_, i) => i !== index);
const updated = { ...currentDoc, fraisSupplementaires: newFrais };
const totaux = calculerTotauxDocument(updated);
setCurrentDoc({ ...updated, ...totaux });
}}
>
×
</button>
</div>
);
})}
</div>
)}
</div>

{/* Totaux */}
<div className="totals-section">
<div className="total-row">
<span className="total-label">{currentDoc.afficherModeTaxation ? 'Sous-total HT' : 'Sous-total'}</span>
<span className="total-value">{formatMontant(currentDoc.sousTotal)}</span>
</div>
{currentDoc.totalRemises > 0 && (
<div className="total-row" style={{ color: '#10B981' }}>
<span className="total-label">Remises</span>
<span className="total-value">-{formatMontant(currentDoc.totalRemises)}</span>
</div>
)}
{currentDoc.fraisSupplementaires && currentDoc.fraisSupplementaires.length > 0 && (
<>
{currentDoc.fraisSupplementaires.map((frais, index) => frais.montant > 0 && (
<div key={index} className="total-row" style={{ fontSize: '0.9rem', color: '#64748B' }}>
<span className="total-label">
+ {frais.libelle || 'Frais'}
{currentDoc.taxMode === 'TTC' && frais.taxable === false && (
<span style={{ marginLeft: '4px', fontSize: '0.7rem', background: '#F1F5F9', color: '#94A3B8', padding: '1px 4px', borderRadius: '3px', fontWeight: '600' }}>HT</span>
)}
</span>
<span className="total-value">{formatMontant(frais.montant)}</span>
</div>
))}
</>
)}
{currentDoc.taxMode === 'TTC' && (
<div className="total-row">
<span className="total-label">TVA ({settings.tauxTVA || 19.25}%)</span>
<span className="total-value">{formatMontant(currentDoc.tva)}</span>
</div>
)}
<div className="total-row" style={{ borderTop: '2px solid var(--primary-color)', paddingTop: '0.5rem', marginTop: '0.5rem' }}>
<span className="total-label" style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
{currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'Total TTC' : 'Total HT') : 'Total'}
</span>
<span className="total-value" style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{formatMontant(currentDoc.total)}</span>
</div>
{currentDoc.montantPaye > 0 && (
<>
<div className="total-row" style={{ color: '#10B981' }}>
<span className="total-label">Montant payé</span>
<span className="total-value">-{formatMontant(currentDoc.montantPaye)}</span>
</div>
<div className="total-row" style={{ color: '#EF4444', fontWeight: 'bold' }}>
<span className="total-label">Reste à payer</span>
<span className="total-value">{formatMontant(currentDoc.montantRestant)}</span>
</div>
</>
)}
{currentDoc.taxMode === 'HT' && (
<div style={{
fontSize: '0.8rem',
color: 'var(--text-light)',
marginTop: '0.5rem',
fontStyle: 'italic',
textAlign: 'right'
}}>
ℹ️ Document sans TVA
</div>
)}
</div>

{/* Informations supplémentaires */}
<div className="form-grid" style={{ marginTop: '2rem' }}>
{currentDoc.type === 'devis' && (
<div className="form-group">
<label className="form-label">Validité (jours)</label>
<input
type="number"
className="form-input"
value={currentDoc.validiteDevis || 30}
onChange={(e) => setCurrentDoc({...currentDoc, validiteDevis: parseInt(e.target.value)})}
min="1"
/>
</div>
)}
<div className="form-group">
<label className="form-label">Statut</label>
<select
className="form-select"
value={currentDoc.statut}
onChange={(e) => handleStatutChange(e.target.value)}
>
{currentDoc.type === 'devis' ? (
<>
<option value="en-attente">En attente</option>
<option value="accepte">Acceptée</option>
<option value="refuse">Refusée</option>
</>
) : (
<>
<option value="impaye">Impayée</option>
<option value="partiel">Partiellement payée</option>
<option value="paye">Payée</option>
<option value="annule">Annulée</option>
</>
)}
</select>
</div>

{/* Afficher la raison d'annulation si le statut est annulé */}
{currentDoc.type === 'facture' && currentDoc.statut === 'annule' && currentDoc.raisonAnnulation && (
<div className="form-group">
<label className="form-label" style={{ color: '#EF4444' }}>❌ Raison de l'annulation</label>
<div style={{
padding: '1rem',
background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
border: '2px solid #EF4444',
borderRadius: '8px',
color: '#991B1B',
fontSize: '0.875rem',
lineHeight: '1.6'
}}>
{currentDoc.raisonAnnulation}
</div>
{currentDoc.dateAnnulation && (
<div style={{
fontSize: '0.75rem',
color: '#64748B',
marginTop: '0.5rem',
fontStyle: 'italic'
}}>
Annulée le {new Date(currentDoc.dateAnnulation).toLocaleDateString('fr-FR')} à {new Date(currentDoc.dateAnnulation).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
</div>
)}
</div>
)}

{/* Montant avancé pour statut "Partiellement payée" */}
{currentDoc.type === 'facture' && currentDoc.statut === 'partiel' && (
<div className="form-group">
<label className="form-label">Montant avancé (FCFA) *</label>
<input
type="number"
className="form-input"
value={currentDoc.montantAvance || 0}
onChange={(e) => {
const montant = parseFloat(e.target.value) || 0;
const restant = Math.max(0, (currentDoc.total || 0) - montant);
// Mettre à jour le journal des paiements
const paiementsExistants = currentDoc.paiements || [];
const paiementAvance = paiementsExistants.find(p => p.type === 'avance');
let nouveauxPaiements;
if (montant > 0) {
  const entree = {
    id: paiementAvance?.id || Date.now(),
    date: new Date().toISOString().split('T')[0],
    montant: montant,
    methode: currentDoc.methodePaiement || 'virement',
    type: 'avance',
    reference: '',
    notes: 'Avance partielle'
  };
  nouveauxPaiements = paiementsExistants.filter(p => p.type !== 'avance');
  nouveauxPaiements = [entree, ...nouveauxPaiements];
} else {
  nouveauxPaiements = paiementsExistants.filter(p => p.type !== 'avance');
}
setCurrentDoc({
  ...currentDoc,
  montantAvance: montant,
  montantPaye: montant,
  montantRestant: restant,
  paiements: nouveauxPaiements,
});
}}
min="0"
max={currentDoc.total}
step="1"
placeholder="Entrez le montant déjà payé"
style={{
borderColor: '#F59E0B',
borderWidth: '2px'
}}
/>
<div style={{
fontSize: '0.8rem',
color: '#64748B',
marginTop: '0.5rem'
}}>
Montant restant à payer : <strong style={{ color: '#EF4444' }}>
{formatMontant(currentDoc.total - (currentDoc.montantAvance || 0))}
</strong>
</div>
{currentDoc.montantAvance > currentDoc.total && (
<div style={{
fontSize: '0.8rem',
color: '#EF4444',
marginTop: '0.5rem',
fontWeight: 600
}}>
⚠️ Le montant avancé ne peut pas dépasser le total de la facture !
</div>
)}
</div>
)}
</div>

<div className="form-group" style={{ marginTop: '1rem' }}>
<label className="form-label">Notes / Conditions</label>
<textarea
className="form-textarea"
value={currentDoc.notes || ''}
onChange={(e) => setCurrentDoc({...currentDoc, notes: e.target.value})}
placeholder="Conditions de paiement, garanties, etc."
/>
</div>

<div className="form-group" style={{ marginTop: '1rem' }}>
<label className="form-label">Méthode de paiement</label>
<select
className="form-select"
value={currentDoc.methodePaiement || 'toutes'}
onChange={(e) => setCurrentDoc({...currentDoc, methodePaiement: e.target.value})}
>
{METHODES_PAIEMENT.map(methode => (
<option key={methode.code} value={methode.code}>
{methode.libelle}
</option>
))}
</select>
</div>

{/* Date d'échéance et Pénalités (uniquement pour les factures) */}
{currentDoc.type === 'facture' && (
<div style={{ marginTop: '1.5rem', padding: '1rem', background: '#FEF3C7', borderRadius: '8px', border: '1px solid #F59E0B' }}>
<h4 style={{ marginBottom: '1rem', color: '#D97706', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
⏰ Échéance et Pénalités de Retard
</h4>

<div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
<div className="form-group">
<label className="form-label">Délai de paiement (jours)</label>
<input
type="number"
className="form-input"
value={currentDoc.delaiPaiement || 30}
onChange={(e) => {
const delai = parseInt(e.target.value) || 30;
const echeance = calculerDateEcheance(currentDoc.date, delai);
setCurrentDoc({
...currentDoc,
delaiPaiement: delai,
dateEcheance: echeance
});
}}
min="1"
/>
</div>

<div className="form-group">
<label className="form-label">Date d'échéance</label>
<input
type="date"
className="form-input"
value={currentDoc.dateEcheance || ''}
onChange={(e) => setCurrentDoc({...currentDoc, dateEcheance: e.target.value})}
/>
</div>
</div>

<div className="form-group" style={{ marginTop: '1rem' }}>
<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
<input
type="checkbox"
checked={currentDoc.penalitesRetard?.actives || false}
onChange={(e) => setCurrentDoc({
...currentDoc,
penalitesRetard: {
...(currentDoc.penalitesRetard || {}),
actives: e.target.checked,
taux: currentDoc.penalitesRetard?.taux || 0.03,
montant: 0,
joursRetard: 0
}
})}
style={{ width: 'auto' }}
/>
<span style={{ fontWeight: '500' }}>Appliquer des pénalités de retard</span>
</label>
</div>

{currentDoc.penalitesRetard?.actives && (
<div className="form-group" style={{ marginTop: '1rem' }}>
<label className="form-label">Taux journalier de pénalités (%)</label>
<input
type="number"
className="form-input"
step="0.01"
value={currentDoc.penalitesRetard?.taux || 0.03}
onChange={(e) => setCurrentDoc({
...currentDoc,
penalitesRetard: {
...currentDoc.penalitesRetard,
taux: parseFloat(e.target.value) || 0.03
}
})}
min="0"
max="10"
/>
<small style={{ color: '#78716C', fontSize: '0.85rem', display: 'block', marginTop: '0.25rem' }}>
Exemple : 0.03 = 0.03% par jour de retard
</small>
</div>
)}

{currentDoc.penalitesRetard?.joursRetard > 0 && (
<div style={{
marginTop: '1rem',
padding: '0.75rem',
background: '#FEE2E2',
border: '2px solid #EF4444',
borderRadius: '6px',
color: '#991B1B'
}}>
<strong style={{ display: 'block', marginBottom: '0.5rem' }}>⚠️ RETARD DE PAIEMENT DÉTECTÉ</strong>
<div style={{ fontSize: '0.9rem' }}>
<div>Retard : <strong>{currentDoc.penalitesRetard.joursRetard} jours</strong></div>
<div>Pénalités : <strong>{formatMontant(currentDoc.penalitesRetard.montant)}</strong></div>
<div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#7F1D1D' }}>
Calcul : {formatMontant(currentDoc.montantRestant || currentDoc.total)} × {currentDoc.penalitesRetard.taux}% × {currentDoc.penalitesRetard.joursRetard} jours
</div>
</div>
</div>
)}
</div>
)}

{/* Journal des Paiements (uniquement pour les factures) */}
{currentDoc.type === 'facture' && (
<div style={{ marginTop: '1.5rem', padding: '1rem', background: '#ECFDF5', borderRadius: '8px', border: '1px solid #10B981' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
<h4 style={{ margin: 0, color: '#047857', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
💳 Journal des Paiements
</h4>
<button
className="btn btn-success btn-sm"
onClick={() => {
const nouveauPaiement = {
id: Date.now(),
date: new Date().toISOString().split('T')[0],
montant: 0,
methode: 'virement',
reference: '',
notes: ''
};
setCurrentDoc({
...currentDoc,
paiements: [...(currentDoc.paiements || []), nouveauPaiement]
});
}}
style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
>
+ Nouveau paiement
</button>
</div>

{currentDoc.paiements && currentDoc.paiements.length > 0 ? (
<>
<div style={{ overflowX: 'auto' }}>
<table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
<thead>
<tr style={{ background: '#D1FAE5', borderBottom: '2px solid #10B981' }}>
<th style={{ padding: '0.5rem', textAlign: 'left' }}>Date</th>
<th style={{ padding: '0.5rem', textAlign: 'right' }}>Montant</th>
<th style={{ padding: '0.5rem', textAlign: 'left' }}>Méthode</th>
<th style={{ padding: '0.5rem', textAlign: 'left' }}>Référence / Téléphone</th>
<th style={{ padding: '0.5rem', textAlign: 'left' }}>Notes / ID Transaction</th>
<th style={{ padding: '0.5rem', textAlign: 'center', width: '90px' }}>Actions</th>
</tr>
</thead>
<tbody>
{currentDoc.paiements.map((paiement, index) => (
<tr key={paiement.id || index} style={{ borderBottom: '1px solid #A7F3D0' }}>
<td style={{ padding: '0.5rem' }}>
<input
type="date"
className="form-input"
value={paiement.date || ''}
onChange={(e) => {
const newPaiements = [...currentDoc.paiements];
newPaiements[index].date = e.target.value;
setCurrentDoc({...currentDoc, paiements: newPaiements});
}}
style={{ width: '130px', padding: '0.3rem', fontSize: '0.85rem' }}
/>
</td>
<td style={{ padding: '0.5rem' }}>
<input
type="number"
className="form-input"
value={paiement.montant || 0}
onChange={(e) => {
const newPaiements = [...currentDoc.paiements];
newPaiements[index].montant = parseFloat(e.target.value) || 0;
setCurrentDoc({...currentDoc, paiements: newPaiements});
}}
min="0"
placeholder="Montant"
style={{ width: '110px', padding: '0.3rem', fontSize: '0.85rem', textAlign: 'right' }}
/>
</td>
<td style={{ padding: '0.5rem' }}>
<select
className="form-select"
value={paiement.methode || 'virement'}
onChange={(e) => {
const newPaiements = [...currentDoc.paiements];
newPaiements[index].methode = e.target.value;
setCurrentDoc({...currentDoc, paiements: newPaiements});
}}
style={{ width: '120px', padding: '0.3rem', fontSize: '0.85rem' }}
>
<option value="virement">Virement</option>
<option value="cheque">Chèque</option>
<option value="especes">Espèces</option>
<option value="carte">Carte bancaire</option>
<option value="mobile">📱 Mobile Money</option>
</select>
</td>
{/* Champs conditionnels Mobile Money */}
{paiement.methode === 'mobile' ? (
<>
<td style={{ padding: '0.5rem' }}>
<input
type="tel"
className="form-input"
value={paiement.telephoneMobile || ''}
onChange={(e) => {
const newPaiements = [...currentDoc.paiements];
newPaiements[index].telephoneMobile = e.target.value;
setCurrentDoc({...currentDoc, paiements: newPaiements});
}}
placeholder="📱 N° téléphone"
style={{ width: '140px', padding: '0.3rem', fontSize: '0.85rem', border: '1.5px solid #6366F1', borderRadius: '6px' }}
/>
</td>
<td style={{ padding: '0.5rem' }}>
<input
type="text"
className="form-input"
value={paiement.idTransaction || ''}
onChange={(e) => {
const newPaiements = [...currentDoc.paiements];
newPaiements[index].idTransaction = e.target.value;
setCurrentDoc({...currentDoc, paiements: newPaiements});
}}
placeholder="🔑 ID transaction"
style={{ width: '140px', padding: '0.3rem', fontSize: '0.85rem', border: '1.5px solid #6366F1', borderRadius: '6px' }}
/>
</td>
</>
) : (
<>
<td style={{ padding: '0.5rem' }}>
<input
type="text"
className="form-input"
value={paiement.reference || ''}
onChange={(e) => {
const newPaiements = [...currentDoc.paiements];
newPaiements[index].reference = e.target.value;
setCurrentDoc({...currentDoc, paiements: newPaiements});
}}
placeholder="N° chèque, réf..."
style={{ width: '140px', padding: '0.3rem', fontSize: '0.85rem' }}
/>
</td>
<td style={{ padding: '0.5rem' }}>
<input
type="text"
className="form-input"
value={paiement.notes || ''}
onChange={(e) => {
const newPaiements = [...currentDoc.paiements];
newPaiements[index].notes = e.target.value;
setCurrentDoc({...currentDoc, paiements: newPaiements});
}}
placeholder="Commentaire..."
style={{ width: '140px', padding: '0.3rem', fontSize: '0.85rem' }}
/>
</td>
</>
)}
<td style={{ padding: '0.5rem', textAlign: 'center' }}>
<div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'center' }}>
<button
className="btn btn-danger btn-icon"
onClick={() => {
const newPaiements = currentDoc.paiements.filter((_, i) => i !== index);
setCurrentDoc({...currentDoc, paiements: newPaiements});
}}
style={{ fontSize: '0.85rem', padding: '0.2rem 0.5rem' }}
title="Supprimer ce paiement"
>
🗑️
</button>
<button
className="btn btn-primary btn-icon"
onClick={() => {
setRecuPaiement({ paiement, doc: currentDoc });
setShowRecuModal(true);
}}
style={{ fontSize: '0.85rem', padding: '0.2rem 0.5rem', background: '#6366F1', border: 'none', color: 'white', borderRadius: '6px', cursor: 'pointer' }}
title="Générer le reçu de paiement"
>
🧾
</button>
</div>
</td>
</tr>
))}
</tbody>
</table>
</div>

{/* Résumé des paiements */}
<div style={{
marginTop: '1rem',
padding: '0.75rem',
background: '#D1FAE5',
borderRadius: '6px',
display: 'grid',
gridTemplateColumns: '1fr 1fr 1fr',
gap: '1rem',
fontSize: '0.9rem'
}}>
<div style={{ textAlign: 'center' }}>
<div style={{ color: '#047857', fontSize: '0.75rem', marginBottom: '0.25rem' }}>TOTAL PAYÉ</div>
<div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 'bold', color: '#065F46', fontSize: '1.1rem' }}>
{formatMontant((currentDoc.montantAvance || 0) + (currentDoc.paiements || []).filter(p => p.type !== 'avance').reduce((sum, p) => sum + (p.montant || 0), 0))}
</div>
</div>
<div style={{ textAlign: 'center' }}>
<div style={{ color: '#047857', fontSize: '0.75rem', marginBottom: '0.25rem' }}>RESTE À PAYER</div>
<div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 'bold', color: '#DC2626', fontSize: '1.1rem' }}>
{formatMontant(currentDoc.total - (currentDoc.montantAvance || 0) - (currentDoc.paiements || []).filter(p => p.type !== 'avance').reduce((sum, p) => sum + (p.montant || 0), 0))}
</div>
</div>
<div style={{ textAlign: 'center' }}>
<div style={{ color: '#047857', fontSize: '0.75rem', marginBottom: '0.25rem' }}>NOMBRE PAIEMENTS</div>
<div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 'bold', color: '#047857', fontSize: '1.1rem' }}>
{(currentDoc.paiements || []).length}
</div>
</div>
</div>
</>
) : (
<div style={{
textAlign: 'center',
padding: '2rem',
color: '#6B7280',
fontStyle: 'italic',
fontSize: '0.9rem'
}}>
Aucun paiement enregistré. Cliquez sur "+ Nouveau paiement" pour commencer.
</div>
)}
</div>
)}
</div>

<div className="modal-footer">
<button className="btn btn-secondary" onClick={fermerEditeur}>
Annuler
</button>
{/* Bouton avoir dans l'édition — uniquement pour une facture déjà sauvegardée et non-annulée */}
{currentDoc && currentDoc.type === 'facture' &&
documents.find(d => d.id === currentDoc.id) &&
currentDoc.statut !== 'annule' && (
<button
onClick={async () => {
const docSauvegarde = documents.find(d => d.id === currentDoc.id);
await fermerEditeur();
setTimeout(() => ouvrirModalAvoir(docSauvegarde || currentDoc), 150);
}}
style={{
padding: '0.75rem 1.5rem',
background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
color: 'white', border: 'none', borderRadius: '10px',
cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem',
display: 'flex', alignItems: 'center', gap: '0.5rem',
boxShadow: '0 4px 14px rgba(124,58,237,0.4)'
}}>
🔵 Émettre un avoir
</button>
)}
<button className="btn btn-success" onClick={sauvegarderDocument} disabled={isSaving} style={{opacity: isSaving ? 0.6 : 1, cursor: isSaving ? 'not-allowed' : 'pointer'}}>
{isSaving ? '⏳ Sauvegarde...' : '💾 Sauvegarder'}
</button>
</div>
</div>
</div>
)}

{/* Modal Prévisualisation */}
{showPreview && currentDoc && (
<div className="modal-overlay">
<div className="modal-content">
<div className="modal-header">
<h2 className="modal-title">
👁️ Prévisualisation - {currentDoc.numero}
</h2>
<button className="close-btn" onClick={() => setShowPreview(false)}>✕</button>
</div>

<div className="modal-body">
{(() => {
// Déterminer le modèle à utiliser
const modeleActif = currentDoc.type === 'devis'
? (settings.modeleDevis || 'classique')
: (settings.modeleFacture || 'classique');

// Rendu selon le modèle sélectionné
if (modeleActif === 'moderne') {
// MODÈLE MODERNE - Design Professionnel Inspiré
return (
<div className="preview-container" style={{
background: '#F5F5F0',
padding: '0.625rem',
fontFamily: 'Arial, sans-serif'
}}>
{/* Filigrane en arrière-plan */}
<div style={{
position: 'absolute',
top: '50%',
left: '50%',
transform: 'translate(-50%, -50%) rotate(-45deg)',
fontSize: '15rem',
fontWeight: 'bold',
color: 'rgba(0, 0, 0, 0.015)',
pointerEvents: 'none',
zIndex: 0,
letterSpacing: '1rem'
}}>
{currentDoc.type === 'devis' ? 'DEVIS' : 'FACTURE'}
</div>

{/* Contenu principal */}
<div style={{ position: 'relative', zIndex: 1 }}>
{/* Header supérieur avec bordures verticales */}
<div style={{
display: 'grid',
gridTemplateColumns: '140px 1fr 280px',
alignItems: 'flex-start',
marginBottom: '1rem',
paddingBottom: '1rem',
borderBottom: '3px solid #2C5F5D'
}}>
{/* Gauche - Logo dans cadre orange avec bordure droite */}
<div style={{
borderRight: '2px solid #CBD5E0',
paddingRight: '1rem',
display: 'flex',
alignItems: 'flex-start'
}}>
<div style={{
width: '120px',
height: '120px',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
border: '3px solid #F5A623',
borderRadius: '12px',
background: 'white',
padding: '0.75rem',
boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
}}>
{settings.logo ? (
<img src={settings.logo} alt="Logo" style={{
maxWidth: '100%',
maxHeight: '100%',
objectFit: 'contain'
}} />
) : (
<div style={{
fontSize: '0.7rem',
color: '#94A3B8',
textAlign: 'center',
fontWeight: 'bold'
}}>
LOGO
</div>
)}
</div>
</div>

{/* Centre - Info entreprise avec bordure droite */}
<div style={{
borderRight: '2px solid #CBD5E0',
paddingLeft: '1.5rem',
paddingRight: '1.5rem'
}}>
<div style={{
fontSize: '1.4rem',
fontWeight: 'bold',
color: '#2C5F5D',
marginBottom: '0.5rem',
textTransform: 'uppercase',
letterSpacing: '0.5px'
}}>
{settings.raisonSociale}
</div>
<div style={{ fontSize: '0.75rem', color: '#64748B', lineHeight: '1.6' }}>
{settings.adresse && <div>{settings.adresse}</div>}
{settings.telephone && <div>Tél : {settings.telephone}</div>}
{settings.email && <div>{settings.email}</div>}
{settings.siteWeb && <div>{settings.siteWeb}</div>}
<div style={{ marginTop: '0.5rem' }}>
{settings.nui && <div>NUI : {settings.nui}</div>}
{settings.rccm && <div>RCCM : {settings.rccm}</div>}
</div>
</div>
</div>

{/* Droite - Numéro facture */}
<div style={{ paddingLeft: '1.5rem' }}>
{/* Numéro de facture */}
<div style={{
background: '#FFFFFF',
color: '#000000',
padding: '0.75rem 1.25rem',
borderRadius: '8px',
marginBottom: '1rem',
boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
}}>
<div style={{ fontSize: '0.65rem', opacity: 0.8, marginBottom: '0.25rem', letterSpacing: '0.5px' }}>
{currentDoc.type === 'devis' ? 'DEVIS N°' : 'FACTURE N°'}
</div>
<div style={{ fontSize: '0.9rem', fontWeight: 'bold', fontFamily: 'monospace', letterSpacing: '0.5px' }}>
{currentDoc.numero}
</div>
</div>

{/* Dates */}
<div style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.5rem' }}>
<div><strong>DATE :</strong> {new Date(currentDoc.date).toLocaleDateString('fr-FR')}</div>
<div>DATE : {new Date(currentDoc.date).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }).replace(',', ' :').toUpperCase()}</div>
</div>

{/* Validité du devis (uniquement pour les devis) */}
{currentDoc.type === 'devis' && (
<div style={{
fontSize: '0.7rem',
color: '#2C5F5D',
fontWeight: 'bold',
padding: '0.5rem',
background: '#E0F2F1',
borderRadius: '4px',
border: '1px solid #2C5F5D'
}}>
Validité : {currentDoc.validiteDevis || 30} jours
</div>
)}
</div>
</div>

{/* Barre FACTURE avec fond teal */}
<div style={{
background: '#FFFFFF',
color: '#000000',
padding: '1rem 2rem',
marginBottom: '1.5rem',
borderRadius: '0',
fontSize: '1.75rem',
fontWeight: 'bold',
letterSpacing: '0.3em',
border: '2px solid #000000'
}}>
{currentDoc.type === 'devis' ? 'DEVIS' : 'FACTURE'}
</div>

{/* Objet du document */}
{currentDoc.objet && (
<div style={{
background: 'linear-gradient(135deg, rgba(44, 95, 93, 0.05) 0%, rgba(245, 166, 35, 0.05) 100%)',
padding: '1rem 1.5rem',
marginBottom: '1.5rem',
borderLeft: '4px solid #2C5F5D',
borderRadius: '4px'
}}>
<div style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.25rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
Objet
</div>
<div style={{ fontSize: '0.9rem', color: '#1E293B', fontWeight: '600', lineHeight: '1.4' }}>
{currentDoc.objet}
</div>
</div>
)}

{/* Tableau avec header teal */}
<table style={{
width: '100%',
borderCollapse: 'collapse',
fontSize: '0.75rem',
marginBottom: '1.5rem',
background: 'white',
boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
pageBreakInside: 'auto'
}}>
<thead style={{ display: 'table-header-group', pageBreakInside: 'avoid', pageBreakAfter: 'avoid' }}>
<tr style={{ background: '#FFFFFF', color: '#000000', borderBottom: '2px solid #000000' }}>
<th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
DÉSIGNATION
</th>
<th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', fontSize: '0.7rem', width: '90px' }}>
UNITÉ
</th>
<th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', fontSize: '0.7rem', width: '90px' }}>
QUANTITÉ
</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', width: '110px' }}>
PRIX UNIT.
</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', width: '110px' }}>
TOTAL
</th>
</tr>
</thead>
<tbody style={{ pageBreakInside: 'auto' }}>
{renderPrintItems(currentDoc.items, { tableBodySize: '10pt', tableCellPadding: '0.75rem', compact: true })}
</tbody>
</table>

{/* Section totaux en bas */}
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
{/* Infos paiement gauche avec FACTURÉ À */}
<div style={{ flex: 1, marginRight: '2rem' }}>
{/* FACTURÉ À */}
<div style={{ marginBottom: '1rem' }}>
<div style={{
fontSize: '0.75rem',
fontWeight: 'bold',
color: '#2C5F5D',
marginBottom: '0.5rem',
letterSpacing: '0.5px'
}}>
{currentDoc.type === 'devis' ? 'SOUMIS À :' : 'FACTURÉ À :'}
</div>
<div style={{
background: '#FFFFFF',
padding: '0.75rem',
border: '1px solid #CBD5E0',
borderRadius: '6px',
fontSize: '0.75rem',
lineHeight: '1.5',
color: '#1E293B'
}}>
<div style={{ fontWeight: 'bold', marginBottom: '0.4rem', fontSize: '0.8rem' }}>
{(currentDoc.client?.nom||'')}
</div>
{currentDoc.client?.adresse && <div style={{ fontSize: '0.7rem' }}>{currentDoc.client?.adresse}</div>}
{currentDoc.client?.telephone && <div style={{ fontSize: '0.7rem' }}>Tél : {currentDoc.client?.telephone}</div>}
{currentDoc.client?.email && <div style={{ fontSize: '0.7rem' }}>{currentDoc.client?.email}</div>}
{currentDoc.client?.nui && <div style={{ fontSize: '0.7rem', marginTop: '0.4rem' }}>NUI : {currentDoc.client?.nui}</div>}
{currentDoc.client?.rccm && <div style={{ fontSize: '0.7rem' }}>RCCM : {currentDoc.client?.rccm}</div>}
</div>
</div>

{/* MÉTHODE DE PAIEMENT */}
<div style={{ marginTop: '1rem' }}>
<div style={{
fontSize: '0.7rem',
fontWeight: 'bold',
color: '#2C5F5D',
marginBottom: '0.5rem',
letterSpacing: '0.5px'
}}>
MÉTHODE DE PAIEMENT :
</div>
<div style={{
background: '#FFFFFF',
padding: '0.75rem',
border: '1px solid #CBD5E0',
borderRadius: '6px',
fontSize: '0.75rem',
color: '#1E293B',
fontWeight: '600'
}}>
{formatMethodePaiement(currentDoc.methodePaiement || 'toutes')}
</div>
</div>
</div>

{/* Totaux droite */}
<div style={{ width: '380px' }}>
<div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
<div style={{ padding: '0.75rem 1rem' }}>

{/* SOUS-TOTAL */}
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', fontSize: '0.85rem' }}>
<span style={{ color: '#64748B' }}>{currentDoc.afficherModeTaxation ? 'SOUS-TOTAL HT' : 'SOUS-TOTAL'}</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{formatMontant(currentDoc.sousTotal)}</span>
</div>

{/* REMISE — avec fallback calculé depuis sousTotal - (totalHT - frais) */}
{(() => {
const totalFrais = (currentDoc.fraisSupplementaires || []).reduce((s, f) => s + (f.montant || 0), 0);
const remiseMontant = currentDoc.remiseGlobale?.montant > 0
  ? currentDoc.remiseGlobale.montant
  : Math.max(0, Math.round(((currentDoc.sousTotal || 0) - ((currentDoc.total || 0) - (currentDoc.tva || 0) - totalFrais)) * 100) / 100);
return remiseMontant > 0 ? (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', fontSize: '0.85rem' }}>
<span style={{ color: '#64748B' }}>REMISE</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600', color: '#10B981' }}>
-{formatMontant(remiseMontant)}
</span>
</div>
) : null;
})()}

{/* FRAIS SUPPLÉMENTAIRES — total si frais > 0 */}
{(() => {
const totalFrais = (currentDoc.fraisSupplementaires || []).reduce((s, f) => s + (f.montant || 0), 0);
return totalFrais > 0 ? (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', fontSize: '0.85rem' }}>
<span style={{ color: '#64748B' }}>FRAIS SUPPLÉMENTAIRES</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600' }}>+{formatMontant(totalFrais)}</span>
</div>
) : null;
})()}

{/* TVA — uniquement si afficherModeTaxation activé ET mode TTC */}
{currentDoc.afficherModeTaxation && currentDoc.taxMode === 'TTC' && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', fontSize: '0.85rem' }}>
<span style={{ color: '#64748B' }}>TVA ({settings.tauxTVA || 19.25}%)</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{formatMontant(currentDoc.tva)}</span>
</div>
)}

{/* TOTAL */}
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', fontSize: '0.9rem', borderTop: '1px solid #E2E8F0', marginTop: '0.25rem' }}>
<span style={{ color: '#1E293B', fontWeight: '600' }}>
{currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'TOTAL TTC' : 'TOTAL HT') : 'TOTAL'}
</span>
<span style={{ fontFamily: 'monospace', fontWeight: '700' }}>{formatMontant(currentDoc.total)}</span>
</div>

{/* MONTANT PAYÉ / RESTE À PAYER — calcul local avec fallback sur montantAvance */}
{(() => {
const paye = currentDoc.montantPaye > 0
  ? currentDoc.montantPaye
  : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
return (
<>
{paye > 0 && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', fontSize: '0.85rem', borderTop: '1px solid #E2E8F0', marginTop: '0.25rem' }}>
<span style={{ color: '#64748B' }}>MONTANT PAYÉ</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600', color: '#10B981' }}>
-{formatMontant(paye)}
</span>
</div>
)}
{paye > 0 && restant > 0 && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0', fontSize: '1rem', borderTop: '2px solid #000000', marginTop: '0.25rem' }}>
<span style={{ fontWeight: 'bold', letterSpacing: '0.05em' }}>
{currentDoc.afficherModeTaxation
  ? (currentDoc.taxMode === 'TTC' ? 'RESTE À PAYER TTC' : 'RESTE À PAYER HT')
  : 'RESTE À PAYER'}
</span>
<span style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.2rem' }}>
{formatMontant(restant)}
</span>
</div>
)}
</>
);
})()}

</div>

{/* Arrêté à la somme de */}
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
return (
<div style={{ padding: '0.75rem 1rem', background: '#F8FAFC', borderTop: '1px solid #E2E8F0', fontSize: '0.7rem', textAlign: 'center', color: '#64748B', fontStyle: 'italic' }}>
<div style={{ fontWeight: 'bold', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
Arrêté la présente {currentDoc.type === 'devis' ? 'estimation' : 'facture'} à la somme de :
</div>
<div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#2C5F5D' }}>
{nombreEnLettres(Math.floor(restant > 0 ? restant : currentDoc.total))} francs CFA
</div>
</div>
);
})()}
</div>
</div>
</div>

{/* Footer avec QR Code */}
<div style={{
marginTop: '2rem',
paddingTop: '1rem',
borderTop: '3px solid #2C5F5D',
display: 'flex',
justifyContent: 'space-between',
alignItems: 'flex-start'
}}>
<div style={{ flex: 1 }}>
{/* NOTES / CONDITIONS */}
{currentDoc.notes && (
<div style={{
marginBottom: '1.5rem',
padding: '0.75rem',
background: '#FEF3C7',
borderLeft: '3px solid #F59E0B',
borderRadius: '4px'
}}>
<div style={{
fontSize: '0.75rem',
fontWeight: 'bold',
color: '#92400E',
marginBottom: '0.5rem',
letterSpacing: '0.5px'
}}>
NOTES / CONDITIONS :
</div>
<div style={{
fontSize: '0.7rem',
color: '#78350F',
lineHeight: '1.5',
whiteSpace: 'pre-wrap'
}}>
{currentDoc.notes}
</div>
</div>
)}

{/* MERCI POUR VOTRE CONFIANCE */}
<div style={{ fontSize: '0.75rem', color: '#64748B', lineHeight: '1.6' }}>
<div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>MERCI POUR VOTRE CONFIANCE!</div>
<div style={{ fontSize: '0.7rem' }}>{settings.siteWeb || 'www.solstice.cm'}</div>
</div>
</div>
<div style={{
width: '100px',
height: '100px',
border: '2px solid #E2E8F0',
borderRadius: '6px',
padding: '0.25rem',
background: 'white',
display: 'flex',
flexDirection: 'column',
alignItems: 'center',
justifyContent: 'center'
}}>
{(() => {
const _paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const montantAPayer = _paye > 0 ? Math.max(0, (currentDoc.total || 0) - _paye) : currentDoc.total;
const qrData = `${currentDoc.numero}|${(currentDoc.client?.nom||'')}|${montantAPayer}`;
const encodedData = encodeURIComponent(qrData);
const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodedData}`;

return (
<>
<img
src={qrCodeUrl}
alt="QR Code"
style={{ width: '90px', height: '90px', objectFit: 'contain' }}
onError={(e) => {
e.target.style.display = 'none';
e.target.nextSibling.style.display = 'flex';
}}
/>
<div style={{
display: 'none',
width: '90px',
height: '90px',
alignItems: 'center',
justifyContent: 'center',
flexDirection: 'column',
background: '#F8FAFC',
borderRadius: '4px',
fontSize: '0.6rem',
color: '#94A3B8',
textAlign: 'center',
padding: '0.5rem'
}}>
<div style={{ marginBottom: '0.25rem' }}>📱</div>
<div>CODE QR</div>
</div>
<div style={{
fontSize: '0.6rem',
color: '#94A3B8',
marginTop: '0.25rem',
textAlign: 'center'
}}>
SCANNEZ-MOI
</div>
</>
);
})()}
</div>
</div>
</div>
</div>
);
} else if (modeleActif === 'compact') {
// MODÈLE COMPACT - Inspiré du design professionnel
return (
<div className="preview-container" style={{
background: '#F5F5F0',
padding: '2rem',
fontFamily: 'Arial, sans-serif',
position: 'relative'
}}>
{/* Filigrane en arrière-plan */}
<div style={{
position: 'absolute',
top: '50%',
left: '50%',
transform: 'translate(-50%, -50%)',
fontSize: '8rem',
fontWeight: 'bold',
color: 'rgba(0, 0, 0, 0.03)',
pointerEvents: 'none',
zIndex: 0
}}>
{currentDoc.type === 'devis' ? 'DEVIS' : 'FACTURE'}
</div>

{/* Contenu principal */}
<div style={{ position: 'relative', zIndex: 1 }}>
{/* Header avec logo et infos entreprise */}
<div style={{
display: 'flex',
justifyContent: 'space-between',
alignItems: 'flex-start',
marginBottom: '1.5rem',
paddingBottom: '1rem',
borderBottom: '3px solid #0A2540'
}}>
{/* Logo et nom entreprise */}
<div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
{settings.logo && (
<div style={{
width: '80px',
height: '80px',
border: '3px solid #0A2540',
borderRadius: '8px',
padding: '0.5rem',
background: 'white',
display: 'flex',
alignItems: 'center',
justifyContent: 'center'
}}>
<img src={settings.logo} alt="Logo" style={{
maxWidth: '100%',
maxHeight: '100%',
objectFit: 'contain'
}} />
</div>
)}
<div>
<div style={{
fontSize: '1.3rem',
fontWeight: 'bold',
color: '#0A2540',
letterSpacing: '0.5px',
marginBottom: '0.25rem'
}}>
{settings.raisonSociale}
</div>
<div style={{ fontSize: '0.75rem', color: '#64748B', lineHeight: '1.4' }}>
{settings.adresse && <div>{settings.adresse}</div>}
{settings.telephone && <span>{settings.telephone}</span>}
{settings.email && <span> | {settings.email}</span>}
</div>
</div>
</div>

{/* Infos facture/devis */}
<div style={{ textAlign: 'right' }}>
<div style={{
background: '#0A2540',
color: 'white',
padding: '0.5rem 1.5rem',
borderRadius: '6px',
marginBottom: '0.5rem'
}}>
<div style={{ fontSize: '0.7rem', opacity: 0.8, marginBottom: '0.25rem' }}>
{currentDoc.type === 'devis' ? 'DEVIS N°' : 'FACTURE N°'}
</div>
<div style={{ fontSize: '1rem', fontWeight: 'bold', fontFamily: 'monospace' }}>
{currentDoc.numero}
</div>
</div>
<div style={{ fontSize: '0.75rem', color: '#64748B' }}>
<div><strong>DATE :</strong> {new Date(currentDoc.date).toLocaleDateString('fr-FR')}</div>
{currentDoc.dateMode === 'auto' && currentDoc.dateTime && (
<div style={{ fontSize: '0.7rem' }}>
{new Date(currentDoc.dateTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
</div>
)}
</div>
</div>
</div>

{/* Section BILL TO et FROM */}
<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '1.5rem' }}>
{/* BILL TO */}
<div>
<div style={{
fontSize: '0.75rem',
fontWeight: 'bold',
color: '#0A2540',
marginBottom: '0.5rem',
letterSpacing: '0.5px'
}}>
{currentDoc.type === 'devis' ? 'SOUMIS À :' : 'FACTURÉ À :'}
</div>
<div style={{
background: '#E8E8E3',
padding: '1rem',
borderRadius: '6px',
fontSize: '0.8rem',
lineHeight: '1.6',
color: '#1E293B'
}}>
<div style={{ fontWeight: 'bold', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
{(currentDoc.client?.nom||'')}
</div>
{currentDoc.client?.adresse && <div>{currentDoc.client?.adresse}</div>}
{currentDoc.client?.telephone && <div>Tél : {currentDoc.client?.telephone}</div>}
{currentDoc.client?.email && <div>{currentDoc.client?.email}</div>}
{currentDoc.client?.nui && <div style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>NUI : {currentDoc.client?.nui}</div>}
</div>
</div>

{/* FROM / BILLING TO */}
<div>
<div style={{
fontSize: '0.75rem',
fontWeight: 'bold',
color: '#0A2540',
marginBottom: '0.5rem',
letterSpacing: '0.5px'
}}>
ÉMETTEUR :
</div>
<div style={{
background: '#E8E8E3',
padding: '1rem',
borderRadius: '6px',
fontSize: '0.8rem',
lineHeight: '1.6',
color: '#1E293B'
}}>
<div style={{ fontWeight: 'bold', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
{settings.raisonSociale}
</div>
{settings.adresse && <div>{settings.adresse}</div>}
<div>Tél : {settings.telephone || 'N/A'}</div>
{settings.nui && <div style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>NUI : {settings.nui}</div>}
{settings.rccm && <div style={{ fontSize: '0.75rem' }}>RCCM : {settings.rccm}</div>}
</div>
</div>
</div>

{/* Tableau des articles avec header bleu-vert */}
<div style={{ marginBottom: '1.5rem' }}>
<table style={{
width: '100%',
borderCollapse: 'separate',
borderSpacing: '0',
fontSize: '0.8rem',
background: 'white',
boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
borderRadius: '8px',
overflow: 'hidden'
}}>
<thead>
<tr style={{
background: 'linear-gradient(135deg, #5F9EA0 0%, #4682B4 100%)',
color: 'white'
}}>
<th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
DÉSIGNATION
</th>
<th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', fontSize: '0.75rem', width: '80px' }}>
QTÉ
</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', fontSize: '0.75rem', width: '120px' }}>
PRIX UNIT.
</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', fontSize: '0.75rem', width: '120px' }}>
TOTAL
</th>
</tr>
</thead>
<tbody>
{renderPrintItems(currentDoc.items, { tableBodySize: '10pt', tableCellPadding: '0.75rem', compact: true })}
</tbody>
</table>
</div>

{/* Section totaux avec design élégant */}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
{/* Notes de paiement */}
{currentDoc.notes && (
<div style={{
flex: 1,
marginRight: '2rem',
padding: '1rem',
background: '#FEF3C7',
borderRadius: '6px',
borderLeft: '4px solid #F59E0B'
}}>
<div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#92400E', marginBottom: '0.5rem' }}>
NOTES / CONDITIONS :
</div>
<div style={{ fontSize: '0.7rem', color: '#78350F', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
{currentDoc.notes}
</div>
</div>
)}

{/* Totaux */}
<div style={{ width: '320px' }}>
<div style={{
background: 'white',
padding: '1rem',
borderRadius: '8px',
boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
}}>
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', fontSize: '0.8rem', color: '#64748B' }}>
<span>{currentDoc.afficherModeTaxation ? 'SOUS-TOTAL HT :' : 'SOUS-TOTAL :'}</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{formatMontant(currentDoc.sousTotal)}</span>
</div>
{(() => {
const totalFrais = (currentDoc.fraisSupplementaires || []).reduce((s, f) => s + (f.montant || 0), 0);
const remiseMontant = currentDoc.remiseGlobale?.montant > 0
? currentDoc.remiseGlobale.montant
: Math.max(0, Math.round(((currentDoc.sousTotal || 0) - ((currentDoc.total || 0) - (currentDoc.tva || 0) - totalFrais)) * 100) / 100);
return remiseMontant > 0 ? (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', fontSize: '0.8rem', color: '#10B981' }}>
<span>REMISE :</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600' }}>-{formatMontant(remiseMontant)}</span>
</div>
) : null;
})()}
{(currentDoc.fraisSupplementaires || []).filter(f => f.montant > 0).map((frais, idx) => (
<div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px', color: '#000' }}>
<span>+ {frais.libelle || frais.description || 'Frais supplémentaires'}</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(frais.montant)}</span>
</div>
))}
{currentDoc.afficherModeTaxation && currentDoc.taxMode === 'TTC' && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', fontSize: '0.8rem', color: '#64748B', borderTop: '1px solid #E2E8F0' }}>
<span>TVA ({settings.tauxTVA || 19.25}%) :</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{formatMontant(currentDoc.tva)}</span>
</div>
)}
<div style={{
display: 'flex',
justifyContent: 'space-between',
padding: '1rem 0',
marginTop: '0.5rem',
borderTop: '2px solid #0A2540',
fontSize: '1.1rem',
fontWeight: 'bold'
}}>
<span style={{ color: '#0A2540' }}>{currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'TOTAL TTC :' : 'TOTAL HT :') : 'TOTAL :'}</span>
<span style={{ fontFamily: 'monospace', color: '#FF6B35', fontSize: '1.3rem' }}>
{formatMontant(currentDoc.total)}
</span>
</div>
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
return paye > 0 ? (
<>
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', fontSize: '0.8rem', color: '#10B981', borderTop: '1px solid #E2E8F0' }}>
<span>MONTANT PAYÉ :</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600' }}>-{formatMontant(paye)}</span>
</div>
{restant > 0 && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', fontSize: '0.9rem', color: '#EF4444', fontWeight: 'bold', borderTop: '1px solid #E2E8F0' }}>
<span>{currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'RESTE À PAYER TTC :' : 'RESTE À PAYER HT :') : 'RESTE À PAYER :'}</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(restant)}</span>
</div>
)}
</>
) : null;
})()}
</div>
</div>
</div>

{/* Footer avec QR Code réel */}
<div style={{
marginTop: '2rem',
paddingTop: '1rem',
borderTop: '3px solid #4682B4',
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center'
}}>
<div style={{ fontSize: '0.75rem', color: '#64748B', lineHeight: '1.6' }}>
<div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>MERCI POUR VOTRE CONFIANCE!</div>
<div>{settings.siteWeb || 'www.solstice.cm'}</div>
</div>
<div style={{
width: '100px',
height: '100px',
border: '2px solid #E2E8F0',
borderRadius: '6px',
padding: '0.25rem',
background: 'white',
display: 'flex',
flexDirection: 'column',
alignItems: 'center',
justifyContent: 'center'
}}>
{(() => {
// Calculer le montant à payer (solde restant)
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const montantAPayer = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : currentDoc.total;

// Créer les données pour le QR Code - format compact
const qrData = `${currentDoc.numero}|${(currentDoc.client?.nom||'')}|${montantAPayer}`;

// Encoder les données pour l'URL
const encodedData = encodeURIComponent(qrData);

// Utiliser l'API QR Server (plus fiable)
const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodedData}`;

return (
<>
<img
src={qrCodeUrl}
alt="QR Code"
style={{
width: '90px',
height: '90px',
objectFit: 'contain'
}}
onError={(e) => {
e.target.style.display = 'none';
e.target.nextSibling.style.display = 'flex';
}}
/>
<div style={{
display: 'none',
width: '90px',
height: '90px',
alignItems: 'center',
justifyContent: 'center',
flexDirection: 'column',
background: '#F8FAFC',
borderRadius: '4px',
fontSize: '0.6rem',
color: '#94A3B8',
textAlign: 'center',
padding: '0.5rem'
}}>
<div style={{ marginBottom: '0.25rem' }}>📱</div>
<div>CODE QR</div>
<div style={{ fontSize: '0.5rem', marginTop: '0.25rem' }}>
{currentDoc.numero}
</div>
</div>
<div style={{
fontSize: '0.6rem',
color: '#94A3B8',
marginTop: '0.25rem',
textAlign: 'center'
}}>
SCANNEZ-MOI
</div>
</>
);
})()}
</div>
</div>
</div>
</div>
);
} else if (modeleActif === 'noir-blanc') {
// MODÈLE NOIR ET BLANC - Optimisé pour impression monochrome
return (
<div className="preview-container" style={{
background: '#FFFFFF',
padding: '2.5rem',
fontFamily: 'Arial, sans-serif',
color: '#000000'
}}>
{/* Contenu principal */}
<div style={{ position: 'relative' }}>
{/* Header avec bordures */}
<div style={{
display: 'grid',
gridTemplateColumns: '140px 1fr 280px',
alignItems: 'flex-start',
marginBottom: '1rem',
paddingBottom: '1rem',
borderBottom: '2px solid #000000'
}}>
{/* Gauche - Logo */}
<div style={{
borderRight: '1px solid #666666',
paddingRight: '1rem',
display: 'flex',
alignItems: 'flex-start'
}}>
{settings.logo ? (
<div style={{
width: '120px',
height: '120px',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
border: '2px solid #000000',
borderRadius: '8px',
padding: '0.5rem'
}}>
<img src={settings.logo} alt="Logo" style={{
maxWidth: '100%',
maxHeight: '100%',
objectFit: 'contain',
filter: 'grayscale(100%)'
}} />
</div>
) : (
<div style={{
width: '120px',
height: '120px',
border: '2px solid #000000',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
fontSize: '0.7rem',
fontWeight: 'bold'
}}>
LOGO
</div>
)}
</div>

{/* Centre - Info entreprise */}
<div style={{
borderRight: '1px solid #666666',
paddingLeft: '1.5rem',
paddingRight: '1.5rem'
}}>
<div style={{
fontSize: '1.4rem',
fontWeight: 'bold',
marginBottom: '0.5rem',
textTransform: 'uppercase',
letterSpacing: '1px'
}}>
{settings.raisonSociale}
</div>
<div style={{ fontSize: '0.75rem', lineHeight: '1.6' }}>
{settings.adresse && <div>{settings.adresse}</div>}
{settings.telephone && <div>Tél : {settings.telephone}</div>}
{settings.email && <div>{settings.email}</div>}
{settings.siteWeb && <div>{settings.siteWeb}</div>}
<div style={{ marginTop: '0.5rem', fontWeight: 'bold' }}>
{settings.nui && <div>NUI : {settings.nui}</div>}
{settings.rccm && <div>RCCM : {settings.rccm}</div>}
</div>
</div>
</div>

{/* Droite - Numéro + Date + Validité */}
<div style={{ paddingLeft: '1.5rem' }}>
{/* Numéro */}
<div style={{
border: '3px solid #000000',
padding: '0.75rem',
marginBottom: '0.75rem',
background: '#FFFFFF',
color: '#000000'
}}>
<div style={{ fontSize: '0.65rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>
{currentDoc.type === 'devis' ? 'DEVIS N°' : 'FACTURE N°'}
</div>
<div style={{ fontSize: '0.9rem', fontWeight: 'bold', fontFamily: 'monospace' }}>
{currentDoc.numero}
</div>
</div>

{/* Dates */}
<div style={{ fontSize: '0.7rem', marginBottom: '0.5rem', fontWeight: 'bold' }}>
<div>DATE : {new Date(currentDoc.date).toLocaleDateString('fr-FR')}</div>
<div style={{ fontSize: '0.65rem', fontWeight: 'normal', marginTop: '0.25rem' }}>
{new Date(currentDoc.date).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }).replace(',', ' :').toUpperCase()}
</div>
</div>

{/* Validité (devis uniquement) */}
{currentDoc.type === 'devis' && (
<div style={{
fontSize: '0.7rem',
fontWeight: 'bold',
padding: '0.5rem',
border: '1px solid #000000',
background: '#F5F5F5'
}}>
Validité : {currentDoc.validiteDevis || 30} jours
</div>
)}
</div>
</div>

{/* Barre titre */}
<div style={{
background: '#FFFFFF',
color: '#000000',
padding: '1rem 2rem',
marginBottom: '1.5rem',
fontSize: '1.75rem',
fontWeight: 'bold',
letterSpacing: '0.5em',
textAlign: 'center',
border: '3px solid #000000',
borderLeft: 'none',
borderRight: 'none'
}}>
{currentDoc.type === 'devis' ? 'DEVIS' : 'FACTURE'}
</div>

{/* Objet du document */}
{currentDoc.objet && (
<div style={{
marginBottom: '1.5rem',
padding: '0.75rem 1rem',
border: '2px solid #000000',
background: '#F5F5F5'
}}>
<div style={{
fontSize: '0.7rem',
fontWeight: 'bold',
marginBottom: '0.5rem',
letterSpacing: '0.5px'
}}>
OBJET :
</div>
<div style={{ fontSize: '0.85rem', fontWeight: '600', lineHeight: '1.4' }}>
{currentDoc.objet}
</div>
</div>
)}

{/* Tableau */}
<table style={{
width: '100%',
borderCollapse: 'collapse',
fontSize: '0.75rem',
marginBottom: '1.5rem',
border: '1px solid #000000'
}}>
<thead>
<tr style={{ background: '#FFFFFF', color: '#000000', borderBottom: '2px solid #000000' }}>
<th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '700', borderRight: '1px solid #000000' }}>
DÉSIGNATION
</th>
<th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '700', width: '80px', borderRight: '1px solid #000000' }}>
UNITÉ
</th>
<th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '700', width: '80px', borderRight: '1px solid #000000' }}>
QUANTITÉ
</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '700', width: '120px', borderRight: '1px solid #000000' }}>
PRIX UNIT.
</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '700', width: '120px' }}>
TOTAL
</th>
</tr>
</thead>
<tbody>
{renderPrintItems(currentDoc.items, { tableBodySize: '10pt', tableCellPadding: '0.75rem', compact: true })}
</tbody>
</table>

{/* Section bas : Client + Totaux */}
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem', gap: '2rem' }}>
{/* Gauche - Client + Méthode paiement */}
<div style={{ flex: 1 }}>
{/* Client */}
<div style={{ marginBottom: '1rem' }}>
<div style={{
fontSize: '0.75rem',
fontWeight: 'bold',
marginBottom: '0.5rem',
letterSpacing: '0.5px'
}}>
{currentDoc.type === 'devis' ? 'SOUMIS À :' : 'FACTURÉ À :'}
</div>
<div style={{
border: '1px solid #000000',
padding: '0.75rem',
fontSize: '0.75rem',
lineHeight: '1.5'
}}>
<div style={{ fontWeight: 'bold', marginBottom: '0.4rem', fontSize: '0.8rem' }}>
{(currentDoc.client?.nom||'')}
</div>
{currentDoc.client?.adresse && <div style={{ fontSize: '0.7rem' }}>{currentDoc.client?.adresse}</div>}
{currentDoc.client?.telephone && <div style={{ fontSize: '0.7rem' }}>Tél : {currentDoc.client?.telephone}</div>}
{currentDoc.client?.email && <div style={{ fontSize: '0.7rem' }}>{currentDoc.client?.email}</div>}
{currentDoc.client?.nui && <div style={{ fontSize: '0.7rem', marginTop: '0.4rem', fontWeight: 'bold' }}>NUI : {currentDoc.client?.nui}</div>}
{currentDoc.client?.rccm && <div style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>RCCM : {currentDoc.client?.rccm}</div>}
</div>
</div>

{/* Méthode paiement */}
<div>
<div style={{
fontSize: '0.7rem',
fontWeight: 'bold',
marginBottom: '0.5rem'
}}>
MÉTHODE DE PAIEMENT :
</div>
<div style={{
border: '1px solid #000000',
padding: '0.75rem',
fontSize: '0.75rem',
fontWeight: '600'
}}>
{formatMethodePaiement(currentDoc.methodePaiement || 'toutes')}
</div>
</div>
</div>

{/* Droite - Totaux */}
<div style={{ width: '380px' }}>
<div style={{ border: '2px solid #000000' }}>
{/* Détails */}
<div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #000000' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', fontSize: '0.8rem' }}>
<span>{currentDoc.afficherModeTaxation ? 'SOUS-TOTAL HT' : 'SOUS-TOTAL'}</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{formatMontant(currentDoc.sousTotal)}</span>
</div>
{(() => {
const totalFrais = (currentDoc.fraisSupplementaires || []).reduce((s, f) => s + (f.montant || 0), 0);
const remiseMontant = currentDoc.remiseGlobale?.montant > 0
? currentDoc.remiseGlobale.montant
: Math.max(0, Math.round(((currentDoc.sousTotal || 0) - ((currentDoc.total || 0) - (currentDoc.tva || 0) - totalFrais)) * 100) / 100);
return remiseMontant > 0 ? (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', fontSize: '0.8rem', color: '#000000' }}>
<span>REMISE</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600' }}>-{formatMontant(remiseMontant)}</span>
</div>
) : null;
})()}
{(currentDoc.fraisSupplementaires || []).filter(f => f.montant > 0).map((frais, idx) => (
<div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px', color: '#000' }}>
<span>+ {frais.libelle || frais.description || 'Frais supplémentaires'}</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(frais.montant)}</span>
</div>
))}
{currentDoc.afficherModeTaxation && currentDoc.taxMode === 'TTC' && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', fontSize: '0.8rem' }}>
<span>TVA ({settings.tauxTVA || 19.25}%)</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{formatMontant(currentDoc.tva)}</span>
</div>
)}
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', fontSize: '0.8rem', borderTop: '1px solid #000000', marginTop: '0.25rem', paddingTop: '0.5rem' }}>
<span>{currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'TOTAL TTC' : 'TOTAL HT') : 'TOTAL'}</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{formatMontant(currentDoc.total)}</span>
</div>
</div>

{/* Montant payé / Reste à payer (si paiement partiel) */}
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
return paye > 0 ? (
<div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #000000', background: '#F5F5F5' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.3rem' }}>
<span>MONTANT PAYÉ</span>
<span style={{ fontFamily: 'monospace', fontWeight: '600' }}>- {formatMontant(paye)}</span>
</div>
{restant > 0 && (
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', fontWeight: 'bold', color: '#000000' }}>
<span>{currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'RESTE À PAYER TTC' : 'RESTE À PAYER HT') : 'RESTE À PAYER'}</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(restant)}</span>
</div>
)}
</div>
) : null;
})()}

{/* Total à payer */}
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
const montantAffiche = restant > 0 ? restant : currentDoc.total;
return (
<>
<div style={{
padding: '1rem',
background: '#FFFFFF',
color: '#000000',
textAlign: 'center',
border: '3px solid #000000',
borderBottom: 'none'
}}>
<div style={{ fontSize: '0.7rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>{currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'TOTAL TTC' : 'TOTAL HT') : 'TOTAL'}</div>
<div style={{ fontSize: '1.4rem', fontWeight: 'bold', fontFamily: 'monospace' }}>
{formatMontant(montantAffiche)}
</div>
</div>

{/* Montant en lettres */}
<div style={{
padding: '0.75rem 1rem',
borderTop: '1px solid #000000',
fontSize: '0.7rem',
textAlign: 'center',
fontStyle: 'italic',
background: '#F9F9F9'
}}>
<div style={{ fontWeight: 'bold', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
Arrêté la présente {currentDoc.type === 'devis' ? 'estimation' : 'facture'} à :
</div>
<div style={{ fontSize: '0.75rem', fontWeight: '600' }}>
{nombreEnLettres(Math.floor(montantAffiche))} francs CFA
</div>
</div>
</>
);
})()}
</div>
</div>
</div>

{/* Footer */}
<div style={{
marginTop: '2rem',
paddingTop: '1rem',
borderTop: '2px solid #000000',
display: 'flex',
justifyContent: 'space-between',
alignItems: 'flex-start'
}}>
<div style={{ flex: 1 }}>
{/* Notes */}
{currentDoc.notes && (
<div style={{
marginBottom: '1.5rem',
padding: '0.75rem',
border: '1px solid #000000',
background: '#F9F9F9'
}}>
<div style={{
fontSize: '0.75rem',
fontWeight: 'bold',
marginBottom: '0.5rem'
}}>
NOTES / CONDITIONS :
</div>
<div style={{
fontSize: '0.7rem',
lineHeight: '1.5',
whiteSpace: 'pre-wrap'
}}>
{currentDoc.notes}
</div>
</div>
)}

{/* Merci */}
<div style={{ fontSize: '0.75rem', lineHeight: '1.6' }}>
<div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>MERCI POUR VOTRE CONFIANCE!</div>
<div style={{ fontSize: '0.7rem' }}>{settings.siteWeb || 'www.solstice.cm'}</div>
</div>
</div>

{/* QR Code */}
<div style={{
width: '100px',
height: '100px',
border: '2px solid #000000',
padding: '0.25rem',
display: 'flex',
flexDirection: 'column',
alignItems: 'center',
justifyContent: 'center'
}}>
{(() => {
const _paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const montantAPayer = _paye > 0 ? Math.max(0, (currentDoc.total || 0) - _paye) : currentDoc.total;
const qrData = `${currentDoc.numero}|${(currentDoc.client?.nom||'')}|${montantAPayer}`;
const encodedData = encodeURIComponent(qrData);
const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodedData}`;

return (
<>
<img
src={qrCodeUrl}
alt="QR Code"
style={{ width: '90px', height: '90px', objectFit: 'contain' }}
onError={(e) => {
e.target.style.display = 'none';
e.target.nextSibling.style.display = 'flex';
}}
/>
<div style={{
display: 'none',
width: '90px',
height: '90px',
alignItems: 'center',
justifyContent: 'center',
flexDirection: 'column',
fontSize: '0.7rem',
textAlign: 'center'
}}>
<div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>📱</div>
<div style={{ fontWeight: 'bold' }}>QR CODE</div>
<div style={{ fontSize: '0.6rem', marginTop: '0.25rem' }}>{currentDoc.numero}</div>
</div>
<div style={{
fontSize: '0.6rem',
marginTop: '0.25rem',
fontWeight: 'bold',
textAlign: 'center'
}}>
SCANNEZ-MOI
</div>
</>
);
})()}
</div>
</div>
</div>
</div>
);
} else if (modeleActif === 'noir-blanc2') {
// MODÈLE NOIR ET BLANC 2 OPTIMISÉ - Économe en encre, informatif et élégant
return (
<div className="preview-container" style={{
background: '#FFFFFF',
padding: '12mm 15mm',
fontFamily: 'Arial, sans-serif',
color: '#000000',
fontSize: '9pt',
lineHeight: '1.3'
}}>
{/* EN-TÊTE OPTIMISÉ - 3 colonnes : Société | Client | Facture+QR */}
<div style={{
display: 'grid',
gridTemplateColumns: '1.2fr 1fr 140px',
gap: '20px',
marginBottom: '8mm',
paddingBottom: '5mm',
borderBottom: '1px solid #000'
}}>
{/* Colonne 1 : Société (compact) */}
<div>
<div style={{
fontSize: '14pt',
fontWeight: 'bold',
letterSpacing: '1px',
marginBottom: '3px',
textTransform: 'uppercase'
}}>
{settings.raisonSociale}
</div>
<div style={{ fontSize: '8pt', lineHeight: '1.4' }}>
{settings.adresse && <div>{settings.adresse}</div>}
<div>Tél: {settings.telephone || '—'} | Email: {settings.email || '—'}</div>
<div style={{ marginTop: '2px' }}>
<span style={{ fontWeight: 'bold' }}>NUI:</span> {settings.nui || '—'} |
<span style={{ fontWeight: 'bold' }}> RCCM:</span> {settings.rccm || '—'}
</div>
</div>
</div>

{/* Colonne 2 : Client (encadré léger) */}
<div style={{
border: '0.5px solid #000',
padding: '6px 10px',
fontSize: '8pt'
}}>
<div style={{ fontWeight: 'bold', fontSize: '7pt', textTransform: 'uppercase', marginBottom: '3px', borderBottom: '0.5px solid #ccc', paddingBottom: '2px' }}>
Facturé à
</div>
<div style={{ fontWeight: 'bold', fontSize: '9pt', marginBottom: '2px' }}>
{(currentDoc.client?.nom||'')}
</div>
<div>{currentDoc.client?.adresse || '—'}</div>
<div>Tél: {currentDoc.client?.telephone || '—'}</div>
<div style={{ marginTop: '2px', fontSize: '7.5pt' }}>
NUI: {currentDoc.client?.nui || 'N/A'} | RCCM: {currentDoc.client?.rccm || 'N/A'}
</div>
</div>

{/* Colonne 3 : Facture + QR (vertical) */}
<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
{(() => {
const _paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const montantAPayer = _paye > 0 ? Math.max(0, (currentDoc.total || 0) - _paye) : currentDoc.total;
const qrData = `${currentDoc.numero}|${(currentDoc.client?.nom||'')}|${montantAPayer}`;
const encodedData = encodeURIComponent(qrData);
const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodedData}`;

return (
<>
<img
src={qrCodeUrl}
alt="QR"
style={{ width: '60px', height: '60px', objectFit: 'contain' }}
onError={(e) => e.target.style.display = 'none'}
/>
<div style={{ fontSize: '6pt', textAlign: 'center', fontWeight: 'bold' }}>
SCANNER
</div>
</>
);
})()}

<div style={{
border: '0.5px solid #000',
padding: '4px',
textAlign: 'center',
width: '100%',
marginTop: '2px'
}}>
<div style={{ fontSize: '6pt', fontWeight: 'bold' }}>
{currentDoc.type === 'devis' ? 'DEVIS' : 'FACTURE'}
</div>
<div style={{ fontSize: '8pt', fontFamily: 'monospace', fontWeight: 'bold' }}>
{currentDoc.numero}
</div>
</div>
</div>
</div>

{/* LIGNE INFO RAPIDE (Date, Échéance, Validité) */}
<div style={{
display: 'flex',
justifyContent: 'space-between',
fontSize: '8pt',
marginBottom: '6mm',
padding: '4px 0',
borderBottom: '0.5px solid #ccc'
}}>
<div>
<span style={{ fontWeight: 'bold' }}>Date émission:</span> {new Date(currentDoc.date).toLocaleDateString('fr-FR')}
{currentDoc.dateMode === 'auto' && currentDoc.dateTime && (
<span style={{ fontSize: '7pt', marginLeft: '5px' }}>({new Date(currentDoc.dateTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })})</span>
)}
</div>
{currentDoc.type === 'facture' && currentDoc.dateEcheance && (
<div>
<span style={{ fontWeight: 'bold' }}>Date échéance:</span> {new Date(currentDoc.dateEcheance).toLocaleDateString('fr-FR')}
</div>
)}
{currentDoc.type === 'devis' && (
<div>
<span style={{ fontWeight: 'bold' }}>Validité:</span> {currentDoc.validiteDevis || 30} jours
</div>
)}
<div>
<span style={{ fontWeight: 'bold' }}>Page:</span> 1/1
</div>
</div>

{/* OBJET (si présent) */}
{currentDoc.objet && (
<div style={{ marginBottom: '5mm', fontSize: '9pt' }}>
<span style={{ fontWeight: 'bold' }}>Objet:</span> {currentDoc.objet}
</div>
)}

{/* TABLEAU ARTICLES - Lignes fines, avec support Sections / Notes */}
<table style={{
width: '100%',
borderCollapse: 'collapse',
fontSize: '8.5pt',
marginBottom: '6mm'
}}>
<thead>
<tr style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000' }}>
<th style={{ padding: '5px 3px', textAlign: 'left', fontWeight: 'bold', width: '45%' }}>DÉSIGNATION</th>
<th style={{ padding: '5px 3px', textAlign: 'center', fontWeight: 'bold', width: '8%' }}>UN</th>
<th style={{ padding: '5px 3px', textAlign: 'center', fontWeight: 'bold', width: '8%' }}>QTÉ</th>
<th style={{ padding: '5px 3px', textAlign: 'right', fontWeight: 'bold', width: '15%' }}>P.U. HT</th>
<th style={{ padding: '5px 3px', textAlign: 'right', fontWeight: 'bold', width: '12%' }}>REM</th>
<th style={{ padding: '5px 3px', textAlign: 'right', fontWeight: 'bold', width: '12%' }}>{currentDoc.afficherModeTaxation ? 'TOTAL HT' : 'TOTAL'}</th>
</tr>
</thead>
<tbody>
{renderPrintItems(currentDoc.items)}
</tbody>
</table>

{/* SECTION TOTALS - Disposition horizontale économe */}
<div style={{
display: 'grid',
gridTemplateColumns: '1fr 220px',
gap: '15px',
marginBottom: '5mm'
}}>
{/* Gauche: Conditions et paiement */}
<div style={{ fontSize: '7.5pt', lineHeight: '1.3' }}>
{currentDoc.notes && (
<div style={{ marginBottom: '4mm' }}>
<div style={{ fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '2px' }}>Conditions:</div>
<div style={{ textAlign: 'justify' }}>{currentDoc.notes}</div>
</div>
)}

<div style={{ marginTop: '3mm' }}>
<div style={{ fontWeight: 'bold' }}>Mode de règlement:</div>
<div>{formatMethodePaiement(currentDoc.methodePaiement || 'toutes')}</div>
</div>

{currentDoc.type === 'facture' && currentDoc.penalitesRetard?.actives && (
<div style={{ marginTop: '3mm', fontStyle: 'italic', fontSize: '7pt' }}>
Pénalités de retard: {currentDoc.penalitesRetard.taux}% par jour après échéance.
</div>
)}
</div>

{/* Droite: Bloc totaux (sans fond gris) */}
<div style={{ border: '0.5px solid #000', padding: '8px' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px', fontSize: '8.5pt' }}>
<span>{currentDoc.afficherModeTaxation ? 'Total HT:' : 'Total:'}</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(currentDoc.sousTotal)}</span>
</div>

{currentDoc.totalRemises > 0 && (
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px', fontSize: '8pt', color: '#000' }}>
<span>Remises:</span>
<span style={{ fontFamily: 'monospace' }}>-{formatMontant(currentDoc.totalRemises)}</span>
</div>
)}

{currentDoc.fraisSupplementaires?.map((frais, idx) => (
<div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px', fontSize: '8pt' }}>
<span>{frais.libelle || 'Frais'}:</span>
<span style={{ fontFamily: 'monospace' }}>+{formatMontant(frais.montant)}</span>
</div>
))}

<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px', fontSize: '8.5pt' }}>
<span>Base taxable:</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(currentDoc.totalAvantTaxe || currentDoc.sousTotal)}</span>
</div>

{currentDoc.afficherModeTaxation && currentDoc.taxMode === 'TTC' && (
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px', fontSize: '8.5pt' }}>
<span>TVA ({settings.tauxTVA || 19.25}%):</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(currentDoc.tva)}</span>
</div>
)}

{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
return paye > 0 ? (
<>
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px', fontSize: '8pt', color: '#666' }}>
<span>Montant payé:</span>
<span style={{ fontFamily: 'monospace' }}>-{formatMontant(paye)}</span>
</div>
<div style={{
borderTop: '1px solid #000',
marginTop: '4px',
paddingTop: '4px',
display: 'flex',
justifyContent: 'space-between',
fontWeight: 'bold',
fontSize: '10pt'
}}>
<span>{currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'RESTE À PAYER TTC' : 'RESTE À PAYER HT') : 'RESTE À PAYER'}:</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(restant)}</span>
</div>
</>
) : (
<div style={{
borderTop: '1px solid #000',
marginTop: '4px',
paddingTop: '4px',
display: 'flex',
justifyContent: 'space-between',
fontWeight: 'bold',
fontSize: '10pt'
}}>
<span>{currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'TOTAL TTC' : 'TOTAL HT') : 'TOTAL'}:</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(currentDoc.total)}</span>
</div>
);
})()}
</div>
</div>

{/* MONTANT EN LETTRES - Style discret */}
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
const montantLettres = restant > 0 ? restant : currentDoc.total;
return (
<div style={{
textAlign: 'center',
fontSize: '8pt',
fontStyle: 'italic',
marginBottom: '6mm',
padding: '3px',
border: '0.5px solid #ccc'
}}>
Arrêté la présente {currentDoc.type === 'devis' ? 'facture proforma' : 'facture'} à la somme de :
<span style={{ fontWeight: 'bold', fontStyle: 'normal', marginLeft: '5px' }}>
{nombreEnLettres(Math.floor(montantLettres))} francs CFA
</span>
</div>
);
})()}

{/* SIGNATURES - 3 colonnes économes */}
<div style={{
display: 'grid',
gridTemplateColumns: '1fr 1fr 1fr',
gap: '10px',
marginTop: '8mm',
fontSize: '7.5pt',
textAlign: 'center'
}}>
<div>
<div style={{ borderBottom: '0.5px solid #000', marginBottom: '15px', height: '30px' }}></div>
<div style={{ fontWeight: 'bold' }}>Pour l'émetteur</div>
<div style={{ fontSize: '6.5pt', color: '#666' }}>Date et signature</div>
</div>
<div>
<div style={{ borderBottom: '0.5px solid #000', marginBottom: '15px', height: '30px' }}></div>
<div style={{ fontWeight: 'bold' }}>Pour le client</div>
<div style={{ fontSize: '6.5pt', color: '#666' }}>Date, signature et cachet</div>
</div>
<div style={{ fontSize: '7pt', textAlign: 'left', paddingLeft: '5px' }}>
<div style={{ marginBottom: '2px' }}><strong>Réf:</strong> {currentDoc.numero}</div>
{currentDoc.client?.numeroBonCommande && (
<div style={{ marginBottom: '2px' }}><strong>Votre BC:</strong> {currentDoc.client?.numeroBonCommande}</div>
)}
<div style={{ marginTop: '5px', fontSize: '6.5pt', color: '#666' }}>
Document généré le {new Date().toLocaleDateString('fr-FR')}
</div>
</div>
</div>

{/* FOOTER LÉGER */}
<div style={{
marginTop: '5mm',
paddingTop: '3mm',
borderTop: '0.5px solid #ccc',
textAlign: 'center',
fontSize: '7pt',
color: '#333'
}}>
<strong>{settings.raisonSociale}</strong> — {settings.adresse} — Tél: {settings.telephone}
{settings.siteWeb && ` — ${settings.siteWeb}`}
<br/>
<span style={{ fontSize: '6.5pt' }}>Les factures sont payables à réception. Aucun escompte accordé pour paiement anticipé.</span>
</div>
</div>
);
} else if (modeleActif === 'compact-duplicata') {
// MODÈLE COMPACT DUPLICATA - 2 factures A5 sur une page A4 - NOUVEAU DESIGN
// Fonction pour rendre une facture A5
const renderFactureA5 = () => {
// Gestion dynamique de l'espace selon le nombre d'articles
const nbArticles = currentDoc.items.length;
const maxArticlesToShow = Math.min(nbArticles, 10);

// Configuration adaptative selon le nombre d'articles
let config = {};

if (nbArticles <= 5) {
// Mode confortable : beaucoup d'espace
config = {
padding: '8mm',
fontSize: '8pt',
lineHeight: '1.3',
logoSize: '50px',
companyNameSize: '9.5pt',
companyDetailSize: '6.5pt',
clientLabelSize: '6.5pt',
clientNameSize: '7pt',
clientDetailSize: '6pt',
invoiceNumSize: '7pt',
invoiceDateSize: '6.5pt',
titleSize: '10pt',
titlePadding: '5px',
headerMargin: '6px',
tableHeaderSize: '7pt',
tableBodySize: '6.5pt',
tableCellPadding: '3px 5px',
tableHeaderPadding: '3px 5px',
tableMargin: '8px',
totalFontSize: '7pt',
totalLabelSize: '7.5pt',
totalPadding: '5px 8px',
qrSize: '42px',
totalPayerLabel: '7pt',
totalPayerAmount: '11pt',
totalPayerText: '6pt',
paymentMethodSize: '6pt',
footerSize: '6pt',
sectionSpacing: '6px'
};
} else if (nbArticles <= 8) {
// Mode intermédiaire : bon équilibre
config = {
padding: '6.5mm',
fontSize: '7.5pt',
lineHeight: '1.25',
logoSize: '47px',
companyNameSize: '9pt',
companyDetailSize: '6.2pt',
clientLabelSize: '6.2pt',
clientNameSize: '6.7pt',
clientDetailSize: '5.7pt',
invoiceNumSize: '6.7pt',
invoiceDateSize: '6.2pt',
titleSize: '9.5pt',
titlePadding: '4.5px',
headerMargin: '5px',
tableHeaderSize: '6.7pt',
tableBodySize: '6.2pt',
tableCellPadding: '2.5px 4.5px',
tableHeaderPadding: '2.5px 4.5px',
tableMargin: '6px',
totalFontSize: '6.7pt',
totalLabelSize: '7.2pt',
totalPadding: '4.5px 7px',
qrSize: '40px',
totalPayerLabel: '6.7pt',
totalPayerAmount: '10.5pt',
totalPayerText: '5.7pt',
paymentMethodSize: '5.7pt',
footerSize: '5.7pt',
sectionSpacing: '5px'
};
} else {
// Mode compact : optimisé pour 9-10 articles
config = {
padding: '6mm',
fontSize: '7pt',
lineHeight: '1.2',
logoSize: '45px',
companyNameSize: '8.5pt',
companyDetailSize: '6pt',
clientLabelSize: '6pt',
clientNameSize: '6.5pt',
clientDetailSize: '5.5pt',
invoiceNumSize: '6.5pt',
invoiceDateSize: '6pt',
titleSize: '9pt',
titlePadding: '4px',
headerMargin: '4px',
tableHeaderSize: '6.5pt',
tableBodySize: '6pt',
tableCellPadding: '2px 4px',
tableHeaderPadding: '2px 4px',
tableMargin: '5px',
totalFontSize: '6.5pt',
totalLabelSize: '7pt',
totalPadding: '4px 6px',
qrSize: '38px',
totalPayerLabel: '6.5pt',
totalPayerAmount: '10pt',
totalPayerText: '5.5pt',
paymentMethodSize: '5.5pt',
footerSize: '5.5pt',
sectionSpacing: '4px'
};
}

return (
<div style={{
width: '100%',
height: '148.5mm',
padding: config.padding,
background: 'white',
fontSize: config.fontSize,
lineHeight: config.lineHeight,
boxSizing: 'border-box',
border: '1px solid #E5E7EB',
fontFamily: 'Arial, sans-serif',
display: 'flex',
flexDirection: 'column'
}}>
{/* En-tête complet */}
<div style={{ marginBottom: config.headerMargin }}>
{/* Première ligne : Logo + Infos Entreprise | Infos Client (centre) | Numéro Facture (droite) */}
<div style={{
display: 'grid',
gridTemplateColumns: 'auto 1fr auto',
gap: '6px',
marginBottom: config.sectionSpacing,
alignItems: 'start'
}}>
{/* Logo + Infos entreprise */}
<div style={{ display: 'flex', gap: '5px', alignItems: 'start' }}>
{/* Logo */}
<div>
{settings.logo && (
<img src={settings.logo} alt="Logo" style={{ width: config.logoSize, height: config.logoSize, objectFit: 'contain' }} />
)}
</div>

{/* Infos entreprise */}
<div style={{ textAlign: 'left' }}>
<div style={{ fontWeight: 'bold', fontSize: config.companyNameSize, marginBottom: '1px' }}>
{settings.raisonSociale || 'SOLSTICE'}
</div>
<div style={{ fontSize: config.companyDetailSize, color: '#000000', lineHeight: '1.3' }}>
<div>{settings.adresse || 'LOGBESSOU - DOUALA'}</div>
<div>{settings.telephone || '+237682376850'}</div>
<div>NUI: {settings.nui || 'M102316152527W'}</div>
<div>RCCM: {settings.rccm || 'RC/DLA/2023/B/6242'}</div>
</div>
</div>
</div>

{/* Infos client au centre dans encadré gris */}
<div style={{
padding: '4px 6px',
background: '#F3F4F6',
border: '1px solid #D1D5DB',
fontSize: config.clientLabelSize,
textAlign: 'left',
justifySelf: 'center',
maxWidth: '160px'
}}>
<div style={{ fontWeight: 'bold', fontSize: config.clientLabelSize, marginBottom: '2px' }}>
Facturé à:
</div>
<div style={{ fontWeight: 'bold', fontSize: config.clientNameSize, marginBottom: '1px' }}>
{(currentDoc.client?.nom||'') || 'INSTITUT UNIVERSITAIRE DE LA CÔTE (IUC.SA)'}
</div>
<div style={{ fontSize: config.clientDetailSize, lineHeight: '1.2' }}>
<div>BP: {currentDoc.client?.bp || '3001 Dla'}</div>
<div>Tél: {currentDoc.client?.telephone}</div>
<div>NUI: {currentDoc.client?.nui || 'M030202001464SZ'}</div>
<div>RCCM: {currentDoc.client?.rccm || 'RC/DLA/2023/B/5504'}</div>
</div>
</div>

{/* Numéro et Date dans encadré gris à droite */}
<div style={{
padding: '4px 8px',
background: '#E5E7EB',
border: '1px solid #9CA3AF',
fontSize: config.invoiceNumSize,
textAlign: 'center',
minWidth: '120px'
}}>
<div style={{ fontWeight: 'bold', marginBottom: '1px', fontSize: config.invoiceNumSize }}>
{currentDoc.type === 'devis' ? 'DEVIS N°' : 'FACTURE N°'} {currentDoc.numero}
</div>
<div style={{ fontSize: config.invoiceDateSize }}>
DATE: {new Date(currentDoc.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase().replace('.', '')}
</div>
</div>
</div>
</div>

{/* Titre FACTURE avec fond blanc et texte noir */}
<div style={{
textAlign: 'center',
padding: config.titlePadding,
background: '#FFFFFF',
color: '#000000',
fontSize: config.titleSize,
fontWeight: 'bold',
letterSpacing: '0.3em',
marginBottom: config.sectionSpacing,
border: '2px solid #000000'
}}>
{currentDoc.type === 'devis' ? 'D E V I S' : 'F A C T U R E'}
</div>

{/* Tableau articles - optimisé dynamiquement */}
<table style={{ width: '100%', fontSize: config.tableBodySize, borderCollapse: 'collapse', marginBottom: config.tableMargin }}>
<thead>
<tr style={{ background: '#F9FAFB', borderTop: '1.5px solid #000000', borderBottom: '1.5px solid #000000' }}>
<th style={{ padding: config.tableHeaderPadding, textAlign: 'left', fontWeight: 'bold', fontSize: config.tableHeaderSize }}>DÉSIGNATION</th>
<th style={{ padding: config.tableHeaderPadding, textAlign: 'center', fontWeight: 'bold', width: '35px', fontSize: config.tableHeaderSize }}>UNITÉ</th>
<th style={{ padding: config.tableHeaderPadding, textAlign: 'center', fontWeight: 'bold', width: '30px', fontSize: config.tableHeaderSize }}>QTÉ</th>
<th style={{ padding: config.tableHeaderPadding, textAlign: 'right', fontWeight: 'bold', width: '65px', fontSize: config.tableHeaderSize }}>PRIX UNIT.</th>
<th style={{ padding: config.tableHeaderPadding, textAlign: 'right', fontWeight: 'bold', width: '70px', fontSize: config.tableHeaderSize }}>TOTAL</th>
</tr>
</thead>
<tbody>
{renderPrintItems(currentDoc.items, { tableBodySize: config.tableBodySize, tableCellPadding: config.tableCellPadding, compact: true })}
</tbody>
</table>

{/* Section totaux - adaptative */}
<div style={{ marginBottom: config.sectionSpacing, marginTop: 'auto' }}>
{/* Sous-total et Total HT avec fond gris */}
<div style={{
background: '#F3F4F6',
padding: config.totalPadding,
borderTop: '1px solid #D1D5DB',
borderBottom: '1.5px solid #000000'
}}>
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: config.totalFontSize, marginBottom: '2px' }}>
<span style={{ fontWeight: '600' }}>{currentDoc.afficherModeTaxation ? 'SOUS-TOTAL HT' : 'SOUS-TOTAL'}</span>
<span style={{ fontWeight: '600' }}>{formatMontant(currentDoc.sousTotal)}</span>
</div>
{currentDoc.totalRemises > 0 && (
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: config.totalLabelSize, marginBottom: '2px', color: '#10B981' }}>
<span>Remises</span>
<span>-{formatMontant(currentDoc.totalRemises)}</span>
</div>
)}
{(currentDoc.fraisSupplementaires || []).filter(f => f.montant > 0).map((frais, idx) => (
<div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: config.totalLabelSize, marginBottom: '2px', color: '#F59E0B' }}>
<span>+ {frais.libelle || frais.description || 'Frais supplémentaires'}</span>
<span>{formatMontant(frais.montant)}</span>
</div>
))}
{currentDoc.afficherModeTaxation && currentDoc.taxMode === 'TTC' && (
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: config.totalLabelSize, marginBottom: '2px' }}>
<span>TVA ({settingsEffectifs.tauxTVA || 19.25}%)</span>
<span>{formatMontant(currentDoc.tva)}</span>
</div>
)}
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: config.totalLabelSize, fontWeight: 'bold', paddingTop: '2px', borderTop: '0.5px solid #D1D5DB' }}>
<span>{currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'TOTAL TTC' : 'TOTAL HT') : 'TOTAL'}</span>
<span>{formatMontant(currentDoc.total)}</span>
</div>
</div>

{/* Encadré TOTAL À PAYER avec QR code */}
<div style={{
display: 'flex',
alignItems: 'center',
gap: '6px',
marginTop: config.sectionSpacing,
padding: '5px',
border: '1.5px solid #000000'
}}>
{/* QR Code à gauche */}
{(() => {
const _paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const montantAPayer = _paye > 0 ? Math.max(0, (currentDoc.total || 0) - _paye) : currentDoc.total;

const qrData = `${currentDoc.numero}|${(currentDoc.client?.nom||'')}|${montantAPayer}`;
const encodedData = encodeURIComponent(qrData);
const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodedData}`;

return (
<img
src={qrCodeUrl}
alt="QR Code"
style={{
width: config.qrSize,
height: config.qrSize,
objectFit: 'contain',
flexShrink: 0
}}
onError={(e) => {
e.target.style.display = 'none';
}}
/>
);
})()}

{/* TOTAL À PAYER à droite */}
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
const montantAffiche = restant > 0 ? restant : currentDoc.total;
return (
<div style={{ flex: 1, textAlign: 'center' }}>
<div style={{ fontSize: config.totalPayerLabel, fontWeight: 'bold', marginBottom: '1px' }}>{paye > 0 && restant > 0 ? (currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'RESTE À PAYER TTC' : 'RESTE À PAYER HT') : 'RESTE À PAYER') : (currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'TOTAL TTC' : 'TOTAL HT') : 'TOTAL')}</div>
<div style={{ fontSize: config.totalPayerAmount, fontWeight: 'bold' }}>{formatMontant(montantAffiche)}</div>
<div style={{ fontSize: config.totalPayerText, fontStyle: 'italic', marginTop: '1px', lineHeight: '1.1' }}>
{nombreEnLettres(Math.floor(montantAffiche))} francs CFA
</div>
</div>
);
})()}
</div>
</div>

{/* Méthode de paiement - adaptative */}
{currentDoc.type === 'facture' && (
<div style={{ fontSize: config.paymentMethodSize, marginBottom: '3px', padding: '2px 4px', background: '#F9FAFB', border: '0.5px solid #E5E7EB' }}>
<strong>MÉTHODE DE PAIEMENT:</strong> {formatMethodePaiement(currentDoc.methodePaiement || 'toutes')}
</div>
)}

{/* Footer - adaptatif */}
<div style={{
textAlign: 'center',
fontSize: config.footerSize,
color: '#6B7280',
borderTop: '0.5px solid #E5E7EB',
paddingTop: '3px'
}}>
<div style={{ fontWeight: '600', marginBottom: '1px' }}>MERCI POUR VOTRE CONFIANCE!</div>
<div>{settings.siteWeb || 'https://solstice-sarl.com'}</div>
</div>
</div>
);
};

return (
<div style={{
width: '210mm',
minHeight: '297mm',
background: 'white',
margin: '0 auto',
display: 'flex',
flexDirection: 'column'
}}>
{/* Première facture A5 */}
{renderFactureA5()}

{/* Ligne de coupe */}
<div style={{
borderTop: '2px dashed #94A3B8',
padding: '0.5rem 0',
textAlign: 'center',
fontSize: '0.7rem',
color: '#64748B',
fontWeight: '600',
background: '#F9FAFB'
}}>
✂️ DÉCOUPER ICI ✂️
</div>

{/* Deuxième facture A5 (identique) */}
{renderFactureA5()}
</div>
);
} else if (modeleActif === 'compact-duplicata-sans-ht') {
// MODÈLE COMPACT DUPLICATA A5 SANS HT - 2 factures A5 sur une page A4
// Fonction pour rendre une facture A5
const renderFactureA5 = () => {
// Gestion dynamique de l'espace selon le nombre d'articles
const nbArticles = currentDoc.items.length;
const maxArticlesToShow = Math.min(nbArticles, 10);

// Configuration adaptative selon le nombre d'articles
let config = {};

if (nbArticles <= 5) {
// Mode confortable : beaucoup d'espace
config = {
padding: '8mm',
fontSize: '8pt',
lineHeight: '1.3',
logoSize: '50px',
companyNameSize: '9.5pt',
companyDetailSize: '6.5pt',
clientLabelSize: '6.5pt',
clientNameSize: '7pt',
clientDetailSize: '6pt',
invoiceNumSize: '7pt',
invoiceDateSize: '6.5pt',
titleSize: '10pt',
titlePadding: '5px',
headerMargin: '6px',
tableHeaderSize: '7pt',
tableBodySize: '6.5pt',
tableCellPadding: '3px 5px',
tableHeaderPadding: '3px 5px',
tableMargin: '8px',
totalFontSize: '7pt',
totalLabelSize: '7.5pt',
totalPadding: '5px 8px',
qrSize: '42px',
totalPayerLabel: '7pt',
totalPayerAmount: '11pt',
totalPayerText: '6pt',
paymentMethodSize: '6pt',
footerSize: '6pt',
sectionSpacing: '6px'
};
} else if (nbArticles <= 8) {
// Mode intermédiaire : bon équilibre
config = {
padding: '6.5mm',
fontSize: '7.5pt',
lineHeight: '1.25',
logoSize: '47px',
companyNameSize: '9pt',
companyDetailSize: '6.2pt',
clientLabelSize: '6.2pt',
clientNameSize: '6.7pt',
clientDetailSize: '5.7pt',
invoiceNumSize: '6.7pt',
invoiceDateSize: '6.2pt',
titleSize: '9.5pt',
titlePadding: '4.5px',
headerMargin: '5px',
tableHeaderSize: '6.7pt',
tableBodySize: '6.2pt',
tableCellPadding: '2.5px 4.5px',
tableHeaderPadding: '2.5px 4.5px',
tableMargin: '6px',
totalFontSize: '6.7pt',
totalLabelSize: '7.2pt',
totalPadding: '4.5px 7px',
qrSize: '40px',
totalPayerLabel: '6.7pt',
totalPayerAmount: '10.5pt',
totalPayerText: '5.7pt',
paymentMethodSize: '5.7pt',
footerSize: '5.7pt',
sectionSpacing: '5px'
};
} else {
// Mode compact : optimisé pour 9-10 articles
config = {
padding: '6mm',
fontSize: '7pt',
lineHeight: '1.2',
logoSize: '45px',
companyNameSize: '8.5pt',
companyDetailSize: '6pt',
clientLabelSize: '6pt',
clientNameSize: '6.5pt',
clientDetailSize: '5.5pt',
invoiceNumSize: '6.5pt',
invoiceDateSize: '6pt',
titleSize: '9pt',
titlePadding: '4px',
headerMargin: '4px',
tableHeaderSize: '6.5pt',
tableBodySize: '6pt',
tableCellPadding: '2px 4px',
tableHeaderPadding: '2px 4px',
tableMargin: '5px',
totalFontSize: '6.5pt',
totalLabelSize: '7pt',
totalPadding: '4px 6px',
qrSize: '38px',
totalPayerLabel: '6.5pt',
totalPayerAmount: '10pt',
totalPayerText: '5.5pt',
paymentMethodSize: '5.5pt',
footerSize: '5.5pt',
sectionSpacing: '4px'
};
}

return (
<div style={{
width: '100%',
height: '148.5mm',
padding: config.padding,
background: 'white',
fontSize: config.fontSize,
lineHeight: config.lineHeight,
boxSizing: 'border-box',
border: '1px solid #E5E7EB',
fontFamily: 'Arial, sans-serif',
display: 'flex',
flexDirection: 'column'
}}>
{/* En-tête complet */}
<div style={{ marginBottom: config.headerMargin }}>
{/* Première ligne : Logo + Infos Entreprise | Infos Client (centre) | Numéro Facture (droite) */}
<div style={{
display: 'grid',
gridTemplateColumns: 'auto 1fr auto',
gap: '6px',
marginBottom: config.sectionSpacing,
alignItems: 'start'
}}>
{/* Logo + Infos entreprise */}
<div style={{ display: 'flex', gap: '5px', alignItems: 'start' }}>
{/* Logo */}
<div>
{settings.logo && (
<img src={settings.logo} alt="Logo" style={{ width: config.logoSize, height: config.logoSize, objectFit: 'contain' }} />
)}
</div>

{/* Infos entreprise */}
<div style={{ textAlign: 'left' }}>
<div style={{ fontWeight: 'bold', fontSize: config.companyNameSize, marginBottom: '1px' }}>
{settings.raisonSociale || 'SOLSTICE'}
</div>
<div style={{ fontSize: config.companyDetailSize, color: '#000000', lineHeight: '1.3' }}>
<div>{settings.adresse || 'LOGBESSOU - DOUALA'}</div>
<div>{settings.telephone || '+237682376850'}</div>
<div>NUI: {settings.nui || 'M102316152527W'}</div>
<div>RCCM: {settings.rccm || 'RC/DLA/2023/B/6242'}</div>
</div>
</div>
</div>

{/* Infos client au centre dans encadré gris */}
<div style={{
padding: '4px 6px',
background: '#F3F4F6',
border: '1px solid #D1D5DB',
fontSize: config.clientLabelSize,
textAlign: 'left',
justifySelf: 'center',
maxWidth: '160px'
}}>
<div style={{ fontWeight: 'bold', fontSize: config.clientLabelSize, marginBottom: '2px' }}>
Facturé à:
</div>
<div style={{ fontWeight: 'bold', fontSize: config.clientNameSize, marginBottom: '1px' }}>
{(currentDoc.client?.nom||'') || 'INSTITUT UNIVERSITAIRE DE LA CÔTE (IUC.SA)'}
</div>
<div style={{ fontSize: config.clientDetailSize, lineHeight: '1.2' }}>
<div>BP: {currentDoc.client?.bp || '3001 Dla'}</div>
<div>Tél: {currentDoc.client?.telephone}</div>
<div>NUI: {currentDoc.client?.nui || 'M030202001464SZ'}</div>
<div>RCCM: {currentDoc.client?.rccm || 'RC/DLA/2023/B/5504'}</div>
</div>
</div>

{/* Numéro et Date dans encadré gris à droite */}
<div style={{
padding: '4px 8px',
background: '#E5E7EB',
border: '1px solid #9CA3AF',
fontSize: config.invoiceNumSize,
textAlign: 'center',
minWidth: '120px'
}}>
<div style={{ fontWeight: 'bold', marginBottom: '1px', fontSize: config.invoiceNumSize }}>
{currentDoc.type === 'devis' ? 'DEVIS N°' : 'FACTURE N°'} {currentDoc.numero}
</div>
<div style={{ fontSize: config.invoiceDateSize }}>
DATE: {new Date(currentDoc.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase().replace('.', '')}
</div>
</div>
</div>
</div>

{/* Titre FACTURE avec fond blanc et texte noir */}
<div style={{
textAlign: 'center',
padding: config.titlePadding,
background: '#FFFFFF',
color: '#000000',
fontSize: config.titleSize,
fontWeight: 'bold',
letterSpacing: '0.3em',
marginBottom: config.sectionSpacing,
border: '2px solid #000000'
}}>
{currentDoc.type === 'devis' ? 'D E V I S' : 'F A C T U R E'}
</div>

{/* Tableau articles - optimisé dynamiquement */}
<table style={{ width: '100%', fontSize: config.tableBodySize, borderCollapse: 'collapse', marginBottom: config.tableMargin }}>
<thead>
<tr style={{ background: '#F9FAFB', borderTop: '1.5px solid #000000', borderBottom: '1.5px solid #000000' }}>
<th style={{ padding: config.tableHeaderPadding, textAlign: 'left', fontWeight: 'bold', fontSize: config.tableHeaderSize }}>DÉSIGNATION</th>
<th style={{ padding: config.tableHeaderPadding, textAlign: 'center', fontWeight: 'bold', width: '35px', fontSize: config.tableHeaderSize }}>UNITÉ</th>
<th style={{ padding: config.tableHeaderPadding, textAlign: 'center', fontWeight: 'bold', width: '30px', fontSize: config.tableHeaderSize }}>QTÉ</th>
<th style={{ padding: config.tableHeaderPadding, textAlign: 'right', fontWeight: 'bold', width: '65px', fontSize: config.tableHeaderSize }}>PRIX UNIT.</th>
<th style={{ padding: config.tableHeaderPadding, textAlign: 'right', fontWeight: 'bold', width: '70px', fontSize: config.tableHeaderSize }}>TOTAL</th>
</tr>
</thead>
<tbody>
{renderPrintItems(currentDoc.items, { tableBodySize: config.tableBodySize, tableCellPadding: config.tableCellPadding, compact: true })}
</tbody>
</table>

{/* Section totaux - adaptative selon le mode HT/TTC */}
<div style={{ marginBottom: config.sectionSpacing, marginTop: 'auto' }}>
{/* Affichage différent selon le mode HT ou TTC */}
{currentDoc.taxMode === 'TTC' ? (
// Mode TTC : Afficher HT, TVA et TTC
<div style={{
background: '#F3F4F6',
padding: config.totalPadding,
borderTop: '1px solid #D1D5DB',
borderBottom: '1.5px solid #000000'
}}>
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: config.totalFontSize, marginBottom: '2px' }}>
<span style={{ fontWeight: '600' }}>{currentDoc.afficherModeTaxation ? 'SOUS-TOTAL HT' : 'SOUS-TOTAL'}</span>
<span style={{ fontWeight: '600' }}>{formatMontant(currentDoc.sousTotal)}</span>
</div>
{(currentDoc.fraisSupplementaires || []).filter(f => f.montant > 0).map((frais, idx) => (
<div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: config.totalFontSize, marginBottom: '2px', color: '#000' }}>
<span style={{ fontWeight: '600' }}>+ {frais.libelle || frais.description || 'Frais supplémentaires'}</span>
<span style={{ fontWeight: '600' }}>{formatMontant(frais.montant)}</span>
</div>
))}
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: config.totalFontSize, marginBottom: '2px' }}>
<span style={{ fontWeight: '600' }}>TVA ({settings.tauxTVA || 19.25}%)</span>
<span style={{ fontWeight: '600' }}>{formatMontant(currentDoc.tva)}</span>
</div>
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: config.totalLabelSize, fontWeight: 'bold', paddingTop: '2px', borderTop: '0.5px solid #D1D5DB' }}>
<span>{currentDoc.afficherModeTaxation ? 'TOTAL TTC' : 'TOTAL'}</span>
<span>{formatMontant(currentDoc.total)}</span>
</div>
</div>
) : (
// Mode HT : Afficher sans mention HT
<div style={{
background: '#F3F4F6',
padding: config.totalPadding,
borderTop: '1px solid #D1D5DB',
borderBottom: '1.5px solid #000000'
}}>
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: config.totalFontSize, marginBottom: '2px' }}>
<span style={{ fontWeight: '600' }}>{currentDoc.afficherModeTaxation ? 'SOUS-TOTAL HT' : 'SOUS-TOTAL'}</span>
<span style={{ fontWeight: '600' }}>{formatMontant(currentDoc.sousTotal)}</span>
</div>
{(currentDoc.fraisSupplementaires || []).filter(f => f.montant > 0).map((frais, idx) => (
<div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: config.totalLabelSize, marginBottom: '2px', color: '#000' }}>
<span style={{ fontWeight: '600' }}>+ {frais.libelle || frais.description || 'Frais supplémentaires'}</span>
<span style={{ fontWeight: '600' }}>{formatMontant(frais.montant)}</span>
</div>
))}
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: config.totalLabelSize, fontWeight: 'bold', paddingTop: '2px', borderTop: '0.5px solid #D1D5DB' }}>
<span>{currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'TOTAL TTC' : 'TOTAL HT') : 'TOTAL'}</span>
<span>{formatMontant(currentDoc.total)}</span>
</div>
</div>
)}

{/* Encadré TOTAL À PAYER avec QR code */}
<div style={{
display: 'flex',
alignItems: 'center',
gap: '6px',
marginTop: config.sectionSpacing,
padding: '5px',
border: '1.5px solid #000000'
}}>
{/* QR Code à gauche */}
{(() => {
const _paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const montantAPayer = _paye > 0 ? Math.max(0, (currentDoc.total || 0) - _paye) : currentDoc.total;

const qrData = `${currentDoc.numero}|${(currentDoc.client?.nom||'')}|${montantAPayer}`;
const encodedData = encodeURIComponent(qrData);
const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodedData}`;

return (
<img
src={qrCodeUrl}
alt="QR Code"
style={{
width: config.qrSize,
height: config.qrSize,
objectFit: 'contain',
flexShrink: 0
}}
onError={(e) => {
e.target.style.display = 'none';
}}
/>
);
})()}

{/* TOTAL À PAYER à droite */}
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
const montantAffiche = restant > 0 ? restant : currentDoc.total;
return (
<div style={{ flex: 1, textAlign: 'center' }}>
<div style={{ fontSize: config.totalPayerLabel, fontWeight: 'bold', marginBottom: '1px' }}>{paye > 0 && restant > 0 ? (currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'RESTE À PAYER TTC' : 'RESTE À PAYER HT') : 'RESTE À PAYER') : (currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'TOTAL TTC' : 'TOTAL HT') : 'TOTAL')}</div>
<div style={{ fontSize: config.totalPayerAmount, fontWeight: 'bold' }}>{formatMontant(montantAffiche)}</div>
<div style={{ fontSize: config.totalPayerText, fontStyle: 'italic', marginTop: '1px', lineHeight: '1.1' }}>
{nombreEnLettres(Math.floor(montantAffiche))} francs CFA
</div>
</div>
);
})()}
</div>
</div>

{/* Méthode de paiement - adaptative */}
{currentDoc.type === 'facture' && (
<div style={{ fontSize: config.paymentMethodSize, marginBottom: '3px', padding: '2px 4px', background: '#F9FAFB', border: '0.5px solid #E5E7EB' }}>
<strong>MÉTHODE DE PAIEMENT:</strong> {formatMethodePaiement(currentDoc.methodePaiement || 'toutes')}
</div>
)}

{/* Footer - adaptatif */}
<div style={{
textAlign: 'center',
fontSize: config.footerSize,
color: '#6B7280',
borderTop: '0.5px solid #E5E7EB',
paddingTop: '3px'
}}>
<div style={{ fontWeight: '600', marginBottom: '1px' }}>MERCI POUR VOTRE CONFIANCE!</div>
<div>{settings.siteWeb || 'https://solstice-sarl.com'}</div>
</div>
</div>
);
};

return (
<div style={{
width: '210mm',
minHeight: '297mm',
background: 'white',
margin: '0 auto',
display: 'flex',
flexDirection: 'column'
}}>
{/* Première facture A5 */}
{renderFactureA5()}

{/* Ligne de coupe */}
<div style={{
borderTop: '2px dashed #94A3B8',
padding: '0.5rem 0',
textAlign: 'center',
fontSize: '0.7rem',
color: '#64748B',
fontWeight: '600',
background: '#F9FAFB'
}}>
✂️ DÉCOUPER ICI ✂️
</div>

{/* Deuxième facture A5 (identique) */}
{renderFactureA5()}
</div>
);
} else if (modeleActif === 'noir-blanc2-a5') {
// MODÈLE NOIR ET BLANC 2 A5 - Format compact avec filigrane et duplicata
const renderFactureA5NB2 = () => {
return (
<div style={{
width: '100%',
height: '148.5mm',
padding: '6mm',
background: 'white',
fontFamily: 'Arial, sans-serif',
fontSize: '7.5pt',
lineHeight: '1.2',
color: '#000000',
boxSizing: 'border-box',
border: '0.5px solid #000',
position: 'relative',
overflow: 'hidden'
}}>
{/* FILIGRANE LOGO */}
{settings.logo && (
<div style={{
position: 'absolute',
top: '50%',
left: '50%',
transform: 'translate(-50%, -50%)',
opacity: 0.08,
pointerEvents: 'none',
zIndex: 0,
width: '60%',
height: '60%',
display: 'flex',
alignItems: 'center',
justifyContent: 'center'
}}>
<img
src={settings.logo}
alt=""
style={{
maxWidth: '100%',
maxHeight: '100%',
objectFit: 'contain',
filter: 'grayscale(100%)'
}}
/>
</div>
)}

{/* Contenu principal */}
<div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>

{/* HEADER ULTRA-COMPACT */}
<div style={{
display: 'grid',
gridTemplateColumns: 'auto 1fr auto',
gap: '4px',
marginBottom: '3mm',
paddingBottom: '2mm',
borderBottom: '1px solid #000',
alignItems: 'start'
}}>
{/* Logo miniature */}
<div style={{ width: '35px', height: '35px' }}>
{settings.logo ? (
<img src={settings.logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
) : (
<div style={{ width: '100%', height: '100%', border: '1px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '6pt', fontWeight: 'bold' }}>
LOGO
</div>
)}
</div>

{/* Infos entreprise */}
<div style={{ fontSize: '6.5pt', lineHeight: '1.15' }}>
<div style={{ fontWeight: 'bold', fontSize: '9pt', marginBottom: '1px', textTransform: 'uppercase' }}>
{settings.raisonSociale}
</div>
<div>{settings.adresse}</div>
<div>Tél: {settings.telephone || '—'}</div>
<div style={{ fontSize: '6pt', marginTop: '1px' }}>
NUI: {settings.nui || '—'} | RCCM: {settings.rccm || '—'}
</div>
</div>

{/* Bloc Facture N° + Date */}
<div style={{
border: '1px solid #000',
padding: '2px 4px',
textAlign: 'center',
minWidth: '70px'
}}>
<div style={{ fontWeight: 'bold', fontSize: '6.5pt' }}>
{currentDoc.type === 'devis' ? 'DEVIS' : 'FACTURE'} N°
</div>
<div style={{ fontSize: '7pt', fontFamily: 'monospace', fontWeight: 'bold' }}>
{currentDoc.numero}
</div>
<div style={{ fontSize: '6pt', marginTop: '1px' }}>
{new Date(currentDoc.date).toLocaleDateString('fr-FR')}
</div>
</div>
</div>

{/* CLIENT ENCADRÉ */}
<div style={{
border: '0.5px solid #000',
padding: '2px 4px',
marginBottom: '2mm',
fontSize: '6.5pt',
background: 'rgba(0,0,0,0.02)'
}}>
<div style={{ fontWeight: 'bold', fontSize: '6pt', textTransform: 'uppercase', marginBottom: '1px' }}>
Facturé à:
</div>
<div style={{ fontWeight: 'bold', fontSize: '7.5pt' }}>
{(currentDoc.client?.nom||'')}
</div>
<div style={{ fontSize: '6pt', lineHeight: '1.1' }}>
{currentDoc.client?.adresse && <div>{currentDoc.client?.adresse}</div>}
<div>NUI: {currentDoc.client?.nui || 'N/A'} | RCCM: {currentDoc.client?.rccm || 'N/A'}</div>
</div>
</div>

{/* TABLEAU ARTICLES */}
<table style={{
width: '100%',
fontSize: '6.5pt',
borderCollapse: 'collapse',
marginBottom: '2mm',
flex: '1'
}}>
<thead>
<tr style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000', background: '#f5f5f5' }}>
<th style={{ padding: '2px', textAlign: 'left', fontWeight: 'bold', width: '45%' }}>DÉSIGNATION</th>
<th style={{ padding: '2px', textAlign: 'center', fontWeight: 'bold', width: '8%' }}>UN</th>
<th style={{ padding: '2px', textAlign: 'center', fontWeight: 'bold', width: '8%' }}>QTÉ</th>
<th style={{ padding: '2px', textAlign: 'right', fontWeight: 'bold', width: '18%' }}>P.U.</th>
<th style={{ padding: '2px', textAlign: 'right', fontWeight: 'bold', width: '21%' }}>TOTAL</th>
</tr>
</thead>
<tbody>
{renderPrintItems(currentDoc.items, { tableBodySize: '6.5pt', tableCellPadding: '1.5px 2px', compact: true })}
</tbody>
</table>

{/* SECTION TOTAUX */}
<div style={{ marginTop: 'auto' }}>
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
const montantAffiche = restant > 0 ? restant : currentDoc.total;
return (
<>
<div style={{
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
borderTop: '1px solid #000',
borderBottom: '1px solid #000',
padding: '2px 0',
marginBottom: '2mm',
background: 'rgba(0,0,0,0.03)'
}}>
<div style={{ fontSize: '7pt', fontWeight: 'bold' }}>
{paye > 0 && restant > 0 ? (currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'RESTE À PAYER TTC' : 'RESTE À PAYER HT') : 'RESTE À PAYER') : (currentDoc.afficherModeTaxation ? `TOTAL ${currentDoc.taxMode === 'TTC' ? 'TTC' : 'HT'}` : 'TOTAL')}
</div>
<div style={{ fontSize: '10pt', fontWeight: 'bold', fontFamily: 'monospace' }}>
{formatMontant(montantAffiche)}
</div>
</div>

<div style={{
textAlign: 'center',
fontSize: '6pt',
fontStyle: 'italic',
marginBottom: '2mm',
borderBottom: '0.5px solid #ccc',
paddingBottom: '1mm'
}}>
Arrêté la présente {currentDoc.type === 'devis' ? 'facture proforma' : 'facture'} à la somme de :
<span style={{ fontWeight: 'bold', fontStyle: 'normal', marginLeft: '2px' }}>
{nombreEnLettres(Math.floor(montantAffiche))} francs CFA
</span>
</div>
</>
);
})()}

<div style={{
display: 'flex',
justifyContent: 'space-between',
fontSize: '5.5pt',
borderTop: '0.5px solid #000',
paddingTop: '1mm'
}}>
<div>
<strong>Mode de règlement:</strong> {formatMethodePaiement(currentDoc.methodePaiement || 'toutes')}
</div>
<div style={{ textAlign: 'right' }}>
<div style={{ fontWeight: 'bold' }}>{settings.raisonSociale}</div>
<div>Merci pour votre confiance!</div>
</div>
</div>
</div>
</div>
</div>
);
};

return (
<div style={{
width: '210mm',
minHeight: '297mm',
background: 'white',
margin: '0 auto',
display: 'flex',
flexDirection: 'column',
padding: '0'
}}>
{renderFactureA5NB2()}

<div style={{
borderTop: '1px dashed #999',
margin: '2mm 0',
textAlign: 'center',
fontSize: '6pt',
color: '#666',
padding: '1mm 0'
}}>
✂️ DÉCOUPER ICI ✂️
</div>

{renderFactureA5NB2()}
</div>
);
} else if (modeleActif === 'minimaliste-elegant') {
// ═══════════════════════════════════════
// MODÈLE MINIMALISTE ÉLÉGANT
// ═══════════════════════════════════════
return (
<div className="preview-container" style={{
background: '#FFFFFF',
padding: '40px 48px',
fontFamily: 'Georgia, "Times New Roman", serif',
color: '#1A1A1A',
maxWidth: '210mm',
margin: '0 auto'
}}>
{/* Header : logo à gauche, infos entreprise à droite */}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '48px', paddingBottom: '24px', borderBottom: '1px solid #E5E5E5' }}>
<div style={{ width: '90px', height: '90px', border: '1.5px dashed #CCC', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#CCC', fontSize: '0.65rem', letterSpacing: '1px', borderRadius: '4px', flexShrink: 0 }}>
{settings.logo
? <img src={settings.logo} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
: 'LOGO'}
</div>
<div style={{ textAlign: 'right' }}>
<div style={{ fontSize: '14px', fontWeight: '600', fontFamily: '"Inter", Arial, sans-serif', color: '#1A1A1A' }}>{settings.raisonSociale}</div>
<div style={{ fontSize: '11px', color: '#666', marginTop: '8px', lineHeight: '1.7' }}>
{settings.adresse && <div>{settings.adresse}</div>}
{settings.telephone && <div>Tél : {settings.telephone}</div>}
{settings.email && <div>{settings.email}</div>}
</div>
{(settings.nui || settings.rccm) && (
<div style={{ fontSize: '10px', color: '#999', marginTop: '6px' }}>
{settings.nui && `NUI : ${settings.nui}`}{settings.nui && settings.rccm && ' | '}{settings.rccm && `RCCM : ${settings.rccm}`}
</div>
)}
</div>
</div>

{/* Titre centré */}
<div style={{ textAlign: 'center', marginBottom: '40px' }}>
<h1 style={{ fontSize: '42px', fontWeight: '300', letterSpacing: '10px', color: '#2C5F5D', margin: '0', fontFamily: '"Inter", Arial, sans-serif' }}>
{currentDoc.type === 'devis' ? 'DEVIS' : 'FACTURE'}
</h1>
<div style={{ fontSize: '12px', color: '#666', marginTop: '14px', letterSpacing: '0.5px', fontFamily: '"Inter", Arial, sans-serif' }}>
N° {currentDoc.numero} &nbsp;|&nbsp; {new Date(currentDoc.date).toLocaleDateString('fr-FR')}
{currentDoc.type === 'facture' && currentDoc.dateEcheance && (
<span> &nbsp;|&nbsp; Échéance : {new Date(currentDoc.dateEcheance).toLocaleDateString('fr-FR')}</span>
)}
</div>
{currentDoc.objet && (
<div style={{ fontSize: '11px', color: '#888', marginTop: '6px', fontStyle: 'italic', fontFamily: '"Inter", Arial, sans-serif' }}>
Objet : {currentDoc.objet}
</div>
)}
</div>

{/* Client */}
<div style={{ marginBottom: '40px' }}>
<div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px', fontFamily: '"Inter", Arial, sans-serif' }}>
{currentDoc.type === 'devis' ? 'Destinataire' : 'Facturé à'}
</div>
<div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '4px', fontFamily: '"Inter", Arial, sans-serif' }}>{(currentDoc.client?.nom||'')}</div>
<div style={{ fontSize: '11px', color: '#666', lineHeight: '1.7', fontFamily: '"Inter", Arial, sans-serif' }}>
{currentDoc.client?.adresse && <div>{currentDoc.client?.adresse}</div>}
{currentDoc.client?.telephone && <div>Tél : {currentDoc.client?.telephone}</div>}
{currentDoc.client?.email && <div>{currentDoc.client?.email}</div>}
{currentDoc.client?.nui && <div style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>NUI : {currentDoc.client?.nui}</div>}
</div>
</div>

{/* Tableau des articles */}
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '36px', fontFamily: '"Inter", Arial, sans-serif' }}>
<thead>
<tr style={{ borderBottom: '2px solid #1A1A1A' }}>
<th style={{ textAlign: 'left', padding: '10px 0', fontWeight: '600' }}>Description</th>
<th style={{ textAlign: 'center', padding: '10px 0', width: '60px', fontWeight: '600' }}>Qté</th>
<th style={{ textAlign: 'center', padding: '10px 0', width: '70px', fontWeight: '600' }}>Unité</th>
<th style={{ textAlign: 'right', padding: '10px 0', width: '130px', fontWeight: '600' }}>Prix unit.</th>
<th style={{ textAlign: 'right', padding: '10px 0', width: '130px', fontWeight: '600' }}>Total</th>
</tr>
</thead>
<tbody>
{currentDoc.items.map((item, i) => (
<tr key={i} style={{ borderBottom: '1px solid #EFEFEF' }}>
<td style={{ padding: '10px 0', verticalAlign: 'top' }}>
<div>{getItemDescriptionComplete(item)}</div>
{item.remiseType && item.remiseType !== 'aucune' && item.remiseValeur > 0 && (
<div style={{ fontSize: '10px', color: '#2C5F5D', marginTop: '2px' }}>
Remise : {item.remiseType === 'pourcentage' ? `${item.remiseValeur}%` : `${formatMontant(item.remiseValeur)}`}
</div>
)}
</td>
<td style={{ textAlign: 'center', padding: '10px 0', color: '#555' }}>{item.quantite}</td>
<td style={{ textAlign: 'center', padding: '10px 0', color: '#888', fontSize: '10px' }}>{item.unite || 'PCS'}</td>
<td style={{ textAlign: 'right', padding: '10px 0', fontFamily: 'monospace', color: '#555' }}>{formatMontant(item.prixUnitaire)}</td>
<td style={{ textAlign: 'right', padding: '10px 0', fontFamily: 'monospace', fontWeight: '600' }}>{formatMontant(item.montantApresRemise || item.quantite * item.prixUnitaire)}</td>
</tr>
))}
</tbody>
</table>

{/* Totaux */}
<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '32px' }}>
<div style={{ width: '280px', fontFamily: '"Inter", Arial, sans-serif' }}>
{currentDoc.remiseGlobale && currentDoc.remiseGlobale.type !== 'aucune' && currentDoc.remiseGlobale.montant > 0 && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #E5E5E5', fontSize: '12px', color: '#666' }}>
<span>Remise globale</span>
<span style={{ fontFamily: 'monospace', color: '#2C5F5D' }}>- {formatMontant(currentDoc.remiseGlobale.montant)}</span>
</div>
)}
{currentDoc.fraisSupplementaires && currentDoc.fraisSupplementaires.length > 0 && currentDoc.fraisSupplementaires.map((frais, i) => (
<div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #E5E5E5', fontSize: '12px', color: '#666' }}>
<span>{frais.libelle}</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(frais.montant)}</span>
</div>
))}
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #E5E5E5', fontSize: '12px', color: '#666' }}>
<span>{currentDoc.afficherModeTaxation ? 'Sous-total H.T.' : 'Sous-total'}</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(currentDoc.totalAvantTaxe || currentDoc.sousTotal)}</span>
</div>
{currentDoc.afficherModeTaxation && currentDoc.taxMode === 'TTC' && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #E5E5E5', fontSize: '12px', color: '#666' }}>
<span>TVA ({settings.tauxTVA || 19.25}%)</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(currentDoc.tva)}</span>
</div>
)}
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
return (
<>
{paye > 0 && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #E5E5E5', fontSize: '12px', color: '#10B981' }}>
<span>Montant payé</span>
<span style={{ fontFamily: 'monospace', fontWeight: 600 }}>-{formatMontant(paye)}</span>
</div>
)}
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0', borderTop: '2px solid #1A1A1A', marginTop: '4px', fontSize: '16px', fontWeight: '600' }}>
<span>{paye > 0 && restant > 0 ? (currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'Reste à payer T.T.C.' : 'Reste à payer H.T.') : 'Reste à payer') : (currentDoc.afficherModeTaxation ? `Total ${currentDoc.taxMode === 'TTC' ? 'T.T.C.' : 'H.T.'}` : 'Total')}</span>
<span style={{ fontFamily: 'monospace', color: paye > 0 && restant > 0 ? '#EF4444' : '#2C5F5D' }}>{formatMontant(restant > 0 ? restant : currentDoc.total)}</span>
</div>
</>
);
})()}
</div>
</div>

{/* Montant en lettres */}
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
return (
<div style={{ textAlign: 'center', padding: '16px 20px', background: '#F5F5F5', fontSize: '11px', color: '#666', marginBottom: '28px', fontFamily: '"Inter", Arial, sans-serif', borderRadius: '2px' }}>
Arrêté la présente {currentDoc.type === 'devis' ? 'offre de prix' : 'facture'} à la somme de : <strong>{nombreEnLettres(Math.floor(restant > 0 ? restant : currentDoc.total))} francs CFA</strong>
</div>
);
})()}

{/* Notes */}
{currentDoc.notes && (
<div style={{ marginBottom: '20px', padding: '12px 16px', background: '#FAFAFA', borderLeft: '3px solid #2C5F5D', fontSize: '11px', color: '#555', lineHeight: '1.6', fontFamily: '"Inter", Arial, sans-serif' }}>
<div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#2C5F5D' }}>Notes / Conditions</div>
<div style={{ whiteSpace: 'pre-wrap' }}>{currentDoc.notes}</div>
</div>
)}

{/* CGV */}
{settings.cgv && settings.cgv.activer && settings.cgv.position !== 'page-separee' && settings.cgv.texte && (
<div style={{ marginTop: '20px', padding: '12px 16px', background: '#FAFAFA', borderLeft: '3px solid #CCC', fontSize: '10px', color: '#777', lineHeight: '1.6', fontFamily: '"Inter", Arial, sans-serif' }}>
<div style={{ fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px', color: '#999' }}>Conditions Générales</div>
<div style={{ whiteSpace: 'pre-wrap' }}>{settings.cgv.texte}</div>
</div>
)}

{/* Footer */}
<div style={{ marginTop: '36px', paddingTop: '16px', borderTop: '1px solid #E5E5E5', textAlign: 'center', fontSize: '10px', color: '#999', fontFamily: '"Inter", Arial, sans-serif' }}>
<div>Merci pour votre confiance</div>
{settings.siteWeb && <div style={{ marginTop: '3px' }}>{settings.siteWeb}</div>}
</div>
</div>
);

} else if (modeleActif === 'corporate-premium') {
// ═══════════════════════════════════════
// MODÈLE CORPORATE PREMIUM
// ═══════════════════════════════════════
return (
<div className="preview-container" style={{
background: '#FFFFFF',
fontFamily: '"Montserrat", Arial, sans-serif',
maxWidth: '210mm',
margin: '0 auto'
}}>
{/* Bandeau header bleu marine */}
<div style={{ background: '#1E3A5F', padding: '28px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
<div style={{ width: '80px', height: '80px', background: 'white', borderRadius: '6px', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
{settings.logo
? <img src={settings.logo} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
: <div style={{ color: '#1E3A5F', fontSize: '0.6rem', textAlign: 'center', fontWeight: '700', letterSpacing: '1px' }}>LOGO</div>
}
</div>
<div style={{ textAlign: 'right' }}>
<h1 style={{ color: 'white', fontSize: '34px', fontWeight: '700', letterSpacing: '5px', margin: '0' }}>
{currentDoc.type === 'devis' ? 'DEVIS' : 'FACTURE'}
</h1>
</div>
</div>

{/* Corps */}
<div style={{ padding: '32px 40px' }}>
{/* Émetteur / Destinataire */}
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '28px', gap: '40px' }}>
<div style={{ flex: 1 }}>
<div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Émetteur</div>
<div style={{ fontSize: '14px', fontWeight: '600', color: '#1E3A5F', marginBottom: '5px' }}>{settings.raisonSociale}</div>
<div style={{ fontSize: '11px', color: '#666', lineHeight: '1.7' }}>
{settings.adresse && <div>{settings.adresse}</div>}
{settings.telephone && <div>Tél : {settings.telephone}</div>}
{settings.email && <div>{settings.email}</div>}
</div>
{(settings.nui || settings.rccm) && (
<div style={{ fontSize: '10px', color: '#999', marginTop: '8px' }}>
{settings.nui && `NUI : ${settings.nui}`}{settings.nui && settings.rccm && ' | '}{settings.rccm && `RCCM : ${settings.rccm}`}
</div>
)}
</div>
<div style={{ flex: 1, textAlign: 'right' }}>
<div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Destinataire</div>
<div style={{ fontSize: '14px', fontWeight: '600', color: '#1E3A5F', marginBottom: '5px' }}>{(currentDoc.client?.nom||'')}</div>
<div style={{ fontSize: '11px', color: '#666', lineHeight: '1.7' }}>
{currentDoc.client?.adresse && <div>{currentDoc.client?.adresse}</div>}
{currentDoc.client?.telephone && <div>Tél : {currentDoc.client?.telephone}</div>}
{currentDoc.client?.email && <div>{currentDoc.client?.email}</div>}
</div>
{currentDoc.client?.nui && (
<div style={{ fontSize: '10px', color: '#999', marginTop: '8px' }}>NUI : {currentDoc.client?.nui}</div>
)}
</div>
</div>

{/* Bande de référence avec accent doré */}
<div style={{ display: 'flex', gap: '36px', marginBottom: '28px', padding: '13px 18px', background: '#F8FAFC', borderLeft: '4px solid #F5A623' }}>
<div>
<div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '3px' }}>{currentDoc.type === 'devis' ? 'Devis N°' : 'Facture N°'}</div>
<div style={{ fontSize: '15px', fontWeight: '600', color: '#1E3A5F', fontFamily: 'monospace' }}>{currentDoc.numero}</div>
</div>
<div>
<div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '3px' }}>Date</div>
<div style={{ fontSize: '14px', fontWeight: '600', color: '#1E3A5F' }}>{new Date(currentDoc.date).toLocaleDateString('fr-FR')}</div>
</div>
{currentDoc.type === 'facture' && currentDoc.dateEcheance && (
<div>
<div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '3px' }}>Échéance</div>
<div style={{ fontSize: '14px', fontWeight: '600', color: '#1E3A5F' }}>{new Date(currentDoc.dateEcheance).toLocaleDateString('fr-FR')}</div>
</div>
)}
{currentDoc.objet && (
<div>
<div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '3px' }}>Objet</div>
<div style={{ fontSize: '11px', fontWeight: '500', color: '#1E3A5F', fontStyle: 'italic' }}>{currentDoc.objet}</div>
</div>
)}
</div>

{/* Tableau */}
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '28px' }}>
<thead>
<tr style={{ background: '#E8EEF4' }}>
<th style={{ textAlign: 'left', padding: '12px 14px', fontWeight: '600', color: '#1E3A5F', fontSize: '11px' }}>Description</th>
<th style={{ textAlign: 'center', padding: '12px 10px', width: '55px', fontWeight: '600', color: '#1E3A5F', fontSize: '11px' }}>Qté</th>
<th style={{ textAlign: 'center', padding: '12px 10px', width: '65px', fontWeight: '600', color: '#1E3A5F', fontSize: '11px' }}>Unité</th>
<th style={{ textAlign: 'right', padding: '12px 14px', width: '120px', fontWeight: '600', color: '#1E3A5F', fontSize: '11px' }}>P.U.</th>
<th style={{ textAlign: 'right', padding: '12px 14px', width: '120px', fontWeight: '600', color: '#1E3A5F', fontSize: '11px' }}>Total</th>
</tr>
</thead>
<tbody>
{currentDoc.items.map((item, i) => (
<tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
<td style={{ padding: '11px 14px', verticalAlign: 'top' }}>
<div style={{ color: '#1E293B' }}>{getItemDescriptionComplete(item)}</div>
{item.remiseType && item.remiseType !== 'aucune' && item.remiseValeur > 0 && (
<div style={{ fontSize: '10px', color: '#F5A623', marginTop: '2px' }}>
Remise : {item.remiseType === 'pourcentage' ? `${item.remiseValeur}%` : `${formatMontant(item.remiseValeur)}`}
</div>
)}
</td>
<td style={{ textAlign: 'center', padding: '11px 10px', color: '#64748B' }}>{item.quantite}</td>
<td style={{ textAlign: 'center', padding: '11px 10px', color: '#94A3B8', fontSize: '10px' }}>{item.unite || 'PCS'}</td>
<td style={{ textAlign: 'right', padding: '11px 14px', fontFamily: 'monospace', color: '#64748B', fontSize: '11px' }}>{formatMontant(item.prixUnitaire)}</td>
<td style={{ textAlign: 'right', padding: '11px 14px', fontFamily: 'monospace', fontWeight: '600', color: '#1E293B' }}>{formatMontant(item.montantApresRemise || item.quantite * item.prixUnitaire)}</td>
</tr>
))}
</tbody>
</table>

{/* Totaux */}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
{/* Notes à gauche */}
<div style={{ flex: 1, marginRight: '24px' }}>
{currentDoc.notes && (
<div style={{ padding: '12px 14px', background: '#FFF8EE', borderLeft: '3px solid #F5A623', fontSize: '11px', color: '#555', lineHeight: '1.6' }}>
<div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '10px', textTransform: 'uppercase', color: '#B8860B' }}>Notes / Conditions</div>
<div style={{ whiteSpace: 'pre-wrap' }}>{currentDoc.notes}</div>
</div>
)}
</div>
{/* Totaux à droite */}
<div style={{ width: '300px', border: '2px solid #1E3A5F', borderRadius: '6px', overflow: 'hidden', flexShrink: 0 }}>
<div style={{ padding: '14px 18px' }}>
{currentDoc.remiseGlobale && currentDoc.remiseGlobale.type !== 'aucune' && currentDoc.remiseGlobale.montant > 0 && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '12px', color: '#F5A623', borderBottom: '1px solid #E2E8F0' }}>
<span>Remise globale</span>
<span style={{ fontFamily: 'monospace' }}>- {formatMontant(currentDoc.remiseGlobale.montant)}</span>
</div>
)}
{currentDoc.fraisSupplementaires && currentDoc.fraisSupplementaires.map((frais, i) => (
<div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '12px', color: '#666', borderBottom: '1px solid #E2E8F0' }}>
<span>{frais.libelle}</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(frais.montant)}</span>
</div>
))}
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', fontSize: '12px', color: '#666', borderBottom: '1px solid #E2E8F0' }}>
<span>{currentDoc.afficherModeTaxation ? 'Sous-total H.T.' : 'Sous-total'}</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(currentDoc.totalAvantTaxe || currentDoc.sousTotal)}</span>
</div>
{currentDoc.afficherModeTaxation && currentDoc.taxMode === 'TTC' && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', fontSize: '12px', color: '#666' }}>
<span>TVA ({settings.tauxTVA || 19.25}%)</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(currentDoc.tva)}</span>
</div>
)}
</div>
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
return (
<>
{paye > 0 && (
<div style={{ background: '#1E3A5F', color: '#A7C5E8', padding: '8px 18px', display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
<span>Montant payé</span>
<span style={{ fontFamily: 'monospace' }}>-{formatMontant(paye)}</span>
</div>
)}
<div style={{ background: '#1E3A5F', color: 'white', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
<span style={{ fontSize: '13px', fontWeight: '600' }}>{paye > 0 && restant > 0 ? (currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'RESTE À PAYER T.T.C.' : 'RESTE À PAYER H.T.') : 'RESTE À PAYER') : (currentDoc.afficherModeTaxation ? `TOTAL ${currentDoc.taxMode === 'TTC' ? 'T.T.C.' : 'H.T.'}` : 'TOTAL')}</span>
<span style={{ fontSize: '18px', fontWeight: '700', fontFamily: 'monospace' }}>{formatMontant(restant > 0 ? restant : currentDoc.total)}</span>
</div>
</>
);
})()}
</div>
</div>

{/* Montant en lettres */}
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
return (
<div style={{ textAlign: 'center', padding: '12px 16px', background: '#F8FAFC', borderLeft: '4px solid #F5A623', fontSize: '11px', color: '#555', marginBottom: '20px' }}>
Arrêté à la somme de : <strong style={{ color: '#1E3A5F' }}>{nombreEnLettres(Math.floor(restant > 0 ? restant : currentDoc.total))} francs CFA</strong>
</div>
);
})()}

{/* CGV */}
{settings.cgv && settings.cgv.activer && settings.cgv.position !== 'page-separee' && settings.cgv.texte && (
<div style={{ marginTop: '16px', padding: '10px 14px', background: '#F8FAFC', fontSize: '10px', color: '#777', lineHeight: '1.6', borderTop: '1px solid #E2E8F0' }}>
<div style={{ fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px', color: '#999' }}>Conditions Générales de Vente</div>
<div style={{ whiteSpace: 'pre-wrap' }}>{settings.cgv.texte}</div>
</div>
)}

{/* Footer */}
<div style={{ marginTop: '28px', paddingTop: '16px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10px', color: '#999' }}>
<div>Merci pour votre confiance !</div>
<div style={{ textAlign: 'right' }}>
{settings.siteWeb && <div>{settings.siteWeb}</div>}
<div>Conditions : Paiement sous {currentDoc.delaiPaiement || 30} jours</div>
</div>
</div>
</div>
</div>
);

} else if (modeleActif === 'classique-ameliore') {
// ═══════════════════════════════════════
// MODÈLE CLASSIQUE AMÉLIORÉ
// ═══════════════════════════════════════
return (
<div className="preview-container" style={{
background: '#FFFFFF',
padding: '28px 32px',
fontFamily: 'Georgia, "Times New Roman", serif',
color: '#1A1A1A',
maxWidth: '210mm',
margin: '0 auto',
border: '3px double #1A1A1A'
}}>
{/* Header avec double cadre */}
<div style={{ border: '2px solid #1A1A1A', padding: '18px', marginBottom: '22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
<div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
<div style={{ width: '72px', height: '72px', border: '2px solid #C9A227', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
{settings.logo
? <img src={settings.logo} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
: <span style={{ color: '#C9A227', fontSize: '0.6rem', fontWeight: 'bold', letterSpacing: '1px' }}>LOGO</span>
}
</div>
<div>
<div style={{ fontSize: '16px', fontWeight: 'bold', letterSpacing: '2px', fontFamily: 'Georgia, serif' }}>{settings.raisonSociale}</div>
<div style={{ fontSize: '10px', color: '#666', marginTop: '4px', lineHeight: '1.6', fontFamily: 'Georgia, serif' }}>
{settings.adresse && <div>{settings.adresse}</div>}
{settings.telephone && <div>Tél : {settings.telephone}</div>}
{settings.email && <div>{settings.email}</div>}
</div>
{(settings.nui || settings.rccm) && (
<div style={{ fontSize: '9px', color: '#999', marginTop: '3px', fontFamily: 'Georgia, serif' }}>
{settings.nui && `NUI : ${settings.nui}`}{settings.nui && settings.rccm && ' | '}{settings.rccm && `RCCM : ${settings.rccm}`}
</div>
)}
</div>
</div>
</div>

{/* Bandeau titre gris */}
<div style={{ background: '#4A4A4A', padding: '13px', textAlign: 'center', marginBottom: '20px' }}>
<h1 style={{ color: 'white', fontSize: '24px', fontWeight: 'normal', letterSpacing: '8px', margin: '0', fontFamily: 'Georgia, serif' }}>
{currentDoc.type === 'devis' ? 'DEVIS' : 'FACTURE'}
</h1>
</div>

{/* Référence et date */}
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '18px', fontSize: '12px', fontFamily: 'Georgia, serif' }}>
<div><strong>N° :</strong> {currentDoc.numero}</div>
<div><strong>Date :</strong> {new Date(currentDoc.date).toLocaleDateString('fr-FR')}</div>
{currentDoc.type === 'facture' && currentDoc.dateEcheance && (
<div><strong>Échéance :</strong> {new Date(currentDoc.dateEcheance).toLocaleDateString('fr-FR')}</div>
)}
</div>

{/* Objet */}
{currentDoc.objet && (
<div style={{ marginBottom: '16px', padding: '8px 12px', background: '#FAFAFA', border: '1px solid #DDD', fontSize: '11px', fontFamily: 'Georgia, serif' }}>
<strong>Objet :</strong> <em>{currentDoc.objet}</em>
</div>
)}

{/* Section client */}
<div style={{ marginBottom: '20px' }}>
<div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px', borderBottom: '1px solid #CCC', paddingBottom: '4px', fontFamily: 'Georgia, serif' }}>
{currentDoc.type === 'devis' ? 'Destinataire' : 'Facturé à'}
</div>
<div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '3px', fontFamily: 'Georgia, serif' }}>{(currentDoc.client?.nom||'')}</div>
<div style={{ fontSize: '11px', color: '#555', lineHeight: '1.6', fontFamily: 'Georgia, serif' }}>
{currentDoc.client?.adresse && <div>{currentDoc.client?.adresse}</div>}
{currentDoc.client?.telephone && <div>Tél : {currentDoc.client?.telephone}</div>}
{currentDoc.client?.email && <div>{currentDoc.client?.email}</div>}
{currentDoc.client?.nui && <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>NUI : {currentDoc.client?.nui}</div>}
</div>
</div>

{/* Tableau avec bordures complètes */}
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginBottom: '20px', border: '2px solid #1A1A1A', fontFamily: 'Georgia, serif' }}>
<thead>
<tr style={{ background: '#F0F0F0' }}>
<th style={{ textAlign: 'left', padding: '10px 10px', border: '1px solid #1A1A1A', fontWeight: 'bold' }}>Description</th>
<th style={{ textAlign: 'center', padding: '10px 8px', width: '50px', border: '1px solid #1A1A1A', fontWeight: 'bold' }}>Qté</th>
<th style={{ textAlign: 'center', padding: '10px 8px', width: '60px', border: '1px solid #1A1A1A', fontWeight: 'bold' }}>Unité</th>
<th style={{ textAlign: 'right', padding: '10px 10px', width: '120px', border: '1px solid #1A1A1A', fontWeight: 'bold' }}>Prix unitaire</th>
<th style={{ textAlign: 'right', padding: '10px 10px', width: '120px', border: '1px solid #1A1A1A', fontWeight: 'bold' }}>Montant total</th>
</tr>
</thead>
<tbody>
{currentDoc.items.map((item, i) => (
<tr key={i}>
<td style={{ padding: '8px 10px', border: '1px solid #1A1A1A', verticalAlign: 'top' }}>
<div>{getItemDescriptionComplete(item)}</div>
{item.remiseType && item.remiseType !== 'aucune' && item.remiseValeur > 0 && (
<div style={{ fontSize: '9px', color: '#C9A227', marginTop: '2px', fontStyle: 'italic' }}>
Remise : {item.remiseType === 'pourcentage' ? `${item.remiseValeur}%` : `${formatMontant(item.remiseValeur)}`}
</div>
)}
</td>
<td style={{ textAlign: 'center', padding: '8px', border: '1px solid #1A1A1A' }}>{item.quantite}</td>
<td style={{ textAlign: 'center', padding: '8px', border: '1px solid #1A1A1A', color: '#888', fontSize: '10px' }}>{item.unite || 'PCS'}</td>
<td style={{ textAlign: 'right', padding: '8px 10px', border: '1px solid #1A1A1A', fontFamily: '"Courier New", monospace' }}>{formatMontant(item.prixUnitaire)}</td>
<td style={{ textAlign: 'right', padding: '8px 10px', border: '1px solid #1A1A1A', fontFamily: '"Courier New", monospace', fontWeight: 'bold' }}>{formatMontant(item.montantApresRemise || item.quantite * item.prixUnitaire)}</td>
</tr>
))}
</tbody>
</table>

{/* Totaux encadrés */}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
{/* Notes à gauche */}
<div style={{ flex: 1, marginRight: '20px' }}>
{currentDoc.notes && (
<div style={{ padding: '10px 12px', border: '1px solid #CCC', fontSize: '10px', color: '#555', lineHeight: '1.6', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
<div style={{ fontWeight: 'bold', fontStyle: 'normal', marginBottom: '4px', textTransform: 'uppercase', fontSize: '9px', letterSpacing: '1px' }}>Notes :</div>
<div style={{ whiteSpace: 'pre-wrap' }}>{currentDoc.notes}</div>
</div>
)}
</div>
{/* Totaux à droite */}
<div style={{ width: '300px', border: '2px solid #1A1A1A', padding: '16px', background: '#FAFAFA', flexShrink: 0 }}>
{currentDoc.remiseGlobale && currentDoc.remiseGlobale.type !== 'aucune' && currentDoc.remiseGlobale.montant > 0 && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '11px', borderBottom: '1px dotted #CCC', fontFamily: 'Georgia, serif', color: '#C9A227' }}>
<span>Remise globale</span>
<span style={{ fontFamily: '"Courier New", monospace' }}>- {formatMontant(currentDoc.remiseGlobale.montant)}</span>
</div>
)}
{currentDoc.fraisSupplementaires && currentDoc.fraisSupplementaires.map((frais, i) => (
<div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '11px', borderBottom: '1px dotted #CCC', fontFamily: 'Georgia, serif' }}>
<span>{frais.libelle}</span>
<span style={{ fontFamily: '"Courier New", monospace' }}>{formatMontant(frais.montant)}</span>
</div>
))}
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '11px', borderBottom: '1px dotted #CCC', fontFamily: 'Georgia, serif' }}>
<span>{currentDoc.afficherModeTaxation ? 'Sous-total H.T.' : 'Sous-total'}</span>
<span style={{ fontFamily: '"Courier New", monospace' }}>{formatMontant(currentDoc.totalAvantTaxe || currentDoc.sousTotal)}</span>
</div>
{currentDoc.afficherModeTaxation && currentDoc.taxMode === 'TTC' && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '11px', borderBottom: '1px dotted #CCC', fontFamily: 'Georgia, serif' }}>
<span>T.V.A. ({settings.tauxTVA || 19.25}%)</span>
<span style={{ fontFamily: '"Courier New", monospace' }}>{formatMontant(currentDoc.tva)}</span>
</div>
)}
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
return (
<>
{paye > 0 && (
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '11px', borderBottom: '1px dotted #CCC', fontFamily: 'Georgia, serif', color: '#10B981' }}>
<span>Montant payé</span>
<span style={{ fontFamily: '"Courier New", monospace' }}>-{formatMontant(paye)}</span>
</div>
)}
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', marginTop: '6px', borderTop: '2px solid #1A1A1A', fontSize: '13px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
<span>{paye > 0 && restant > 0 ? (currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'RESTE À PAYER T.T.C.' : 'RESTE À PAYER H.T.') : 'RESTE À PAYER') : (currentDoc.afficherModeTaxation ? `MONTANT TOTAL ${currentDoc.taxMode === 'TTC' ? 'T.T.C.' : 'H.T.'}` : 'MONTANT TOTAL')}</span>
<span style={{ fontFamily: '"Courier New", monospace' }}>{formatMontant(restant > 0 ? restant : currentDoc.total)}</span>
</div>
</>
);
})()}
</div>
</div>

{/* Montant en lettres */}
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
return (
<div style={{ padding: '12px 16px', border: '1px solid #CCC', background: '#FAFAFA', fontSize: '11px', color: '#555', textAlign: 'center', marginBottom: '20px', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
Arrêté la présente {currentDoc.type === 'devis' ? 'offre de prix' : 'facture'} à la somme de : <strong style={{ fontStyle: 'normal' }}>{nombreEnLettres(Math.floor(restant > 0 ? restant : currentDoc.total))} francs CFA</strong>
</div>
);
})()}

{/* CGV */}
{settings.cgv && settings.cgv.activer && settings.cgv.position !== 'page-separee' && settings.cgv.texte && (
<div style={{ marginBottom: '16px', padding: '10px 12px', border: '1px solid #DDD', fontSize: '10px', color: '#666', lineHeight: '1.6', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
<div style={{ fontStyle: 'normal', fontWeight: 'bold', marginBottom: '4px', textTransform: 'uppercase', fontSize: '9px', letterSpacing: '1px' }}>Conditions Générales de Vente</div>
<div style={{ whiteSpace: 'pre-wrap' }}>{settings.cgv.texte}</div>
</div>
)}

{/* Conditions et footer */}
<div style={{ marginTop: '24px', paddingTop: '14px', borderTop: '1px solid #CCC', fontSize: '10px', color: '#666', textAlign: 'center', fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>
<div>Conditions de paiement : Net {currentDoc.delaiPaiement || 30} jours</div>
<div style={{ marginTop: '4px' }}>Merci pour votre confiance !</div>
</div>
</div>
);

} else {
// MODÈLE CLASSIQUE (par défaut)
return (
<div className="preview-container">
{/* Header avec logo et titre */}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', borderBottom: '3px solid #0F172A', paddingBottom: '0.75rem' }}>
{/* Logo et infos entreprise */}
<div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flex: 1 }}>
{settings.logo && (
<img src={settings.logo} alt="Logo" style={{ width: '80px', height: '80px', objectFit: 'contain' }} />
)}
<div>
<h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#0F172A' }}>
{settings.raisonSociale}
</h2>
<div style={{ fontSize: '0.875rem', lineHeight: '1.6', color: '#64748B' }}>
{settings.adresse && <div>{settings.adresse}</div>}
{settings.telephone && <div>Tél: {settings.telephone}</div>}
{settings.email && <div>Email: {settings.email}</div>}
{settings.siteWeb && <div>Web: {settings.siteWeb}</div>}
{settings.nui && <div>NUI: {settings.nui}</div>}
{settings.rccm && <div>RCCM: {settings.rccm}</div>}
</div>
</div>
</div>

{/* Type et numéro de document */}
<div style={{ textAlign: 'right' }}>
<h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#FF6B35', marginBottom: '0.5rem' }}>
{currentDoc.type === 'devis' ? 'DEVIS' : 'FACTURE'}
</h1>
<div style={{ fontSize: '0.875rem', color: '#1E293B', fontFamily: 'JetBrains Mono, monospace' }}>
<div style={{ marginBottom: '0.25rem' }}><strong>N° {currentDoc.numero}</strong></div>
<div style={{ marginBottom: '0.25rem' }}>
Date: {new Date(currentDoc.date).toLocaleDateString('fr-FR')}
{currentDoc.dateMode === 'auto' && currentDoc.dateTime && (
<> à {new Date(currentDoc.dateTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</>
)}
</div>
{currentDoc.type === 'facture' && currentDoc.devisOrigine && (
<div style={{
marginBottom: '0.25rem',
color: '#059669',
fontSize: '0.8rem',
fontWeight: 600
}}>
Devis: {currentDoc.devisOrigine}
</div>
)}
{currentDoc.client?.numeroBonCommande && (
<div>N° BC: {currentDoc.client?.numeroBonCommande}</div>
)}
</div>
</div>
</div>

{/* Section Client */}
<div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
<h3 style={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#64748B', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
CLIENT
</h3>
<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
<div style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>
<div style={{ fontWeight: 'bold', fontSize: '1rem', marginBottom: '0.5rem', color: '#0F172A' }}>
{(currentDoc.client?.nom||'')}
</div>
{currentDoc.client?.adresse && <div>{currentDoc.client?.adresse}</div>}
{currentDoc.client?.telephone && <div>Tél: {currentDoc.client?.telephone}</div>}
{currentDoc.client?.email && <div>Email: {currentDoc.client?.email}</div>}
</div>
<div style={{ fontSize: '0.875rem', lineHeight: '1.6', fontFamily: 'JetBrains Mono, monospace' }}>
{currentDoc.client?.nui && <div>NUI: {currentDoc.client?.nui}</div>}
{currentDoc.client?.rccm && <div>RCCM: {currentDoc.client?.rccm}</div>}
{currentDoc.validiteDevis && (
<div style={{ marginTop: '0.5rem', color: '#F59E0B' }}>
Validité: {currentDoc.validiteDevis} jours
</div>
)}
</div>
</div>
</div>

{/* Objet du document */}
{currentDoc.objet && (
<div style={{
marginBottom: '1.5rem',
padding: '1rem',
background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 197, 253, 0.05) 100%)',
borderRadius: '8px',
borderLeft: '4px solid #3B82F6'
}}>
<h3 style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#64748B', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
OBJET
</h3>
<div style={{ fontSize: '0.95rem', color: '#1E293B', fontWeight: '600', lineHeight: '1.5' }}>
{currentDoc.objet}
</div>
</div>
)}

{/* Tableau des prestations */}
<div style={{ marginBottom: '1.5rem' }}>
<h3 style={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#0F172A', textTransform: 'uppercase', marginBottom: '1rem' }}>
DÉTAIL DES PRESTATIONS
</h3>
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
<thead>
<tr style={{ background: '#FFFFFF', color: '#000000', borderBottom: '2px solid #000000' }}>
<th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'uppercase' }}>DÉSIGNATION</th>
<th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'uppercase', width: '8%' }}>QTÉ</th>
<th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'uppercase', width: '10%' }}>UNITÉ</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'uppercase', width: '15%' }}>PU HT</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'uppercase', width: '15%' }}>{currentDoc.afficherModeTaxation ? 'TOTAL HT' : 'TOTAL'}</th>
{currentDoc.taxMode === 'TTC' && (
<>
<th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'uppercase', width: '10%' }}>TVA</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'uppercase', width: '17%' }}>{currentDoc.afficherModeTaxation ? 'TOTAL TTC' : 'TOTAL'}</th>
</>
)}
</tr>
</thead>
<tbody>
{(() => {
const stMapScreen = calculerSousTotaux(currentDoc.items, currentDoc.taxMode, currentDoc.tauxTVA || settings.tauxTVA);
const screenRows = [];
currentDoc.items.forEach((item, index) => {
const lt = item.lineType || 'article';
if (lt === 'section') {
screenRows.push(
<tr key={'ss-'+index} style={{ background: '#F0F4F8', borderTop: '1px solid #888', borderBottom: '1px solid #888' }}>
<td colSpan={currentDoc.taxMode === 'TTC' ? 7 : 5} style={{ padding: '0.6rem 0.75rem', fontWeight: 'bold', fontSize: '0.82rem' }}>
▸ {item.sectionLabel || 'Section'}
</td>
</tr>
);
} else if (lt === 'note') {
screenRows.push(
<tr key={'sn-'+index}>
<td colSpan={currentDoc.taxMode === 'TTC' ? 7 : 5} style={{ padding: '0.4rem 0.75rem 0.4rem 1.5rem', fontStyle: 'italic', fontSize: '9pt', color: '#333', borderBottom: '0.5px dotted #ccc' }}>
✎ {item.noteLabel || ''}
</td>
</tr>
);
} else {
const totalHT = (item.quantite || 0) * (item.prixUnitaire || 0);
const tvaItem = currentDoc.taxMode === 'TTC' ? totalHT * ((settings.tauxTVA || 19.25) / 100) : 0;
const totalTTC = totalHT + tvaItem;
screenRows.push(
<tr key={'sa-'+index} style={{ borderBottom: '1px solid #E2E8F0' }}>
<td style={{ padding: '0.75rem', textAlign: 'left' }}>{getItemDescriptionComplete(item)}</td>
<td style={{ padding: '0.75rem', textAlign: 'center', fontFamily: 'JetBrains Mono, monospace' }}>{item.quantite}</td>
<td style={{ padding: '0.75rem', textAlign: 'center' }}>{formatUnite(item.unite)}</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace' }}>{formatMontant(item.prixUnitaire)}</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{formatMontant(totalHT)}</td>
{currentDoc.taxMode === 'TTC' && (<><td style={{ padding: '0.75rem', textAlign: 'center' }}>{settings.tauxTVA || 19.25}%</td><td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 'bold' }}>{formatMontant(totalTTC)}</td></>)}
</tr>
);
}
const next = currentDoc.items[index + 1];
const endG = !next || (next.lineType || 'article') === 'section';
if (lt !== 'section' && endG) {
let spi = null;
for (let j = index; j >= 0; j--) { if ((currentDoc.items[j].lineType || 'article') === 'section') { spi = j; break; } }
if (spi !== null && stMapScreen[spi] !== undefined) {
screenRows.push(
<tr key={'sst-'+spi+'-'+index} style={{ background: '#F8FAFC', borderTop: '0.5px solid #CBD5E1' }}>
<td colSpan={currentDoc.taxMode === 'TTC' ? 6 : 4} style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontSize: '0.78rem', color: '#64748B', fontStyle: 'italic' }}>
Sous-total {currentDoc.items[spi].sectionLabel || 'Section'}{currentDoc.afficherModeTaxation ? ` (${currentDoc.taxMode === 'TTC' ? 'TTC' : 'HT'})` : ''} :
</td>
<td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: '0.85rem' }}>
{formatMontant(stMapScreen[spi])}
</td>
</tr>
);
}
}
});
return screenRows;
})()}
</tbody>
</table>
</div>

{/* Totaux dans un encadré */}
<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
<div style={{ minWidth: '400px', padding: '1.5rem', background: '#F8FAFC', borderRadius: '12px', border: '2px solid #E2E8F0' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid #E2E8F0' }}>
<span style={{ fontSize: '0.875rem' }}>{currentDoc.afficherModeTaxation ? 'Sous-total HT:' : 'Sous-total:'}</span>
<span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
{formatMontant(currentDoc.sousTotal)}
</span>
</div>

{/* Remise globale si présente */}
{(() => {
const totalFrais = (currentDoc.fraisSupplementaires || []).reduce((s, f) => s + (f.montant || 0), 0);
const remiseMontant = currentDoc.remiseGlobale?.montant > 0
? currentDoc.remiseGlobale.montant
: Math.max(0, Math.round(((currentDoc.sousTotal || 0) - ((currentDoc.total || 0) - (currentDoc.tva || 0) - totalFrais)) * 100) / 100);
return remiseMontant > 0 ? (
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid #E2E8F0', color: '#10B981' }}>
<span style={{ fontSize: '0.875rem' }}>
Remise {currentDoc.remiseGlobale?.type === 'pourcentage' ? `(${currentDoc.remiseGlobale.valeur}%)` : 'globale'}:
</span>
<span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
-{formatMontant(remiseMontant)}
</span>
</div>
) : null;
})()}

{/* Frais supplémentaires si présents */}
{currentDoc.fraisSupplementaires && currentDoc.fraisSupplementaires.length > 0 && currentDoc.fraisSupplementaires.map((frais, index) => (
frais.montant > 0 && (
<div key={index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid #E2E8F0', color: '#F59E0B' }}>
<span style={{ fontSize: '0.875rem' }}>{frais.libelle || frais.description || 'Frais supplémentaires'}:</span>
<span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
+{formatMontant(frais.montant)}
</span>
</div>
)
))}

{currentDoc.afficherModeTaxation && currentDoc.taxMode === 'TTC' && (
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #E2E8F0' }}>
<span style={{ fontSize: '0.875rem' }}>TVA ({settings.tauxTVA || 19.25}%):</span>
<span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
{formatMontant(currentDoc.tva)}
</span>
</div>
)}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.5rem', borderTop: '3px solid #0F172A' }}>
<span style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#0F172A' }}>
{currentDoc.afficherModeTaxation ? `TOTAL ${currentDoc.taxMode === 'TTC' ? 'TTC' : 'HT'}:` : 'TOTAL:'}
</span>
<div style={{ textAlign: 'right' }}>
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
return (
<div style={{ fontSize: '0.75rem', color: '#64748B', marginBottom: '0.25rem', textTransform: 'capitalize' }}>
({nombreEnLettres(Math.floor(restant > 0 ? restant : currentDoc.total))} francs CFA)
</div>
);
})()}
<div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '1.5rem', fontWeight: 'bold', color: '#FF6B35' }}>
{formatMontant(currentDoc.total)}
</div>
</div>
</div>
{(() => {
const paye = currentDoc.montantPaye > 0 ? currentDoc.montantPaye : (currentDoc.montantAvance || 0);
const restant = paye > 0 ? Math.max(0, (currentDoc.total || 0) - paye) : 0;
return paye > 0 ? (
<div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #E2E8F0' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#10B981' }}>
<span>MONTANT PAYÉ :</span>
<span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 'bold' }}>-{formatMontant(paye)}</span>
</div>
{restant > 0 && (
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 'bold', color: '#EF4444' }}>
<span>{currentDoc.afficherModeTaxation ? (currentDoc.taxMode === 'TTC' ? 'RESTE À PAYER TTC :' : 'RESTE À PAYER HT :') : 'RESTE À PAYER :'}</span>
<span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatMontant(restant)}</span>
</div>
)}
</div>
) : null;
})()}

{/* Date d'échéance avec pénalités potentielles (pour les factures) */}
{currentDoc.type === 'facture' && currentDoc.dateEcheance && (
<div style={{
marginTop: '1rem',
padding: '0.75rem',
background: currentDoc.penalitesRetard && currentDoc.penalitesRetard.joursRetard > 0
? 'rgba(239, 68, 68, 0.1)'
: 'rgba(59, 130, 246, 0.1)',
borderRadius: '6px',
border: currentDoc.penalitesRetard && currentDoc.penalitesRetard.joursRetard > 0
? '1px solid #EF4444'  : '1px solid #3B82F6',
fontSize: '0.85rem'
}}>
<div style={{
textAlign: 'center',
color: currentDoc.penalitesRetard && currentDoc.penalitesRetard.joursRetard > 0
? '#991B1B'
: '#1E40AF',
fontWeight: '500'
}}>
📅 Date d'échéance : {' '}
<strong>{new Date(currentDoc.dateEcheance).toLocaleDateString('fr-FR', {
day: '2-digit',
month: 'long',
year: 'numeric'
})}</strong>
</div>

{/* Afficher les pénalités si activées */}
{currentDoc.penalitesRetard && currentDoc.penalitesRetard.actives && (
<div style={{
marginTop: '0.5rem',
paddingTop: '0.5rem',
borderTop: '1px solid rgba(0,0,0,0.1)',
fontSize: '0.8rem',
textAlign: 'center'
}}>
{currentDoc.penalitesRetard.joursRetard > 0 ? (
<div style={{ color: '#991B1B', fontWeight: 'bold' }}>
⚠️ RETARD : {currentDoc.penalitesRetard.joursRetard} jour{currentDoc.penalitesRetard.joursRetard > 1 ? 's' : ''}
• Pénalités : {formatMontant(currentDoc.penalitesRetard.montantPenalites)}
{' '}({currentDoc.penalitesRetard.taux}% /jour)
</div>
) : (
<div style={{ color: '#64748B', fontStyle: 'italic' }}>
Pénalités en cas de retard : {currentDoc.penalitesRetard.taux}% par jour
</div>
)}
</div>
)}
</div>
)}

{/* Pénalités de retard détaillées (si applicable) - SUPPRIMÉ car intégré ci-dessus */}
{false && currentDoc.type === 'facture' && currentDoc.penalitesRetard && currentDoc.penalitesRetard.joursRetard > 0 && (
<div style={{
marginTop: '1rem',
padding: '1rem',
background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
borderRadius: '8px',
border: '2px solid #EF4444'
}}>
<div style={{ fontWeight: 'bold', marginBottom: '0.75rem', color: '#991B1B', fontSize: '0.9rem', textAlign: 'center' }}>
⚠️ PÉNALITÉS DE RETARD
</div>
<div style={{ fontSize: '0.85rem', color: '#7F1D1D' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
<span>Retard de paiement :</span>
<strong>{currentDoc.penalitesRetard.joursRetard} jours</strong>
</div>
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
<span>Taux appliqué :</span>
<strong>{currentDoc.penalitesRetard.taux}% par jour</strong>
</div>
<div style={{
display: 'flex',
justifyContent: 'space-between',
paddingTop: '0.5rem',
borderTop: '1px solid rgba(239, 68, 68, 0.3)',
marginTop: '0.5rem'
}}>
<span style={{ fontWeight: 'bold' }}>Montant des pénalités :</span>
<span style={{
fontFamily: 'JetBrains Mono, monospace',
fontSize: '1.1rem',
fontWeight: 'bold',
color: '#991B1B'
}}>
{formatMontant(currentDoc.penalitesRetard.montant)}
</span>
</div>
<div style={{
marginTop: '0.75rem',
fontSize: '0.75rem',
fontStyle: 'italic',
textAlign: 'center',
color: '#7F1D1D'
}}>
Calcul : {formatMontant(currentDoc.montantRestant || currentDoc.total)} × {currentDoc.penalitesRetard.taux}% × {currentDoc.penalitesRetard.joursRetard} jours
</div>
</div>
</div>
)}

{/* Journal des Paiements */}
{currentDoc.type === 'facture' && currentDoc.paiements && currentDoc.paiements.length > 0 && (
<div style={{
marginTop: '1rem',
padding: '1rem',
background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
borderRadius: '8px',
border: '2px solid #10B981'
}}>
<div style={{ fontWeight: 'bold', marginBottom: '0.75rem', color: '#065F46', fontSize: '0.9rem', textAlign: 'center' }}>
💳 HISTORIQUE DES PAIEMENTS
</div>
<table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
<thead>
<tr style={{ borderBottom: '2px solid #10B981', color: '#065F46' }}>
<th style={{ padding: '0.5rem', textAlign: 'left' }}>Date</th>
<th style={{ padding: '0.5rem', textAlign: 'right' }}>Montant</th>
<th style={{ padding: '0.5rem', textAlign: 'left' }}>Méthode</th>
<th style={{ padding: '0.5rem', textAlign: 'left' }}>Référence</th>
<th style={{ padding: '0.5rem', textAlign: 'left' }}>Notes</th>
</tr>
</thead>
<tbody>
{currentDoc.paiements.map((paiement, index) => (
<tr key={index} style={{ borderBottom: '1px solid rgba(16, 185, 129, 0.2)' }}>
<td style={{ padding: '0.5rem', color: '#047857' }}>
{new Date(paiement.date).toLocaleDateString('fr-FR')}
</td>
<td style={{ padding: '0.5rem', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 'bold', color: '#059669' }}>
{formatMontant(paiement.montant)}
</td>
<td style={{ padding: '0.5rem', color: '#047857' }}>
{paiement.methode === 'virement' && 'Virement'}
{paiement.methode === 'cheque' && 'Chèque'}
{paiement.methode === 'especes' && 'Espèces'}
{paiement.methode === 'carte' && 'Carte bancaire'}
{paiement.methode === 'mobile' && '📱 Mobile Money'}
</td>
<td style={{ padding: '0.5rem', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem', color: '#047857' }}>
{paiement.methode === 'mobile'
? (paiement.telephoneMobile || '-')
: (paiement.reference || '-')}
</td>
<td style={{ padding: '0.5rem', fontStyle: 'italic', fontSize: '0.75rem', color: '#6B7280' }}>
{paiement.methode === 'mobile'
? (paiement.idTransaction ? `ID: ${paiement.idTransaction}` : '-')
: (paiement.notes || '-')}
</td>
</tr>
))}
</tbody>
</table>
<div style={{
marginTop: '0.75rem',
paddingTop: '0.75rem',
borderTop: '2px solid #10B981',
display: 'grid',
gridTemplateColumns: '1fr 1fr',
gap: '1rem',
fontSize: '0.85rem'
}}>
<div style={{ display: 'flex', justifyContent: 'space-between' }}>
<span style={{ color: '#047857', fontWeight: 'bold' }}>TOTAL PAYÉ :</span>
<span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 'bold', color: '#059669', fontSize: '1rem' }}>
{formatMontant((currentDoc.paiements || []).reduce((sum, p) => sum + (p.montant || 0), 0))}
</span>
</div>
<div style={{ display: 'flex', justifyContent: 'space-between' }}>
<span style={{ color: '#DC2626', fontWeight: 'bold' }}>RESTE À PAYER :</span>
<span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 'bold', color: '#DC2626', fontSize: '1rem' }}>
{formatMontant(currentDoc.total - (currentDoc.paiements || []).reduce((sum, p) => sum + (p.montant || 0), 0))}
</span>
</div>
</div>
</div>
)}
</div>
</div>

{/* Notes */}
{currentDoc.notes && (
<div style={{ marginTop: '1.5rem', padding: '1rem', background: '#FEF3C7', borderRadius: '8px', borderLeft: '4px solid #F59E0B' }}>
<div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#92400E', fontSize: '0.875rem' }}>Notes:</div>
<div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: '0.8rem', color: '#78350F' }}>
{currentDoc.notes}
</div>
</div>
)}

{/* Conditions Générales de Vente (CGV) */}
{settings.cgv && settings.cgv.activer && currentDoc.cgvIncluses !== false && settings.cgv.texte && (
<div style={{
marginTop: '2rem',
padding: '1.5rem',
background: '#F9FAFB',
borderRadius: '8px',
border: '1px solid #E5E7EB',
pageBreakBefore: settings.cgv.position === 'page-separee' ? 'always' : 'auto'
}}>
<h3 style={{
margin: '0 0 1rem 0',
color: '#1F2937',
fontSize: '1rem',
fontWeight: 'bold',
textAlign: 'center',
textTransform: 'uppercase',
borderBottom: '2px solid #3B82F6',
paddingBottom: '0.5rem'
}}>
📜 Conditions Générales de Vente
</h3>
<div style={{
whiteSpace: 'pre-wrap',
lineHeight: '1.6',
fontSize: '0.75rem',
color: '#374151'
}}>
{settings.cgv.texte}
</div>
</div>
)}

{/* Footer */}
<div style={{ marginTop: '2rem', textAlign: 'center', paddingTop: '1rem', borderTop: '1px solid #E2E8F0' }}>
<p style={{ fontSize: '0.875rem', color: '#64748B', marginBottom: '0.25rem' }}>
Merci de votre confiance
</p>
<p style={{ fontSize: '0.75rem', color: '#94A3B8' }}>
Document généré par SOLSTICE - Système de gestion
</p>
</div>
</div>
);
}
})()}
</div>

<div className="modal-footer">
<button className="btn btn-secondary" onClick={() => setShowPreview(false)}>
Fermer
</button>
{/* Bouton avoir dans la prévisualisation — uniquement pour les factures non-annulées */}
{currentDoc && currentDoc.type === 'facture' && currentDoc.statut !== 'annule' && (
<button
onClick={() => { setShowPreview(false); setTimeout(() => ouvrirModalAvoir(currentDoc), 150); }}
style={{
padding: '0.75rem 1.5rem',
background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
color: 'white', border: 'none', borderRadius: '10px',
cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem',
display: 'flex', alignItems: 'center', gap: '0.5rem',
boxShadow: '0 4px 14px rgba(124,58,237,0.4)'
}}>
🔵 Émettre un avoir
</button>
)}
<button className="btn btn-success" onClick={() => window.print()}>
🖨️ Imprimer / PDF
</button>
</div>
</div>
</div>
)}

{/* Modal Gestion des Clients */}
{showClients && !showClientForm && (
<div className="modal-overlay">
<div className="modal-content">
<div className="modal-header">
<h2 className="modal-title">👥 Gestion des Clients ({clients.length})</h2>
<button className="close-btn" onClick={() => {
setShowClients(false);
setSearchClient('');
}}>✕</button>
</div>

<div className="modal-body">
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
<input
type="text"
className="search-input"
placeholder="🔍 Rechercher un client..."
value={searchClient}
onChange={(e) => setSearchClient(e.target.value)}
style={{ flex: 1, minWidth: '200px' }}
/>
<button className="btn btn-primary" onClick={nouveauClient}>
➕ Nouveau Client
</button>
</div>

{filteredClients.length === 0 ? (
<div className="empty-state" style={{ padding: '3rem 2rem' }}>
<div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
<h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '0.5rem' }}>
{searchClient ? 'Aucun client trouvé' : 'Aucun client enregistré'}
</h3>
<p style={{ fontSize: '1rem', color: 'var(--text-light)' }}>
{searchClient ? 'Essayez une autre recherche' : 'Créez votre premier client pour commencer'}
</p>
</div>
) : (
<div>
<p style={{ marginBottom: '1rem', color: 'var(--text-dark)', fontSize: '0.875rem' }}>
{filteredClients.length} client(s) trouvé(s)
</p>

<div className="table-container">
<table className="documents-table" style={{ background: 'white' }}>
<thead>
<tr>
<th style={{ color: 'var(--text-dark)' }}>Nom</th>
<th style={{ color: 'var(--text-dark)' }}>Téléphone</th>
<th style={{ color: 'var(--text-dark)' }}>Email</th>
<th style={{ color: 'var(--text-dark)' }}>NUI</th>
<th style={{ color: 'var(--text-dark)' }}>Actions</th>
</tr>
</thead>
<tbody>
{filteredClients.map(client => (
<tr key={client.id}>
<td className="doc-client" style={{ color: 'var(--text-dark)', fontWeight: 600 }}>{client.nom||''}</td>
<td style={{ color: 'var(--text-dark)' }}>{client.telephone || '-'}</td>
<td style={{ color: 'var(--text-dark)' }}>{client.email || '-'}</td>
<td style={{ color: 'var(--text-dark)', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.875rem' }}>
{client.nui || '-'}
</td>
<td>
<div className="action-buttons">
{currentDoc && (
<button
className="action-btn btn-view"
onClick={() => selectionnerClient(client)}
title="Sélectionner ce client"
>
✓
</button>
)}
<button
className="action-btn btn-edit"
onClick={() => editerClient(client)}
>
✏️
</button>
<button
className="action-btn btn-delete"
onClick={() => supprimerClient(client.id)}
>
🗑️
</button>
</div>
</td>
</tr>
))}
</tbody>
</table>
</div>
</div>
)}
</div>

<div className="modal-footer">
<button className="btn btn-secondary" onClick={() => {
setShowClients(false);
setSearchClient('');
}}>
Fermer
</button>
</div>
</div>
</div>
)}

{/* Modal Formulaire Client */}
{showClientForm && currentClient && (
<div className="modal-overlay">
<div className="modal-content">
<div className="modal-header">
<h2 className="modal-title">
👤 {clients.find(c => c.id === currentClient.id) ? 'Éditer' : 'Nouveau'} Client
</h2>
<button className="close-btn" onClick={() => {
setShowClientForm(false);
setCurrentClient(null);
}}>✕</button>
</div>

<div className="modal-body">
<div className="form-grid">
<div className="form-group" style={{ gridColumn: '1 / -1' }}>
<label className="form-label">Nom du client / Raison sociale *</label>
<input
type="text"
className="form-input"
value={currentClient.nom||''}
onChange={(e) => handleClientFormChange('nom', e.target.value)}
placeholder="Entreprise ou Nom Prénom"
/>
</div>

<div className="form-group">
<label className="form-label">Téléphone</label>
<input
type="tel"
className="form-input"
value={currentClient.telephone}
onChange={(e) => handleClientFormChange('telephone', e.target.value)}
placeholder="+237 6XX XXX XXX"
/>
</div>

<div className="form-group">
<label className="form-label">Email</label>
<input
type="email"
className="form-input"
value={currentClient.email}
onChange={(e) => handleClientFormChange('email', e.target.value)}
placeholder="client@exemple.com"
/>
</div>

<div className="form-group">
<label className="form-label">NUI (Numéro d'Identification Unique)</label>
<input
type="text"
className="form-input"
value={currentClient.nui || ''}
onChange={(e) => handleClientFormChange('nui', e.target.value)}
placeholder="M000000000000X"
/>
</div>

<div className="form-group">
<label className="form-label">RCCM (Registre du Commerce)</label>
<input
type="text"
className="form-input"
value={currentClient.rccm || ''}
onChange={(e) => handleClientFormChange('rccm', e.target.value)}
placeholder="RC/XXX/XXXX/X/XXXXX"
/>
</div>

<div className="form-group" style={{ gridColumn: '1 / -1' }}>
<label className="form-label">Adresse complète</label>
<input
type="text"
className="form-input"
value={currentClient.adresse}
onChange={(e) => handleClientFormChange('adresse', e.target.value)}
placeholder="Adresse complète"
/>
</div>
</div>

<div style={{
marginTop: '1.5rem',
padding: '1rem',
background: 'rgba(0, 212, 170, 0.1)',
borderRadius: '10px',
border: '1px solid rgba(0, 212, 170, 0.3)'
}}>
<p style={{ fontSize: '0.875rem', color: 'var(--text-dark)', marginBottom: '0.5rem' }}>
💡 <strong>Information:</strong>
</p>
<p style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
Les clients enregistrés peuvent être rapidement sélectionnés lors de la création de devis et factures.
</p>
</div>
</div>

<div className="modal-footer">
<button className="btn btn-secondary" onClick={() => {
setShowClientForm(false);
setCurrentClient(null);
}}>
Annuler
</button>
<button className="btn btn-success" onClick={sauvegarderClient}>
💾 Sauvegarder
</button>
</div>
</div>
</div>
)}

{/* ============================================================ */}
{/* MODAL LISTE FOURNISSEURS                                       */}
{/* ============================================================ */}
{showFournisseurs && !showFournisseurForm && (
<div className="modal-overlay" onClick={(e) => { if(e.target===e.currentTarget){setShowFournisseurs(false);setSearchFournisseur('');} }}>
<div className="modal-content" style={{ maxWidth: '860px', width: '96vw' }}>
<div className="modal-header" style={{ background: 'linear-gradient(135deg,#0A2540,#1E3A5F)', color: 'white' }}>
<h2 className="modal-title" style={{ color: 'white' }}>🏭 Fournisseurs ({fournisseurs.length})</h2>
<button className="close-btn" style={{ color: 'white' }} onClick={() => { setShowFournisseurs(false); setSearchFournisseur(''); }}>✕</button>
</div>
<div className="modal-body">
<div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
<input type="text" className="search-input" placeholder="🔍 Rechercher un fournisseur..."
value={searchFournisseur} onChange={e => setSearchFournisseur(e.target.value)} style={{ flex: 1, minWidth: '220px' }} />
<button className="btn btn-primary" onClick={nouveauFournisseur}
style={{ background: 'linear-gradient(135deg,#0A2540,#1E3A5F)', whiteSpace: 'nowrap' }}>
➕ Nouveau Fournisseur
</button>
</div>
{filteredFournisseurs.length === 0 ? (
<div style={{ padding: '3rem', textAlign: 'center', color: '#64748B' }}>
<p style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🏭</p>
<p>{fournisseurs.length === 0 ? 'Aucun fournisseur enregistré.' : 'Aucun fournisseur trouvé.'}</p>
</div>
) : (
<div style={{ overflowX: 'auto' }}>
<table className="documents-table">
<thead><tr>
<th>Nom / Raison Sociale</th>
<th>Téléphone</th>
<th>Email</th>
<th>NUI</th>
<th>Délai Livraison</th>
<th>Actions</th>
</tr></thead>
<tbody>
{filteredFournisseurs.map(f => (
<tr key={f.id}>
<td style={{ fontWeight: 600 }}>{f.nom}</td>
<td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.875rem' }}>{f.telephone||'—'}</td>
<td style={{ fontSize: '0.875rem', color: '#3B82F6' }}>{f.email||'—'}</td>
<td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>{f.nui||'—'}</td>
<td style={{ fontSize: '0.85rem', color: '#475569' }}>{f.delaiLivraison||'—'}</td>
<td>
<div className="action-buttons">
<button className="action-btn" onClick={() => editerFournisseur(f)} title="Éditer"
style={{ background: 'linear-gradient(135deg,#F59E0B,#D97706)', color: 'white' }}>✏️</button>
<button className="action-btn btn-delete" onClick={() => supprimerFournisseur(f.id)} title="Supprimer">🗑️</button>
</div>
</td>
</tr>
))}
</tbody>
</table>
</div>
)}
</div>
<div className="modal-footer">
<button className="btn btn-secondary" onClick={() => { setShowFournisseurs(false); setSearchFournisseur(''); }}>Fermer</button>
</div>
</div>
</div>
)}

{/* ============================================================ */}
{/* MODAL FORMULAIRE FOURNISSEUR                                   */}
{/* ============================================================ */}
{showFournisseurForm && currentFournisseur && (
<div className="modal-overlay">
<div className="modal-content">
<div className="modal-header" style={{ background: 'linear-gradient(135deg,#0A2540,#1E3A5F)', color: 'white' }}>
<h2 className="modal-title" style={{ color: 'white' }}>
🏭 {fournisseurs.find(f => f.id === currentFournisseur.id) ? 'Éditer' : 'Nouveau'} Fournisseur
</h2>
<button className="close-btn" style={{ color: 'white' }} onClick={() => { setShowFournisseurForm(false); setCurrentFournisseur(null); }}>✕</button>
</div>
<div className="modal-body">
<div className="form-grid">
<div className="form-group" style={{ gridColumn: '1 / -1' }}>
<label className="form-label">Nom / Raison Sociale *</label>
<input type="text" className="form-input" value={currentFournisseur.nom||''}
onChange={e => handleFournisseurFormChange('nom', e.target.value)} placeholder="Entreprise ou Nom Prénom" />
</div>
<div className="form-group">
<label className="form-label">Téléphone</label>
<input type="tel" className="form-input" value={currentFournisseur.telephone||''}
onChange={e => handleFournisseurFormChange('telephone', e.target.value)} placeholder="+237 6XX XXX XXX" />
</div>
<div className="form-group">
<label className="form-label">Email</label>
<input type="email" className="form-input" value={currentFournisseur.email||''}
onChange={e => handleFournisseurFormChange('email', e.target.value)} placeholder="fournisseur@exemple.com" />
</div>
<div className="form-group">
<label className="form-label">NUI (Numéro d'Identification Unique)</label>
<input type="text" className="form-input" value={currentFournisseur.nui||''}
onChange={e => handleFournisseurFormChange('nui', e.target.value)} placeholder="M000000000000X" />
</div>
<div className="form-group">
<label className="form-label">RCCM (Registre du Commerce)</label>
<input type="text" className="form-input" value={currentFournisseur.rccm||''}
onChange={e => handleFournisseurFormChange('rccm', e.target.value)} placeholder="RC/XXX/XXXX/X/XXXXX" />
</div>
<div className="form-group" style={{ gridColumn: '1 / -1' }}>
<label className="form-label">Adresse complète</label>
<input type="text" className="form-input" value={currentFournisseur.adresse||''}
onChange={e => handleFournisseurFormChange('adresse', e.target.value)} placeholder="Adresse complète" />
</div>
<div className="form-group">
<label className="form-label">💳 Numéro de compte bancaire</label>
<input type="text" className="form-input" value={currentFournisseur.compteBancaire||''}
onChange={e => handleFournisseurFormChange('compteBancaire', e.target.value)} placeholder="Ex: CM21-XXXX-XXXX-XXXX" />
</div>
<div className="form-group">
<label className="form-label">⏱️ Délai de livraison habituel</label>
<input type="text" className="form-input" value={currentFournisseur.delaiLivraison||''}
onChange={e => handleFournisseurFormChange('delaiLivraison', e.target.value)} placeholder="Ex: 7 jours ouvrables" />
</div>
</div>
<div style={{ marginTop:'1.5rem', padding:'1rem', background:'rgba(10,37,64,0.06)', borderRadius:'10px', border:'1px solid rgba(10,37,64,0.15)' }}>
<p style={{ fontSize:'0.875rem', color:'var(--text-light)' }}>
💡 Les fournisseurs enregistrés peuvent être sélectionnés directement lors de la création d'un Bon de Commande.
</p>
</div>
</div>
<div className="modal-footer">
<button className="btn btn-secondary" onClick={() => { setShowFournisseurForm(false); setCurrentFournisseur(null); }}>Annuler</button>
<button className="btn btn-success" onClick={sauvegarderFournisseur}>💾 Sauvegarder</button>
</div>
</div>
</div>
)}

{/* ============================================================ */}
{/* MODAL NOUVEAU / ÉDITER BON DE COMMANDE                        */}
{/* ============================================================ */}
{showBCModal && currentBC && (
<div className="modal-overlay">
<div className="modal-content" style={{ maxWidth: '1700px', width: '98vw' }}>
<div className="modal-header" style={{ background: 'linear-gradient(135deg,#0A2540,#1E3A5F)', color: 'white' }}>
<h2 className="modal-title" style={{ color: 'white' }}>
📦 {bonsCommande.find(b => b.id === currentBC.id) ? 'Éditer' : 'Nouveau'} Bon de Commande
<span style={{ marginLeft:'0.75rem', padding:'0.35rem 0.875rem', background:'rgba(0,212,170,0.25)', border:'2px solid rgba(0,212,170,0.5)', borderRadius:'8px', fontSize:'0.9rem', fontWeight:700, fontFamily:'JetBrains Mono, monospace' }}>
{currentBC.numero}
</span>
</h2>
<button className="close-btn" style={{ color:'white' }} onClick={() => { document.querySelectorAll('.lt-popup').forEach(p => p.remove()); setShowBCModal(false); setCurrentBC(null); }}>✕</button>
</div>

<div className="modal-body">
{/* Infos générales */}
<div className="form-grid">
<div className="form-group">
<label className="form-label">Numéro</label>
<input type="text" className="form-input" value={currentBC.numero} readOnly style={{ background:'#f5f5f5' }} />
</div>
<div className="form-group">
<label className="form-label">Date</label>
<input type="date" className="form-input" value={currentBC.date}
onChange={e => setCurrentBC({...currentBC, date: e.target.value})} />
</div>
<div className="form-group">
<label className="form-label">Statut</label>
<select className="form-select" value={currentBC.statut||'en-attente'}
onChange={e => setCurrentBC({...currentBC, statut: e.target.value})}>
<option value="en-attente">⏳ En attente d'envoi</option>
<option value="envoye">📤 Envoyé au fournisseur</option>
<option value="recu-partiel">📬 Reçu partiellement</option>
<option value="recu">✅ Réception complète</option>
<option value="annule">❌ Annulé</option>
</select>
</div>
<div className="form-group">
<label className="form-label">Modèle d'impression</label>
<select className="form-select" value={currentBC.modeleBC||'classique'}
onChange={e => setCurrentBC({...currentBC, modeleBC: e.target.value})}>
<option value="classique">Classique</option>
<option value="compact">Compact</option>
<option value="moderne">Moderne</option>
</select>
</div>
<div className="form-group" style={{ gridColumn:'1 / -1' }}>
<label className="form-label">Objet du bon de commande</label>
<input type="text" className="form-input" value={currentBC.objet||''}
onChange={e => setCurrentBC({...currentBC, objet: e.target.value})}
placeholder="Ex: Fourniture de matériaux de construction" />
</div>
</div>

{/* Informations fournisseur */}
<h3 style={{ marginTop:'2rem', marginBottom:'1rem', color:'var(--text-dark)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'1rem' }}>
<span>🏭 Informations Fournisseur</span>
<button type="button" className="btn btn-secondary"
onClick={() => setShowFournisseurSelectInBC(true)}
style={{ fontSize:'0.875rem', padding:'0.5rem 1rem' }}>
📋 Sélectionner un fournisseur
</button>
</h3>

{/* Mini-modal de sélection fournisseur dans BC */}
{showFournisseurSelectInBC && (
<div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:20000, display:'flex', alignItems:'center', justifyContent:'center' }}
onClick={e => { if(e.target===e.currentTarget) setShowFournisseurSelectInBC(false); }}>
<div style={{ background:'white', borderRadius:'16px', padding:'1.5rem', maxWidth:'600px', width:'95vw', maxHeight:'70vh', display:'flex', flexDirection:'column' }}>
<div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
<h3 style={{ color:'#0A2540', fontWeight:700 }}>🏭 Choisir un fournisseur</h3>
<button onClick={() => setShowFournisseurSelectInBC(false)} style={{ background:'none', border:'none', fontSize:'1.3rem', cursor:'pointer', color:'#64748B' }}>✕</button>
</div>
<input type="text" className="form-input" placeholder="Rechercher..." value={searchFournisseurBC}
onChange={e => setSearchFournisseurBC(e.target.value)} style={{ marginBottom:'1rem' }} />
<div style={{ overflowY:'auto', flex:1 }}>
{fournisseurs.length === 0 ? (
<div style={{ textAlign:'center', color:'#94A3B8', padding:'2rem' }}>
<p>Aucun fournisseur enregistré.</p>
<button className="btn btn-primary" onClick={() => { setShowFournisseurSelectInBC(false); setShowFournisseurs(false); nouveauFournisseur(); }}
style={{ marginTop:'1rem', background:'linear-gradient(135deg,#0A2540,#1E3A5F)' }}>
➕ Créer un fournisseur
</button>
</div>
) : fournisseurs.filter(f => {
const s = searchFournisseurBC.toLowerCase();
return !s || (f.nom||'').toLowerCase().includes(s) || (f.telephone||'').includes(s);
}).map(f => (
<div key={f.id} onClick={() => selectionnerFournisseurPourBC(f)}
style={{ padding:'0.875rem 1rem', border:'2px solid #E2E8F0', borderRadius:'10px', marginBottom:'0.5rem', cursor:'pointer', transition:'all 0.2s' }}
onMouseEnter={e => { e.currentTarget.style.border='2px solid #0A2540'; e.currentTarget.style.background='#F0F4FF'; }}
onMouseLeave={e => { e.currentTarget.style.border='2px solid #E2E8F0'; e.currentTarget.style.background='white'; }}>
<div style={{ fontWeight:700, color:'#0A2540' }}>{f.nom||''}</div>
{f.telephone && <div style={{ fontSize:'0.8rem', color:'#64748B' }}>📞 {f.telephone}</div>}
{f.email && <div style={{ fontSize:'0.8rem', color:'#64748B' }}>✉ {f.email}</div>}
{f.delaiLivraison && <div style={{ fontSize:'0.78rem', color:'#10B981', marginTop:'0.2rem' }}>⏱️ {f.delaiLivraison}</div>}
</div>
))}
</div>
</div>
</div>
)}

<div className="form-grid">
<div className="form-group" style={{ gridColumn:'1 / -1' }}>
<label className="form-label">Nom du fournisseur *</label>
<input type="text" className="form-input" value={currentBC.fournisseur?.nom||''}
onChange={e => handleBCFournisseurChange('nom', e.target.value)} placeholder="Entreprise ou Nom Prénom" />
</div>
<div className="form-group">
<label className="form-label">Téléphone</label>
<input type="tel" className="form-input" value={currentBC.fournisseur?.telephone||''}
onChange={e => handleBCFournisseurChange('telephone', e.target.value)} placeholder="+237 6XX XXX XXX" />
</div>
<div className="form-group">
<label className="form-label">Email</label>
<input type="email" className="form-input" value={currentBC.fournisseur?.email||''}
onChange={e => handleBCFournisseurChange('email', e.target.value)} placeholder="fournisseur@exemple.com" />
</div>
<div className="form-group">
<label className="form-label">NUI</label>
<input type="text" className="form-input" value={currentBC.fournisseur?.nui||''}
onChange={e => handleBCFournisseurChange('nui', e.target.value)} placeholder="M000000000000X" />
</div>
<div className="form-group">
<label className="form-label">RCCM</label>
<input type="text" className="form-input" value={currentBC.fournisseur?.rccm||''}
onChange={e => handleBCFournisseurChange('rccm', e.target.value)} placeholder="RC/XXX/XXXX/X/XXXXX" />
</div>
<div className="form-group" style={{ gridColumn:'1 / -1' }}>
<label className="form-label">Adresse</label>
<input type="text" className="form-input" value={currentBC.fournisseur?.adresse||''}
onChange={e => handleBCFournisseurChange('adresse', e.target.value)} placeholder="Adresse complète" />
</div>
</div>

{/* Livraison */}
<h3 style={{ marginTop:'2rem', marginBottom:'1rem', color:'var(--text-dark)' }}>📦 Conditions de Livraison</h3>
<div className="form-grid">
<div className="form-group">
<label className="form-label">⏱️ Délai de livraison souhaité</label>
<input type="text" className="form-input" value={currentBC.delaiLivraison||''}
onChange={e => setCurrentBC({...currentBC, delaiLivraison: e.target.value})} placeholder="Ex: 15 jours ouvrables" />
</div>
<div className="form-group">
<label className="form-label">📍 Lieu de livraison</label>
<input type="text" className="form-input" value={currentBC.lieuLivraison||''}
onChange={e => setCurrentBC({...currentBC, lieuLivraison: e.target.value})} placeholder="Ex: Entrepôt principal — Yaoundé" />
</div>
</div>

{/* Mode taxation */}
<div style={{ marginTop:'1.5rem', marginBottom:'1.5rem', padding:'1rem', background:'linear-gradient(135deg,rgba(0,212,170,0.1),rgba(255,107,53,0.1))', borderRadius:'12px', border:'2px solid rgba(0,212,170,0.2)' }}>
<h3 style={{ color:'var(--text-dark)', marginBottom:'1rem' }}>💰 Mode de Taxation</h3>
<div style={{ display:'flex', gap:'1rem', flexWrap:'wrap' }}>
{['TTC','HT'].map(mode => (
<label key={mode} style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer', padding:'0.75rem 1.25rem', background: currentBC.taxMode===mode ? (mode==='TTC'?'rgba(16,185,129,0.2)':'rgba(59,130,246,0.2)') : 'rgba(255,255,255,0.5)', borderRadius:'8px', border: currentBC.taxMode===mode ? `2px solid ${mode==='TTC'?'var(--success)':'#3B82F6'}` : '2px solid transparent', transition:'all 0.3s' }}>
<input type="radio" name="bcTaxMode" value={mode} checked={currentBC.taxMode===mode}
onChange={() => {
const tots = recalculerTotauxBC(currentBC.items, mode);
setCurrentBC({...currentBC, taxMode: mode, ...tots});
}} style={{ accentColor: mode==='TTC' ? 'var(--success)' : '#3B82F6' }} />
<span>{mode==='TTC' ? `✅ TTC (TVA ${settings.tauxTVA||19.25}%)` : '🚫 HT (sans TVA)'}</span>
</label>
))}
</div>
<div
onClick={() => setCurrentBC({...currentBC, afficherModeTaxation: !currentBC.afficherModeTaxation})}
style={{
  marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0.6rem 0.9rem', borderRadius: '10px', cursor: 'pointer',
  background: currentBC.afficherModeTaxation ? 'rgba(0,168,150,0.08)' : 'rgba(0,0,0,0.03)',
  border: `1px solid ${currentBC.afficherModeTaxation ? 'rgba(0,168,150,0.4)' : '#E2E8F0'}`,
  transition: 'all 0.2s',
}}>
  <div>
    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>🏷️ Afficher le mode de taxation sur le document imprimé</span>
    <div style={{ fontSize: '0.72rem', color: '#94A3B8', marginTop: '2px' }}>
      {currentBC.afficherModeTaxation
        ? (currentBC.taxMode === 'TTC'
            ? 'Activé — SOUS-TOTAL HT · TOTAL TTC · TOTAL À PAYER TTC'
            : 'Activé — SOUS-TOTAL HT · TOTAL HT · TOTAL À PAYER HT')
        : 'Désactivé — SOUS-TOTAL · TOTAL · TOTAL À PAYER'}
    </div>
  </div>
  <div style={{
    width: 44, height: 24, borderRadius: 12, position: 'relative', flexShrink: 0,
    background: currentBC.afficherModeTaxation ? '#00A896' : '#CBD5E1',
    transition: 'background 0.2s',
  }}>
    <div style={{
      position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%', background: 'white',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
      left: currentBC.afficherModeTaxation ? 23 : 3,
    }} />
  </div>
</div>
</div>

{/* Lignes articles */}
<div className="items-section">
<div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem', flexWrap:'wrap', gap:'0.75rem' }}>
<h3 style={{ color:'var(--text-dark)' }}>📦 Articles commandés</h3>
<button className="btn btn-secondary" onClick={ajouterArticleBC}
style={{ background:'linear-gradient(135deg,#0A2540,#1E3A5F)', color:'white', border:'none' }}>
＋ Ajouter un article
</button>
</div>
<div style={{ overflowX:'auto', border:'1.5px solid #E2E8F0', borderRadius:'14px', overflow:'hidden' }}>
<table className="items-table" style={{ marginBottom:0 }}>
<thead><tr>
<th>Désignation / Description</th>
<th style={{ width:'100px' }}>Unité</th>
<th style={{ width:'90px' }}>Quantité</th>
<th style={{ width:'130px' }}>Prix Unit.</th>
<th style={{ width:'130px' }}>{currentBC.afficherModeTaxation ? 'Total HT' : 'Total'}</th>
<th style={{ width:'50px' }}></th>
</tr></thead>
<tbody>
{(currentBC.items||[]).length === 0 ? (
<tr><td colSpan="6" style={{ textAlign:'center', padding:'2rem', color:'#94A3B8' }}>
Aucun article. Cliquez sur "Ajouter un article".
</td></tr>
) : (currentBC.items||[]).map((item, idx) => (
<tr key={item.id||idx}>
<td><input type="text" className="form-input" value={item.description||''}
onChange={e => modifierArticleBC(idx,'description',e.target.value)} placeholder="Description de l'article" /></td>
<td><select className="form-select" value={item.unite||'PCS'}
onChange={e => modifierArticleBC(idx,'unite',e.target.value)}>
{[{code:'PCS',libelle:'Pièce'},{code:'KG',libelle:'Kilogramme'},{code:'L',libelle:'Litre'},{code:'M2',libelle:'M²'},{code:'ML',libelle:'M linéaire'},{code:'M3',libelle:'M³'},{code:'SAC',libelle:'Sac'},{code:'BOITE',libelle:'Boîte'},{code:'ROUL',libelle:'Rouleau'},{code:'JOUR',libelle:'Jour(s)'}].map(u => (
<option key={u.code} value={u.code}>{u.libelle}</option>
))}
</select></td>
<td><input type="number" className="form-input" value={item.quantite||0} min="0"
onChange={e => modifierArticleBC(idx,'quantite',parseFloat(e.target.value)||0)} style={{ textAlign:'right' }} /></td>
<td><input type="number" className="form-input" value={item.prixUnitaire||0} min="0"
onChange={e => modifierArticleBC(idx,'prixUnitaire',parseFloat(e.target.value)||0)} style={{ textAlign:'right' }} /></td>
<td style={{ fontFamily:'JetBrains Mono, monospace', fontWeight:600, textAlign:'right', padding:'0.5rem' }}>
{formatMontant((item.quantite||0)*(item.prixUnitaire||0))}
</td>
<td><button onClick={() => supprimerArticleBC(idx)}
style={{ background:'#FEE2E2', border:'none', borderRadius:'6px', padding:'0.35rem 0.5rem', cursor:'pointer', fontSize:'0.9rem' }}>🗑️</button></td>
</tr>
))}
</tbody>
</table>
</div>
</div>

{/* Totaux */}
<div style={{ display:'flex', justifyContent:'flex-end', marginTop:'1.5rem' }}>
<div style={{ minWidth:'320px', padding:'1.25rem', background:'#F8FAFC', borderRadius:'12px', border:'2px solid #E2E8F0' }}>
<div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.6rem', fontSize:'0.9rem' }}>
<span style={{ color:'#64748B' }}>{currentBC.afficherModeTaxation ? 'Sous-total HT:' : 'Sous-total:'}</span>
<span style={{ fontFamily:'monospace', fontWeight:600 }}>{formatMontant(currentBC.sousTotal||0)}</span>
</div>
{currentBC.taxMode==='TTC' && (
<div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.75rem', paddingBottom:'0.75rem', borderBottom:'1px solid #E2E8F0', fontSize:'0.9rem' }}>
<span style={{ color:'#64748B' }}>TVA ({settings.tauxTVA||19.25}%):</span>
<span style={{ fontFamily:'monospace', fontWeight:600 }}>{formatMontant(currentBC.tva||0)}</span>
</div>
)}
<div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:'0.5rem', borderTop:'3px solid #0A2540' }}>
<span style={{ fontSize:'1.1rem', fontWeight:'bold' }}>{currentBC.afficherModeTaxation ? `TOTAL ${currentBC.taxMode==='TTC'?'TTC':'HT'}:` : 'TOTAL:'}</span>
<span style={{ fontFamily:'monospace', fontSize:'1.4rem', fontWeight:'bold', color:'#FF6B35' }}>{formatMontant(currentBC.total||0)}</span>
</div>
</div>
</div>

{/* Notes */}
<div className="form-group" style={{ marginTop:'1.5rem' }}>
<label className="form-label">📝 Notes / Conditions particulières</label>
<textarea className="form-textarea" rows="3" value={currentBC.notes||''}
onChange={e => setCurrentBC({...currentBC, notes: e.target.value})}
placeholder="Conditions de paiement, modalités de livraison, remarques..." />
</div>
</div>

<div className="modal-footer">
<button className="btn btn-secondary" onClick={() => { document.querySelectorAll('.lt-popup').forEach(p => p.remove()); setShowBCModal(false); setCurrentBC(null); }}>Annuler</button>
<button className="btn btn-primary" onClick={() => { if(currentBC) imprimerBC(currentBC); }}
style={{ background:'linear-gradient(135deg,#10B981,#059669)' }}>🖨️ Aperçu / Imprimer</button>
<button className="btn btn-success" onClick={sauvegarderBC} disabled={isBCSaving} style={{opacity: isBCSaving ? 0.6 : 1, cursor: isBCSaving ? 'not-allowed' : 'pointer'}}>
{isBCSaving ? '⏳ Sauvegarde...' : '💾 Sauvegarder le BC'}
</button>
</div>
</div>
</div>
)}

{/* ============================================================ */}
{/* MODAL APERÇU / IMPRESSION BC (depuis la liste)                */}
{/* ============================================================ */}
{showBCPreview && currentBC && (
<div className="modal-overlay" style={{ zIndex:10012 }} onClick={e => { if(e.target===e.currentTarget){setShowBCPreview(false);} }}>
<div className="modal-content" style={{ maxWidth:'500px', width:'90vw' }}>
<div className="modal-header" style={{ background:'linear-gradient(135deg,#0A2540,#1E3A5F)', color:'white' }}>
<h2 className="modal-title" style={{ color:'white' }}>📦 {currentBC.numero}</h2>
<button className="close-btn" style={{ color:'white' }} onClick={() => setShowBCPreview(false)}>✕</button>
</div>
<div className="modal-body">
<p style={{ color:'#64748B', marginBottom:'1.5rem', fontSize:'0.9rem' }}>Choisissez le modèle d'impression pour ce bon de commande.</p>
<div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
{[
{ key:'classique', icon:'📋', label:'Classique', desc:'Fond blanc, en-tête bleu marine, signatures en bas' },
{ key:'moderne', icon:'✨', label:'Moderne', desc:'Design épuré avec fond gris, bandeau coloré' },
{ key:'compact', icon:'🗂️', label:'Compact', desc:'Style sobre et dense, bordures noires, format bureau' },
{ key:'noir-blanc', icon:'⬜', label:'Noir et Blanc', desc:'Optimisé pour impression monochrome, économe en encre' }
].map(m => (
<button key={m.key} onClick={() => { imprimerBC({...currentBC, modeleBC: m.key}); }}
style={{ display:'flex', gap:'1rem', alignItems:'center', padding:'1rem', background:'#F8FAFC', border:'2px solid #E2E8F0', borderRadius:'12px', cursor:'pointer', textAlign:'left', transition:'all 0.2s' }}
onMouseEnter={e => { e.currentTarget.style.border='2px solid #0A2540'; e.currentTarget.style.background='#F0F4FF'; }}
onMouseLeave={e => { e.currentTarget.style.border='2px solid #E2E8F0'; e.currentTarget.style.background='#F8FAFC'; }}>
<span style={{ fontSize:'1.8rem' }}>{m.icon}</span>
<div><div style={{ fontWeight:700, color:'#0A2540' }}>{m.label}</div><div style={{ fontSize:'0.8rem', color:'#64748B' }}>{m.desc}</div></div>
</button>
))}
</div>
</div>
<div className="modal-footer">
<button className="btn btn-secondary" onClick={() => setShowBCPreview(false)}>Fermer</button>
</div>
</div>
</div>
)}

{/* Modal Paramètres */}
{showSettings && (
<div className="modal-overlay">
<div className="modal-content">
<div className="modal-header">
<h2 className="modal-title">⚙️ Paramètres de l'entreprise</h2>
<button className="close-btn" onClick={annulerParametres}>✕</button>
</div>

<div className="modal-body">
<div className="logo-section">
<label className="form-label">Logo de l'entreprise</label>
{tempSettings.logo ? (
<div className="logo-preview-wrapper">
<img src={tempSettings.logo} alt="Logo" className="logo-preview" />
<div className="logo-actions">
<button
type="button"
className="btn btn-secondary btn-icon"
onClick={supprimerLogo}
title="Supprimer le logo"
>
🗑️
</button>
</div>
</div>
) : (
<div className="logo-upload-area">
<input
type="file"
accept="image/*"
onChange={handleLogoUpload}
/>
<div style={{ pointerEvents: 'none' }}>
<div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📸</div>
<p style={{ color: 'var(--text-dark)', fontWeight: 600, marginBottom: '0.25rem' }}>
Cliquez pour ajouter un logo
</p>
<p style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
PNG, JPG, SVG (max. 2MB)
</p>
</div>
</div>
)}
</div>

<div className="form-grid">
<div className="form-group" style={{ gridColumn: '1 / -1' }}>
<label className="form-label">Raison Sociale *</label>
<input
type="text"
name="raisonSociale"
className="form-input"
value={tempSettings.raisonSociale}
onChange={handleSettingsChange}
placeholder="SOLSTICE"
/>
</div>

<div className="form-group">
<label className="form-label">Téléphone</label>
<input
type="tel"
name="telephone"
className="form-input"
value={tempSettings.telephone}
onChange={handleSettingsChange}
placeholder="+237 6XX XXX XXX"
/>
</div>

<div className="form-group">
<label className="form-label">Site Web</label>
<input
type="text"
name="siteWeb"
className="form-input"
value={tempSettings.siteWeb}
onChange={handleSettingsChange}
placeholder="www.solstice.cm"
/>
</div>

<div className="form-group">
<label className="form-label">NUI (Numéro d'Identification Unique)</label>
<input
type="text"
name="nui"
className="form-input"
value={tempSettings.nui}
onChange={handleSettingsChange}
placeholder="M000000000000X"
/>
</div>

<div className="form-group">
<label className="form-label">RCCM (Registre de Commerce)</label>
<input
type="text"
name="rccm"
className="form-input"
value={tempSettings.rccm}
onChange={handleSettingsChange}
placeholder="RC/XXX/XXXX/X/XXXXX"
/>
</div>

<div className="form-group">
<label className="form-label">Email</label>
<input
type="email"
name="email"
className="form-input"
value={tempSettings.email}
onChange={handleSettingsChange}
placeholder="contact@solstice.cm"
/>
</div>

<div className="form-group" style={{ gridColumn: '1 / -1' }}>
<label className="form-label">Adresse complète</label>
<input
type="text"
name="adresse"
className="form-input"
value={tempSettings.adresse}
onChange={handleSettingsChange}
placeholder="Quartier, Ville, BP..."
/>
</div>

<div className="form-group">
<label className="form-label">
  Nom agent
  <span style={{
    marginLeft:'0.5rem', fontSize:'0.72rem', fontWeight:400,
    background:'rgba(0,212,170,0.1)', color:'#00D4AA',
    border:'1px solid rgba(0,212,170,0.3)', borderRadius:'20px',
    padding:'0.1rem 0.5rem'
  }}>
    Auto : {profil?.nom || '—'}
  </span>
</label>
<input
type="text"
name="agent"
className="form-input"
value={tempSettings.agent || profil?.nom || ''}
onChange={handleSettingsChange}
placeholder={profil?.nom || 'Ex: Toto'}
/>
<small style={{color:'#64748B', fontSize:'0.75rem', marginTop:'0.25rem', display:'block'}}>
  Rempli automatiquement avec le nom de l'utilisateur connecté.
  Modifiez pour forcer une valeur différente.
</small>
</div>
</div>

{/* Section Modèles d'impression */}
<div style={{ marginTop: '2rem' }}>
<h3 style={{
fontSize: '1.1rem',
color: 'var(--text-dark)',
marginBottom: '1rem',
paddingBottom: '0.5rem',
borderBottom: '2px solid #00D4AA'
}}>
🖨️ Modèles d'impression
</h3>
<div className="form-grid">
<div className="form-group">
<label className="form-label">Modèle pour les Devis</label>
<select
name="modeleDevis"
className="form-select"
value={tempSettings.modeleDevis || 'classique'}
onChange={handleSettingsChange}
>
<option value="classique">Classique</option>
<option value="moderne">Moderne</option>
<option value="compact">Compact</option>
<option value="noir-blanc">Noir et Blanc</option>
<option value="noir-blanc2">Noir et Blanc 2</option>
<option value="compact-duplicata">Compact Duplicata (2 par page)</option>
<option value="compact-duplicata-sans-ht">Compact Duplicata A5 (sans HT)</option>
<option value="noir-blanc2-a5">Noir et Blanc 2 - A5 Duplicata</option>
<optgroup label="── Nouveaux modèles ──">
<option value="minimaliste-elegant">✦ Minimaliste Élégant</option>
<option value="corporate-premium">✦ Corporate Premium</option>
<option value="classique-ameliore">✦ Classique Amélioré</option>
</optgroup>
</select>
</div>

<div className="form-group">
<label className="form-label">Modèle pour les Factures</label>
<select
name="modeleFacture"
className="form-select"
value={tempSettings.modeleFacture || 'classique'}
onChange={handleSettingsChange}
>
<option value="classique">Classique</option>
<option value="moderne">Moderne</option>
<option value="compact">Compact</option>
<option value="noir-blanc">Noir et Blanc</option>
<option value="noir-blanc2">Noir et Blanc 2</option>
<option value="compact-duplicata">Compact Duplicata (2 par page)</option>
<option value="compact-duplicata-sans-ht">Compact Duplicata A5 (sans HT)</option>
<option value="noir-blanc2-a5">Noir et Blanc 2 - A5 Duplicata</option>
<optgroup label="── Nouveaux modèles ──">
<option value="minimaliste-elegant">✦ Minimaliste Élégant</option>
<option value="corporate-premium">✦ Corporate Premium</option>
<option value="classique-ameliore">✦ Classique Amélioré</option>
</optgroup>
</select>
</div>
</div>

{/* Modèle Reçu de paiement */}
<div style={{ marginTop: '1.5rem', padding: '1rem', background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(79,70,229,0.08))', borderRadius: '12px', border: '1.5px solid #C7D2FE' }}>
<div style={{ fontWeight: '600', color: '#4F46E5', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem' }}>
🧾 Modèle pour les Reçus de Paiement
</div>
<div className="form-group" style={{ marginBottom: '0.5rem' }}>
<label className="form-label">Choisir le modèle d'affichage du reçu</label>
<select
name="modeleRecu"
className="form-select"
value={tempSettings.modeleRecu || 'classique'}
onChange={handleSettingsChange}
>
<option value="classique">🧾 Classique — Ticket structuré violet</option>
<option value="moderne">✨ Moderne — Design épuré avec bandeau coloré</option>
<option value="minimaliste">⬜ Minimaliste — Noir et blanc sobre</option>
<option value="minimaliste-duplicata">📄 Minimaliste Duplicata — 2 reçus A5 sur A4</option>
<option value="thermique">🖨️ Thermique — Format ticket de caisse 80mm</option>
</select>
</div>

{/* Prévisualisation miniature du modèle sélectionné */}
<div style={{ marginTop: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '8px', background: 'white', border: '1px solid #E2E8F0', fontSize: '0.75rem', color: '#64748B', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
{(tempSettings.modeleRecu || 'classique') === 'classique' && <><span style={{ fontSize: '1.2rem' }}>🧾</span> <span>Ticket structuré avec en-tête société, sections colorées en violet, et récapitulatif financier.</span></>}
{(tempSettings.modeleRecu || 'classique') === 'moderne' && <><span style={{ fontSize: '1.2rem' }}>✨</span> <span>Design épuré avec bandeau dégradé, icônes et mise en page aérée.</span></>}
{(tempSettings.modeleRecu || 'classique') === 'minimaliste' && <><span style={{ fontSize: '1.2rem' }}>⬜</span> <span>Format sobre noir et blanc, idéal pour imprimantes sans couleur.</span></>}
{(tempSettings.modeleRecu || 'classique') === 'minimaliste-duplicata' && <><span style={{ fontSize: '1.2rem' }}>📄</span> <span>Deux reçus A5 identiques (Original + Duplicata) sur une feuille A4, avec ligne de coupe et lignes de signature.</span></>}
{(tempSettings.modeleRecu || 'classique') === 'thermique' && <><span style={{ fontSize: '1.2rem' }}>🖨️</span> <span>Format étroit 80mm optimisé pour imprimantes thermiques (tickets de caisse).</span></>}
</div>
</div>

</div>{/* fin Section Modèles d'impression */}

{/* Section TVA */}
<div style={{ marginTop: '2rem' }}>
<h3 style={{
fontSize: '1.1rem',
color: 'var(--text-dark)',
marginBottom: '1rem',
paddingBottom: '0.5rem',
borderBottom: '2px solid #00D4AA'
}}>
🧾 Taxe sur la Valeur Ajoutée (TVA)
</h3>
<div className="form-grid">
<div className="form-group">
<label className="form-label">Taux de TVA (%)</label>
<input
type="number"
className="form-input"
value={tempSettings.tauxTVA !== undefined ? tempSettings.tauxTVA : 19.25}
onChange={(e) => setTempSettings({ ...tempSettings, tauxTVA: parseFloat(e.target.value) || 0 })}
step="0.01"
min="0"
max="50"
placeholder="Ex : 19.25"
/>
<small style={{ color: '#64748B', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
Exemples : 19,25% (Cameroun) · 18% (Côte d'Ivoire, Sénégal) · 20% (France) · 0% (exonéré)
</small>
</div>
<div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginTop: '1.5rem' }}>
<input
type="checkbox"
checked={!(tempSettings.tauxTVA > 0)}
onChange={(e) => setTempSettings({ ...tempSettings, tauxTVA: e.target.checked ? 0 : 19.25 })}
style={{ width: 'auto' }}
/>
<span style={{ fontWeight: '500' }}>Exonéré de TVA (taux 0%)</span>
</label>
</div>
</div>
<div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '6px', padding: '10px 14px', marginTop: '8px', fontSize: '0.85rem', color: '#166534' }}>
ℹ️ Ce taux s'applique à tous les nouveaux documents en mode TTC. Les documents existants conservent leur taux d'origine.
</div>
</div>

{/* Section CGV */}
<div style={{ marginTop: '2rem' }}>
<h3 style={{
fontSize: '1.1rem',
color: 'var(--text-dark)',
marginBottom: '1rem',
paddingBottom: '0.5rem',
borderBottom: '2px solid #00D4AA'
}}>
📜 Conditions Générales de Vente
</h3>
<div className="form-group">
<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
<input
type="checkbox"
checked={tempSettings.cgv?.activer || false}
onChange={(e) => setTempSettings({
...tempSettings,
cgv: {
...tempSettings.cgv,
activer: e.target.checked
}
})}
style={{ width: 'auto' }}
/>
<span style={{ fontWeight: '500' }}>Inclure les CGV dans les documents</span>
</label>
</div>

{tempSettings.cgv?.activer && (
<div className="form-group">
<label className="form-label">Position des CGV</label>
<select
className="form-select"
value={tempSettings.cgv?.position || 'footer'}
onChange={(e) => setTempSettings({
...tempSettings,
cgv: {
...tempSettings.cgv,
position: e.target.value
}
})}
>
<option value="footer">Pied de page</option>
<option value="page-separee">Page séparée</option>
</select>
</div>
)}

{tempSettings.cgv?.activer && (
<div className="form-group">
<label className="form-label">Texte des CGV</label>
<textarea
className="form-textarea"
value={tempSettings.cgv?.texte || ''}
onChange={(e) => setTempSettings({
...tempSettings,
cgv: {
...tempSettings.cgv,
texte: e.target.value
}
})}
rows="10"
placeholder="Saisissez vos conditions générales de vente..."
/>
</div>
)}
</div>

{/* Section Pénalités */}
<div style={{ marginTop: '2rem' }}>
<h3 style={{
fontSize: '1.1rem',
color: 'var(--text-dark)',
marginBottom: '1rem',
paddingBottom: '0.5rem',
borderBottom: '2px solid #00D4AA'
}}>
⏰ Pénalités de retard
</h3>
<div className="form-group">
<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
<input
type="checkbox"
checked={tempSettings.penalitesRetardActives || false}
onChange={(e) => setTempSettings({
...tempSettings,
penalitesRetardActives: e.target.checked
})}
style={{ width: 'auto' }}
/>
<span style={{ fontWeight: '500' }}>Activer les pénalités de retard par défaut</span>
</label>
</div>

{tempSettings.penalitesRetardActives && (
<>
<div className="form-group">
<label className="form-label">Taux journalier de pénalités (%)</label>
<input
type="number"
name="tauxPenalitesJour"
className="form-input"
value={tempSettings.tauxPenalitesJour || 0.03}
onChange={handleSettingsChange}
step="0.01"
min="0"
max="10"
/>
</div>

<div className="form-group">
<label className="form-label">Message de pénalités (affiché sur les factures)</label>
<textarea
name="messagePenalites"
className="form-textarea"
value={tempSettings.messagePenalites || ''}
onChange={handleSettingsChange}
rows="3"
placeholder="En cas de retard de paiement, des pénalités de ..."
/>
</div>
</>
)}
</div>
</div>

<div className="modal-footer">
<button className="btn btn-secondary" onClick={annulerParametres}>
Annuler
</button>
<button className="btn btn-success" onClick={sauvegarderParametres}>
💾 Enregistrer les paramètres
</button>
</div>
</div>
</div>
)}

{/* Modal Impression en lot */}
{showBatchPrint && (
<div className="modal-overlay">
<div className="modal-content" style={{ maxWidth: '600px' }}>
<div className="modal-header">
<h2 className="modal-title">🖨️ Impression en lot</h2>
<button className="close-btn" onClick={() => setShowBatchPrint(false)}>✕</button>
</div>

<div className="modal-body">
<div style={{
padding: '1.5rem',
background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)',
borderRadius: '12px',
border: '2px solid rgba(59, 130, 246, 0.1)',
marginBottom: '1.5rem'
}}>
<div style={{ textAlign: 'center', marginBottom: '1rem' }}>
<div style={{
fontSize: '3rem',
marginBottom: '0.5rem'
}}>
📄
</div>
<h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1E293B', marginBottom: '0.5rem' }}>
{selectedDocs.length} document{selectedDocs.length > 1 ? 's' : ''} sélectionné{selectedDocs.length > 1 ? 's' : ''}
</h3>
<p style={{ color: '#64748B', fontSize: '0.875rem' }}>
Prêt pour l'impression
</p>
</div>

<div style={{
background: 'white',
borderRadius: '8px',
padding: '1rem',
maxHeight: '200px',
overflowY: 'auto',
border: '1px solid #E5E7EB'
}}>
<div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748B', marginBottom: '0.75rem' }}>
Documents à imprimer :
</div>
{documents.filter(doc => selectedDocs.includes(doc.id)).map((doc, index) => (
<div key={doc.id} style={{
display: 'flex',
alignItems: 'center',
gap: '0.75rem',
padding: '0.625rem',
background: index % 2 === 0 ? '#F9FAFB' : 'white',
borderRadius: '6px',
marginBottom: '0.5rem'
}}>
<div style={{
width: '28px',
height: '28px',
borderRadius: '6px',
background: doc.type === 'devis' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)',
color: doc.type === 'devis' ? '#3B82F6' : '#10B981',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
fontWeight: 'bold',
fontSize: '0.75rem'
}}>
{index + 1}
</div>
<div style={{ flex: 1 }}>
<div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1E293B' }}>
{doc.numero}
</div>
<div style={{ fontSize: '0.75rem', color: '#64748B' }}>
{doc.client?.nom||''} • {formatMontant(doc.total)}
</div>
</div>
<div style={{
padding: '0.25rem 0.5rem',
borderRadius: '4px',
fontSize: '0.7rem',
fontWeight: '600',
background: doc.type === 'devis' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)',
color: doc.type === 'devis' ? '#3B82F6' : '#10B981'
}}>
{doc.type === 'devis' ? 'Devis' : 'Facture'}
</div>
</div>
))}
</div>
</div>

<div style={{
padding: '1rem',
background: '#FEF3C7',
border: '1px solid #FDE047',
borderRadius: '8px',
marginBottom: '1.5rem'
}}>
<div style={{ display: 'flex', gap: '0.75rem' }}>
<div style={{ fontSize: '1.25rem' }}>💡</div>
<div>
<div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#92400E', marginBottom: '0.25rem' }}>
Mode d'impression séquentiel
</div>
<div style={{ fontSize: '0.8125rem', color: '#78350F', lineHeight: '1.4' }}>
Les documents seront imprimés un par un. Validez chaque impression dans la boîte de dialogue de votre navigateur pour passer au document suivant.
</div>
</div>
</div>
</div>

<div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
<button
onClick={() => setShowBatchPrint(false)}
style={{
padding: '0.75rem 1.5rem',
background: 'white',
border: '2px solid #E5E7EB',
borderRadius: '8px',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.875rem',
color: '#64748B',
transition: 'all 0.2s'
}}
>
Annuler
</button>
<button
onClick={imprimerLot}
style={{
padding: '0.75rem 2rem',
background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.875rem',
color: 'white',
boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
transition: 'all 0.2s',
display: 'flex',
alignItems: 'center',
gap: '0.5rem'
}}
>
🖨️ Lancer l'impression
</button>
</div>
</div>
</div>
</div>
)}

{/* Modal Raison d'Annulation */}
{/* ========== MODAL CONFIGURATION DES BOUTONS ACTIONS ========== */}
{showConfigActions && (
<div className="modal-overlay" style={{ zIndex: 10020 }} onClick={e => { if(e.target === e.currentTarget) setShowConfigActions(false); }}>
<div className="modal-content" style={{ maxWidth: '480px', width: '95vw' }}>
<div className="modal-header" style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1E3A5F 100%)', color: 'white' }}>
<h2 className="modal-title" style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
⚙️ Personnaliser les boutons d'action
</h2>
<button className="close-btn" style={{ color: 'white' }} onClick={() => setShowConfigActions(false)}>✕</button>
</div>

<div className="modal-body">
<p style={{ fontSize: '0.875rem', color: '#64748B', marginBottom: '1.5rem', lineHeight: 1.6 }}>
Choisissez les boutons à afficher dans la colonne <strong>Actions</strong> de la liste des documents. Les préférences sont sauvegardées automatiquement.
</p>

{/* Liste des boutons configurables */}
<div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
{[
{ key: 'visualiser', icon: '👁️', label: 'Visualiser', desc: 'Ouvrir l\'aperçu du document', color: '#3B82F6', bg: '#EFF6FF' },
{ key: 'editer',     icon: '✏️', label: 'Éditer',     desc: 'Modifier le document',      color: '#D97706', bg: '#FFFBEB' },
{ key: 'avoir',      icon: '🔵', label: 'Avoir',      desc: 'Émettre une note de crédit (factures uniquement)', color: '#7C3AED', bg: '#F5F3FF' },
{ key: 'dupliquer',  icon: '📄', label: 'Dupliquer',  desc: 'Créer une copie du document', color: '#2563EB', bg: '#EFF6FF' },
{ key: 'imprimer',   icon: '🖨️', label: 'Imprimer / PDF', desc: 'Imprimer ou exporter en PDF', color: '#059669', bg: '#ECFDF5' },
{ key: 'supprimer',  icon: '🗑️', label: 'Supprimer',  desc: 'Supprimer définitivement le document', color: '#DC2626', bg: '#FEF2F2' },
].map(btn => (
<div key={btn.key}
onClick={() => setActionsVisibles(prev => ({ ...prev, [btn.key]: !prev[btn.key] }))}
style={{
display: 'flex', alignItems: 'center', gap: '1rem',
padding: '0.85rem 1rem',
background: actionsVisibles[btn.key] ? btn.bg : '#F8FAFC',
border: `2px solid ${actionsVisibles[btn.key] ? btn.color + '55' : '#E2E8F0'}`,
borderRadius: '12px',
cursor: 'pointer',
transition: 'all 0.2s',
userSelect: 'none'
}}
>
{/* Toggle switch */}
<div style={{
width: '44px', height: '24px', flexShrink: 0,
background: actionsVisibles[btn.key] ? btn.color : '#CBD5E1',
borderRadius: '12px',
position: 'relative',
transition: 'background 0.25s',
boxShadow: actionsVisibles[btn.key] ? `0 0 0 3px ${btn.color}22` : 'none'
}}>
<div style={{
position: 'absolute',
top: '3px',
left: actionsVisibles[btn.key] ? '23px' : '3px',
width: '18px', height: '18px',
background: 'white',
borderRadius: '50%',
transition: 'left 0.25s',
boxShadow: '0 1px 4px rgba(0,0,0,0.25)'
}} />
</div>

{/* Icône bouton */}
<div style={{
width: '36px', height: '36px', flexShrink: 0,
background: actionsVisibles[btn.key] ? btn.color : '#E2E8F0',
borderRadius: '8px',
display: 'flex', alignItems: 'center', justifyContent: 'center',
fontSize: '1.1rem',
transition: 'background 0.2s'
}}>
{btn.icon}
</div>

{/* Label + description */}
<div style={{ flex: 1, minWidth: 0 }}>
<div style={{
fontWeight: 700, fontSize: '0.9rem',
color: actionsVisibles[btn.key] ? '#1E293B' : '#94A3B8',
transition: 'color 0.2s'
}}>
{btn.label}
</div>
<div style={{
fontSize: '0.75rem',
color: actionsVisibles[btn.key] ? '#64748B' : '#CBD5E1',
marginTop: '0.1rem',
transition: 'color 0.2s'
}}>
{btn.desc}
</div>
</div>

{/* Badge actif/masqué */}
<div style={{
padding: '0.2rem 0.55rem',
borderRadius: '20px',
fontSize: '0.68rem', fontWeight: 700,
background: actionsVisibles[btn.key] ? btn.color + '18' : '#F1F5F9',
color: actionsVisibles[btn.key] ? btn.color : '#94A3B8',
border: `1px solid ${actionsVisibles[btn.key] ? btn.color + '40' : '#E2E8F0'}`,
whiteSpace: 'nowrap',
transition: 'all 0.2s'
}}>
{actionsVisibles[btn.key] ? '✓ Visible' : '✗ Masqué'}
</div>
</div>
))}
</div>

{/* Résumé */}
<div style={{
marginTop: '1.25rem',
padding: '0.75rem 1rem',
background: 'linear-gradient(135deg, rgba(0,212,170,0.08), rgba(10,37,64,0.08))',
borderRadius: '10px',
border: '1px solid rgba(0,212,170,0.2)',
fontSize: '0.8rem', color: '#475569',
display: 'flex', justifyContent: 'space-between', alignItems: 'center'
}}>
<span>
<strong style={{ color: '#0A2540' }}>
{Object.values(actionsVisibles).filter(Boolean).length}
</strong> bouton(s) affiché(s) sur 6
</span>
<button
onClick={() => setActionsVisibles({ visualiser: true, editer: true, avoir: true, dupliquer: true, imprimer: true, supprimer: true })}
style={{ background: 'none', border: 'none', color: '#00D4AA', fontWeight: 700, cursor: 'pointer', fontSize: '0.78rem', padding: '0.25rem 0.5rem' }}
>
↺ Tout réafficher
</button>
</div>
</div>

<div className="modal-footer">
<button className="btn btn-primary" onClick={() => setShowConfigActions(false)}>
✓ Appliquer
</button>
</div>
</div>
</div>
)}

{/* ===== MODAL CONFIG ACTIONS BONS DE COMMANDE ===== */}
{showConfigActionsBC && (
<div className="modal-overlay" style={{ zIndex: 10020 }} onClick={e => { if(e.target === e.currentTarget) setShowConfigActionsBC(false); }}>
<div className="modal-content" style={{ maxWidth: '480px', width: '95vw' }}>
<div className="modal-header" style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1E3A5F 100%)', color: 'white' }}>
<h2 className="modal-title" style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
⚙️ Personnaliser les boutons d'action — BC
</h2>
<button className="close-btn" style={{ color: 'white' }} onClick={() => setShowConfigActionsBC(false)}>✕</button>
</div>

<div className="modal-body">
<p style={{ fontSize: '0.875rem', color: '#64748B', marginBottom: '1.5rem', lineHeight: 1.6 }}>
Choisissez les boutons à afficher dans la colonne <strong>Actions</strong> des bons de commande. Les préférences sont sauvegardées automatiquement.
</p>

<div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
{[
{ key: 'apercu',    icon: '👁️', label: 'Aperçu / Imprimer', desc: 'Visualiser et imprimer le bon de commande', color: '#3B82F6', bg: '#EFF6FF' },
{ key: 'editer',    icon: '✏️', label: 'Éditer',             desc: 'Modifier le bon de commande',              color: '#D97706', bg: '#FFFBEB' },
{ key: 'statut',    icon: '🔄', label: 'Changer le statut',  desc: 'Faire avancer le statut du BC',            color: '#059669', bg: '#ECFDF5' },
{ key: 'dupliquer', icon: '📄', label: 'Dupliquer',          desc: 'Créer une copie du bon de commande',       color: '#2563EB', bg: '#EFF6FF' },
{ key: 'imprimer',  icon: '🖨️', label: 'Imprimer',          desc: 'Imprimer ou exporter en PDF',              color: '#059669', bg: '#ECFDF5' },
{ key: 'supprimer', icon: '🗑️', label: 'Supprimer',         desc: 'Supprimer définitivement le BC',           color: '#DC2626', bg: '#FEF2F2' },
].map(btn => (
<div key={btn.key}
onClick={() => setActionsVisiblesBC(prev => ({ ...prev, [btn.key]: !prev[btn.key] }))}
style={{
display: 'flex', alignItems: 'center', gap: '1rem',
padding: '0.85rem 1rem',
background: actionsVisiblesBC[btn.key] ? btn.bg : '#F8FAFC',
border: `2px solid ${actionsVisiblesBC[btn.key] ? btn.color + '55' : '#E2E8F0'}`,
borderRadius: '12px',
cursor: 'pointer',
transition: 'all 0.2s',
userSelect: 'none'
}}
>
{/* Toggle switch */}
<div style={{
width: '44px', height: '24px', flexShrink: 0,
background: actionsVisiblesBC[btn.key] ? btn.color : '#CBD5E1',
borderRadius: '12px',
position: 'relative',
transition: 'background 0.25s',
boxShadow: actionsVisiblesBC[btn.key] ? `0 0 0 3px ${btn.color}22` : 'none'
}}>
<div style={{
position: 'absolute',
top: '3px',
left: actionsVisiblesBC[btn.key] ? '23px' : '3px',
width: '18px', height: '18px',
background: 'white',
borderRadius: '50%',
transition: 'left 0.25s',
boxShadow: '0 1px 4px rgba(0,0,0,0.25)'
}} />
</div>

{/* Icône bouton */}
<div style={{
width: '36px', height: '36px', flexShrink: 0,
background: actionsVisiblesBC[btn.key] ? btn.color : '#E2E8F0',
borderRadius: '8px',
display: 'flex', alignItems: 'center', justifyContent: 'center',
fontSize: '1.1rem',
transition: 'background 0.2s'
}}>
{btn.icon}
</div>

{/* Label + description */}
<div style={{ flex: 1, minWidth: 0 }}>
<div style={{
fontWeight: 700, fontSize: '0.9rem',
color: actionsVisiblesBC[btn.key] ? '#1E293B' : '#94A3B8',
transition: 'color 0.2s'
}}>
{btn.label}
</div>
<div style={{
fontSize: '0.75rem',
color: actionsVisiblesBC[btn.key] ? '#64748B' : '#CBD5E1',
marginTop: '0.1rem',
transition: 'color 0.2s'
}}>
{btn.desc}
</div>
</div>

{/* Badge actif/masqué */}
<div style={{
padding: '0.2rem 0.55rem',
borderRadius: '20px',
fontSize: '0.68rem', fontWeight: 700,
background: actionsVisiblesBC[btn.key] ? btn.color + '18' : '#F1F5F9',
color: actionsVisiblesBC[btn.key] ? btn.color : '#94A3B8',
border: `1px solid ${actionsVisiblesBC[btn.key] ? btn.color + '40' : '#E2E8F0'}`,
whiteSpace: 'nowrap',
transition: 'all 0.2s'
}}>
{actionsVisiblesBC[btn.key] ? '✓ Visible' : '✗ Masqué'}
</div>
</div>
))}
</div>

{/* Résumé */}
<div style={{
marginTop: '1.25rem',
padding: '0.75rem 1rem',
background: 'linear-gradient(135deg, rgba(0,212,170,0.08), rgba(10,37,64,0.08))',
borderRadius: '10px',
border: '1px solid rgba(0,212,170,0.2)',
fontSize: '0.8rem', color: '#475569',
display: 'flex', justifyContent: 'space-between', alignItems: 'center'
}}>
<span>
<strong style={{ color: '#0A2540' }}>
{Object.values(actionsVisiblesBC).filter(Boolean).length}
</strong> bouton(s) affiché(s) sur 6
</span>
<button
onClick={() => setActionsVisiblesBC({ apercu: true, editer: true, statut: true, dupliquer: true, imprimer: true, supprimer: true })}
style={{ background: 'none', border: 'none', color: '#00D4AA', fontWeight: 700, cursor: 'pointer', fontSize: '0.78rem', padding: '0.25rem 0.5rem' }}
>
↺ Tout réafficher
</button>
</div>
</div>

<div className="modal-footer">
<button className="btn btn-primary" onClick={() => setShowConfigActionsBC(false)}>
✓ Appliquer
</button>
</div>
</div>
</div>
)}

{/* ── Modal verrou document ───────────────────────────────────────────── */}
{lockWarning && (
<div className="modal-overlay" style={{ zIndex: 9999 }}>
<div className="modal-content" style={{ maxWidth: '480px', textAlign: 'center' }}>
<div className="modal-header" style={{ justifyContent: 'center', borderBottom: '1px solid rgba(255,107,53,0.3)' }}>
<h2 className="modal-title" style={{ color: '#FF6B35' }}>🔒 Document verrouillé</h2>
</div>
<div className="modal-body" style={{ padding: '2rem 2rem 1.5rem' }}>
<div style={{
width: 64, height: 64, borderRadius: '50%',
background: 'rgba(255,107,53,0.1)',
display: 'flex', alignItems: 'center', justifyContent: 'center',
fontSize: '2rem', margin: '0 auto 1.25rem'
}}>🔒</div>
<p style={{ color: '#CBD5E1', fontSize: '1rem', lineHeight: 1.6, margin: 0 }}>
Ce document est en cours d'édition par <strong style={{ color: '#FF6B35' }}>{lockWarning.nom}</strong>.<br />
Nous vous prions de bien vouloir patienter quelques instants avant de tenter un nouvel accès.
</p>
</div>
<div className="modal-footer" style={{ justifyContent: 'center' }}>
<button
className="btn btn-primary"
onClick={() => setLockWarning(null)}
style={{ minWidth: 120 }}
>
Compris
</button>
</div>
</div>
</div>
)}

{showAnnulationModal && (
<div className="modal-overlay">
<div className="modal-content" style={{ maxWidth: '600px' }}>
<div className="modal-header">
<h2 className="modal-title">❌ Annulation de la facture</h2>
<button className="close-btn" onClick={annulerModalAnnulation}>✕</button>
</div>

<div className="modal-body">
<div style={{
padding: '1.5rem',
background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.05) 100%)',
borderRadius: '12px',
border: '2px solid rgba(239, 68, 68, 0.2)',
marginBottom: '1.5rem'
}}>
<div style={{ textAlign: 'center', marginBottom: '1rem' }}>
<div style={{
fontSize: '3rem',
marginBottom: '0.5rem'
}}>
⚠️
</div>
<h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#991B1B', marginBottom: '0.5rem' }}>
Attention : Annulation de facture
</h3>
<p style={{ color: '#64748B', fontSize: '0.875rem', lineHeight: '1.6' }}>
Vous êtes sur le point d'annuler cette facture. Veuillez indiquer la raison de cette annulation pour la traçabilité.
</p>
</div>
</div>

<div className="form-group">
<label className="form-label" style={{ marginBottom: '0.5rem', display: 'block' }}>
Raison de l'annulation <span style={{ color: '#EF4444' }}>*</span>
</label>
<textarea
id="raison-annulation"
className="form-input"
value={raisonAnnulation}
onChange={(e) => setRaisonAnnulation(e.target.value)}
placeholder="Exemple : Erreur de facturation, Demande du client, Doublon, etc."
rows="4"
style={{
width: '100%',
resize: 'vertical',
fontFamily: 'inherit',
position: 'relative'
}}
/>
<div style={{
fontSize: '0.75rem',
color: '#64748B',
marginTop: '0.5rem',
fontStyle: 'italic'
}}>
Cette raison sera enregistrée et visible dans les détails de la facture.
</div>
</div>
</div>

<div className="modal-footer">
<button
onClick={annulerModalAnnulation}
style={{
padding: '0.75rem 2rem',
background: 'white',
border: '2px solid #E5E7EB',
borderRadius: '8px',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.875rem',
color: '#64748B',
transition: 'all 0.2s'
}}
>
Annuler
</button>
<button
onClick={confirmerAnnulation}
style={{
padding: '0.75rem 2rem',
background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
fontWeight: 600,
fontSize: '0.875rem',
color: 'white',
boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
transition: 'all 0.2s',
display: 'flex',
alignItems: 'center',
gap: '0.5rem'
}}
>
❌ Confirmer l'annulation
</button>
</div>
</div>
</div>
)}

{/* ========== MODAL CRÉATION AVOIR ========== */}
{showAvoirModal && avoirFactureSource && (
<div className="modal-overlay" style={{ zIndex: 10010 }}>
<div className="modal-content" style={{ maxWidth: '780px', width: '96vw' }}>
<div className="modal-header" style={{ background: 'linear-gradient(135deg, #3B0764 0%, #7C3AED 60%, #5B21B6 100%)', color: 'white' }}>
<h2 className="modal-title" style={{ color: 'white' }}>
🔵 Émettre un avoir — Facture {avoirFactureSource.numero}
</h2>
<button className="close-btn" style={{ color: 'white' }} onClick={() => setShowAvoirModal(false)}>✕</button>
</div>
<div className="modal-body">

{/* Bandeau info facture */}
<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem', padding: '0.85rem 1rem', background: 'linear-gradient(135deg, rgba(139,92,246,0.07), rgba(91,33,182,0.10))', borderRadius: '10px', border: '1.5px solid rgba(139,92,246,0.25)' }}>
<div>
<div style={{ fontSize: '0.7rem', color: '#7C3AED', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>Facture d'origine</div>
<div style={{ fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', color: '#3B0764' }}>{avoirFactureSource.numero}</div>
</div>
<div>
<div style={{ fontSize: '0.7rem', color: '#7C3AED', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>Client</div>
<div style={{ fontWeight: 600, color: '#1E293B' }}>{avoirFactureSource.client?.nom}</div>
</div>
<div>
<div style={{ fontSize: '0.7rem', color: '#7C3AED', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>Montant facturé</div>
<div style={{ fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', color: '#1E293B' }}>{formatMontant(avoirFactureSource.total || 0)}</div>
</div>
{/* Avoirs déjà émis */}
{documents.filter(d => d.type === 'avoir' && d.factureOrigineId === avoirFactureSource.id).length > 0 && (
<div style={{ gridColumn: '1 / -1', borderTop: '1px solid rgba(139,92,246,0.2)', paddingTop: '0.6rem', marginTop: '0.1rem' }}>
<div style={{ fontSize: '0.7rem', color: '#7C3AED', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>Avoirs déjà émis</div>
<div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
{documents.filter(d => d.type === 'avoir' && d.factureOrigineId === avoirFactureSource.id).map(av => (
<span key={av.id} style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: '5px', padding: '0.2rem 0.5rem', fontSize: '0.75rem', fontFamily: 'JetBrains Mono, monospace', color: '#5B21B6', fontWeight: 700 }}>
{av.numero} : −{formatMontant(av.total)}
</span>
))}
<span style={{ fontSize: '0.75rem', color: '#DC2626', fontWeight: 700, alignSelf: 'center' }}>
Restant : {formatMontant(Math.max(0, (avoirFactureSource.total || 0) - documents.filter(d => d.type === 'avoir' && d.factureOrigineId === avoirFactureSource.id).reduce((s, a) => s + (a.total || 0), 0)))}
</span>
</div>
</div>
)}
</div>

{/* Motif */}
<div className="form-group" style={{ marginBottom: '1rem' }}>
<label className="form-label">Motif de l'avoir <span style={{ color: '#EF4444' }}>*</span></label>
<input className="form-input" type="text"
placeholder="Ex : Erreur de prix, retour marchandise, prestation non réalisée..."
value={avoirData.motif}
onChange={e => setAvoirData({ ...avoirData, motif: e.target.value })}
/>
</div>

{/* Type d'avoir */}
<div style={{ marginBottom: '1rem' }}>
<label className="form-label" style={{ display: 'block', marginBottom: '0.5rem' }}>Type d'avoir</label>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem' }}>
{[
{ val: 'total', label: '⬛ Total', desc: 'Annule entièrement la facture' },
{ val: 'partiel', label: '🔲 Partiel', desc: 'Sélectionnez les lignes à créditer' },
{ val: 'libre', label: '✏️ Montant libre', desc: 'Saisissez le montant manuellement' }
].map(opt => (
<button key={opt.val}
onClick={() => setAvoirData({ ...avoirData, type: opt.val })}
style={{
padding: '0.75rem', border: `2px solid ${avoirData.type === opt.val ? '#7C3AED' : '#E2E8F0'}`,
borderRadius: '10px', cursor: 'pointer', textAlign: 'left',
background: avoirData.type === opt.val ? 'rgba(139,92,246,0.08)' : 'white',
transition: 'all 0.2s'
}}>
<div style={{ fontWeight: 700, color: avoirData.type === opt.val ? '#7C3AED' : '#1E293B', fontSize: '0.88rem', marginBottom: '0.2rem' }}>{opt.label}</div>
<div style={{ fontSize: '0.72rem', color: '#64748B' }}>{opt.desc}</div>
</button>
))}
</div>
</div>

{/* === MODE PARTIEL : tableau des lignes === */}
{avoirData.type === 'partiel' && (
<div style={{ marginBottom: '1rem', border: '1.5px solid rgba(139,92,246,0.25)', borderRadius: '10px', overflow: 'hidden' }}>
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
<thead style={{ background: 'linear-gradient(135deg, #3B0764, #7C3AED)', color: 'white' }}>
<tr>
<th style={{ padding: '0.5rem 0.7rem', textAlign: 'center', width: '40px' }}>✓</th>
<th style={{ padding: '0.5rem 0.7rem', textAlign: 'left' }}>Désignation</th>
<th style={{ padding: '0.5rem 0.7rem', textAlign: 'center', width: '90px' }}>Qté orig.</th>
<th style={{ padding: '0.5rem 0.7rem', textAlign: 'center', width: '90px' }}>Qté avoir</th>
<th style={{ padding: '0.5rem 0.7rem', textAlign: 'right', width: '130px' }}>P.U.</th>
<th style={{ padding: '0.5rem 0.7rem', textAlign: 'right', width: '130px' }}>Montant avoir</th>
</tr>
</thead>
<tbody>
{avoirData.lignes.map((l, i) => (
<tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#FAF5FF', borderBottom: '1px solid #EDE9FE', opacity: l.inclure ? 1 : 0.4 }}>
<td style={{ textAlign: 'center', padding: '0.4rem' }}>
<input type="checkbox" checked={l.inclure}
onChange={e => {
const nl = [...avoirData.lignes];
nl[i] = { ...nl[i], inclure: e.target.checked };
setAvoirData({ ...avoirData, lignes: nl });
}}
style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#7C3AED' }}
/>
</td>
<td style={{ padding: '0.4rem 0.7rem', color: '#1E293B', fontWeight: 500 }}>{l.designation}</td>
<td style={{ textAlign: 'center', padding: '0.4rem', color: '#64748B' }}>{l.qteOriginale}</td>
<td style={{ textAlign: 'center', padding: '0.4rem' }}>
<input type="number" min="0" max={l.qteOriginale} step="0.01"
value={l.qteAvoir}
disabled={!l.inclure}
onChange={e => {
const nl = [...avoirData.lignes];
const qte = Math.min(parseFloat(e.target.value) || 0, l.qteOriginale);
nl[i] = { ...nl[i], qteAvoir: qte, montantAvoir: qte * l.puOriginal };
setAvoirData({ ...avoirData, lignes: nl });
}}
style={{ width: '70px', padding: '0.3rem', border: '1.5px solid #DDD6FE', borderRadius: '5px', textAlign: 'center', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.82rem' }}
/>
</td>
<td style={{ textAlign: 'right', padding: '0.4rem 0.7rem', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.78rem', color: '#475569' }}>
{(l.puOriginal).toLocaleString('fr-FR')}
</td>
<td style={{ textAlign: 'right', padding: '0.4rem 0.7rem', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#7C3AED' }}>
{(l.inclure ? l.montantAvoir : 0).toLocaleString('fr-FR')} FCFA
</td>
</tr>
))}
</tbody>
</table>
</div>
)}

{/* === MODE LIBRE === */}
{avoirData.type === 'libre' && (
<div className="form-group" style={{ marginBottom: '1rem' }}>
<label className="form-label">Montant de l'avoir (FCFA) <span style={{ color: '#EF4444' }}>*</span></label>
<input className="form-input" type="number" min="0" step="1"
value={avoirData.montantLibre}
onChange={e => setAvoirData({ ...avoirData, montantLibre: parseFloat(e.target.value) || 0 })}
style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: '1.1rem' }}
/>
</div>
)}

{/* Note interne */}
<div className="form-group" style={{ marginBottom: '0.5rem' }}>
<label className="form-label">Note interne (optionnelle)</label>
<textarea className="form-input" rows="2"
placeholder="Remarque interne non imprimée..."
value={avoirData.noteInterne}
onChange={e => setAvoirData({ ...avoirData, noteInterne: e.target.value })}
style={{ resize: 'vertical', fontFamily: 'inherit' }}
/>
</div>

{/* Récapitulatif montant */}
<div style={{ padding: '1rem 1.25rem', background: 'linear-gradient(135deg, rgba(139,92,246,0.1), rgba(91,33,182,0.15))', border: '2px solid rgba(139,92,246,0.35)', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem' }}>
<div>
<div style={{ fontSize: '0.72rem', color: '#7C3AED', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Montant de l'avoir</div>
<div style={{ fontSize: '1.6rem', fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', color: '#3B0764' }}>
−{formatMontant(calculerMontantAvoir())}
</div>
</div>
<div style={{ textAlign: 'right', fontSize: '0.78rem', color: '#64748B' }}>
<div>Ce montant sera imputé</div>
<div>sur le solde de la facture <strong style={{ color: '#7C3AED' }}>{avoirFactureSource.numero}</strong></div>
</div>
</div>
</div>
<div className="modal-footer">
<button className="btn btn-secondary" onClick={() => setShowAvoirModal(false)}>Annuler</button>
<button className="btn btn-primary"
onClick={confirmerCreationAvoir}
style={{ background: 'linear-gradient(135deg, #7C3AED, #5B21B6)', border: 'none' }}>
🔵 Émettre l'avoir
</button>
</div>
</div>
</div>
)}

{/* ========== MODAL APERÇU AVOIR ========== */}
{showAvoirPreview && currentAvoir && (
<div className="modal-overlay" style={{ zIndex: 10011 }}>
<div className="modal-content" style={{ maxWidth: '820px', width: '96vw' }}>
<div className="modal-header" style={{ background: 'linear-gradient(135deg, #3B0764 0%, #7C3AED 100%)', color: 'white' }}>
<h2 className="modal-title" style={{ color: 'white' }}>
🔵 Avoir — {currentAvoir.numero}
</h2>
<div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
<button onClick={() => {
const originalTitle = document.title;
document.title = `Avoir ${currentAvoir.numero}`;
window.print();
setTimeout(() => { document.title = originalTitle; }, 1000);
}}
style={{ background: 'rgba(255,255,255,0.18)', border: '1.5px solid rgba(255,255,255,0.4)', color: 'white', borderRadius: '8px', padding: '0.35rem 0.8rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
🖨️ Imprimer
</button>
<button className="close-btn" style={{ color: 'white' }} onClick={() => setShowAvoirPreview(false)}>✕</button>
</div>
</div>
<div className="modal-body">
{/* Document avoir imprimable */}
<div style={{ background: 'white', padding: '2rem', fontFamily: 'Arial, sans-serif', border: '1px solid #E2E8F0', borderRadius: '8px' }}>
{/* En-tête */}
<div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '3px solid #7C3AED' }}>
<div>
{settings.logo && <img src={settings.logo} alt="Logo" style={{ maxHeight: '60px', maxWidth: '160px', marginBottom: '0.5rem', display: 'block' }} />}
<div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#0A2540' }}>{settings.entreprise || 'Votre Entreprise'}</div>
<div style={{ fontSize: '0.82rem', color: '#64748B', lineHeight: 1.6 }}>{settings.adresse || ''}</div>
{settings.telephone && <div style={{ fontSize: '0.82rem', color: '#64748B' }}>📞 {settings.telephone}</div>}
{settings.email && <div style={{ fontSize: '0.82rem', color: '#64748B' }}>✉ {settings.email}</div>}
</div>
<div style={{ textAlign: 'right' }}>
<div style={{ background: 'linear-gradient(135deg, #7C3AED, #5B21B6)', color: 'white', padding: '0.6rem 1.2rem', borderRadius: '8px', marginBottom: '0.5rem' }}>
<div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', opacity: 0.85 }}>NOTE DE CRÉDIT / AVOIR</div>
<div style={{ fontSize: '1.2rem', fontWeight: 800, fontFamily: 'JetBrains Mono, monospace' }}>{currentAvoir.numero}</div>
</div>
<div style={{ fontSize: '0.82rem', color: '#64748B' }}>Date : <strong>{new Date(currentAvoir.date).toLocaleDateString('fr-FR')}</strong></div>
<div style={{ fontSize: '0.82rem', color: '#64748B', marginTop: '0.2rem' }}>
Réf. facture : <strong style={{ color: '#7C3AED', fontFamily: 'JetBrains Mono, monospace' }}>{currentAvoir.factureOrigineNumero}</strong>
</div>
</div>
</div>

{/* Client */}
<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
<div style={{ padding: '0.75rem 1rem', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
<div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>Avoir émis au profit de</div>
<div style={{ fontWeight: 700, color: '#0A2540', fontSize: '1rem' }}>{currentAvoir.client?.nom}</div>
{currentAvoir.client?.adresse && <div style={{ fontSize: '0.82rem', color: '#64748B', marginTop: '0.2rem' }}>{currentAvoir.client.adresse}</div>}
{currentAvoir.client?.nui && <div style={{ fontSize: '0.78rem', color: '#64748B', marginTop: '0.2rem' }}>NUI : {currentAvoir.client.nui}</div>}
</div>
<div style={{ padding: '0.75rem 1rem', background: 'rgba(139,92,246,0.05)', borderRadius: '8px', border: '1.5px solid rgba(139,92,246,0.25)' }}>
<div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>Motif</div>
<div style={{ color: '#1E293B', fontWeight: 600, fontSize: '0.9rem' }}>{currentAvoir.motif}</div>
<div style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '0.3rem' }}>
Type : {currentAvoir.typeAvoir === 'total' ? 'Avoir total' : currentAvoir.typeAvoir === 'partiel' ? 'Avoir partiel' : 'Montant libre'}
</div>
</div>
</div>

{/* Tableau des lignes */}
<table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.25rem', fontSize: '0.85rem' }}>
<thead>
<tr style={{ background: 'linear-gradient(135deg, #7C3AED, #5B21B6)', color: 'white' }}>
<th style={{ padding: '0.6rem 0.8rem', textAlign: 'left', fontWeight: 600 }}>Désignation</th>
<th style={{ padding: '0.6rem 0.8rem', textAlign: 'center', width: '70px', fontWeight: 600 }}>Qté</th>
<th style={{ padding: '0.6rem 0.8rem', textAlign: 'right', width: '130px', fontWeight: 600 }}>Prix unitaire</th>
<th style={{ padding: '0.6rem 0.8rem', textAlign: 'right', width: '130px', fontWeight: 600 }}>Montant</th>
</tr>
</thead>
<tbody>
{(currentAvoir.items || []).map((item, i) => (
<tr key={i} style={{ borderBottom: '1px solid #EDE9FE', background: i % 2 === 0 ? 'white' : '#FAF5FF' }}>
<td style={{ padding: '0.55rem 0.8rem', color: '#1E293B' }}>{item.description}</td>
<td style={{ padding: '0.55rem 0.8rem', textAlign: 'center', color: '#475569' }}>{item.quantite}</td>
<td style={{ padding: '0.55rem 0.8rem', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: '#475569' }}>{(item.prixUnitaire || 0).toLocaleString('fr-FR')}</td>
<td style={{ padding: '0.55rem 0.8rem', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, color: '#7C3AED' }}>{((item.quantite || 1) * (item.prixUnitaire || 0)).toLocaleString('fr-FR')}</td>
</tr>
))}
</tbody>
</table>

{/* Total */}
<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
<div style={{ minWidth: '280px', background: 'linear-gradient(135deg, rgba(139,92,246,0.1), rgba(91,33,182,0.15))', border: '2px solid rgba(139,92,246,0.35)', borderRadius: '10px', padding: '1rem 1.25rem' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
<span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#5B21B6', textTransform: 'uppercase', letterSpacing: '0.05em' }}>TOTAL AVOIR</span>
<span style={{ fontSize: '1.5rem', fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', color: '#3B0764' }}>
−{formatMontant(currentAvoir.total || 0)}
</span>
</div>
<div style={{ fontSize: '0.72rem', color: '#7C3AED', marginTop: '0.3rem', textAlign: 'right' }}>
Imputable sur la facture {currentAvoir.factureOrigineNumero}
</div>
</div>
</div>

{/* Pied */}
{settings.mentionsLegales && (
<div style={{ borderTop: '1px solid #E2E8F0', paddingTop: '0.75rem', fontSize: '0.72rem', color: '#94A3B8', textAlign: 'center' }}>
{settings.mentionsLegales}
</div>
)}
</div>
</div>
<div className="modal-footer">
<button className="btn btn-secondary" onClick={() => setShowAvoirPreview(false)}>Fermer</button>
</div>
</div>
</div>
)}

{/* ========== MODAL PARCOURIR CATALOGUE ========== */}
{showBrowseCatalogue && (
<div className="modal-overlay" style={{ zIndex: 10005 }} onClick={(e) => { if(e.target === e.currentTarget) setShowBrowseCatalogue(false); }}>
<div className="modal-content" style={{ maxWidth: '1700px', width: '98vw', maxHeight: '96vh', display: 'flex', flexDirection: 'column' }}>

{/* Header */}
<div className="modal-header" style={{ background: 'linear-gradient(135deg, #0A2540 0%, #00D4AA 100%)', color: 'white', flexShrink: 0 }}>
<h2 className="modal-title" style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
📚 Parcourir le catalogue
<span style={{ fontSize: '0.8rem', fontWeight: 400, opacity: 0.85 }}>— sélectionnez un article à insérer</span>
</h2>
<button className="close-btn" style={{ color: 'white' }} onClick={() => setShowBrowseCatalogue(false)}>✕</button>
</div>

{/* Filtres en cascade */}
<div style={{ padding: '1rem 1.25rem', background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', flexShrink: 0 }}>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.6rem' }}>

{/* Domaine */}
<div>
<label style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748B', display: 'block', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>🏭 Domaine</label>
<select value={browseDomaine} onChange={e => changerBrowseDomaine(e.target.value)}
style={{ width: '100%', padding: '0.5rem 0.6rem', border: '2px solid ' + (browseDomaine !== 'tous' ? '#00D4AA' : '#E2E8F0'), borderRadius: '8px', fontSize: '0.82rem', background: browseDomaine !== 'tous' ? '#F0FDF9' : 'white', fontWeight: browseDomaine !== 'tous' ? 600 : 400 }}>
<option value="tous">Tous les domaines</option>
{getBrowseDomainesList().map(d => <option key={d} value={d}>{d}</option>)}
</select>
</div>

{/* Catégorie mère */}
<div>
<label style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748B', display: 'block', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>📂 Catégorie mère</label>
<select value={browseCatMere} onChange={e => changerBrowseCatMere(e.target.value)}
style={{ width: '100%', padding: '0.5rem 0.6rem', border: '2px solid ' + (browseCatMere !== 'toutes' ? '#00D4AA' : '#E2E8F0'), borderRadius: '8px', fontSize: '0.82rem', background: browseCatMere !== 'toutes' ? '#F0FDF9' : 'white', fontWeight: browseCatMere !== 'toutes' ? 600 : 400 }}>
<option value="toutes">Toutes</option>
{getBrowseCatsMeresList().map(c => <option key={c} value={c}>{c}</option>)}
</select>
</div>

{/* Catégorie fille */}
<div>
<label style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748B', display: 'block', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>📁 Catégorie fille</label>
<select value={browseCatFille} onChange={e => setBrowseCatFille(e.target.value)}
style={{ width: '100%', padding: '0.5rem 0.6rem', border: '2px solid ' + (browseCatFille !== 'toutes' ? '#00D4AA' : '#E2E8F0'), borderRadius: '8px', fontSize: '0.82rem', background: browseCatFille !== 'toutes' ? '#F0FDF9' : 'white', fontWeight: browseCatFille !== 'toutes' ? 600 : 400 }}>
<option value="toutes">Toutes</option>
{getBrowseCatsFillesList().map(c => <option key={c} value={c}>{c}</option>)}
</select>
</div>

{/* Recherche texte */}
<div style={{ gridColumn: 'span 1' }}>
<label style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748B', display: 'block', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>🔍 Recherche</label>
<input type="text" value={browseSearch} onChange={e => setBrowseSearch(e.target.value)}
placeholder="Nom, référence..."
style={{ width: '100%', padding: '0.5rem 0.6rem', border: '2px solid ' + (browseSearch ? '#00D4AA' : '#E2E8F0'), borderRadius: '8px', fontSize: '0.82rem' }}
/>
</div>

</div>

{/* Compteur + reset */}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.6rem' }}>
<span style={{ fontSize: '0.78rem', color: '#64748B' }}>
<strong style={{ color: '#0A2540' }}>{browseArticlesFiltres.length}</strong> article(s){browseArticlesFiltres.length !== catalogueIndex.actifs.length && ` sur ${catalogueIndex.actifs.length.toLocaleString()}`} — cliquez sur une carte pour l'insérer dans la ligne
</span>
{(browseDomaine !== 'tous' || browseCatMere !== 'toutes' || browseCatFille !== 'toutes' || browseSearch) && (
<button onClick={() => { changerBrowseDomaine('tous'); setBrowseSearch(''); }}
style={{ padding: '0.3rem 0.7rem', background: '#FEE2E2', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', color: '#EF4444', fontWeight: 600 }}>
✕ Effacer les filtres
</button>
)}
</div>
</div>

{/* Grille d'articles */}
<div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
{browseArticlesFiltres.length === 0 ? (
<div style={{ textAlign: 'center', padding: '3rem', color: '#94A3B8' }}>
<div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔍</div>
<div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: '0.3rem' }}>Aucun article trouvé</div>
<div style={{ fontSize: '0.85rem' }}>Modifiez ou effacez les filtres pour élargir la recherche.</div>
</div>
) : (
<div>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.75rem' }}>
{browseArticlesPaginés.map((art, i) => (
<div key={i}
onClick={() => selectionnerArticleBrowse(art)}
style={{
background: 'white',
border: '2px solid #E2E8F0',
borderRadius: '12px',
padding: '0.85rem 1rem',
cursor: 'pointer',
transition: 'all 0.15s ease',
display: 'flex',
flexDirection: 'column',
gap: '0.4rem',
boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
}}
onMouseEnter={e => {
e.currentTarget.style.borderColor = '#00D4AA';
e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,212,170,0.2)';
e.currentTarget.style.transform = 'translateY(-2px)';
}}
onMouseLeave={e => {
e.currentTarget.style.borderColor = '#E2E8F0';
e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)';
e.currentTarget.style.transform = 'translateY(0)';
}}
>
{/* Codes */}
<div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
<span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', background: '#0A2540', color: 'white', padding: '0.15rem 0.45rem', borderRadius: '4px', fontWeight: 700 }}>
{art.codeArticle}
</span>
{art.ref && (
<span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', background: '#F0FDF9', color: '#00D4AA', padding: '0.15rem 0.45rem', borderRadius: '4px', fontWeight: 700, border: '1px solid #CCFBF1' }}>
{art.ref}
</span>
)}
</div>

{/* Désignation */}
<div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#1E293B', lineHeight: 1.3 }}>
{art.designation}
</div>

{/* Catégorie fille */}
<div style={{ fontSize: '0.72rem', color: '#64748B', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
<span style={{ background: '#F0FDF9', color: '#0F766E', padding: '0.1rem 0.4rem', borderRadius: '3px', fontWeight: 500 }}>
{art.categorieFille}
</span>
</div>

{/* Footer: unité + prix */}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.15rem', paddingTop: '0.5rem', borderTop: '1px solid #F1F5F9' }}>
<span style={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace', background: '#F8FAFC', padding: '0.15rem 0.4rem', borderRadius: '3px' }}>
{art.unite} · {art.uniteLibelle}
</span>
<span style={{ fontSize: '0.82rem', fontWeight: 700, color: art.prixUnitaire > 0 ? '#0A2540' : '#CBD5E1' }}>
{art.prixUnitaire > 0 ? art.prixUnitaire.toLocaleString('fr-FR') + ' XAF' : '— XAF'}
</span>
</div>
</div>
))}
</div>
{/* Pagination browse */}
{browseTotalPages > 1 && (
<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', padding: '12px 0', borderTop: '1px solid #E2E8F0', marginTop: '12px' }}>
<button onClick={() => setBrowsePage(1)} disabled={browsePage === 1} style={{ padding: '6px 10px', border: '1px solid #E2E8F0', borderRadius: '6px', background: 'white', cursor: 'pointer', fontSize: '0.8rem' }}>⟪</button>
<button onClick={() => setBrowsePage(p => Math.max(1, p-1))} disabled={browsePage === 1} style={{ padding: '6px 10px', border: '1px solid #E2E8F0', borderRadius: '6px', background: 'white', cursor: 'pointer', fontSize: '0.8rem' }}>←</button>
<span style={{ fontSize: '0.82rem', color: '#64748B', padding: '0 8px' }}>
<strong>{browsePage}</strong> / {browseTotalPages} &nbsp;·&nbsp; {browseArticlesFiltres.length} articles
</span>
<button onClick={() => setBrowsePage(p => Math.min(browseTotalPages, p+1))} disabled={browsePage === browseTotalPages} style={{ padding: '6px 10px', border: '1px solid #E2E8F0', borderRadius: '6px', background: 'white', cursor: 'pointer', fontSize: '0.8rem' }}>→</button>
<button onClick={() => setBrowsePage(browseTotalPages)} disabled={browsePage === browseTotalPages} style={{ padding: '6px 10px', border: '1px solid #E2E8F0', borderRadius: '6px', background: 'white', cursor: 'pointer', fontSize: '0.8rem' }}>⟫</button>
</div>
)}
</div>
)}
</div>

<div className="modal-footer" style={{ flexShrink: 0 }}>
<button className="btn btn-secondary" onClick={() => setShowBrowseCatalogue(false)}>Annuler</button>
</div>
</div>
</div>
)}

{/* ========== MODAL CATALOGUE ========== */}
{showCatalogue && (
<div
className="modal-overlay"
onClick={(e) => { if(!cataloguePleinEcran && e.target === e.currentTarget) setShowCatalogue(false); }}
style={cataloguePleinEcran ? {
position: 'fixed', inset: 0, zIndex: 9999,
background: 'rgba(0,0,0,0.0)', display: 'flex',
alignItems: 'stretch', justifyContent: 'stretch', padding: 0
} : {}}
>
<div className="modal-content" style={cataloguePleinEcran ? {
maxWidth: '100%', width: '100vw', height: '100vh', margin: 0,
borderRadius: 0, display: 'flex', flexDirection: 'column'
} : { maxWidth: '1100px', width: '96vw' }}>
<div className="modal-header" style={{ background: 'linear-gradient(135deg, #0D1B35 0%, #1B5E20 40%, #E65100 100%)', color: 'white', flexShrink: 0 }}>
<h2 className="modal-title" style={{ color: 'white' }}>📦 Catalogue Articles — Base Complète Cameroun</h2>
<div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
{/* Bouton plein écran / sortie plein écran */}
<button
onClick={() => setCataloguePleinEcran(v => !v)}
title={cataloguePleinEcran ? 'Quitter le plein écran' : 'Plein écran'}
style={{
background: 'rgba(255,255,255,0.15)',
border: '1.5px solid rgba(255,255,255,0.4)',
borderRadius: '8px',
color: 'white',
cursor: 'pointer',
padding: '0.35rem 0.65rem',
fontSize: '1.05rem',
display: 'flex', alignItems: 'center', gap: '0.4rem',
fontWeight: 600,
transition: 'background 0.2s',
}}
onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.28)'}
onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.15)'}
>
{cataloguePleinEcran ? (
<><span style={{fontSize:'1rem'}}>⊡</span><span style={{fontSize:'0.78rem'}}>Quitter</span></>
) : (
<><span style={{fontSize:'1rem'}}>⛶</span><span style={{fontSize:'0.78rem'}}>Plein écran</span></>
)}
</button>
<button className="close-btn" style={{ color: 'white' }} onClick={() => { setShowCatalogue(false); setCataloguePleinEcran(false); }}>✕</button>
</div>
</div>
<div className="modal-body" style={cataloguePleinEcran ? { flex: 1, overflowY: 'auto' } : {}}>
{/* Barre d'outils */}
<div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
<button className="btn btn-primary" onClick={ouvrirNouvelArticleCatalogue}
style={{ background: 'linear-gradient(135deg, #0A2540, #00D4AA)', whiteSpace: 'nowrap' }}>
➕ Nouvel article
</button>
<label className="btn btn-secondary" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
📥 Import CSV
<input type="file" accept=".csv,.txt" onChange={importerCatalogueCSV} style={{ display: 'none' }} />
</label>
<button className="btn btn-secondary" onClick={exporterCatalogueCSV}>
📤 Export CSV
</button>
<button className="btn btn-secondary" onClick={reinitialiserCatalogue}
style={{ color: '#EF4444', borderColor: '#EF4444' }}>
🔄 Réinitialiser
</button>
{can('catalogue_ia', 'write') && (
<button className="btn btn-secondary" onClick={() => { setEnrichEtape('form'); setEnrichResultats([]); setEnrichErreur(null); setShowEnrichissementIA(true); }}
style={{ background: 'linear-gradient(135deg, #6B21A8, #A855F7)', color: 'white', whiteSpace: 'nowrap', border: 'none' }}>
🤖 Enrichir avec IA
</button>
)}
</div>

{/* Filtres en cascade */}
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem', marginBottom: '0.75rem', padding: '0.75rem', background: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
{/* Recherche */}
<div style={{ gridColumn: '1 / -1' }}>
<label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748B', display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>🔍 Recherche</label>
<input
type="text"
value={catalogueSearchInput}
onChange={e => setCatalogueSearchInput(e.target.value)}
placeholder="Référence, code article ou désignation..."
style={{ width: '100%', padding: '0.55rem 0.75rem', border: '2px solid #E2E8F0', borderRadius: '8px', fontSize: '0.875rem' }}
/>
</div>
{/* Filtre Domaine */}
<div>
<label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748B', display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>🏭 Domaine</label>
<select value={filtresDomaine} onChange={e => changerFiltresDomaine(e.target.value)}
style={{ width: '100%', padding: '0.55rem', border: '2px solid #E2E8F0', borderRadius: '8px', fontSize: '0.85rem', background: filtresDomaine !== 'tous' ? '#EFF6FF' : 'white' }}>
<option value="tous">Tous les domaines</option>
{domaines.map(d => <option key={d} value={d}>{d}</option>)}
</select>
</div>
{/* Filtre Catégorie Mère */}
<div>
<label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748B', display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>📂 Catégorie mère</label>
<select value={filtresCatMere} onChange={e => changerFiltresCatMere(e.target.value)}
style={{ width: '100%', padding: '0.55rem', border: '2px solid #E2E8F0', borderRadius: '8px', fontSize: '0.85rem', background: filtresCatMere !== 'toutes' ? '#EFF6FF' : 'white' }}>
<option value="toutes">Toutes</option>
{catsM.map(c => <option key={c} value={c}>{c}</option>)}
</select>
</div>
{/* Filtre Catégorie Fille */}
<div>
<label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748B', display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>📁 Catégorie fille</label>
<select value={filtresCatFille} onChange={e => setFiltresCatFille(e.target.value)}
style={{ width: '100%', padding: '0.55rem', border: '2px solid #E2E8F0', borderRadius: '8px', fontSize: '0.85rem', background: filtresCatFille !== 'toutes' ? '#EFF6FF' : 'white' }}>
<option value="toutes">Toutes</option>
{catsF.map(c => <option key={c} value={c}>{c}</option>)}
</select>
</div>
{/* Bouton reset filtres */}
<div style={{ display: 'flex', alignItems: 'flex-end' }}>
{(filtresDomaine !== 'tous' || filtresCatMere !== 'toutes' || filtresCatFille !== 'toutes' || catalogueSearchInput) && (
<button onClick={() => { changerFiltresDomaine('tous'); setCatalogueSearchInput(''); }}
style={{ width: '100%', padding: '0.55rem', background: '#FEE2E2', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.82rem', color: '#EF4444', fontWeight: 600 }}>
✕ Réinitialiser les filtres
</button>
)}
</div>
</div>

{/* Compteur */}
<div style={{ marginBottom: '0.75rem', fontSize: '0.82rem', color: '#64748B', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
<span>📊 <strong style={{ color: '#0A2540' }}>{catalogueFiltré.length}</strong> article(s) affiché(s) sur <strong>{catalogue.length}</strong></span>
<span>✅ {catalogue.filter(a=>a.actif).length} actifs</span>
<span>⏸ {catalogue.filter(a=>!a.actif).length} désactivés</span>
</div>

{/* Tableau */}
<div style={{ overflowX: 'auto', maxHeight: cataloguePleinEcran ? 'calc(100vh - 340px)' : '60vh', overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: '10px' }}>
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
<thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
<tr style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1B5E20 40%, #E65100 100%)', color: 'white', fontSize: '0.78rem' }}>
<th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>Code article</th>
<th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, minWidth: '240px' }}>Désignation / Description</th>
<th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, minWidth: '140px' }}>Catégorie fille</th>
<th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, minWidth: '160px' }}>Catégorie mère</th>
<th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>Marque</th>
<th style={{ padding: '0.6rem 0.75rem', textAlign: 'center', fontWeight: 600 }}>Unité</th>
<th style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>Prix unitaire</th>
<th style={{ padding: '0.6rem 0.75rem', textAlign: 'center', fontWeight: 600 }}>TVA</th>
<th style={{ padding: '0.6rem 0.75rem', textAlign: 'center', fontWeight: 600 }}>Statut</th>
<th style={{ padding: '0.6rem 0.75rem', textAlign: 'center', fontWeight: 600 }}>Actions</th>
</tr>
</thead>
<tbody>
{catalogueFiltré.length === 0 ? (
<tr><td colSpan="10" style={{ textAlign: 'center', padding: '2rem', color: '#94A3B8' }}>
Aucun article trouvé. Modifiez votre recherche ou ajoutez un article.
</td></tr>
) : cataloguePaginé.map((art, i) => {
const realIdx = catalogue.indexOf(art);
return (
<tr key={i} style={{
borderBottom: '1px solid #F1F5F9',
opacity: art.actif ? 1 : 0.45,
background: i % 2 === 0 ? 'white' : '#F0F9FF',
fontSize: '0.82rem'
}}>
<td style={{ padding: '0.5rem 0.75rem', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem', color: '#0077CC', fontWeight: 700, whiteSpace: 'nowrap' }}>
{art.codeArticle || art.ref}
</td>
<td style={{ padding: '0.5rem 0.75rem', maxWidth: '280px' }}>
<div style={{ fontWeight: 600, color: '#1E293B', lineHeight: 1.3 }}>{art.designation}</div>
{art.description && <div style={{ fontSize: '0.7rem', color: '#64748B', marginTop: '2px', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{art.description}</div>}
</td>
<td style={{ padding: '0.5rem 0.75rem' }}>
<span style={{ background: '#ECFDF5', color: '#065F46', padding: '0.15rem 0.45rem', borderRadius: '4px', fontSize: '0.71rem', fontWeight: 500, display: 'inline-block', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
title={art.categorieFille}>
{art.codeCategorieFille && <span style={{ fontFamily: 'monospace', marginRight: '0.3rem', opacity: 0.6, fontSize: '0.67rem' }}>{art.codeCategorieFille}</span>}
{art.categorieFille}
</span>
</td>
<td style={{ padding: '0.5rem 0.75rem' }}>
<span style={{ background: '#EFF6FF', color: '#1E40AF', padding: '0.15rem 0.45rem', borderRadius: '4px', fontSize: '0.71rem', fontWeight: 500, display: 'inline-block', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
title={art.categoreMere}>
{art.codeCategorieMere && <span style={{ fontFamily: 'monospace', marginRight: '0.3rem', opacity: 0.6, fontSize: '0.67rem' }}>{art.codeCategorieMere}</span>}
{art.categoreMere}
</span>
</td>
<td style={{ padding: '0.5rem 0.75rem', fontSize: '0.78rem', color: '#374151' }}>
{art.marque ? <span style={{ background: '#FFF7ED', color: '#C2410C', padding: '0.1rem 0.4rem', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 600 }}>{art.marque}</span> : <span style={{ color: '#CBD5E1', fontSize: '0.72rem' }}>—</span>}
</td>
<td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem', color: '#475569' }}>
<span style={{ background: '#F1F5F9', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>{art.unite}</span>
</td>
<td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, color: '#1E293B' }}>
{art.prixUnitaire > 0 ? art.prixUnitaire.toLocaleString('fr-FR') + ' XAF' :
<span style={{ color: '#94A3B8', fontWeight: 400 }}>À définir</span>}
</td>
<td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
<span style={{
background: art.tva === 'TTC' ? '#DCFCE7' : '#FEF3C7',
color: art.tva === 'TTC' ? '#16A34A' : '#D97706',
padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600
}}>{art.tva}</span>
</td>
<td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
<span style={{
background: art.actif ? '#DCFCE7' : '#F1F5F9',
color: art.actif ? '#16A34A' : '#94A3B8',
padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600
}}>{art.actif ? '● Actif' : '○ Inactif'}</span>
</td>
<td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
<div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'center' }}>
<button
onClick={() => ouvrirEditionArticleCatalogue(realIdx)}
style={{ padding: '0.3rem 0.5rem', background: '#EFF6FF', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
title="Modifier"
>✏️</button>
<button
onClick={() => toggleActifArticle(realIdx)}
style={{ padding: '0.3rem 0.5rem', background: art.actif ? '#FEF3C7' : '#DCFCE7', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
title={art.actif ? 'Désactiver' : 'Activer'}
>{art.actif ? '⏸' : '▶'}</button>
<button
onClick={() => supprimerArticleCatalogue(realIdx)}
style={{ padding: '0.3rem 0.5rem', background: '#FEE2E2', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
title="Supprimer"
>🗑️</button>
</div>
</td>
</tr>
);
})}
</tbody>
</table>
</div>

{/* Pagination */}
{cataloguePageCount > 1 && (
<div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
{[...Array(cataloguePageCount)].map((_, i) => (
<button
key={i + 1}
onClick={() => { setCataloguePage(i + 1); setCatalogueSearchInput(''); }}
style={{
padding: '0.4rem 0.75rem',
background: cataloguePage === i + 1 ? 'linear-gradient(135deg, #FF6B35, #00D4AA)' : '#E2E8F0',
color: cataloguePage === i + 1 ? 'white' : '#0A2540',
border: 'none',
borderRadius: '6px',
cursor: 'pointer',
fontSize: '0.85rem',
fontWeight: cataloguePage === i + 1 ? 600 : 400
}}
>{i + 1}</button>
))}
<span style={{ fontSize: '0.8rem', color: '#64748B', marginLeft: '0.5rem' }}>
({cataloguePage}/{cataloguePageCount})
</span>
</div>
)}

{/* Note import CSV */}
<div style={{ marginTop: '1rem', padding: '0.75rem', background: '#F0FDF9', border: '1px solid #00D4AA', borderRadius: '8px', fontSize: '0.8rem', color: '#0A2540' }}>
💡 <strong>Format CSV pour l'import</strong> (séparateur point-virgule) :<br/>
<code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem' }}>
codeArticle;ref;designation;description;categoreMere;codeCategorieMere;categorieFille;codeCategorieFille;marque;unite;prixUnitaire;tva
</code>
<br/>Exemple : <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem' }}>101A001001;101A001001;Panneau solaire 450W;Description...;Systèmes photovoltaïques;101A;Panneaux monocristallins;101A001;;pièce;0;TTC</code>
</div>
</div>
<div className="modal-footer">
{cataloguePleinEcran && (
<button className="btn btn-secondary"
onClick={() => setCataloguePleinEcran(false)}
style={{ marginRight: 'auto', background: 'linear-gradient(135deg,#0A2540,#1B5E20)', color: 'white', border: 'none' }}>
⊡ Quitter le plein écran
</button>
)}
<button className="btn btn-secondary" onClick={() => { setShowCatalogue(false); setCataloguePleinEcran(false); }}>Fermer</button>
</div>
</div>
</div>
)}

{/* ═══════════════════════════════════════════════════════════════════
    MODAL — ENRICHISSEMENT IA CATALOGUE
    Accès : super_admin + gestionnaire (can('catalogue_ia','write'))
    Stack : Jina AI Reader (gratuit) + Gemini API (free tier)
    ═══════════════════════════════════════════════════════════════════ */}
{showEnrichissementIA && (
<div className="modal-overlay" style={{ zIndex: 10002 }} onClick={e => { if (e.target === e.currentTarget) setShowEnrichissementIA(false); }}>
<div className="modal-content" style={{ maxWidth: '780px', width: '96vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

  {/* En-tête */}
  <div className="modal-header" style={{ background: 'linear-gradient(135deg, #4C1D95, #7C3AED, #A855F7)', color: 'white', flexShrink: 0 }}>
    <h2 className="modal-title" style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      🤖 Enrichissement IA du Catalogue
      {enrichEtape === 'resultats' && enrichResultats.length > 0 && (
        <span style={{ fontSize: '0.8rem', fontWeight: 400, opacity: 0.85 }}>— {enrichResultats.length} article(s) trouvé(s)</span>
      )}
    </h2>
    <button className="close-btn" style={{ color: 'white' }} onClick={() => setShowEnrichissementIA(false)}>✕</button>
  </div>

  <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>

    {/* ── ÉTAPE 1 : Formulaire ─────────────────────────────────────── */}
    {enrichEtape === 'form' && (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* Section 1 — Cibler la catégorie */}
      <div>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
          1. Cibler la catégorie
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6rem' }}>

          {/* Domaine */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', color: '#475569', marginBottom: '0.3rem', fontWeight: 600 }}>Domaine</label>
            <select value={enrichDomaine} onChange={e => {
              const val = e.target.value;
              const found = [...new Set(catalogue.map(a => a.domaine).filter(Boolean))];
              setEnrichDomaine(val);
              // Retrouver codeDomaine depuis le catalogue
              const art = catalogue.find(a => a.domaine === val);
              setEnrichCodeDomaine(art?.codeDomaine || '');
              setEnrichCatMere(''); setEnrichCodeCatMere('');
              setEnrichCatFille(''); setEnrichCodeCatFille('');
            }}
            style={{ width: '100%', padding: '0.5rem 0.6rem', border: '2px solid ' + (enrichDomaine ? '#7C3AED' : '#E2E8F0'), borderRadius: '8px', fontSize: '0.82rem', background: enrichDomaine ? '#FAF5FF' : 'white' }}>
              <option value="">— Choisir —</option>
              {[...new Set(catalogue.map(a => a.domaine).filter(Boolean))].sort().map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          {/* Catégorie mère */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', color: '#475569', marginBottom: '0.3rem', fontWeight: 600 }}>Catégorie mère</label>
            <select value={enrichCatMere} onChange={e => {
              const val = e.target.value;
              setEnrichCatMere(val);
              const art = catalogue.find(a => a.domaine === enrichDomaine && a.categoreMere === val);
              setEnrichCodeCatMere(art?.codeCategorieMere || '');
              setEnrichCatFille(''); setEnrichCodeCatFille('');
            }}
            disabled={!enrichDomaine}
            style={{ width: '100%', padding: '0.5rem 0.6rem', border: '2px solid ' + (enrichCatMere ? '#7C3AED' : '#E2E8F0'), borderRadius: '8px', fontSize: '0.82rem', background: enrichCatMere ? '#FAF5FF' : 'white', opacity: !enrichDomaine ? 0.5 : 1 }}>
              <option value="">— Choisir —</option>
              {[...new Set(catalogue.filter(a => a.domaine === enrichDomaine).map(a => a.categoreMere).filter(Boolean))].sort().map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Catégorie fille */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', color: '#475569', marginBottom: '0.3rem', fontWeight: 600 }}>Catégorie fille</label>
            <select value={enrichCatFille} onChange={e => {
              const val = e.target.value;
              setEnrichCatFille(val);
              const art = catalogue.find(a => a.categoreMere === enrichCatMere && a.categorieFille === val);
              setEnrichCodeCatFille(art?.codeCategorieFille || '');
            }}
            disabled={!enrichCatMere}
            style={{ width: '100%', padding: '0.5rem 0.6rem', border: '2px solid ' + (enrichCatFille ? '#7C3AED' : '#E2E8F0'), borderRadius: '8px', fontSize: '0.82rem', background: enrichCatFille ? '#FAF5FF' : 'white', opacity: !enrichCatMere ? 0.5 : 1 }}>
              <option value="">— Choisir —</option>
              {[...new Set(catalogue.filter(a => a.categoreMere === enrichCatMere).map(a => a.categorieFille).filter(Boolean))].sort().map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
        {enrichCodeCatFille && (
          <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: '#7C3AED', fontFamily: 'monospace' }}>
            Code catégorie : <strong>{enrichCodeCatFille}</strong> — Prochain article : <strong>{enrichCodeCatFille}???</strong>
          </div>
        )}
      </div>

      {/* Section 2 — Marque / Fournisseur */}
      <div>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
          2. Marque / Fournisseur <span style={{ fontWeight: 400, color: '#94A3B8' }}>(optionnel)</span>
        </div>
        <input
          type="text"
          value={enrichMarque}
          onChange={e => setEnrichMarque(e.target.value)}
          placeholder="ex: Schneider Electric, Legrand, ABB..."
          style={{ width: '100%', padding: '0.5rem 0.75rem', border: '2px solid #E2E8F0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' }}
        />
      </div>

      {/* Section 3 — URL catalogue */}
      <div>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
          3. URL du catalogue fournisseur
        </div>
        <input
          type="url"
          value={enrichUrl}
          onChange={e => setEnrichUrl(e.target.value)}
          placeholder="https://www.fournisseur.com/catalogue/categorie"
          style={{ width: '100%', padding: '0.5rem 0.75rem', border: '2px solid ' + (enrichUrl && !enrichUrl.startsWith('http') ? '#EF4444' : '#E2E8F0'), borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' }}
        />
        <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: '#64748B' }}>
          ℹ️ L'URL doit pointer vers une page listant des produits publiquement accessibles
        </div>
        {enrichUrl && !enrichUrl.startsWith('http') && (
          <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#EF4444' }}>⚠️ L'URL doit commencer par http:// ou https://</div>
        )}
      </div>

      {/* Message d'erreur */}
      {enrichErreur && (
        <div style={{ padding: '0.75rem 1rem', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', color: '#DC2626', fontSize: '0.83rem' }}>
          ⚠️ {enrichErreur}
        </div>
      )}

      {/* Messages de validation — champs manquants */}
      {(!enrichCatFille || !enrichUrl) && (
        <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '8px', padding: '0.6rem 0.9rem', fontSize: '0.8rem', color: '#92400E', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <strong>Champs requis avant de lancer l'analyse :</strong>
          {!enrichCatFille && <span>▸ Sélectionnez un domaine, une catégorie mère et une catégorie fille</span>}
          {!enrichUrl && <span>▸ Renseignez l'URL du catalogue fournisseur</span>}
          {enrichUrl && !enrichUrl.startsWith('http') && <span>▸ L'URL doit commencer par https://</span>}
        </div>
      )}

      {/* Bouton lancer */}
      <button
        className="btn btn-primary"
        disabled={!enrichUrl.startsWith('http') || !enrichCatFille || !enrichCodeCatFille}
        onClick={lancerEnrichissementIA}
        style={{ background: 'linear-gradient(135deg, #6B21A8, #A855F7)', border: 'none', padding: '0.7rem 1.5rem', fontSize: '0.95rem', fontWeight: 700, cursor: (!enrichUrl.startsWith('http') || !enrichCatFille) ? 'not-allowed' : 'pointer', opacity: (!enrichUrl.startsWith('http') || !enrichCatFille) ? 0.45 : 1 }}>
        🔍 Analyser le catalogue
      </button>
    </div>
    )}

    {/* ── ÉTAPE 2 : Chargement ─────────────────────────────────────── */}
    {enrichEtape === 'loading' && (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', padding: '2rem 0' }}>
      <div style={{ fontSize: '2.5rem' }}>🤖</div>
      <div style={{ fontWeight: 700, fontSize: '1rem', color: '#4C1D95' }}>Analyse en cours…</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', alignSelf: 'stretch', maxWidth: '360px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem', color: '#475569' }}>
          <span>✅</span> Lecture du catalogue fournisseur (Jina AI)
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem', color: '#7C3AED' }}>
          <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Extraction et structuration des articles (Gemini)…
        </div>
      </div>
      <div style={{ width: '240px', height: '4px', background: '#E9D5FF', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ height: '100%', background: 'linear-gradient(90deg, #7C3AED, #A855F7)', borderRadius: 99, animation: 'slide 1.4s ease-in-out infinite', width: '40%' }} />
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
    )}

    {/* ── ÉTAPE 3 : Résultats / Validation ────────────────────────── */}
    {enrichEtape === 'resultats' && (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

      {enrichErreur && (
        <div style={{ padding: '0.75rem 1rem', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', color: '#DC2626', fontSize: '0.83rem' }}>
          ⚠️ {enrichErreur}
        </div>
      )}

      {enrichResultats.length === 0 && !enrichErreur && (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: '#64748B', fontSize: '0.9rem' }}>
          Aucun article trouvé pour cette catégorie dans la page analysée.
          <br /><br />
          <button className="btn btn-secondary" onClick={() => setEnrichEtape('form')}>← Modifier les paramètres</button>
        </div>
      )}

      {enrichResultats.length > 0 && (
      <>
        {/* Barre de sélection */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.83rem', fontWeight: 600 }}>
            <input type="checkbox"
              checked={enrichSelection.length === enrichResultats.filter(a => !a._doublon).length && enrichResultats.filter(a => !a._doublon).length > 0}
              onChange={e => setEnrichSelection(e.target.checked ? enrichResultats.filter(a => !a._doublon).map((_, i) => i) : [])}
            />
            Tout sélectionner
          </label>
          <span style={{ fontSize: '0.8rem', color: '#64748B' }}>
            {enrichSelection.length} / {enrichResultats.filter(a => !a._doublon).length} article(s) sélectionné(s)
          </span>
          {enrichResultats.some(a => a._doublon) && (
            <span style={{ fontSize: '0.78rem', color: '#F59E0B', background: '#FFFBEB', padding: '0.2rem 0.5rem', borderRadius: '99px', border: '1px solid #FDE68A' }}>
              ⚠️ {enrichResultats.filter(a => a._doublon).length} déjà présent(s) dans le catalogue
            </span>
          )}
        </div>

        {/* Tableau des articles */}
        <div style={{ border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'center', width: 36 }}></th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left' }}>Désignation</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left' }}>Réf. fourn.</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left' }}>Unité</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Prix</th>
              </tr>
            </thead>
            <tbody>
              {enrichResultats.map((article, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #F1F5F9', background: article._doublon ? '#FFFBEB' : enrichSelection.includes(idx) ? '#FAF5FF' : 'white', opacity: article._doublon ? 0.6 : 1 }}>
                  <td style={{ padding: '0.5rem 0.6rem', textAlign: 'center' }}>
                    {article._doublon
                      ? <span title="Déjà présent dans le catalogue" style={{ color: '#F59E0B', fontSize: '0.9rem' }}>⚠️</span>
                      : <input type="checkbox" checked={enrichSelection.includes(idx)} onChange={e => {
                          setEnrichSelection(prev => e.target.checked ? [...prev, idx] : prev.filter(i => i !== idx));
                        }} />
                    }
                  </td>
                  <td style={{ padding: '0.5rem 0.6rem' }}>
                    <div style={{ fontWeight: 600, color: '#1E293B' }}>{article.designation}</div>
                    {article.description && <div style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '0.1rem' }}>{article.description}</div>}
                    {article.marque && <div style={{ fontSize: '0.72rem', color: '#A855F7', marginTop: '0.1rem' }}>{article.marque}</div>}
                    {article._doublon && <div style={{ fontSize: '0.7rem', color: '#D97706', marginTop: '0.1rem', fontStyle: 'italic' }}>Déjà présent dans le catalogue</div>}
                  </td>
                  <td style={{ padding: '0.5rem 0.6rem', color: '#475569', fontFamily: 'monospace', fontSize: '0.78rem' }}>{article.refFournisseur || '—'}</td>
                  <td style={{ padding: '0.5rem 0.6rem', color: '#475569' }}>{article.unite}</td>
                  <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', color: article.prixUnitaire > 0 ? '#0F172A' : '#94A3B8', fontFamily: 'monospace' }}>
                    {article.prixUnitaire > 0 ? article.prixUnitaire.toLocaleString('fr-FR') + ' FCFA' : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
      )}
    </div>
    )}

  </div>

  {/* Pied de modale */}
  <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #E2E8F0', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexShrink: 0, background: '#F8FAFC' }}>
    {enrichEtape === 'resultats' && (
      <button className="btn btn-secondary" onClick={() => { setEnrichEtape('form'); setEnrichErreur(null); }}>
        ← Recommencer
      </button>
    )}
    {enrichEtape === 'resultats' && enrichSelection.length > 0 && (
      <button
        className="btn btn-primary"
        onClick={validerEnrichissementIA}
        style={{ background: 'linear-gradient(135deg, #6B21A8, #A855F7)', border: 'none', fontWeight: 700 }}>
        ✅ Enregistrer la sélection ({enrichSelection.length})
      </button>
    )}
    <button className="btn btn-secondary" onClick={() => setShowEnrichissementIA(false)}>
      Fermer
    </button>
  </div>

</div>
</div>
)}

{/* Modal Formulaire Article Catalogue */}
{showCatalogueArticleModal && (
<div className="modal-overlay" style={{ zIndex: 10001 }}>
<div className="modal-content" style={{ maxWidth: '600px', width: '95vw' }}>
<div className="modal-header" style={{ background: 'linear-gradient(135deg, #0A2540, #00D4AA)', color: 'white' }}>
<h2 className="modal-title" style={{ color: 'white' }}>
{editingCatalogueArticle === null ? '➕ Nouvel article catalogue' : '✏️ Modifier l\'article'}
</h2>
<button className="close-btn" style={{ color: 'white' }} onClick={() => setShowCatalogueArticleModal(false)}>✕</button>
</div>
<div className="modal-body">
<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
{/* Identifiants */}
<div>
<label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.85rem', color: '#0A2540' }}>Code Article *</label>
<input type="text" value={catalogueArticleForm.codeArticle || catalogueArticleForm.ref}
onChange={e => setCatalogueArticleForm({...catalogueArticleForm, codeArticle: e.target.value, ref: e.target.value})}
placeholder="112A001001" style={{ width: '100%', padding: '0.6rem', border: '2px solid #E2E8F0', borderRadius: '8px', fontFamily: 'JetBrains Mono, monospace' }}
/>
</div>
<div>
<label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.85rem', color: '#0A2540' }}>Unité *</label>
<input type="text" value={catalogueArticleForm.unite}
onChange={e => setCatalogueArticleForm({...catalogueArticleForm, unite: e.target.value, uniteLibelle: e.target.value})}
placeholder="pièce, mètre, lot..." style={{ width: '100%', padding: '0.6rem', border: '2px solid #E2E8F0', borderRadius: '8px' }}
list="unite-suggestions"
/>
<datalist id="unite-suggestions">
<option value="pièce"/><option value="mètre"/><option value="lot"/><option value="boîte"/><option value="rouleau"/>
</datalist>
</div>
{/* Désignation */}
<div style={{ gridColumn: '1/-1' }}>
<label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.85rem', color: '#0A2540' }}>Désignation *</label>
<input type="text" value={catalogueArticleForm.designation}
onChange={e => setCatalogueArticleForm({...catalogueArticleForm, designation: e.target.value})}
placeholder="Nom complet de l'article" style={{ width: '100%', padding: '0.6rem', border: '2px solid #E2E8F0', borderRadius: '8px' }}
/>
</div>
{/* Description */}
<div style={{ gridColumn: '1/-1' }}>
<label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.85rem', color: '#0A2540' }}>Description technique</label>
<textarea value={catalogueArticleForm.description || ''}
onChange={e => setCatalogueArticleForm({...catalogueArticleForm, description: e.target.value})}
placeholder="Spécifications techniques détaillées (résolution, portée IR, PoE, etc.)" rows={3}
style={{ width: '100%', padding: '0.6rem', border: '2px solid #E2E8F0', borderRadius: '8px', resize: 'vertical', fontSize: '0.82rem' }}
/>
</div>
{/* Catégorie Mère */}
<div>
<label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.85rem', color: '#0A2540' }}>📂 Catégorie mère</label>
<input type="text" value={catalogueArticleForm.categoreMere}
onChange={e => setCatalogueArticleForm({...catalogueArticleForm, categoreMere: e.target.value})}
placeholder="Ex: Caméras de surveillance"
list="catmere-suggestions"
style={{ width: '100%', padding: '0.6rem', border: '2px solid #E2E8F0', borderRadius: '8px' }}
/>
<datalist id="catmere-suggestions">
{categoriesCatalogue().map(c => <option key={c} value={c}/>)}
</datalist>
</div>
<div>
<label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.85rem', color: '#0A2540' }}>Code cat. mère</label>
<input type="text" value={catalogueArticleForm.codeCategorieMere}
onChange={e => setCatalogueArticleForm({...catalogueArticleForm, codeCategorieMere: e.target.value})}
placeholder="112A" style={{ width: '100%', padding: '0.6rem', border: '2px solid #E2E8F0', borderRadius: '8px', fontFamily: 'JetBrains Mono, monospace' }}
/>
</div>
{/* Catégorie Fille */}
<div>
<label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.85rem', color: '#0A2540' }}>📁 Catégorie fille</label>
<input type="text" value={catalogueArticleForm.categorieFille}
onChange={e => setCatalogueArticleForm({...catalogueArticleForm, categorieFille: e.target.value})}
placeholder="Ex: Caméras IP dôme intérieur"
list="catfille-suggestions"
style={{ width: '100%', padding: '0.6rem', border: '2px solid #E2E8F0', borderRadius: '8px' }}
/>
<datalist id="catfille-suggestions">
{catsF.map(c => <option key={c} value={c}/>)}
</datalist>
</div>
<div>
<label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.85rem', color: '#0A2540' }}>Code cat. fille</label>
<input type="text" value={catalogueArticleForm.codeCategorieFille}
onChange={e => setCatalogueArticleForm({...catalogueArticleForm, codeCategorieFille: e.target.value})}
placeholder="112A001" style={{ width: '100%', padding: '0.6rem', border: '2px solid #E2E8F0', borderRadius: '8px', fontFamily: 'JetBrains Mono, monospace' }}
/>
</div>
{/* Marque & Réf fournisseur */}
<div>
<label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.85rem', color: '#0A2540' }}>🏷️ Marque</label>
<input type="text" value={catalogueArticleForm.marque}
onChange={e => setCatalogueArticleForm({...catalogueArticleForm, marque: e.target.value})}
placeholder="Ex: Hikvision, Dahua..." style={{ width: '100%', padding: '0.6rem', border: '2px solid #E2E8F0', borderRadius: '8px' }}
/>
</div>
<div>
<label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.85rem', color: '#0A2540' }}>Réf. fournisseur</label>
<input type="text" value={catalogueArticleForm.refFournisseur}
onChange={e => setCatalogueArticleForm({...catalogueArticleForm, refFournisseur: e.target.value})}
placeholder="Référence constructeur" style={{ width: '100%', padding: '0.6rem', border: '2px solid #E2E8F0', borderRadius: '8px' }}
/>
</div>
{/* Prix & TVA */}
<div>
<label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.85rem', color: '#0A2540' }}>💰 Prix unitaire (XAF)</label>
<input type="number" value={catalogueArticleForm.prixUnitaire} min="0"
onChange={e => setCatalogueArticleForm({...catalogueArticleForm, prixUnitaire: parseFloat(e.target.value)||0})}
style={{ width: '100%', padding: '0.6rem', border: '2px solid #E2E8F0', borderRadius: '8px' }}
/>
</div>
<div>
<label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.85rem', color: '#0A2540' }}>TVA applicable</label>
<select value={catalogueArticleForm.tva}
onChange={e => setCatalogueArticleForm({...catalogueArticleForm, tva: e.target.value})}
style={{ width: '100%', padding: '0.6rem', border: '2px solid #E2E8F0', borderRadius: '8px' }}
>
<option value="TTC">TTC ({settings.tauxTVA || 19.25}%)</option>
<option value="HT">HT (exonéré)</option>
</select>
</div>
</div>
</div>
<div className="modal-footer">
<button className="btn btn-secondary" onClick={() => setShowCatalogueArticleModal(false)}>Annuler</button>
<button className="btn btn-primary" onClick={sauvegarderArticleCatalogue}
style={{ background: 'linear-gradient(135deg, #0A2540, #00D4AA)' }}>
{editingCatalogueArticle === null ? '➕ Ajouter au catalogue' : '✅ Enregistrer'}
</button>
</div>
</div>
</div>
)}

{/* Modal Statistiques */}
{showStatistiques && (
<div className="modal-overlay">
<div className="modal-content">
<div className="modal-header">
<h2 className="modal-title">📊 Statistiques Détaillées</h2>
<button className="close-btn" onClick={() => setShowStatistiques(false)}>✕</button>
</div>

<div className="modal-body">
<div style={{ marginBottom: '1.5rem' }}>
<h3 style={{ color: 'var(--text-dark)', marginBottom: '0.5rem' }}>Période d'analyse</h3>
<div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
<input
type="date"
className="form-input"
value={dateDebut}
onChange={(e) => setDateDebut(e.target.value)}
placeholder="Date de début"
style={{ width: '150px' }}
/>
<input
type="date"
className="form-input"
value={dateFin}
onChange={(e) => setDateFin(e.target.value)}
placeholder="Date de fin"
style={{ width: '150px' }}
/>
<button
className="btn btn-secondary"
onClick={() => {
setDateDebut('');
setDateFin('');
}}
>
Réinitialiser
</button>
</div>
</div>

{(() => {
const statsDetaillees = calculerStatistiquesDetailees();

return (
<>
{/* Statistiques globales */}
<div style={{ marginBottom: '2rem' }}>
<h3 style={{ marginBottom: '1rem', color: 'var(--text-dark)' }}>📈 Vue d'ensemble</h3>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
<div style={{
padding: '1.5rem',
background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
borderRadius: '12px',
color: 'white',
boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
}}>
<div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem' }}>Total Documents</div>
<div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{statsDetaillees.totalDocuments}</div>
</div>
<div style={{
padding: '1.5rem',
background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
borderRadius: '12px',
color: 'white',
boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
}}>
<div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem' }}>Taux de Conversion</div>
<div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{statsDetaillees.tauxConversion}%</div>
</div>
<div style={{
padding: '1.5rem',
background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
borderRadius: '12px',
color: 'white',
boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
}}>
<div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem' }}>Taux de Paiement</div>
<div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{statsDetaillees.tauxPaiement}%</div>
</div>
<div style={{
padding: '1.5rem',
background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
borderRadius: '12px',
color: '#1E293B',
boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
}}>
<div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.5rem', fontWeight: '600' }}>Taux de Recouvrement</div>
<div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{statsDetaillees.tauxRecouvrement}%</div>
</div>
</div>
</div>

{/* Statistiques des devis */}
<div style={{ marginBottom: '2rem' }}>
<h3 style={{ marginBottom: '1rem', color: 'var(--text-dark)' }}>📋 Devis</h3>
<div style={{
background: '#F8FAFC',
borderRadius: '12px',
padding: '1.5rem',
border: '1px solid #E2E8F0'
}}>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1.5rem' }}>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>Total</div>
<div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3B82F6' }}>{statsDetaillees.devis.total}</div>
</div>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>En attente</div>
<div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#F59E0B' }}>{statsDetaillees.devis.enAttente}</div>
</div>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>Acceptés</div>
<div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10B981' }}>{statsDetaillees.devis.acceptes}</div>
</div>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>Refusés</div>
<div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#EF4444' }}>{statsDetaillees.devis.refuses}</div>
</div>
</div>
<div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '2px solid #E2E8F0' }}>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>Montant total</div>
<div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1E293B', fontFamily: 'JetBrains Mono, monospace' }}>
{formatMontant(statsDetaillees.devis.montantTotal)}
</div>
</div>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>Montant acceptés</div>
<div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#10B981', fontFamily: 'JetBrains Mono, monospace' }}>
{formatMontant(statsDetaillees.devis.montantAcceptes)}
</div>
</div>
</div>
</div>
</div>
</div>

{/* Statistiques des factures */}
<div style={{ marginBottom: '2rem' }}>
<h3 style={{ marginBottom: '1rem', color: 'var(--text-dark)' }}>🧾 Factures</h3>
<div style={{
background: '#F8FAFC',
borderRadius: '12px',
padding: '1.5rem',
border: '1px solid #E2E8F0'
}}>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1.5rem' }}>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>Total</div>
<div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10B981' }}>{statsDetaillees.factures.total}</div>
</div>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>Payées</div>
<div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10B981' }}>{statsDetaillees.factures.payees}</div>
</div>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>Impayées</div>
<div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#EF4444' }}>{statsDetaillees.factures.impayees}</div>
</div>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>Partielles</div>
<div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#F59E0B' }}>{statsDetaillees.factures.partielles}</div>
</div>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>Annulées</div>
<div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#94A3B8' }}>{statsDetaillees.factures.annulees}</div>
</div>
</div>
<div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '2px solid #E2E8F0' }}>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>Montant total facturé</div>
<div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1E293B', fontFamily: 'JetBrains Mono, monospace' }}>
{formatMontant(statsDetaillees.factures.montantTotal)}
</div>
</div>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>Montant encaissé</div>
<div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#10B981', fontFamily: 'JetBrains Mono, monospace' }}>
{formatMontant(statsDetaillees.factures.montantEncaisse)}
</div>
{statsDetaillees.factures.montantAvance > 0 && (
<div style={{ fontSize: '0.7rem', color: '#F59E0B', marginTop: '0.25rem' }}>
dont {formatMontant(statsDetaillees.factures.montantAvance)} d'avances
</div>
)}
</div>
<div>
<div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>Montant en attente</div>
<div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#EF4444', fontFamily: 'JetBrains Mono, monospace' }}>
{formatMontant(statsDetaillees.factures.montantEnAttente)}
</div>
{statsDetaillees.factures.montantRestantPartielles > 0 && (
<div style={{ fontSize: '0.7rem', color: '#F59E0B', marginTop: '0.25rem' }}>
dont {formatMontant(statsDetaillees.factures.montantRestantPartielles)} en paiement partiel
</div>
)}
</div>
</div>
</div>
</div>
</div>

{/* Récapitulatif par client */}
<div>
<h3 style={{ marginBottom: '1rem', color: 'var(--text-dark)' }}>👥 Récapitulatif par client</h3>
{statsDetaillees.clients.length === 0 ? (
<div style={{
padding: '3rem',
textAlign: 'center',
background: '#F8FAFC',
borderRadius: '12px',
color: '#64748B'
}}>
<p style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</p>
<p>Aucune donnée client pour la période sélectionnée</p>
</div>
) : (
<div style={{ overflowX: 'auto' }}>
<table className="documents-table">
<thead>
<tr>
<th>Client</th>
<th>Devis</th>
<th>Factures</th>
<th>Montant Devis</th>
<th>Montant Factures</th>
<th>Payé</th>
<th>Impayé</th>
<th>Total</th>
</tr>
</thead>
<tbody>
{statsDetaillees.clients.map((client, index) => (
<tr key={index}>
<td style={{ fontWeight: 600 }}>{client.nom||''}</td>
<td style={{ textAlign: 'center' }}>
{client.nombreDevis}
{client.devisAcceptes > 0 && (
<span style={{ fontSize: '0.75rem', color: '#10B981', marginLeft: '0.25rem' }}>
(✓{client.devisAcceptes})
</span>
)}
</td>
<td style={{ textAlign: 'center' }}>{client.nombreFactures}</td>
<td style={{ fontFamily: 'JetBrains Mono, monospace', textAlign: 'right' }}>
{formatMontant(client.montantDevis)}
</td>
<td style={{ fontFamily: 'JetBrains Mono, monospace', textAlign: 'right' }}>
{formatMontant(client.montantFactures)}
</td>
<td style={{ fontFamily: 'JetBrains Mono, monospace', textAlign: 'right', color: '#10B981', fontWeight: 600 }}>
{formatMontant(client.montantPaye)}
{client.montantAvance > 0 && (
<div style={{ fontSize: '0.7rem', color: '#F59E0B', fontWeight: 'normal', marginTop: '0.15rem' }}>
({formatMontant(client.montantAvance)} avance)
</div>
)}
</td>
<td style={{ fontFamily: 'JetBrains Mono, monospace', textAlign: 'right', color: '#EF4444', fontWeight: 600 }}>
{formatMontant(client.montantImpaye)}
</td>
<td style={{ fontFamily: 'JetBrains Mono, monospace', textAlign: 'right', fontWeight: 'bold', fontSize: '1.05rem' }}>
{formatMontant(client.montantDevis + client.montantFactures)}
</td>
</tr>
))}
</tbody>
</table>
</div>
)}
</div>

{/* ===== NOUVELLES STATISTIQUES ===== */}

{/* Statistiques par unité */}
{statsDetaillees.unites && statsDetaillees.unites.length > 0 && (
<div style={{ marginBottom: '2rem' }}>
<h3 style={{ marginBottom: '1rem', color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
📦 Statistiques par Unité
</h3>
<div style={{
background: '#F8FAFC',
borderRadius: '12px',
padding: '1.5rem',
border: '1px solid #E2E8F0'
}}>
<table style={{ width: '100%', borderCollapse: 'collapse' }}>
<thead>
<tr style={{ borderBottom: '2px solid #E2E8F0' }}>
<th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Unité</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Quantité Totale</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Nb Articles</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Montant Total</th>
</tr>
</thead>
<tbody>
{statsDetaillees.unites.map((stat, index) => (
<tr key={index} style={{ borderBottom: '1px solid #E2E8F0' }}>
<td style={{ padding: '0.75rem', fontWeight: '600', color: '#1E293B' }}>
<span style={{
background: '#E0F2FE',
padding: '0.25rem 0.75rem',
borderRadius: '6px',
fontSize: '0.85rem',
fontWeight: 'bold',
color: '#0369A1'
}}>
{stat.unite}
</span>
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'monospace', color: '#1E293B' }}>
{stat.quantiteTotale.toFixed(2)}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', color: '#64748B' }}>
{stat.nombreArticles}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: '600', color: '#10B981' }}>
{formatMontant(stat.montantTotal)}
</td>
</tr>
))}
</tbody>
</table>
</div>
</div>
)}

{/* Statistiques par méthode de paiement */}
{statsDetaillees.methodesPaiement && statsDetaillees.methodesPaiement.length > 0 && (
<div style={{ marginBottom: '2rem' }}>
<h3 style={{ marginBottom: '1rem', color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
💳 Statistiques par Méthode de Paiement
</h3>
<div style={{
background: '#F8FAFC',
borderRadius: '12px',
padding: '1.5rem',
border: '1px solid #E2E8F0'
}}>
<table style={{ width: '100%', borderCollapse: 'collapse' }}>
<thead>
<tr style={{ borderBottom: '2px solid #E2E8F0' }}>
<th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Méthode</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Nb Docs</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Montant Total</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Payé</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Impayé</th>
</tr>
</thead>
<tbody>
{statsDetaillees.methodesPaiement.map((stat, index) => {
const methodeLabels = {
'toutes': 'Toutes',
'virement': 'Virement Bancaire',
'cheque': 'Chèque',
'especes': 'Espèces',
'mobile': 'Mobile Money'
};
return (
<tr key={index} style={{ borderBottom: '1px solid #E2E8F0' }}>
<td style={{ padding: '0.75rem', fontWeight: '600', color: '#1E293B' }}>
{methodeLabels[stat.methode] || stat.methode}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', color: '#64748B' }}>
{stat.nombreDocuments}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: '600', color: '#1E293B' }}>
{formatMontant(stat.montantTotal)}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'monospace', color: '#10B981' }}>
{formatMontant(stat.montantPaye)}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'monospace', color: '#EF4444' }}>
{formatMontant(stat.montantImpaye)}
</td>
</tr>
);
})}
</tbody>
</table>
</div>
</div>
)}

{/* Top 10 des articles */}
{statsDetaillees.topArticles && statsDetaillees.topArticles.length > 0 && (
<div style={{ marginBottom: '2rem' }}>
<h3 style={{ marginBottom: '1rem', color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
🏆 Top 10 des Articles
</h3>
<div style={{
background: '#F8FAFC',
borderRadius: '12px',
padding: '1.5rem',
border: '1px solid #E2E8F0'
}}>
<table style={{ width: '100%', borderCollapse: 'collapse' }}>
<thead>
<tr style={{ borderBottom: '2px solid #E2E8F0' }}>
<th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Article</th>
<th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Unité</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Quantité</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Nb Ventes</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>CA Total</th>
</tr>
</thead>
<tbody>
{statsDetaillees.topArticles.map((article, index) => (
<tr key={index} style={{ borderBottom: '1px solid #E2E8F0' }}>
<td style={{ padding: '0.75rem', color: '#1E293B' }}>
<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
<span style={{
background: index < 3 ? '#FFD700' : '#E0F2FE',
color: index < 3 ? '#000' : '#0369A1',
width: '24px',
height: '24px',
borderRadius: '50%',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
fontSize: '0.75rem',
fontWeight: 'bold'
}}>
{index + 1}
</span>
<span style={{ fontWeight: '500' }}>{article.description}</span>
</div>
</td>
<td style={{ padding: '0.75rem', textAlign: 'center' }}>
<span style={{
background: '#E0F2FE',
padding: '0.25rem 0.5rem',
borderRadius: '4px',
fontSize: '0.75rem',
fontWeight: 'bold',
color: '#0369A1'
}}>
{article.unite}
</span>
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'monospace', color: '#1E293B' }}>
{article.quantiteTotale}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', color: '#64748B' }}>
{article.nombreVentes}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: '600', color: '#10B981' }}>
{formatMontant(article.montantTotal)}
</td>
</tr>
))}
</tbody>
</table>
</div>
</div>
)}

{/* Statistiques par validité */}
{statsDetaillees.validites && statsDetaillees.validites.length > 0 && (
<div style={{ marginBottom: '2rem' }}>
<h3 style={{ marginBottom: '1rem', color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
⏱️ Devis par Durée de Validité
</h3>
<div style={{
background: '#F8FAFC',
borderRadius: '12px',
padding: '1.5rem',
border: '1px solid #E2E8F0'
}}>
<table style={{ width: '100%', borderCollapse: 'collapse' }}>
<thead>
<tr style={{ borderBottom: '2px solid #E2E8F0' }}>
<th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Validité</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Total</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Acceptés</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Refusés</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>En Attente</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Taux Accept.</th>
</tr>
</thead>
<tbody>
{statsDetaillees.validites.map((stat, index) => (
<tr key={index} style={{ borderBottom: '1px solid #E2E8F0' }}>
<td style={{ padding: '0.75rem', fontWeight: '600', color: '#1E293B' }}>
{stat.validite} jours
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', color: '#1E293B' }}>
{stat.nombre}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', color: '#10B981' }}>
{stat.acceptes}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', color: '#EF4444' }}>
{stat.refuses}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', color: '#F59E0B' }}>
{stat.enAttente}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#10B981' }}>
{stat.tauxAcceptation}%
</td>
</tr>
))}
</tbody>
</table>
</div>
</div>
)}

{/* Évolution mensuelle */}
{statsDetaillees.parMois && statsDetaillees.parMois.length > 0 && (
<div style={{ marginBottom: '2rem' }}>
<h3 style={{ marginBottom: '1rem', color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
📈 Évolution Mensuelle
</h3>
<div style={{
background: '#F8FAFC',
borderRadius: '12px',
padding: '1.5rem',
border: '1px solid #E2E8F0',
overflowX: 'auto'
}}>
<table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
<thead>
<tr style={{ borderBottom: '2px solid #E2E8F0' }}>
<th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Mois</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Nb Devis</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Nb Factures</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Montant Devis</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Montant Factures</th>
<th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.8rem', color: '#64748B', fontWeight: '600' }}>Encaissé</th>
</tr>
</thead>
<tbody>
{statsDetaillees.parMois.map((stat, index) => {
const [annee, mois] = stat.mois.split('-');
const nomMois = new Date(annee, parseInt(mois) - 1).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' });
return (
<tr key={index} style={{ borderBottom: '1px solid #E2E8F0' }}>
<td style={{ padding: '0.75rem', fontWeight: '600', color: '#1E293B', textTransform: 'capitalize' }}>
{nomMois}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', color: '#3B82F6' }}>
{stat.nombreDevis}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', color: '#10B981' }}>
{stat.nombreFactures}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'monospace', color: '#3B82F6' }}>
{formatMontant(stat.montantDevis)}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'monospace', color: '#10B981' }}>
{formatMontant(stat.montantFactures)}
</td>
<td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: '600', color: '#10B981' }}>
{formatMontant(stat.montantEncaisse)}
</td>
</tr>
);
})}
</tbody>
</table>
</div>
</div>
)}

</>
);
})()}
</div>

<div className="modal-footer">
<button className="btn btn-secondary" onClick={() => setShowStatistiques(false)}>
Fermer
</button>
<button
className="btn btn-success"
onClick={() => exporterExcel(documents)}
>
📊 Exporter les données
</button>
</div>
</div>
</div>
)}

{/* ========================================================= */}
{/* MODALE REÇU DE PAIEMENT — MULTI-MODÈLES                    */}
{/* ========================================================= */}
{/* ====== MODAL EXPORT COMPTABLE FEC/OHADA ====== */}
{showExportComptable && (
<div className="modal-overlay" onClick={() => setShowExportComptable(false)} style={{ zIndex: 2000 }}>
<div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px' }}>
<div className="modal-header">
<h2 className="modal-title">📒 Export Comptable FEC / OHADA</h2>
<button className="btn btn-secondary" onClick={() => setShowExportComptable(false)} style={{ padding: '0.4rem 0.8rem' }}>✕</button>
</div>
<div className="modal-body">
<p style={{ marginBottom: '1rem', color: '#334155', lineHeight: 1.6 }}>
Génère un fichier CSV au format <strong>FEC / OHADA</strong> compatible avec les logiciels comptables locaux
(<strong>Sage</strong>, <strong>SAARI</strong>, etc.).
</p>
<p style={{ marginBottom: '1.5rem', color: '#64748b', fontSize: '0.875rem', lineHeight: 1.6 }}>
Chaque facture génère 2 ou 3 lignes d'écritures comptables (compte client 411, compte ventes 701, TVA collectée 443100).
Le séparateur utilisé est <code>|</code> (pipe), standard FEC français et OHADA.
</p>

<div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
<button
className="btn btn-primary"
style={{ background: 'linear-gradient(135deg, #1e40af, #3b82f6)', justifyContent: 'flex-start', gap: '0.75rem', padding: '0.9rem 1.2rem' }}
onClick={() => {
exporterComptable(documents.filter(d => d.type === 'facture'), 'VE');
setShowExportComptable(false);
}}
>
<span style={{ fontSize: '1.4rem' }}>📗</span>
<span>
<strong>Journal des Ventes (JV)</strong><br />
<small style={{ fontWeight: 'normal', opacity: 0.85 }}>Toutes les factures — comptes 411 / 701 / 443100</small>
</span>
</button>

<button
className="btn btn-primary"
style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)', justifyContent: 'flex-start', gap: '0.75rem', padding: '0.9rem 1.2rem' }}
onClick={() => {
const avoirs = documents.filter(d => d.estAvoir === true || d.type === 'avoir');
if (avoirs.length === 0) { alert('Aucun avoir trouvé à exporter.'); return; }
exporterComptable(avoirs, 'AV');
setShowExportComptable(false);
}}
>
<span style={{ fontSize: '1.4rem' }}>📙</span>
<span>
<strong>Journal des Avoirs (AV)</strong><br />
<small style={{ fontWeight: 'normal', opacity: 0.85 }}>Notes de crédit — écritures inversées</small>
</span>
</button>

<button
className="btn btn-primary"
style={{ background: 'linear-gradient(135deg, #0f766e, #14b8a6)', justifyContent: 'flex-start', gap: '0.75rem', padding: '0.9rem 1.2rem' }}
onClick={() => {
exporterComptable(documents, 'ALL');
setShowExportComptable(false);
}}
>
<span style={{ fontSize: '1.4rem' }}>📘</span>
<span>
<strong>Export complet (Ventes + Avoirs)</strong><br />
<small style={{ fontWeight: 'normal', opacity: 0.85 }}>Tous les documents dans un seul fichier</small>
</span>
</button>
</div>

<div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f1f5f9', borderRadius: '10px', fontSize: '0.8rem', color: '#475569', lineHeight: 1.7 }}>
<strong>📋 Plan de comptes OHADA utilisé :</strong><br />
• <code>411000</code> — Clients<br />
• <code>701000</code> — Ventes de marchandises (HT)<br />
• <code>701900</code> — Avoirs sur ventes<br />
• <code>443100</code> — TVA collectée ({settings.tauxTVA || 19.25}%)<br />
• Devise : <strong>XAF</strong> (Franc CFA)
</div>
</div>
<div className="modal-footer">
<button className="btn btn-secondary" onClick={() => setShowExportComptable(false)}>Fermer</button>
</div>
</div>
</div>
)}

{showRecuModal && recuPaiement && (() => {
const { paiement, doc } = recuPaiement;
const montantPaye = (doc.paiements || []).reduce((sum, p) => sum + (p.montant || 0), 0);
const resteAPayer = (doc.total || 0) - montantPaye;
const methodeLabels = {
virement: 'Virement Bancaire',
cheque: 'Chèque',
especes: 'Espèces',
carte: 'Carte bancaire',
mobile: 'Mobile Money'
};
const numRecu = `RECU-${doc.numero}-${paiement.id || Date.now()}`;
const clientNom = doc.client && typeof doc.client === 'object' ? ((doc.client||{}).nom || '-') : (doc.client || '-');
const clientTel = doc.client && typeof doc.client === 'object' ? doc.client?.telephone : '';
const clientEmail = doc.client && typeof doc.client === 'object' ? doc.client?.email : '';
const modele = settings.modeleRecu || 'classique';
const dateGen = new Date().toLocaleDateString('fr-FR');
const heureGen = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

// ── Fonctions helper partagées ──
const LigneInfo = ({ label, valeur, gras, mono, couleur }) => (
React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', alignItems: 'flex-start' } },
React.createElement('span', { style: { color: couleur || '#64748B', flexShrink: 0, marginRight: '0.5rem' } }, label),
React.createElement('span', { style: { fontWeight: gras ? 'bold' : 'normal', fontFamily: mono ? 'monospace' : 'inherit', textAlign: 'right' } }, valeur)
)
);

const InfoMobile = () => paiement.methode === 'mobile' ? React.createElement(React.Fragment, null,
React.createElement(LigneInfo, { label: '📱 N° téléphone', valeur: paiement.telephoneMobile || '-', gras: true, mono: true }),
React.createElement(LigneInfo, { label: '🔑 ID transaction', valeur: paiement.idTransaction || '-', gras: true, mono: true })
) : (paiement.reference ? React.createElement(LigneInfo, { label: 'Référence', valeur: paiement.reference, mono: true }) : null);

const printRecu = () => {
const printContent = document.getElementById('recu-print-zone').innerHTML;
const isThermique = modele === 'thermique';
const isDuplicata = modele === 'minimaliste-duplicata';
const w = window.open('', '_blank', isThermique ? 'width=320,height=700' : isDuplicata ? 'width=794,height=1123' : 'width=500,height=720');
w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Reçu ${numRecu}</title>
<link href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet">
<style>
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family: 'Bricolage Grotesque', Arial, sans-serif; background:#fff; color:#1E293B; }
${isThermique ? '@page { size: 80mm auto; margin: 2mm; } @media print { body { width:80mm; font-size:11px; } }' : isDuplicata ? '@page { size: A4 portrait; margin: 6mm; } @media print { body { width:100%; } }' : '@page { size: A5; margin: 8mm; }'}
</style></head><body>${printContent}</body></html>`);
w.document.close();
w.focus();
setTimeout(() => { w.print(); w.close(); }, 500);
};
return (
<div className="modal-overlay" style={{ zIndex: 3000 }} onClick={() => setShowRecuModal(false)}>
<div className="modal-container" style={{ maxWidth: modele === 'thermique' ? '380px' : modele === 'minimaliste-duplicata' ? '860px' : '520px', width: '95%' }} onClick={e => e.stopPropagation()}>

<div className="modal-header" style={{
background: modele === 'classique' ? 'linear-gradient(135deg, #6366F1, #4F46E5)'
: modele === 'moderne' ? 'linear-gradient(135deg, #0EA5E9, #0284C7)'
: (modele === 'minimaliste' || modele === 'minimaliste-duplicata') ? '#1E293B'
: 'linear-gradient(135deg, #374151, #111827)',
color: 'white'
}}>
<h2 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
🧾 Reçu de Paiement
<span style={{ fontSize: '0.7rem', opacity: 0.8, fontWeight: 400 }}>
[{modele.charAt(0).toUpperCase() + modele.slice(1)}]
</span>
</h2>
<button className="close-btn" onClick={() => setShowRecuModal(false)}>✕</button>
</div>

<div className="modal-body" style={{ padding: '1.25rem', maxHeight: '75vh', overflowY: 'auto' }}>

{modele === 'classique' && (
<div id="recu-print-zone" style={{ background: '#fff', border: '2px dashed #6366F1', borderRadius: '12px', padding: '1.5rem', fontFamily: 'Arial, sans-serif', fontSize: '0.85rem', color: '#1E293B' }}>
<div style={{ textAlign: 'center', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '2px solid #6366F1' }}>
{settings.logo && <img src={settings.logo} alt="logo" style={{ maxHeight: '45px', marginBottom: '0.4rem' }} />}
<div style={{ fontWeight: 'bold', fontSize: '1.05rem', color: '#4F46E5' }}>{settings.raisonSociale || 'SOLSTICE'}</div>
{settings.adresse && <div style={{ fontSize: '0.72rem', color: '#64748B' }}>{settings.adresse}</div>}
{settings.telephone && <div style={{ fontSize: '0.72rem', color: '#64748B' }}>📞 {settings.telephone}</div>}
{settings.email && <div style={{ fontSize: '0.72rem', color: '#64748B' }}>✉️ {settings.email}</div>}
{settings.nui && <div style={{ fontSize: '0.72rem', color: '#64748B' }}>NUI : {settings.nui}</div>}
</div>
<div style={{ textAlign: 'center', margin: '0.75rem 0' }}>
<div style={{ background: '#6366F1', color: 'white', display: 'inline-block', padding: '0.3rem 1.2rem', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.9rem', letterSpacing: '1px' }}>REÇU DE PAIEMENT</div>
<div style={{ fontSize: '0.68rem', color: '#64748B', marginTop: '0.3rem', fontFamily: 'monospace' }}>N° {numRecu}</div>
</div>
<div style={{ background: '#F1F5F9', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.75rem', fontSize: '0.8rem' }}>
<LigneInfo label="Facture réf." valeur={doc.numero} gras mono />
<LigneInfo label="Client" valeur={clientNom} gras />
{clientTel && <LigneInfo label="Tél." valeur={clientTel} mono />}
{clientEmail && <LigneInfo label="Email" valeur={clientEmail} />}
{doc.objet && <LigneInfo label="Objet" valeur={doc.objet} />}
</div>
<div style={{ background: '#EEF2FF', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.75rem', border: '1px solid #C7D2FE', fontSize: '0.8rem' }}>
<div style={{ fontWeight: 'bold', color: '#4F46E5', marginBottom: '0.5rem', fontSize: '0.78rem' }}>💳 DÉTAILS DU PAIEMENT</div>
<LigneInfo label="Date" valeur={paiement.date ? new Date(paiement.date).toLocaleDateString('fr-FR') : '-'} gras />
<LigneInfo label="Mode" valeur={methodeLabels[paiement.methode] || paiement.methode} gras />
<InfoMobile />
{paiement.notes && paiement.methode !== 'mobile' && <LigneInfo label="Notes" valeur={paiement.notes} />}
</div>
<div style={{ borderTop: '2px solid #6366F1', paddingTop: '0.75rem', marginBottom: '0.75rem', fontSize: '0.8rem' }}>
<LigneInfo label="Total facture" valeur={formatMontant(doc.total || 0)} />
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', background: '#DCFCE7', padding: '0.4rem 0.5rem', borderRadius: '6px' }}>
<span style={{ color: '#047857', fontWeight: 'bold' }}>✅ MONTANT ENCAISSÉ</span>
<span style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1.1rem', color: '#065F46' }}>{formatMontant(paiement.montant || 0)}</span>
</div>
<div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0.5rem', borderRadius: '6px', background: resteAPayer <= 0 ? '#DCFCE7' : '#FEF2F2' }}>
<span style={{ color: resteAPayer <= 0 ? '#047857' : '#DC2626', fontWeight: 'bold' }}>{resteAPayer <= 0 ? '🎉 SOLDÉ' : '⏳ RESTE À PAYER'}</span>
<span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: resteAPayer <= 0 ? '#065F46' : '#DC2626' }}>{resteAPayer <= 0 ? formatMontant(0) : formatMontant(resteAPayer)}</span>
</div>
</div>
<div style={{ textAlign: 'center', fontSize: '0.68rem', color: '#94A3B8', paddingTop: '0.75rem', borderTop: '1px dashed #CBD5E1' }}>
<div>Généré le {dateGen} à {heureGen}</div>
<div style={{ marginTop: '0.2rem' }}>Merci pour votre paiement !</div>
</div>
</div>
)}

{modele === 'moderne' && (
<div id="recu-print-zone" style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', fontFamily: 'Arial, sans-serif', fontSize: '0.85rem', color: '#1E293B', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
<div style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)', padding: '1.5rem', color: 'white', textAlign: 'center' }}>
{settings.logo && <img src={settings.logo} alt="logo" style={{ maxHeight: '40px', marginBottom: '0.5rem', filter: 'brightness(0) invert(1)' }} />}
<div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{settings.raisonSociale || 'SOLSTICE'}</div>
{settings.telephone && <div style={{ fontSize: '0.72rem', opacity: 0.85, marginTop: '0.2rem' }}>📞 {settings.telephone}</div>}
<div style={{ marginTop: '1rem', background: 'rgba(255,255,255,0.15)', display: 'inline-block', padding: '0.4rem 1.5rem', borderRadius: '30px', fontSize: '0.9rem', fontWeight: 'bold', letterSpacing: '2px' }}>REÇU DE PAIEMENT</div>
<div style={{ fontSize: '0.65rem', opacity: 0.75, marginTop: '0.4rem', fontFamily: 'monospace' }}>N° {numRecu}</div>
</div>
<div style={{ padding: '1.25rem' }}>
<div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem', padding: '0.75rem', background: '#F8FAFC', borderRadius: '10px', borderLeft: '4px solid #0EA5E9' }}>
<div style={{ fontSize: '1.5rem' }}>👤</div>
<div style={{ flex: 1 }}>
<div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{clientNom}</div>
{clientTel && <div style={{ fontSize: '0.72rem', color: '#64748B' }}>📞 {clientTel}</div>}
{clientEmail && <div style={{ fontSize: '0.72rem', color: '#64748B' }}>✉️ {clientEmail}</div>}
{doc.objet && <div style={{ fontSize: '0.72rem', color: '#64748B', marginTop: '0.2rem', fontStyle: 'italic' }}>{doc.objet}</div>}
</div>
<div style={{ textAlign: 'right', fontSize: '0.72rem', color: '#64748B', flexShrink: 0 }}>
<div>Facture</div>
<div style={{ fontWeight: 'bold', fontFamily: 'monospace', color: '#0EA5E9' }}>{doc.numero}</div>
</div>
</div>
<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
<div style={{ background: '#F1F5F9', borderRadius: '8px', padding: '0.6rem 0.75rem' }}>
<div style={{ fontSize: '0.65rem', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.2rem' }}>Date</div>
<div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{paiement.date ? new Date(paiement.date).toLocaleDateString('fr-FR') : '-'}</div>
</div>
<div style={{ background: '#F1F5F9', borderRadius: '8px', padding: '0.6rem 0.75rem' }}>
<div style={{ fontSize: '0.65rem', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.2rem' }}>Mode</div>
<div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{methodeLabels[paiement.methode] || paiement.methode}</div>
</div>
{paiement.methode === 'mobile' && paiement.telephoneMobile && (
<div style={{ background: '#EEF2FF', borderRadius: '8px', padding: '0.6rem 0.75rem' }}>
<div style={{ fontSize: '0.65rem', color: '#94A3B8', textTransform: 'uppercase', marginBottom: '0.2rem' }}>📱 Téléphone</div>
<div style={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: '0.85rem' }}>{paiement.telephoneMobile}</div>
</div>
)}
{paiement.methode === 'mobile' && paiement.idTransaction && (
<div style={{ background: '#EEF2FF', borderRadius: '8px', padding: '0.6rem 0.75rem' }}>
<div style={{ fontSize: '0.65rem', color: '#94A3B8', textTransform: 'uppercase', marginBottom: '0.2rem' }}>🔑 ID Transaction</div>
<div style={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: '0.78rem' }}>{paiement.idTransaction}</div>
</div>
)}
{paiement.reference && paiement.methode !== 'mobile' && (
<div style={{ background: '#F1F5F9', borderRadius: '8px', padding: '0.6rem 0.75rem', gridColumn: 'span 2' }}>
<div style={{ fontSize: '0.65rem', color: '#94A3B8', textTransform: 'uppercase', marginBottom: '0.2rem' }}>Référence</div>
<div style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{paiement.reference}</div>
</div>
)}
</div>
<div style={{ background: 'linear-gradient(135deg, #0EA5E9, #6366F1)', borderRadius: '12px', padding: '1rem', color: 'white', textAlign: 'center', marginBottom: '0.75rem' }}>
<div style={{ fontSize: '0.7rem', opacity: 0.85, marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Montant Encaissé</div>
<div style={{ fontSize: '1.8rem', fontWeight: 'bold', fontFamily: 'monospace' }}>{formatMontant(paiement.montant || 0)}</div>
</div>
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', padding: '0.5rem 0.75rem', background: resteAPayer <= 0 ? '#DCFCE7' : '#FEF2F2', borderRadius: '8px' }}>
<span style={{ color: resteAPayer <= 0 ? '#047857' : '#DC2626', fontWeight: 'bold' }}>{resteAPayer <= 0 ? '🎉 Facture soldée' : '⏳ Reste à payer'}</span>
<span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: resteAPayer <= 0 ? '#065F46' : '#DC2626' }}>{resteAPayer <= 0 ? '—' : formatMontant(resteAPayer)}</span>
</div>
</div>
<div style={{ textAlign: 'center', fontSize: '0.65rem', color: '#94A3B8', padding: '0.75rem', borderTop: '1px solid #E2E8F0' }}>
Généré le {dateGen} à {heureGen} — Merci pour votre paiement !
</div>
</div>
)}

{modele === 'minimaliste' && (
<div id="recu-print-zone" style={{ background: '#fff', border: '1px solid #1E293B', fontFamily: 'Arial, sans-serif', fontSize: '0.82rem', color: '#1E293B', padding: '1.5rem' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '2px solid #1E293B' }}>
<div>
{settings.logo && <img src={settings.logo} alt="logo" style={{ maxHeight: '35px', marginBottom: '0.3rem', filter: 'grayscale(100%)' }} />}
<div style={{ fontWeight: 'bold', fontSize: '1rem' }}>{settings.raisonSociale || 'SOLSTICE'}</div>
{settings.adresse && <div style={{ fontSize: '0.7rem', color: '#475569' }}>{settings.adresse}</div>}
{settings.telephone && <div style={{ fontSize: '0.7rem', color: '#475569' }}>{settings.telephone}</div>}
{settings.nui && <div style={{ fontSize: '0.7rem', color: '#475569' }}>NUI : {settings.nui}</div>}
</div>
<div style={{ textAlign: 'right' }}>
<div style={{ fontWeight: 'bold', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '1px' }}>REÇU</div>
<div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#475569', marginTop: '0.2rem' }}>{numRecu}</div>
<div style={{ fontSize: '0.7rem', color: '#475569', marginTop: '0.2rem' }}>Le {dateGen}</div>
</div>
</div>
<div style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid #E2E8F0' }}>
<div style={{ fontSize: '0.68rem', textTransform: 'uppercase', color: '#94A3B8', marginBottom: '0.3rem', letterSpacing: '0.5px' }}>Reçu de</div>
<div style={{ fontWeight: 'bold' }}>{clientNom}</div>
{clientTel && <div style={{ fontSize: '0.72rem', color: '#475569' }}>{clientTel}</div>}
<div style={{ fontSize: '0.72rem', color: '#475569', marginTop: '0.2rem' }}>Réf. facture : <strong>{doc.numero}</strong></div>
</div>
<table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse', marginBottom: '0.75rem' }}>
<tbody>
<tr><td style={{ padding: '0.25rem 0', color: '#475569' }}>Mode de paiement</td><td style={{ textAlign: 'right', fontWeight: 'bold' }}>{methodeLabels[paiement.methode] || paiement.methode}</td></tr>
{paiement.methode === 'mobile' && paiement.telephoneMobile && <tr><td style={{ padding: '0.25rem 0', color: '#475569' }}>N° téléphone</td><td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{paiement.telephoneMobile}</td></tr>}
{paiement.methode === 'mobile' && paiement.idTransaction && <tr><td style={{ padding: '0.25rem 0', color: '#475569' }}>ID transaction</td><td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: '0.72rem' }}>{paiement.idTransaction}</td></tr>}
{paiement.reference && paiement.methode !== 'mobile' && <tr><td style={{ padding: '0.25rem 0', color: '#475569' }}>Référence</td><td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{paiement.reference}</td></tr>}
{doc.objet && <tr><td style={{ padding: '0.25rem 0', color: '#475569' }}>Objet</td><td style={{ textAlign: 'right' }}>{doc.objet}</td></tr>}
</tbody>
</table>
<div style={{ borderTop: '2px solid #1E293B', paddingTop: '0.75rem' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.78rem' }}>
<span style={{ color: '#475569' }}>Total facture</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(doc.total || 0)}</span>
</div>
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.4rem', paddingTop: '0.4rem', borderTop: '1px solid #1E293B' }}>
<span style={{ textTransform: 'uppercase' }}>Montant reçu</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(paiement.montant || 0)}</span>
</div>
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: resteAPayer <= 0 ? '#475569' : '#DC2626' }}>
<span>{resteAPayer <= 0 ? '✓ Facture entièrement réglée' : 'Reste à payer'}</span>
<span style={{ fontFamily: 'monospace' }}>{resteAPayer <= 0 ? '' : formatMontant(resteAPayer)}</span>
</div>
</div>
<div style={{ marginTop: '1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', fontSize: '0.72rem', color: '#94A3B8' }}>
<div style={{ textAlign: 'center' }}><div style={{ borderTop: '1px solid #CBD5E1', paddingTop: '0.3rem', marginTop: '2rem' }}>Signature émetteur</div></div>
<div style={{ textAlign: 'center' }}><div style={{ borderTop: '1px solid #CBD5E1', paddingTop: '0.3rem', marginTop: '2rem' }}>Signature bénéficiaire</div></div>
</div>
<div style={{ textAlign: 'center', fontSize: '0.65rem', color: '#CBD5E1', marginTop: '1rem' }}>Document généré le {dateGen} à {heureGen}</div>
</div>
)}

{modele === 'minimaliste-duplicata' && (
<div id="recu-print-zone" style={{ background: '#fff', fontFamily: 'Arial, sans-serif', fontSize: '0.82rem', color: '#1E293B' }}>

{/* Bloc helper réutilisable pour un seul reçu minimaliste */}
{(() => {
const RecuMini = ({ label }) => (
<div style={{ border: '1px solid #1E293B', padding: '1.2rem', background: '#fff', width: '100%' }}>
{/* Header */}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.9rem', paddingBottom: '0.6rem', borderBottom: '2px solid #1E293B' }}>
<div>
{settings.logo && <img src={settings.logo} alt="logo" style={{ maxHeight: '32px', marginBottom: '0.25rem', filter: 'grayscale(100%)' }} />}
<div style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>{settings.raisonSociale || 'SOLSTICE'}</div>
{settings.adresse && <div style={{ fontSize: '0.68rem', color: '#475569' }}>{settings.adresse}</div>}
{settings.telephone && <div style={{ fontSize: '0.68rem', color: '#475569' }}>{settings.telephone}</div>}
{settings.nui && <div style={{ fontSize: '0.68rem', color: '#475569' }}>NUI : {settings.nui}</div>}
</div>
<div style={{ textAlign: 'right' }}>
<div style={{ fontWeight: 'bold', fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '1px' }}>REÇU</div>
<div style={{ background: '#1E293B', color: '#fff', fontSize: '0.6rem', padding: '0.1rem 0.4rem', borderRadius: '3px', marginTop: '0.2rem', display: 'inline-block', letterSpacing: '1px' }}>{label}</div>
<div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#475569', marginTop: '0.25rem' }}>{numRecu}</div>
<div style={{ fontSize: '0.65rem', color: '#475569' }}>Le {dateGen}</div>
</div>
</div>
{/* Client */}
<div style={{ marginBottom: '0.6rem', paddingBottom: '0.6rem', borderBottom: '1px solid #E2E8F0' }}>
<div style={{ fontSize: '0.63rem', textTransform: 'uppercase', color: '#94A3B8', marginBottom: '0.2rem', letterSpacing: '0.5px' }}>Reçu de</div>
<div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{clientNom}</div>
{clientTel && <div style={{ fontSize: '0.68rem', color: '#475569' }}>{clientTel}</div>}
{clientEmail && <div style={{ fontSize: '0.68rem', color: '#475569' }}>{clientEmail}</div>}
<div style={{ fontSize: '0.68rem', color: '#475569', marginTop: '0.15rem' }}>Réf. facture : <strong>{doc.numero}</strong></div>
{doc.objet && <div style={{ fontSize: '0.68rem', color: '#475569', fontStyle: 'italic' }}>{doc.objet}</div>}
</div>
{/* Détails paiement */}
<table style={{ width: '100%', fontSize: '0.73rem', borderCollapse: 'collapse', marginBottom: '0.6rem' }}>
<tbody>
<tr style={{ borderBottom: '1px solid #F1F5F9' }}>
<td style={{ padding: '0.2rem 0', color: '#475569' }}>Date</td>
<td style={{ textAlign: 'right', fontWeight: 'bold' }}>{paiement.date ? new Date(paiement.date).toLocaleDateString('fr-FR') : '-'}</td>
</tr>
<tr style={{ borderBottom: '1px solid #F1F5F9' }}>
<td style={{ padding: '0.2rem 0', color: '#475569' }}>Mode de paiement</td>
<td style={{ textAlign: 'right', fontWeight: 'bold' }}>{methodeLabels[paiement.methode] || paiement.methode}</td>
</tr>
{paiement.methode === 'mobile' && paiement.telephoneMobile && (
<tr style={{ borderBottom: '1px solid #F1F5F9' }}>
<td style={{ padding: '0.2rem 0', color: '#475569' }}>📱 N° téléphone</td>
<td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{paiement.telephoneMobile}</td>
</tr>
)}
{paiement.methode === 'mobile' && paiement.idTransaction && (
<tr style={{ borderBottom: '1px solid #F1F5F9' }}>
<td style={{ padding: '0.2rem 0', color: '#475569' }}>🔑 ID transaction</td>
<td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: '0.65rem' }}>{paiement.idTransaction}</td>
</tr>
)}
{paiement.reference && paiement.methode !== 'mobile' && (
<tr style={{ borderBottom: '1px solid #F1F5F9' }}>
<td style={{ padding: '0.2rem 0', color: '#475569' }}>Référence</td>
<td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{paiement.reference}</td>
</tr>
)}
</tbody>
</table>
{/* Montants */}
<div style={{ borderTop: '2px solid #1E293B', paddingTop: '0.6rem', marginBottom: '0.8rem' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontSize: '0.73rem' }}>
<span style={{ color: '#475569' }}>Total facture</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(doc.total || 0)}</span>
</div>
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', fontWeight: 'bold', marginBottom: '0.3rem', paddingTop: '0.3rem', borderTop: '1px solid #1E293B' }}>
<span style={{ textTransform: 'uppercase' }}>Montant reçu</span>
<span style={{ fontFamily: 'monospace' }}>{formatMontant(paiement.montant || 0)}</span>
</div>
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.73rem', color: resteAPayer <= 0 ? '#475569' : '#DC2626' }}>
<span>{resteAPayer <= 0 ? '✓ Facture entièrement réglée' : 'Reste à payer'}</span>
<span style={{ fontFamily: 'monospace' }}>{resteAPayer <= 0 ? '' : formatMontant(resteAPayer)}</span>
</div>
</div>
{/* Signatures */}
<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', fontSize: '0.65rem', color: '#94A3B8', marginBottom: '0.6rem' }}>
<div style={{ textAlign: 'center' }}>
<div style={{ borderTop: '1px solid #CBD5E1', paddingTop: '0.25rem', marginTop: '1.8rem' }}>Signature émetteur</div>
</div>
<div style={{ textAlign: 'center' }}>
<div style={{ borderTop: '1px solid #CBD5E1', paddingTop: '0.25rem', marginTop: '1.8rem' }}>Signature bénéficiaire</div>
</div>
</div>
{/* Pied */}
<div style={{ textAlign: 'center', fontSize: '0.6rem', color: '#CBD5E1', paddingTop: '0.4rem', borderTop: '1px dashed #E2E8F0' }}>
Généré le {dateGen} à {heureGen}
</div>
</div>
);

return (
<div style={{ display: 'flex', flexDirection: 'column', gap: '0', width: '100%' }}>
{/* ─── ORIGINAL ─── */}
<RecuMini label="ORIGINAL" />

{/* ─── Ligne de coupe ─── */}
<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0', userSelect: 'none' }}>
<div style={{ flex: 1, borderTop: '1.5px dashed #94A3B8' }} />
<span style={{ fontSize: '0.7rem', color: '#94A3B8', fontStyle: 'italic', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
✂️ Ligne de découpe
</span>
<div style={{ flex: 1, borderTop: '1.5px dashed #94A3B8' }} />
</div>

{/* ─── DUPLICATA ─── */}
<RecuMini label="DUPLICATA" />
</div>
);
})()}
</div>
)}

{modele === 'thermique' && (
<div id="recu-print-zone" style={{ background: '#fff', fontFamily: '"Courier New", monospace', fontSize: '0.8rem', color: '#000', padding: '0.75rem', maxWidth: '300px', margin: '0 auto', border: '1px dashed #ccc' }}>
<div style={{ textAlign: 'center', marginBottom: '0.4rem' }}>================================</div>
<div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
<div style={{ fontWeight: 'bold', fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '1px' }}>{settings.raisonSociale || 'SOLSTICE'}</div>
{settings.adresse && <div style={{ fontSize: '0.72rem' }}>{settings.adresse}</div>}
{settings.telephone && <div style={{ fontSize: '0.72rem' }}>Tel: {settings.telephone}</div>}
{settings.nui && <div style={{ fontSize: '0.72rem' }}>NUI: {settings.nui}</div>}
</div>
<div style={{ textAlign: 'center', marginBottom: '0.4rem' }}>================================</div>
<div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '2px' }}>RECU DE PAIEMENT</div>
<div style={{ textAlign: 'center', fontSize: '0.65rem', marginBottom: '0.5rem', fontFamily: 'monospace' }}>{numRecu}</div>
<div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>--------------------------------</div>
<div style={{ marginBottom: '0.5rem', fontSize: '0.75rem' }}>
<div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Date:</span><span>{paiement.date ? new Date(paiement.date).toLocaleDateString('fr-FR') : '-'}</span></div>
<div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Client:</span><span style={{ maxWidth: '55%', textAlign: 'right', wordBreak: 'break-word' }}>{clientNom}</span></div>
<div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Facture:</span><span>{doc.numero}</span></div>
<div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Mode:</span><span>{methodeLabels[paiement.methode] || paiement.methode}</span></div>
{paiement.methode === 'mobile' && paiement.telephoneMobile && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Tel Mobile:</span><span>{paiement.telephoneMobile}</span></div>}
{paiement.methode === 'mobile' && paiement.idTransaction && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem' }}><span>ID Trans.:</span><span style={{ maxWidth: '60%', textAlign: 'right', wordBreak: 'break-all' }}>{paiement.idTransaction}</span></div>}
{paiement.reference && paiement.methode !== 'mobile' && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Ref.:</span><span>{paiement.reference}</span></div>}
</div>
<div style={{ textAlign: 'center', marginBottom: '0.4rem' }}>================================</div>
<div style={{ fontSize: '0.75rem', marginBottom: '0.5rem' }}>
<div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total facture:</span><span>{formatMontant(doc.total || 0)}</span></div>
<div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '1rem', marginTop: '0.3rem', paddingTop: '0.3rem', borderTop: '1px solid #000' }}>
<span>MONTANT RECU:</span>
<span>{formatMontant(paiement.montant || 0)}</span>
</div>
{resteAPayer > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginTop: '0.2rem' }}><span>Reste a payer:</span><span>{formatMontant(resteAPayer)}</span></div>}
{resteAPayer <= 0 && <div style={{ textAlign: 'center', fontWeight: 'bold', marginTop: '0.3rem' }}>*** SOLDE ***</div>}
</div>
<div style={{ textAlign: 'center', marginBottom: '0.4rem' }}>================================</div>
<div style={{ textAlign: 'center', fontSize: '0.65rem' }}>
<div>{dateGen} {heureGen}</div>
<div style={{ marginTop: '0.3rem', fontWeight: 'bold' }}>Merci pour votre confiance!</div>
</div>
<div style={{ textAlign: 'center', marginTop: '0.4rem' }}>================================</div>
</div>
)}

</div>

<div className="modal-footer">
<button className="btn btn-secondary" onClick={() => setShowRecuModal(false)}>
Fermer
</button>
<button
className="btn btn-primary"
style={{ background: modele === 'classique' ? '#6366F1' : modele === 'moderne' ? '#0EA5E9' : (modele === 'minimaliste' || modele === 'minimaliste-duplicata') ? '#1E293B' : '#374151', border: 'none', color: 'white' }}
onClick={printRecu}
>
🖨️ Imprimer / PDF
</button>
</div>
</div>
</div>
);
})()}

</div>
);
}

export default AppPrincipale
